// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * fix-sprint-20260511-2113 — Extracted top-level VI sections.
 *
 * vi.ts exceeded the ~1MB MCP transport payload limit, blocking any
 * push of i18n updates against it. The three largest top-level sections
 * (module_tips, framework_requirements, admin) live here so each file
 * stays under the limit. Imports are spread back into vi.ts at module
 * load — runtime shape is unchanged.
 *
 * Folding these back into vi.ts during a future non-MCP authoring pass
 * is harmless; the index.ts deepMerge already handles the canonical
 * `{ vi: { module_tips: ..., framework_requirements: ..., admin: ... } }`
 * shape either way.
 */
export const viAdmin = {
    nav: {
        rights_requests: 'Yêu cầu quyền',
        templates: 'Mẫu biểu',
        response_templates: 'Mẫu Phản Hồi',
        data_mapping: 'Bản đồ dữ liệu',
        governance: 'Quản trị',
        consent: 'Đồng thuận',
        assessments: 'Đánh giá tác động',
        classification: 'Phân loại',
        monitoring: 'Giám sát',
        incidents: 'Sự cố',
        onboarding: 'Khởi tạo',
        connectors: 'Kết nối',
        vendor_risk: 'Rủi ro nhà cung cấp',
        deletion_orchestration: 'Điều phối xóa dữ liệu',
        discovery: 'Khám phá dữ liệu',
        incident_ops: 'Vận hành sự cố',
        program_governance: 'Quản trị chương trình',
        access_accountability: 'Trách nhiệm truy cập',
        consent_properties: 'Thuộc tính đồng thuận',
        localization: 'Bản địa hóa',
        regulatory: 'Quy định',
        partner_workspace: 'Không gian đối tác',
        audit_trail: 'Nhật ký kiểm toán',
        // Sprint 47.5.2 — Forseti AI Legal Advisor.
        forseti: 'Forseti',
        settings: 'Cài đặt',
        help_center: 'Trung tâm trợ giúp',
        dashboard: 'Tổng quan',
        platform: 'Nền tảng',
        integrations: 'Tích hợp',
        cmp_dsar_sync: 'Đồng bộ CMP DSAR',
        cmp_integration: 'Tích hợp CMP',
        purpose_mapping: 'Ánh xạ mục đích',
        webhook_config: 'Cấu hình Webhook',
        bulk_sync: 'Đồng bộ hàng loạt',
        privacy_scanner: 'Quét quyền riêng tư',
        privacy_monitoring: 'Giám sát quyền riêng tư',
        real_time_scanner: 'Quét thời gian thực',
        compliance_forms: 'Biểu mẫu tuân thủ',
        forms_dashboard: 'Tổng quan biểu mẫu',
        form_templates: 'Mẫu biểu mẫu',
        form_catalog: 'Danh mục biểu mẫu',
        pack_catalog: 'Danh mục gói',
        org_groups: 'Nhóm tổ chức',
        consolidation: 'Tổng hợp',
        organizations: 'Tổ chức',
        org_list: 'Tất cả tổ chức',
        intake_configs: 'Cấu hình tiếp nhận',
        task_management: 'Quản lý tác vụ',
        unified_audit: 'Kiểm toán Tổng hợp',
        evidence_packs: 'Gói bằng chứng kiểm toán',
        framework_evidence_packs: 'Gói bằng chứng theo khung tuân thủ',
        audit_periods: 'Kỳ Kiểm toán',
        regulatory_updates: 'Cập nhật Quy định',
        departments: 'Phòng ban',
    },
    sidebar_category_data_privacy_rights: 'Quyền riêng tư & dữ liệu',
    sidebar_category_vendor_third_party: 'Quản lý nhà cung cấp & bên thứ ba',
    sidebar_category_risk_assessment: 'Rủi ro & đánh giá',
    sidebar_category_operations_automation: 'Vận hành & tự động hóa',
    sidebar_category_sector_overlays: 'Lớp phủ ngành',
    sidebar_category_task_workflow: 'Nhiệm vụ & quy trình',
    // Nhãn nhóm phụ cho sidebar (tầng 3). Thêm trong sprint rút gọn sidebar;
    // chỉ EN + VI có giá trị. 14 locale còn lại quay về EN qua deepMerge cho
    // đến khi Sprint 51.0.0 bổ sung bản dịch.
    sidebar_groups: {
        // Consent
        consent_cmp_webhooks: 'CMP & Webhook',
        // Sprint 62.28.0 — marketing-communications registers.
        marketing_communications: 'Liên lạc quảng cáo',
        // Data Mapping
        data_mapping_ropa: 'Sổ RoPA',
        data_mapping_classification: 'Phân loại',
        data_mapping_governance: 'Quản trị',
        // Access & Accountability
        access_live_monitoring: 'Giám sát trực tiếp',
        access_reviews_investigations: 'Đánh giá & Điều tra',
        access_evidence_policies: 'Bằng chứng & Chính sách',
        // Classification
        classification_taxonomies: 'Phân loại & Nhãn',
        classification_workflow: 'Quy trình',
        classification_insights: 'Thông tin chi tiết',
        // Connectors
        connectors_runtime: 'Thực thi',
        connectors_due_diligence: 'Thẩm định',
        connectors_coverage: 'Phạm vi',
        // Consent Properties
        cp_definitions: 'Định nghĩa',
        cp_lifecycle: 'Vòng đời',
        cp_quality: 'Chất lượng & Bằng chứng',
        // Deletion Orchestration
        deletion_execution: 'Thực thi',
        deletion_vendors: 'Nhà cung cấp',
        deletion_evidence: 'Bằng chứng & Tích hợp',
        // Discovery
        discovery_rules: 'Quy tắc & Mẫu',
        discovery_scanning: 'Quét',
        discovery_review: 'Quy trình xem xét',
        discovery_insights: 'Thông tin chi tiết',
        // Governance
        governance_oversight: 'Giám sát',
        governance_reporting: 'Báo cáo',
        governance_vendors: 'Nhà cung cấp & Xuyên biên giới',
        governance_lifecycle: 'Vòng đời',
        // Incident Ops
        incident_ops_dashboards: 'Bảng điều khiển',
        incident_ops_sla: 'Cấu hình SLA',
        incident_ops_playbooks: 'Kịch bản',
        incident_ops_vendor_regulator: 'Nhà cung cấp & Cơ quan',
        incident_ops_evidence: 'Bằng chứng & Xem xét',
        // Incidents
        incidents_workload: 'Khối lượng công việc',
        incidents_regulatory: 'Quy định',
        incidents_reporting: 'Báo cáo',
        // Localization
        localization_content: 'Nội dung',
        // Sprint 63.42.2 — mục Quy định (`admin.nav.regulatory`): nhóm Quản lý khung tuân thủ
        // là nhóm Bản địa hóa › Quy định trước đây. Đã duyệt R-44 (lô
        // 2026-11-01-63.42.2-sidebar-regulatory-sector-overlays): không dùng "Khung tuân thủ"
        // vì mục đầu tiên của nhóm (`localization.nav.frameworks`) đã mang đúng nhãn đó.
        regulatory_frameworks: 'Quản lý khung tuân thủ',
        // Sprint 63.43.0 — R-44 batch 2026-10-31-63.43.0-module-30-navigation.
        localization_regulatory_registers: 'Sổ đăng ký pháp lý',
        localization_penalties_enforcement: 'Chế tài và cưỡng chế',
        // Sprint 63.44.0 (F1) — không còn hiển thị trên thanh điều hướng; giữ lại vì
        // registry R-44 dùng khóa này làm phạm vi của thuật ngữ đã đăng ký (xem en.ts).
        localization_sector_overlays: 'Lớp phủ ngành',
        // Sprint 63.43.0 — Phân hệ Lớp phủ ngành (Module 30): nhóm quản lý và nhóm theo ngành.
        sector_overlays_management: 'Quản lý lớp phủ',
        sector_overlays_banking_payments: 'Ngân hàng và thanh toán',
        sector_overlays_crypto_assets: 'Tài sản mã hóa',
        sector_overlays_manufacturing: 'Sản xuất',
        sector_overlays_food_traceability: 'Truy xuất nguồn gốc thực phẩm',
        sector_overlays_transport_logistics: 'Giao thông vận tải và logistics',
        sector_overlays_public_administration: 'Hành chính công',
        sector_overlays_digital_technology: 'Công nghệ số',
        sector_overlays_publishing: 'Xuất bản',
        sector_overlays_education: 'Giáo dục',
        sector_overlays_cross_sector: 'Liên ngành',
        // Sprint 62.51.0 — R-44 batch 2026-10-24-62.51.0-data-exploitation-leaves.
        localization_data_exploitation: 'Khai thác dữ liệu',
        // Sprint 62.52.0 — R-44 batch 2026-10-27-62.52.0-cybersecurity-assurance-index-leaves.
        localization_cybersecurity_assurance_index: 'Chỉ số bảo đảm an ninh mạng',
        // Sprint 62.49.0 — R-44 batch 2026-10-28-62.49.0-food-traceability-leaves.
        localization_food_traceability: 'Truy xuất nguồn gốc thực phẩm',
        localization_observability: 'Quan sát',
        // Monitoring
        monitoring_metrics: 'Chỉ số',
        monitoring_programs: 'Chương trình',
        monitoring_tasks: 'Tác vụ',
        monitoring_controls: 'Kiểm soát',
        // Organizations
        org_entities: 'Thực thể',
        org_config: 'Cấu hình',
        org_frameworks: 'Khung tuân thủ & Tổng hợp',
        // Partner Workspace
        partner_overview: 'Tổng quan',
        partner_partners: 'Đối tác',
        partner_delivery: 'Giao hàng',
        partner_support: 'Hỗ trợ',
        // Platform
        platform_identity: 'Danh tính & Vai trò',
        platform_access_control: 'Kiểm soát truy cập',
        platform_auth_session: 'Xác thực & Phiên',
        platform_access_reviews: 'Đánh giá truy cập',
        platform_files: 'Tệp',
        platform_notifications: 'Thông báo',
        platform_module_config: 'Cấu hình phân hệ',
        platform_ops: 'Vận hành & Nhật ký',
        // Program Governance
        pg_health: 'Tình trạng chương trình',
        pg_maturity: 'Độ trưởng thành',
        pg_audit: 'Kiểm toán & Bằng chứng',
        pg_training: 'Đào tạo',
        pg_privacy_office: 'Văn phòng bảo vệ dữ liệu cá nhân',
        pg_obligations: 'Nghĩa vụ',
        pg_documents: 'Tài liệu',
        // Vendor Risk
        vr_overview: 'Tổng quan',
        vr_onboarding: 'Tiếp nhận',
        vr_assessment: 'Đánh giá',
        vr_oversight: 'Giám sát',
    },
    tickets: {
        title: 'Danh sách yêu cầu quyền',
        create: 'Tạo yêu cầu mới',
        filters: 'Bộ lọc',
        no_results: 'Không có yêu cầu nào',
        ticket_id: 'Mã phiếu',
        request_type: 'Loại yêu cầu',
        requester: 'Người yêu cầu',
        source: 'Nguồn',
        all_sources: 'Tất cả nguồn',
        assigned_to: 'Phân công cho',
        created: 'Ngày tạo',
        load_error: 'Không thể tải danh sách yêu cầu. Vui lòng thử lại.',
        search_placeholder: 'Tìm kiếm theo mã yêu cầu hoặc người gửi...',
        export_csv: 'Xuất CSV',
        exporting: 'Đang xuất...',
        bulk_export_failed: 'Xuất hàng loạt thất bại',
        // Sprint 43.2.0 batch 4 (E3)
        bulk_export: {
            label: 'Xuất',
            format: {
                pdf: 'Bản tổng hợp PDF',
                zip: 'ZIP (gói bằng chứng theo từng vụ việc)',
                csv: 'CSV',
            },
            warning_over_50: 'Thao tác này sẽ tạo khoảng {count} gói hồ sơ - có thể mất vài phút. Tiếp tục?',
            blocked_over_500: 'Hãy thu hẹp bộ lọc xuống 500 vụ việc hoặc ít hơn trước khi xuất ZIP (hiện tại: {count}).',
            success: 'Đã lưu kết quả xuất hàng loạt vào Trình duyệt Tệp ({count} vụ việc)',
            failed: 'Xuất hàng loạt thất bại',
            continue: 'Tiếp tục',
            cancel: 'Hủy',
        },
        case_creation_failed: 'Tạo trường hợp thất bại',
        business_unit: 'Đơn vị kinh doanh',
        reference: 'Tham chiếu',
        sla: {
            title: 'Trạng thái SLA',
            on_track: '{days} ngày còn lại',
            approaching: 'Gần đến hạn',
            overdue: '{days} ngày quá hạn',
            ack_overdue: '{hours}h quá hạn xác nhận',
            ack_warning: 'Sắp đến hạn xác nhận',
            closed: 'Đã đóng',
            completed_in: 'Hoàn thành trong {days} ngày',
            source_statutory: 'Thời hạn theo quy định',
            source_tenant_target: 'Chỉ tiêu SLA của tổ chức',
            source_platform_default: 'Mặc định của nền tảng, không phải thời hạn theo pháp luật',
            awaiting_activation: 'Chờ hiệu lực từ {date}',
            no_installed_clock: 'Không có thời hạn đã cài đặt nào áp dụng',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            handled_in_legal_matters: 'Được xử lý trong Vụ việc pháp lý',
        },
    },
    templates: {
        title: 'Mẫu biểu phản hồi',
        new: 'Mẫu biểu mới',
        edit: 'Chỉnh sửa mẫu biểu',
        all_types: 'Tất cả loại',
        all_locales: 'Tất cả ngôn ngữ',
        type_access: 'Truy cập/DSAR',
        type_deletion: 'Xóa dữ liệu',
        type_correction: 'Chỉnh sửa',
        type_objection: 'Phản đối',
        type_portability: 'Di chuyển dữ liệu',
        type_other: 'Khác',
        type_restriction: 'Hạn chế',
        type_all: 'Tất cả',
        locale_en: 'Tiếng Anh',
        locale_vi: 'Tiếng Việt',
        locale_all: 'Cả hai',
        name: 'Tên',
        request_type: 'Loại yêu cầu',
        locale: 'Ngôn ngữ',
        subject: 'Chủ đề',
        body: 'Nội dung',
        name_placeholder: 'Tên mẫu biểu...',
        subject_placeholder: 'Chủ đề email/thư...',
        body_placeholder: 'Nội dung mẫu. Sử dụng {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} làm biến thay thế.',
        available_placeholders: 'Các biến thay thế có sẵn:',
        create_template: 'Tạo mẫu biểu',
        preview: 'Xem trước',
        close_preview: 'Đóng xem trước',
        default_badge: 'Mặc định',
        no_templates: 'Không tìm thấy mẫu biểu',
        confirm_delete: 'Xóa mẫu biểu này?',
        load_error: 'Không thể tải mẫu biểu. Vui lòng thử lại.',
        create_error: 'Không thể tạo mẫu biểu',
        update_error: 'Không thể cập nhật mẫu biểu',
        delete_error: 'Không thể xóa mẫu biểu',
        preview_error: 'Không thể xem trước mẫu biểu',
        response_templates_badge: 'Mẫu Phản Hồi',
        browse_form_templates: 'Duyệt Mẫu Biểu Tuân Thủ',
        templates_loaded: 'Đã tải mẫu',
        sample_organization: 'Tổ chức Mẫu',
    },
    detail: {
        requester_info: 'Thông tin người yêu cầu',
        status: 'Trạng thái',
        assignment: 'Phân công',
        notes: 'Ghi chú',
        audit_trail: 'Lịch sử thay đổi',
        add_note: 'Thêm ghi chú',
        case_not_found: 'Không tìm thấy yêu cầu',
        load_error: 'Không thể tải yêu cầu. Vui lòng thử lại.',
        partial_load_failed: 'Không thể tải một số thông tin của yêu cầu này, nên một số phần của trang có thể chưa đầy đủ.',
        partial_load_parts: 'Chưa tải được: {parts}.',
        note_error: 'Không thể thêm ghi chú. Vui lòng thử lại.',
        submitted_from: 'Gửi từ',
        via_cmp: 'quá AesirX CMP',
        cmp_ticket: 'Phiếu CMP:',
        select_template: 'Chọn một Mẫu',
        direction_inbound: 'CMP → GRC',
        direction_outbound: 'GRC → CMP',
        tabs: {
            details: 'Chi tiết',
            evidence: 'Bằng chứng',
            communications: 'Liên lạc',
            escalations: 'Leo thang',
            audit_trail: 'Lịch sử',
            fulfillment: 'Thực hiện',
            response: 'Phản hồi',
            extension: 'Gia hạn',
        },
        requester: {
            name: 'Tên',
            email: 'Email',
            phone: 'Điện thoại',
            language: 'Ngôn ngữ',
            source: 'Nguồn',
            customer_ref: 'Mã khách hàng',
            country: 'Quốc gia',
            // fix-sprint #431 — the country as received, where it was stored as a code.
            country_as_received: '(giá trị nhận được: “{value}”)',
            preferred_channel: 'Kênh ưu tiên',
        },
        // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
        third_party: {
            label: 'Bên xử lý dữ liệu cá nhân hoặc bên thứ ba phải tham gia',
            hint: 'Các bản ghi thời hạn đã cài đặt có thể quy định thời hạn khác khi bên xử lý dữ liệu cá nhân hoặc bên thứ ba phải tham gia. Thay đổi mục này sẽ tính hạn lại hồ sơ từ ngày tiếp nhận theo bản ghi phù hợp; thời gian gia hạn đã được phê duyệt và các khoảng tạm dừng đã kết thúc được cộng vào hạn mới.',
            closed: 'Hồ sơ đã đóng nên không thể thay đổi việc bên xử lý dữ liệu cá nhân hoặc bên thứ ba phải tham gia.',
            confirm_title: 'Tính hạn lại hồ sơ?',
            confirm_body: 'Hồ sơ sẽ được tính hạn lại từ ngày tiếp nhận theo bản ghi thời hạn đã cài đặt phù hợp. Thời gian gia hạn đã được phê duyệt và các khoảng tạm dừng đã kết thúc được cộng vào hạn mới. Hạn cũ và hạn mới đều được ghi nhận.',
            confirm_label: 'Thay đổi và tính hạn lại',
            saved: 'Đã lưu. Hồ sơ hiển thị hạn cũ và hạn mới.',
            redated: 'Đã thay đổi lúc {date}: tính hạn lại từ ngày tiếp nhận. Hạn cũ {old}, hạn mới {new}.',
            not_redated_no_installed_record: 'Đã thay đổi lúc {date}. Không có khung tuân thủ đã cài đặt nào tính hạn cho hồ sơ này, nên hạn ({due}) không thay đổi.',
            not_redated_no_due_date: 'Đã thay đổi lúc {date}. Hồ sơ không có hạn nào được ghi nhận, nên không có gì được tính hạn lại.',
            carried_extension: '{days} ngày gia hạn đã được phê duyệt được cộng vào hạn mới.',
            carried_paused: '{amount} {unit} tạm dừng được cộng vào hạn mới.',
            units: {
                calendar_days: 'ngày theo lịch',
                working_days: 'ngày làm việc',
                hours: 'giờ',
            },
            mapping_invalid: 'Một bản ghi thời hạn đã cài đặt cho loại yêu cầu này không thể đọc được thành một thời hạn, nên không thể xác định lại hạn của hồ sơ và thay đổi chưa được lưu.',
        },
        sla: {
            title: 'SLA',
            on_track: 'Đúng tiến độ',
            due_soon: 'Sắp đến hạn',
            overdue: 'Quá hạn',
            days_remaining: ' ngày còn lại',
            due_date: 'Ngày đến hạn',
            breached: 'Vi phạm',
            paused: 'Tạm dừng',
            total_paused_days: 'Tổng ngày tạm dừng',
            pause_intervals: 'Các khoảng tạm dừng',
            pause_intervals_help: 'Từng khoảng thời gian đồng hồ bị dừng, để phân biệt thời gian chờ người yêu cầu với thời gian xử lý.',
            pause_intervals_none: 'Hồ sơ này chưa ghi nhận lần tạm dừng nào.',
            no_due_date: 'Không có hạn được ghi nhận',
            stopped_case_closed: 'SLA đã dừng — hồ sơ đã đóng',
            pause_interval_open: 'Tạm dừng từ {from} — vẫn đang chờ',
            pause_interval_closed: '{from} đến {to} — {days} ngày',
            pause_interval_by: 'Người tạm dừng: {who}',
            pause_interval_actor_unknown: 'Người tạm dừng: chưa ghi nhận',
            pause_interval_from_status: 'Tạm dừng từ trạng thái {status}',
            pause_interval_from_status_unknown: 'Tạm dừng từ trạng thái: chưa ghi nhận',
            pause_interval_reason_unknown: 'Lý do tạm dừng: chưa ghi nhận',
            pause_interval_resumed_by: 'Người tiếp tục: {who}',
            processing_days: 'Thời gian xử lý',
            waiting_days: 'Thời gian chờ người yêu cầu',
            days_elapsed: 'Số ngày đã qua',
            progress: 'Tiến độ',
            prov_heading_ack: 'Hạn xác nhận',
            prov_heading_fulfil: 'Hạn hoàn thành yêu cầu',
            prov_source_statutory: 'Thời hạn theo quy định: {reference}',
            prov_source_statutory_from_activation: 'Thời hạn theo quy định: {reference}, tính từ ngày {date}, ngày quy định có hiệu lực',
            prov_source_tenant_target: 'Chỉ tiêu SLA của tổ chức: {days} ngày kể từ khi tiếp nhận',
            prov_source_platform_default: 'Mặc định của nền tảng, không phải thời hạn theo pháp luật: {days} ngày kể từ khi tiếp nhận',
            prov_source_none: 'Chưa xác định hạn: không có thời hạn đã cài đặt, chỉ tiêu của tổ chức hay mặc định của nền tảng nào áp dụng',
            prov_due: 'Đến hạn {date}',
            prov_awaiting_activation: 'Chờ hiệu lực từ {date}: thời hạn đã cài đặt {reference} áp dụng từ thời điểm đó và hồ sơ sẽ được xác định hạn theo thời hạn này vào thời điểm đó.',
            prov_no_installed_clock: 'Không có thời hạn đã cài đặt nào áp dụng cho loại yêu cầu này.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            prov_handled_in_legal_matters: 'Được xử lý trong Vụ việc pháp lý: yêu cầu này được tiếp nhận như một vụ việc pháp lý, và vụ việc đó là hồ sơ được xử lý. Bản ghi tiếp nhận này không có thời hạn.',
            prov_open_legal_matter: 'Mở vụ việc pháp lý',
            prov_framework_unnamed: 'một khung tuân thủ đã cài đặt',
            prov_legal_matter_no_access: 'Vụ việc pháp lý hiển thị với người có quyền xem vụ việc pháp lý.',
            prov_excluded: 'Các thời hạn đã cài đặt không xác định được ngày và không được dùng: {reasons}',
            prov_excluded_activation_date_not_stated: '{reference}: không nêu ngày có hiệu lực',
            prov_excluded_framework_record_unreadable: '{reference}: không đọc được bản ghi khung tuân thủ',
            prov_excluded_phase_reference_unresolved: '{reference}: chưa xác định ngày bắt đầu giai đoạn',
        },
        identity: {
            title: 'Xác minh danh tính',
            verified: 'Đã xác minh',
            not_verified: 'Chưa xác minh',
            method: 'Phương pháp',
            notes: 'Ghi chú',
            verified_by: 'Xác minh bởi',
            verified_at: 'Thời gian xác minh',
            warning: 'Danh tính chưa được xác minh.',
            verify_button: 'Xác minh danh tính',
            select_method: '-- Chọn --',
            methods: {
                email_confirmation: 'Xác nhận email',
                id_document: 'Giấy tờ tùy thân',
                phone_call: 'Gọi điện thoại',
                in_person: 'Trực tiếp',
                other: 'Khác',
            },
        },
        closure: {
            reason: 'Lý do đóng',
            summary: 'Tóm tắt đóng',
            summary_placeholder: 'Tóm tắt cách giải quyết yêu cầu...',
            reason_required: 'Cần có tóm tắt và lý do đóng',
            response_not_sent: 'Phải gửi phản hồi chính thức cho chủ thể dữ liệu trước khi đóng vụ việc này',
            reasons: {
                fulfilled: 'Đã thực hiện',
                partially_fulfilled: 'Thực hiện một phần',
                unfounded: 'Không có cơ sở',
                excessive: 'Quá mức',
                withdrawn: 'Đã rút',
                duplicate: 'Trùng lặp',
                identity_unverified: 'Chưa xác minh danh tính',
            },
        },
        // fix-sprint #431 — chuyển vụ việc sang trạng thái chờ sẽ tạm dừng thời hạn,
        // và máy chủ yêu cầu lý do (`pause_reason`).
        pause: {
            reason_label: 'Vì sao vụ việc đang chờ?',
            reason_placeholder: 'Chọn lý do',
            reason_required: 'Chọn lý do chờ trước khi chuyển vụ việc sang trạng thái chờ.',
            reasons: {
                awaiting_subject: 'Chờ chủ thể dữ liệu',
                awaiting_verification: 'Chờ xác minh danh tính',
                third_party_dependency: 'Chờ bên thứ ba',
                other: 'Khác',
            },
        },
        // fix-sprint #431 — từ chối vụ việc cần có mã lý do (`rejection_reason_code`).
        rejection: {
            code_label: 'Lý do từ chối',
            code_placeholder: 'Chọn lý do',
            code_required: 'Chọn lý do từ chối trước khi từ chối vụ việc.',
            description_label: 'Chi tiết lý do từ chối (không bắt buộc)',
            codes: {
                identity_not_verified: 'Chưa xác minh được danh tính',
                manifestly_unfounded: 'Rõ ràng không có cơ sở',
                repetitive: 'Yêu cầu lặp lại',
                exempt: 'Thuộc trường hợp được miễn trừ',
                out_of_scope: 'Ngoài phạm vi',
                duplicate: 'Yêu cầu trùng lặp',
            },
        },
        // fix-sprint #431 — kết quả kiểm tra chuyển dữ liệu xuyên biên giới khi tạo vụ việc.
        cross_border_finding: {
            title: 'Kiểm tra chuyển dữ liệu cá nhân xuyên biên giới khi tạo vụ việc',
            result: 'Kết quả',
            evaluated_at: 'Thời điểm kiểm tra',
            triggered: 'Khớp quy tắc: quốc gia của chủ thể dữ liệu ({subject}) khác với quốc gia của tổ chức ({org}).',
            reasons: {
                request_type_not_disclosing: 'Không kiểm tra thêm: loại yêu cầu này không cung cấp dữ liệu.',
                subject_country_unknown: 'Không khớp: chưa ghi nhận quốc gia của chủ thể dữ liệu.',
                same_country: 'Không khớp: chủ thể dữ liệu ở cùng quốc gia với tổ chức ({org}).',
                // fix-sprint #431 — the organisation records no country: never assumed.
                org_country_not_recorded: 'Chưa kiểm tra: chưa ghi nhận quốc gia của tổ chức.',
                // fix-sprint #431 — a recorded country that reads as no code is not compared.
                subject_country_not_recognised: 'Chưa kiểm tra: quốc gia của chủ thể dữ liệu đã ghi nhận không phải là mã hoặc tên quốc gia mà nền tảng nhận diện được.',
            },
            other_reason: 'Không khớp ({reason}).',
            org_country_pointer: 'Việc kiểm tra chuyển dữ liệu cá nhân xuyên biên giới so sánh quốc gia của chủ thể dữ liệu với quốc gia của tổ chức. Quốc gia của tổ chức được ghi nhận trong hồ sơ tổ chức: Tổ chức › Tất cả tổ chức, tại trường Quốc gia của tổ chức.',
            org_country_link: 'Ghi nhận quốc gia của tổ chức',
            not_evaluated: 'Không thể kiểm tra khi tạo vụ việc.',
            not_recorded: 'Chưa ghi nhận: vụ việc được tạo trước khi việc kiểm tra chạy khi tạo vụ việc.',
            decides_nothing: 'Đây là ghi nhận kết quả của quy tắc. Việc này không thay đổi trạng thái, thời hạn hay người được phân công, và không mở hồ sơ chuyển dữ liệu cá nhân xuyên biên giới.',
            // fix-sprint #431 — xem vụ việc không mở gì; người dùng mở hồ sơ bằng nút.
            current: 'Đánh giá hiện tại',
            dossier: 'Hồ sơ chuyển dữ liệu cá nhân xuyên biên giới',
            dossier_reading: 'Đang đọc…',
            dossier_unreadable: 'Không đọc được thông tin vụ việc này đã có hồ sơ hay chưa.',
            no_dossier: 'Chưa có hồ sơ chuyển dữ liệu cá nhân xuyên biên giới nào được mở cho vụ việc này.',
            dossier_opened: 'Hồ sơ {dossier}, mở lúc {at}.',
            filing: 'Hồ sơ nộp theo quy định',
            filing_no_form: 'Chưa mở: không có khung tuân thủ đã cài đặt nào quy định biểu mẫu nộp cho việc chuyển dữ liệu này.',
            filing_not_opened: 'Chưa mở: không tạo được hồ sơ nộp khi mở hồ sơ chuyển dữ liệu.',
            filing_not_recorded: 'Chưa ghi nhận.',
            open_button: 'Mở hồ sơ chuyển dữ liệu cá nhân xuyên biên giới',
            open_hint: 'Việc mở hồ sơ là thao tác của bạn và được ghi vào nhật ký kiểm toán của vụ việc dưới tên bạn. Nếu một khung tuân thủ đã cài đặt quy định biểu mẫu nộp cho việc chuyển dữ liệu này, hồ sơ nộp theo quy định được mở cùng lúc.',
            opening: 'Đang mở…',
            confirm_title: 'Mở hồ sơ chuyển dữ liệu cá nhân xuyên biên giới?',
            confirm_message: 'Thao tác này mở hồ sơ chuyển dữ liệu cá nhân xuyên biên giới cho vụ việc {case} và, nếu một khung tuân thủ đã cài đặt quy định biểu mẫu nộp cho việc chuyển dữ liệu này, mở cả hồ sơ nộp theo quy định. Thao tác được ghi nhận dưới tên bạn.',
            confirm_label: 'Mở hồ sơ',
            opened_toast: 'Đã mở hồ sơ chuyển dữ liệu cá nhân xuyên biên giới {dossier}.',
            already_open_toast: 'Vụ việc này đã có hồ sơ chuyển dữ liệu cá nhân xuyên biên giới: {dossier}.',
            open_error: 'Không mở được hồ sơ chuyển dữ liệu cá nhân xuyên biên giới.',
            // fix-sprint #431 (gate E12.5 remediation 1) — lý do không mở được hồ sơ.
            not_detected: 'Hiện không phát hiện việc chuyển dữ liệu cá nhân xuyên biên giới đối với vụ việc này nên không có hồ sơ nào được mở.',
            case_not_found: 'Không tìm thấy vụ việc này.',
            // Gate E12.5 remediation 1 — thử mở lại hồ sơ nộp; quốc gia được so sánh và nguồn.
            filing_opening: 'Đang được mở.',
            retry_filing_button: 'Thử mở lại hồ sơ nộp theo quy định',
            retry_filing_hint: 'Không mở được hồ sơ nộp theo quy định cùng với hồ sơ này. Việc thử mở lại là thao tác của bạn và được ghi vào nhật ký kiểm toán của vụ việc dưới tên bạn.',
            retry_filing_failed: 'Vẫn chưa mở được hồ sơ nộp theo quy định.',
            filing_retried_toast: 'Đã mở hồ sơ nộp theo quy định {filing}.',
            // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
            filing_opening_stalled: 'Chưa mở: yêu cầu đang mở hồ sơ nộp đã dừng trước khi hoàn tất.',
            retry_filing_in_progress: 'Một yêu cầu khác đang mở hồ sơ nộp theo quy định nên lần thử này không bắt đầu thao tác nào.',
            retry_filing_no_form: 'Chưa mở hồ sơ nộp theo quy định: hiện không có khung tuân thủ đã cài đặt nào quy định biểu mẫu nộp cho việc chuyển dữ liệu này.',
            retry_filing_unavailable_service: 'Lần thử này không bắt đầu thao tác nào: hệ thống này không mở được hồ sơ nộp theo quy định.',
            retry_filing_unavailable_org_country: 'Lần thử này không bắt đầu thao tác nào: hồ sơ này không ghi nhận quốc gia của tổ chức, thông tin cần có để mở hồ sơ nộp theo quy định.',
            confirm_retry_title: 'Thử mở lại hồ sơ nộp theo quy định?',
            confirm_retry_message: 'Thao tác này thử mở lại hồ sơ nộp theo quy định cho hồ sơ {dossier} của vụ việc {case}. Thao tác được ghi nhận dưới tên bạn.',
            confirm_retry_label: 'Thử mở lại',
            subject_compared: 'Quốc gia của chủ thể dữ liệu được so sánh',
            subject_compared_value: '{country}, lấy từ {source}',
            subject_sources: {
                residence_country: 'quốc gia cư trú ghi nhận cùng yêu cầu',
                case_country: 'trường Quốc gia của vụ việc',
                address_country: 'quốc gia trong địa chỉ của chủ thể dữ liệu',
            },
        },
        export_title: 'Xuất dữ liệu',
        export_pdf: 'Xuất PDF',
        export_zip: 'Xuất ZIP',
        // Sprint 43.1.0 batch 4
        case_file_pdf: 'Xuất PDF (Hồ sơ vụ việc)',
        evidence_pack_zip: 'Xuất ZIP (Gói bằng chứng)',
        // fix-sprint #431 (gate E12.5 remediation 1) — a prompt to review, not a finding that a dossier is required.
        cross_border_warning: 'Yêu cầu này có thể liên quan đến việc chuyển dữ liệu cá nhân xuyên biên giới. Hãy rà soát xem đánh giá tác động chuyển dữ liệu cá nhân xuyên biên giới của bạn đã bao quát trường hợp này chưa.',
        cross_border_warning_link: 'Xem kết quả kiểm tra chuyển dữ liệu cá nhân xuyên biên giới',
        // fix-sprint #431 (gate E12.5 remediation 1) — lý do thay đổi trạng thái bị từ chối.
        status_errors: {
            invalid_transition: 'Vụ việc không thể chuyển sang trạng thái này từ trạng thái hiện tại.',
            invalid_sub_status: 'Trạng thái chi tiết đã chọn không áp dụng cho trạng thái này.',
            assignment_required: 'Hãy phân công vụ việc trước khi chuyển sang Đang xử lý.',
            identity_verification_required: 'Hãy xác minh danh tính người yêu cầu trước khi thực hiện yêu cầu.',
            closure_delivery_required: 'Hãy ghi nhận cách thức và thời điểm gửi phản hồi trước khi đóng vụ việc.',
            invalid_value: 'Chưa lưu thay đổi: còn thiếu một giá trị hoặc có giá trị không được chấp nhận.',
        },
        // Sprint 43.2.0 batch 4 (E5)
        breach: {
            flag_btn: 'Gắn cờ gây vi phạm dữ liệu',
            modal: {
                title: 'Gắn cờ vụ việc là nguyên nhân vi phạm dữ liệu',
                type_label: 'Loại vi phạm',
                type: {
                    misdirected_response: 'Trả lời sai người nhận',
                    unauthorised_disclosure: 'Tiết lộ trái phép',
                    identity_mismatch: 'Không khớp danh tính',
                    other: 'Khác',
                },
                description: 'Mô tả (không bắt buộc)',
                description_placeholder: 'Điều gì đã xảy ra? (tối đa 1000 ký tự)',
                confirm: 'Gắn cờ và bắt đầu đồng hồ thông báo',
                cancel: 'Hủy',
                flagging: 'Đang gắn cờ...',
            },
            banner: {
                title: 'Đã gắn cờ vi phạm dữ liệu: {type}',
                detected_at: 'Phát hiện lúc {timestamp}',
                filing_deadline: 'Hạn thông báo: còn {hours} giờ',
                overdue: 'Hạn thông báo: QUÁ HẠN',
            },
            toasts: {
                flagged: 'Đã gắn cờ vụ việc là nguyên nhân vi phạm dữ liệu - đồng hồ thông báo bắt đầu',
                flag_failed: 'Không thể gắn cờ vụ việc',
            },
        },
        export: {
            generating: 'Đang tạo... vui lòng đợi',
            success: 'Đã lưu file xuất vào Trình duyệt Tệp',
            failed: 'Xuất thất bại - kiểm tra nhật ký kiểm toán',
        },
        no_notes: 'Chưa có ghi chú',
        assign_placeholder: 'ID người dùng để phân công...',
        reassign: 'Phân công lại',
        evidence: {
            checklist_title: 'Danh mục bằng chứng',
            complete_pct: '% hoàn thành',
            no_checklist: 'Không có mục nào',
            attachments: 'Đính kèm',
            upload_attachment: 'Tải lên tệp đính kèm',
            upload_button: 'Tải lên',
            uploading: 'Đang tải...',
            attachment_uploaded: 'Tải lên thành công',
            upload_error: 'Tải lên thất bại',
            category: 'Danh mục',
            categories: {
                identity_proof: 'Bằng chứng danh tính',
                correspondence: 'Thư từ',
                internal_data: 'Dữ liệu nội bộ',
                legal: 'Pháp lý',
                other: 'Khác',
            },
            no_attachments: 'Không có đính kèm',
            references_title: 'Tham chiếu bằng chứng',
            no_references: 'Không có tham chiếu',
            add_reference: 'Thêm tham chiếu',
            ref_type: 'Loại',
            ref_value: 'Giá trị',
            ref_desc: 'Mô tả',
            ref_value_placeholder: 'URL, email ID, hoặc tham chiếu...',
            ref_desc_placeholder: 'Mô tả...',
            ref_types: {
                url: 'URL',
                email_id: 'Email ID',
                crm_record: 'Bản ghi CRM',
                ticket_ref: 'Tham chiếu phiếu',
                other: 'Khác',
            },
        },
        communications: {
            title: 'Liên lạc',
            add: 'Thêm tin nhắn',
            no_records: 'Chưa có liên lạc nào',
            direction: 'Hướng',
            channel: 'Kênh',
            visibility: 'Hiển thị',
            subject: 'Chủ đề',
            body: 'Nội dung',
            send: 'Gửi',
            inbound: 'Đến',
            outbound: 'Đi',
            internal: 'Nội bộ',
            external: 'Bên ngoài',
            tab_all: 'Tất cả',
            tab_internal: 'Nội bộ',
            tab_external: 'Bên ngoài',
            subject_placeholder: 'Chủ đề (tùy chọn)',
            body_placeholder: 'Nội dung tin nhắn...',
            channels: {
                email: 'Email',
                portal: 'Cổng thông tin',
                phone: 'Điện thoại',
                postal: 'Thư tín',
                in_person: 'Trực tiếp',
            },
            apply_template: 'Áp Dụng Mẫu',
            select_template: 'Chọn mẫu...',
            no_templates: 'Không có mẫu nào khả dụng',
            templates_load_error: 'Không tải được mẫu',
            template_applied: 'Đã áp dụng mẫu vào phản hồi.',
            template_apply_error: 'Không thể áp dụng mẫu.',
        },
        escalations: {
            title: 'Lịch sử leo thang',
            no_events: 'Không có sự kiện leo thang',
            tier: 'Cấp độ',
            threshold: 'Ngưỡng',
            action: 'Hành động',
            target: 'Mục tiêu',
            triggered_at: 'Thời gian kích hoạt',
            // fix-sprint #431 (phán quyết của chủ sản phẩm 2026-10-03) — cấp đã làm gì.
            outcome: 'Kết quả',
            outcome_acted: 'Đã thực hiện',
            outcome_baselined: 'Đã vượt qua trước khi bật tự động leo thang',
            // fix-sprint #431 — cấp được ghi nhận do một lần sửa thang leo thang.
            outcome_baselined_ladder_edit: 'Đã vượt qua trước khi cấp này được thêm, bật hoặc hạ ngưỡng',
            outcome_baselined_detail: 'Không ai được thông báo và vụ việc không bị phân công lại.',
        },
        cmp_sync: {
            title: 'Đồng bộ AesirX CMP',
            sync_status: 'Trạng thái đồng bộ',
            last_synced: 'Đồng bộ lần cuối',
            not_synced: 'Chưa đồng bộ',
            resync: 'Đồng bộ lại',
            syncing: 'Đang đồng bộ...',
            sync_history: 'Lịch sử đồng bộ (10 gần nhất)',
            direction: 'Hướng',
            time: 'Thời gian',
            retries: 'Số lần thử lại',
            error_col: 'Lỗi',
        },
        audit: {
            filter_event_placeholder: 'Lọc theo loại sự kiện...',
            filter_actor_placeholder: 'Lọc theo người thực hiện...',
        },
        fulfillment: {
            title: 'Thực hiện theo phòng ban',
            assign_departments: 'Phân công phòng ban',
            department: 'Phòng ban',
            assignee: 'Người thực hiện',
            deadline: 'Hạn chót',
            evidence_count: 'Bằng chứng',
            signoff: 'Ký xác nhận',
            signed_off: 'Đã ký',
            pending_signoff: 'Chờ ký',
            start_button: 'Bắt đầu',
            complete_button: 'Đánh dấu hoàn thành',
            signoff_button: 'Ký xác nhận',
            identity_required: 'Yêu cầu xác minh danh tính trước khi quản lý nhiệm vụ thực hiện.',
            remove_task: 'Xoá nhiệm vụ',
            remove_success: 'Đã xoá nhiệm vụ thực hiện.',
            no_tasks: 'Chưa có nhiệm vụ thực hiện nào.',
            tasks_table: 'Nhiệm vụ thực hiện',
            progress_label: 'Tiến độ thực hiện',
            overdue: 'quá hạn',
            assigned_success: 'Đã phân công nhiệm vụ thực hiện.',
            assign_error: 'Không thể phân công nhiệm vụ.',
            signoff_success: 'Đã ghi nhận ký xác nhận.',
            signoff_error: 'Không thể ghi nhận ký xác nhận.',
            add_evidence_btn: 'Thêm bằng chứng',
            evidence_uploaded: 'Đã tải lên và liên kết bằng chứng',
            evidence_upload_error: 'Tải lên bằng chứng thất bại',
            suggest_data_stores: 'Gợi ý kho dữ liệu',
            suggest_error: 'Không thể tải gợi ý kho dữ liệu.',
            suggest_stores_title: 'Kho dữ liệu được đề xuất',
            suggest_stores_none: 'Không có kho dữ liệu nào phù hợp với hồ sơ này. Cấu hình hệ thống trong Bản đồ Dữ liệu để cải thiện gợi ý.',
            suggest_stores_add: 'Thêm vào hồ sơ',
            suggest_stores_added: 'Đã thêm kho dữ liệu vào hồ sơ',
            no_departments_title: 'Chưa cấu hình phòng ban',
            no_departments_body: 'Phòng ban giúp định tuyến nhiệm vụ thực hiện DSR (Bảo mật, Pháp chế, HR, IT). Cài đặt dữ liệu mẫu hoặc yêu cầu quản trị viên cấu hình phòng ban.',
            no_departments_cta: 'Cài đặt dữ liệu mẫu',
            department_placeholder: 'Tên phòng ban...',
            assignee_placeholder: 'ID người dùng...',
            assign_button: 'Phân công',
            status_pending: 'Chờ xử lý',
            status_in_progress: 'Đang thực hiện',
            status_completed: 'Hoàn thành',
            status_verified: 'Đã xác minh',
            confirm_approve: 'Bạn có chắc muốn phê duyệt gia hạn này?',
            confirm_deny: 'Bạn có chắc muốn từ chối gia hạn này?',
        },
        response: {
            title: 'Tạo phản hồi',
            select_template: 'Mẫu phản hồi',
            choose_template: 'Chọn mẫu...',
            generate: 'Tạo phản hồi',
            preview: 'Xem trước phản hồi',
            send: 'Gửi phản hồi',
            // Sprint 43.1.0 batch 4
            delivery_method: {
                label: 'Phương thức gửi',
                email: 'Email',
                portal: 'Cổng dịch vụ',
                postal: 'Bưu điện',
                in_person: 'Trực tiếp',
            },
            recipient: {
                email: 'Email người nhận',
                portal: 'ID cổng dịch vụ',
                address: 'Địa chỉ bưu điện',
            },
            auto_generate_btn: 'Tạo và gửi phản hồi',
            auto_generate_success: 'Đã tạo và gửi phản hồi',
            auto_generate_missing_recipient: 'Vui lòng cung cấp người nhận trước khi gửi.',
            auto_fields: 'Trường tự động điền',
            case_id: 'Mã yêu cầu',
            requester_name: 'Tên người yêu cầu',
            rights_type: 'Loại quyền',
            history: 'Lịch sử phản hồi',
            generated_at: 'Tạo lúc',
            confirmation: 'Xác nhận',
            sent_label: 'Đã gửi',
            draft_label: 'Nháp',
            generated_success: 'Đã tạo phản hồi thành công.',
            generate_error: 'Không thể tạo phản hồi.',
            sent_success: 'Đã gửi phản hồi thành công.',
            send_error: 'Không thể gửi phản hồi.',
            load_error: 'Không thể tải mẫu phản hồi.',
            pdf_title: 'Thư Phản hồi Chủ thể Dữ liệu',
            download_pdf: 'Tải PDF',
            download_all: 'Tải tất cả',
            download_error: 'Không thể tải phản hồi',
        },
        extension: {
            title: 'Gia hạn thời hạn',
            acknowledgment_deadline: 'Hạn xác nhận',
            fulfillment_deadline: 'Hạn thực hiện',
            request_extension: 'Yêu cầu gia hạn',
            reason: 'Lý do',
            reason_placeholder: 'Lý do yêu cầu thêm thời gian...',
            additional_days: 'Số ngày thêm',
            submit_request: 'Gửi yêu cầu gia hạn',
            history: 'Lịch sử gia hạn',
            requested_at: 'Yêu cầu lúc',
            approve: 'Phê duyệt',
            deny: 'Từ chối',
            request_success: 'Đã gửi yêu cầu gia hạn.',
            request_error: 'Không thể gửi yêu cầu gia hạn.',
            approved_success: 'Đã phê duyệt gia hạn.',
            denied_success: 'Đã từ chối gia hạn.',
            decision_error: 'Không thể xử lý quyết định gia hạn.',
            status_pending: 'Chờ xử lý',
            status_approved: 'Đã phê duyệt',
            status_denied: 'Đã từ chối',
            confirm_approve: 'Bạn có chắc muốn phê duyệt yêu cầu gia hạn này?',
            confirm_deny: 'Bạn có chắc muốn từ chối yêu cầu gia hạn này?',
            decided_by: 'Người quyết định',
        },
        evidence_unlink_read_failed: 'Đã xóa tệp đính kèm, nhưng không đọc được các nhiệm vụ thực hiện, nên tệp có thể vẫn được liệt kê là bằng chứng trên một nhiệm vụ.',
        partial_load_part_names: {
            advisor_summary: 'tóm tắt của cố vấn',
            sla_status: 'trạng thái SLA',
            attachments: 'tệp đính kèm',
            fulfilment_tasks: 'nhiệm vụ thực hiện',
            checklist: 'danh mục kiểm tra bằng chứng',
            references: 'tài liệu tham chiếu',
            escalations: 'leo thang',
            cmp_sync_history: 'lịch sử đồng bộ CMP',
            flagged_breaches: 'vi phạm đã gắn cờ',
            audit_trail: 'nhật ký kiểm toán',
            communications: 'trao đổi',
        },
        partial_load_reload: 'Tải lại các phần còn thiếu',
        request_type_not_granted: 'Loại yêu cầu này không phải là quyền theo các khung tuân thủ mà tổ chức đã cài đặt. Yêu cầu vẫn được giữ lại và vẫn có thể được xử lý.',
    },
    audit: {
        export_csv: 'Xuất CSV',
        exporting: 'Đang xuất...',
        search_placeholder: 'Tìm kiếm lịch sử...',
        filter_by_actor: 'Lọc theo người thực hiện...',
        all_event_types: 'Tất cả loại sự kiện',
        seq: '#',
        timestamp: 'Thời gian',
        event: 'Sự kiện',
        case_col: 'Yêu cầu',
        actor: 'Người thực hiện',
        summary: 'Tóm tắt',
        no_events: 'Không có sự kiện nào',
        export_success: 'Đã xuất nhật ký kiểm toán thành công.',
        export_error: 'Không thể xuất CSV lịch sử',
        load_error: 'Không thể tải lịch sử. Vui lòng thử lại.',
        announce_loaded: 'Đã tải lịch sử kiểm toán',
        start_date: 'Ngày bắt đầu',
        end_date: 'Ngày kết thúc',
        per_page: '{count} / trạng',
        unknown: 'Không rõ',
        // Sprint 47.9.0a — Bộ lọc nguồn cho nhật ký kiểm toán toàn cục.
        source_filter: {
            label: 'Nguồn',
            all: 'Tất cả',
            module: 'Phân hệ',
            forseti: 'Forseti',
            aria_label: 'Lọc sự kiện kiểm toán theo nguồn',
        },
        event_types: {
            case_created: 'Tạo yêu cầu',
            status_changed: 'Thay đổi trạng thái',
            case_assigned: 'Phân công yêu cầu',
            note_added: 'Thêm ghi chú',
            attachment_added: 'Thêm đính kèm',
            attachment_deleted: 'Xóa đính kèm',
            reference_added: 'Thêm tham chiếu',
            checklist_updated: 'Cập nhật danh mục',
            identity_verified: 'Xác minh danh tính',
            closure_summary_added: 'Tóm tắt đóng',
            approval_granted: 'Phê duyệt',
            template_applied: 'Áp dụng mẫu biểu',
            export_generated: 'Xuất dữ liệu',
            sla_warning: 'Cảnh báo SLA',
            sla_overdue: 'SLA quá hạn',
            sla_flag_revisited: 'Rà soát lại đánh dấu SLA',
            third_party_involvement_changed: 'Thay đổi việc bên xử lý dữ liệu cá nhân hoặc bên thứ ba tham gia',
            aml_diligence_linked: 'Liên kết hồ sơ thẩm định phòng, chống rửa tiền',
            aml_diligence_confirmed: 'Xác nhận hồ sơ thẩm định phòng, chống rửa tiền',
            aml_scoping_recorded: 'Ghi nhận xác định phạm vi đối tượng báo cáo',
            aml_scoping_deactivation_refused: 'Từ chối ghi nhận vai trò ngoài chương trình phòng, chống rửa tiền',
            aml_two_person_policy_changed: 'Thay đổi quy tắc hai người đối với vụ việc',
            aml_retention_scope_recorded: 'Ghi nhận phạm vi lưu trữ hồ sơ',
            aml_retention_scope_updated: 'Cập nhật phạm vi lưu trữ hồ sơ',
            aml_indicator_row_updated: 'Cập nhật dòng bao quát dấu hiệu',
            aml_indicator_entity_row_recorded: 'Ghi nhận dấu hiệu do tổ chức tự xác định',
            aml_indicator_config_approved: 'Phê duyệt cấu hình dấu hiệu',
            aml_case_opened: 'Mở vụ việc giao dịch đáng ngờ',
            aml_case_updated: 'Chỉnh sửa vụ việc giao dịch đáng ngờ',
            aml_case_viewed: 'Đọc vụ việc giao dịch đáng ngờ',
            aml_case_listed: 'Đọc danh sách vụ việc giao dịch đáng ngờ',
            aml_case_read_denied: 'Từ chối đọc vụ việc giao dịch đáng ngờ',
            aml_case_determined: 'Ghi nhận kết quả xác định của vụ việc giao dịch đáng ngờ',
            aml_case_approved: 'Phê duyệt kết quả xác định của vụ việc giao dịch đáng ngờ',
            aml_case_self_approval_refused: 'Từ chối phê duyệt của chính người đã xác định vụ việc giao dịch đáng ngờ',
            aml_case_approval_reverted: 'Thu hồi phê duyệt vụ việc giao dịch đáng ngờ',
            aml_case_filing_recorded: 'Ghi nhận mã tham chiếu báo cáo của vụ việc giao dịch đáng ngờ',
            aml_case_closed: 'Đóng vụ việc giao dịch đáng ngờ',
            aml_case_cdd_refresh_requested: 'Đề nghị cập nhật hồ sơ thẩm định từ vụ việc giao dịch đáng ngờ',
            aml_diligence_refresh_requested: 'Đề nghị cập nhật hồ sơ thẩm định phòng, chống rửa tiền',
            aml_beneficial_owner_recorded: 'Ghi nhận chủ sở hữu hưởng lợi',
            aml_beneficial_owner_updated: 'Cập nhật chủ sở hữu hưởng lợi',
            aml_launch_recorded: 'Ghi nhận hồ sơ triển khai',
            aml_launch_scope_determined: 'Ghi nhận kết quả xác định phạm vi của việc triển khai',
            aml_launch_assessment_recorded: 'Ghi nhận đánh giá rủi ro trước khi triển khai',
            aml_launch_assessment_decided: 'Ghi nhận quyết định về đánh giá rủi ro trước khi triển khai',
            aml_launch_approved: 'Phê duyệt triển khai',
            aml_launch_approval_refused: 'Từ chối phê duyệt triển khai',
            aml_inspection_pack_exported: 'Xuất hồ sơ phục vụ thanh tra phòng, chống rửa tiền',
            aml_indicator_config_self_approval_refused: 'Từ chối phê duyệt cấu hình dấu hiệu của người đã chỉnh sửa bản nháp',
            aml_indicator_review_task_raised: 'Tạo nhiệm vụ rà soát dấu hiệu từ đánh giá rủi ro trước khi triển khai',
            aml_indicator_review_task_completed: 'Ghi nhận việc rà soát dấu hiệu',
            aml_retention_scope_approved: 'Ký xác nhận phạm vi lưu trữ hồ sơ',
            aml_retention_scope_self_approval_refused: 'Từ chối chữ ký thứ hai của cùng một người đối với phạm vi lưu trữ hồ sơ',
            aml_case_related_action_recorded: 'Ghi nhận biện pháp liên quan của vụ việc giao dịch đáng ngờ',
            aml_case_retention_start_recorded: 'Ghi nhận sự kiện bắt đầu tính thời hạn lưu trữ của vụ việc giao dịch đáng ngờ',
            aml_launch_self_approval_refused: 'Từ chối phê duyệt triển khai của người đã quyết định về đánh giá',
            aml_launch_post_launch_review_set: 'Ghi nhận ngày rà soát sau khi triển khai',
            aml_case_reads_retention_swept: 'Xóa nhật ký đọc vụ việc giao dịch đáng ngờ khi hết thời hạn lưu trữ',
            org_profile_statement_recorded: 'Ghi nhận khai báo hồ sơ tổ chức',
            dossier_authority_communication_recorded: 'Ghi nhận văn bản của cơ quan có thẩm quyền trên hồ sơ nộp',
            dossier_authority_communication_exported: 'Xuất các văn bản của cơ quan có thẩm quyền đã ghi nhận trên hồ sơ nộp',
            framework_summary_exported: 'Xuất tệp tóm tắt khung tuân thủ',
            framework_briefing_recorded: 'Ghi nhận mục phổ biến thông tin hoặc ý kiến góp ý dự thảo về khung tuân thủ',
            framework_briefing_approved: 'Phê duyệt mục phổ biến thông tin về khung tuân thủ',
            dossier_receipt_evidence_recorded: 'Ghi nhận ngày tiếp nhận hồ sơ trên hồ sơ nộp',
            dossier_handling_statement_recorded: 'Ghi nhận cơ quan xử lý hồ sơ trên hồ sơ nộp',
            dossier_result_recorded: 'Ghi nhận kết quả của cơ quan trên hồ sơ nộp',
            dossier_result_supplement_request_recorded: 'Ghi nhận yêu cầu bổ sung sau kết quả trên hồ sơ nộp',
            dossier_failure_follow_up_recorded: 'Ghi nhận nội dung theo dõi sau kết quả trên hồ sơ nộp',
            dossier_authority_stated_result_date_recorded: 'Ghi nhận ngày trả kết quả do cơ quan nêu trên hồ sơ nộp',
            dossier_completion_request_recorded: 'Ghi nhận yêu cầu hoàn thiện hồ sơ trên hồ sơ nộp',
            dossier_completion_submitted: 'Ghi nhận đã nộp hồ sơ hoàn thiện trên hồ sơ nộp',
            dossier_update_filing_created: 'Tạo hồ sơ cập nhật cho hồ sơ nộp',
            dossier_update_addressee_recorded: 'Ghi nhận nơi nộp và cơ quan có thẩm quyền giải quyết hồ sơ trên hồ sơ cập nhật',
            regulatory_update_comparison_reviewed: 'Ghi nhận rà soát bản so sánh của một cập nhật quy định',
            access_statement_recorded: 'Ghi nhận bảng kê phân quyền truy cập dữ liệu của hoạt động xử lý dữ liệu cá nhân',
        },
        unified_audit: {
            title: 'Nhật ký Kiểm toán Tổng hợp',
            module_filter: 'Phân hệ',
            framework_filter: 'Khung tuân thủ',
            select_framework: 'Chọn khung tuân thủ...',
            entity_type_filter: 'Loại thực thể',
            action_filter: 'Hành động',
            actor_filter: 'Người thực hiện',
            select_actor: 'Chọn người thực hiện...',
            search_filter: 'Tìm kiếm',
            search_placeholder: 'Tìm kiếm sự kiện kiểm toán...',
            filters_heading: 'Bộ lọc',
            date_range: 'Khoảng thời gian',
            no_events_found: 'Không tìm thấy sự kiện kiểm toán nào.',
            event_count: 'Số lượng sự kiện',
            framework_codes: 'Mã khung tuân thủ',
            entity_type_col: 'Loại thực thể',
            aria_table: 'Sự kiện kiểm toán hợp nhất',
            filtering_events: 'Đang lọc sự kiện kiểm toán',
            date_range_end: 'Ngày kết thúc khoảng',
            date_range_start: 'Ngày bắt đầu khoảng',
            action: 'Hành động',
            actor: 'Người thực hiện',
            page_of: 'Trang {page} / {total}',
            entity_types: {
                user: 'Người dùng',
                case: 'Vụ việc',
                incident: 'Sự cố',
                assessment: 'Đánh giá',
                vendor: 'Nhà cung cấp',
                consent: 'Đồng thuận',
                policy: 'Chính sách',
                data_map: 'Bản đồ dữ liệu',
                approval: 'Phê duyệt',
                consent_record: 'Bản ghi sự đồng ý',
                data_flow: 'Luồng dữ liệu',
                org_profile_statement: 'Khai báo hồ sơ tổ chức',
                dpia: 'Đánh giá tác động xử lý dữ liệu cá nhân',
                dpia_mitigation: 'Biện pháp giảm thiểu rủi ro',
                evidence_item: 'Mục bằng chứng',
                processing_activity: 'Hoạt động xử lý',
                transfer_mechanism: 'Cơ chế chuyển giao',
                consent_policy: 'Chính sách về sự đồng ý',
                data_category: 'Danh mục dữ liệu',
                drift_event: 'Sự kiện lệch',
                maturity_model: 'Mô hình mức độ trưởng thành',
                program: 'Chương trình giám sát',
                purpose: 'Mục đích xử lý dữ liệu cá nhân',
                review_record: 'Hồ sơ rà soát',
                risk_score: 'Điểm rủi ro',
                system: 'Hệ thống',
                taxonomy: 'Phân loại',
            },
            actions: {
                // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                service_certificate_certificate_recorded: 'Đã ghi nhận Giấy chứng nhận dịch vụ xử lý dữ liệu cá nhân',
                service_certificate_certificate_corrected: 'Đã đính chính Giấy chứng nhận dịch vụ xử lý dữ liệu cá nhân',
                service_certificate_certificate_document_added: 'Đã thêm tài liệu vào Giấy chứng nhận dịch vụ xử lý dữ liệu cá nhân',
                service_certificate_certificate_revocation_recorded: 'Đã ghi nhận việc thu hồi Giấy chứng nhận',
                service_certificate_certificate_return_recorded: 'Đã ghi nhận việc nộp lại Giấy chứng nhận',
                service_certificate_certificate_publication_recorded: 'Đã ghi nhận công bố việc thu hồi',
                service_certificate_certificate_communication_recorded: 'Đã ghi nhận văn bản của cơ quan có thẩm quyền về Giấy chứng nhận',
                service_certificate_application_created: 'Đã tạo hồ sơ đề nghị cấp Giấy chứng nhận',
                service_certificate_application_updated: 'Đã cập nhật hồ sơ đề nghị cấp Giấy chứng nhận',
                service_certificate_application_part_recorded: 'Đã ghi nhận thành phần hồ sơ',
                service_certificate_application_condition_links_recorded: 'Đã liên kết hồ sơ nộp đánh giá tác động',
                service_certificate_application_filed: 'Đã ghi nhận việc nộp hồ sơ đề nghị',
                service_certificate_application_receipt_recorded: 'Đã ghi nhận ngày cơ quan tiếp nhận hồ sơ đề nghị',
                service_certificate_application_communication_recorded: 'Đã ghi nhận văn bản của cơ quan có thẩm quyền về hồ sơ đề nghị',
                service_certificate_application_supplement_request_recorded: 'Đã ghi nhận thông báo bổ sung, hoàn thiện hồ sơ',
                service_certificate_application_supplement_sent: 'Đã ghi nhận đã gửi hồ sơ bổ sung',
                service_certificate_application_evaluation_recorded: 'Đã ghi nhận kết quả đánh giá hồ sơ',
                service_certificate_application_decision_recorded: 'Đã ghi nhận quyết định đối với hồ sơ đề nghị',
                service_certificate_application_withdrawn: 'Đã rút hồ sơ đề nghị',
                service_certificate_register_exported: 'Đã xuất sổ theo dõi Giấy chứng nhận',
                form_dossier_statement_changed: 'Câu trả lời trong hồ sơ đã thay đổi',
                create: 'Tạo',
                update: 'Cập nhật',
                delete: 'Xóa',
                view: 'Xem',
                export: 'Xuất',
                status_change: 'Thay đổi trạng thái',
                // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                created: 'Đã tạo',
                updated: 'Đã cập nhật',
                deleted: 'Đã xóa',
                viewed: 'Đã xem',
                exported: 'Đã xuất',
                approved: 'Đã phê duyệt',
                rejected: 'Đã từ chối',
                assigned: 'Đã giao',
                completed: 'Đã hoàn thành',
                closed: 'Đã đóng',
                status_changed: 'Đã thay đổi trạng thái',
                submitted_for_review: 'Đã gửi xem xét',
                risk_added: 'Đã thêm rủi ro',
                mitigation_added: 'Đã thêm biện pháp giảm thiểu',
                evidence_linked: 'Đã liên kết bằng chứng',
                scored: 'Đã chấm điểm',
                reviewed: 'Đã xem xét',
                activated: 'Đã kích hoạt',
                period_started: 'Đã bắt đầu kỳ',
                version_created: 'Đã tạo phiên bản',
                evaluated: 'Đã đánh giá',
                triage_completed: 'Đã hoàn tất phân loại sự cố',
                impact_assessed: 'Đã đánh giá tác động',
                breach_assessed: 'Đã đánh giá vi phạm',
                case_created: 'Đã tạo vụ việc',
                assignment_changed: 'Đã thay đổi người phụ trách',
                case_closed: 'Đã đóng vụ việc',
                identity_verified: 'Đã xác minh danh tính',
                maturity_model_created: 'Đã tạo mô hình mức độ trưởng thành',
                maturity_model_activated: 'Đã kích hoạt mô hình mức độ trưởng thành',
                maturity_model_evaluated: 'Đã đánh giá mô hình mức độ trưởng thành',
                maturity_model_approved: 'Đã phê duyệt mô hình mức độ trưởng thành',
                response_generated: 'Đã tạo phản hồi',
                response_delivered: 'Đã gửi phản hồi',
                assembled: 'Đã tập hợp',
                reassembled: 'Đã tập hợp lại',
                verified: 'Đã xác minh',
                returned: 'Đã trả lại',
                recorded_as_submitted: 'Đã ghi nhận là đã nộp',
                audit_evidence_pack_assembled: 'Đã tập hợp gói bằng chứng',
                audit_evidence_pack_reassembled: 'Đã tập hợp lại gói bằng chứng',
                audit_evidence_pack_partial: 'Gói bằng chứng chưa đầy đủ',
                audit_evidence_pack_drift_checked: 'Đã kiểm tra thay đổi nguồn của gói bằng chứng',
                audit_evidence_pack_verified: 'Đã xác minh gói bằng chứng',
                audit_evidence_pack_returned: 'Đã trả lại gói bằng chứng',
                audit_evidence_pack_recorded_as_submitted: 'Đã ghi nhận gói bằng chứng là đã nộp',
                audit_evidence_pack_transition_refused: 'Đã từ chối thay đổi trạng thái gói bằng chứng',
                audit_evidence_pack_exported: 'Đã xuất gói bằng chứng',
                // Sprint 63.37.0 — the form-instance events the sample packages write.
                instance_created: 'Đã tạo biểu mẫu',
                instance_updated: 'Đã cập nhật biểu mẫu',
                consent_captured: 'Đã ghi nhận sự đồng ý',
                revoked: 'Đã thu hồi',
                mitigation_status_changed: 'Đã đổi trạng thái biện pháp giảm thiểu',
                ai_advisor_record_summary_fetched: 'Forseti đã đọc bản tóm tắt bản ghi',
                regulatory_guidance_installed: 'Đã cài đặt hướng dẫn quy định',
                regulatory_guidance_version_bumped: 'Đã chuyển hướng dẫn quy định sang phiên bản mới',
                unknown: 'Không ghi nhận hành động',
            },
            additional_filters: 'Bộ lọc kiểm toán bổ sung',
            sample_marker: 'Mẫu',
            sample_marker_label: 'Dữ liệu mẫu',
            sample_package_label: 'Dữ liệu mẫu từ gói theo ngành {package}',
            actor_system: 'Nền tảng',
            entity_none: 'Không có loại bản ghi',
            summary_composed: '{action} · {entity}',
            // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
            export_title: 'Xuất nhật ký kiểm toán tổng hợp',
            export_generated: 'Thời điểm tạo: {date}',
            export_filters: 'Bộ lọc: {filters}',
            export_no_filters: 'Bộ lọc: không có',
            export_count: 'Số sự kiện: {count}',
            export_source_unreadable: 'Nguồn {source} ({collection}): không đọc được; các sự kiện của nguồn này không có trong tệp.',
            list_sources_unreadable: 'Không đọc được các nguồn kiểm toán sau, nên sự kiện của chúng không được liệt kê và không được đếm: {sources}.',
            list_page_beyond_window: 'Không thể liệt kê các trang vượt quá {max} sự kiện đầu tiên. Hãy thu hẹp bộ lọc, hoặc xuất tập sự kiện đã lọc.',
            list_page_beyond_window_no_export: 'Không thể liệt kê các trang vượt quá {max} sự kiện đầu tiên. Hãy thu hẹp bộ lọc.',
            list_window_note: 'Tại đây chỉ có thể xem theo trang {max} sự kiện đầu tiên trong số {total} sự kiện. Để xem phần còn lại, hãy thu hẹp bộ lọc hoặc xuất tập sự kiện đã lọc.',
            list_window_note_no_export: 'Tại đây chỉ có thể xem theo trang {max} sự kiện đầu tiên trong số {total} sự kiện. Để xem phần còn lại, hãy thu hẹp bộ lọc.',
            export_col_entity_id: 'Mã bản ghi',
            export_col_actor_id: 'Mã người thực hiện',
            export_menu_label: 'Xuất',
            integrity: {
                column: 'Toàn vẹn',
                check: 'Kiểm tra toàn vẹn',
                check_aria: 'Kiểm tra toàn vẹn các sự kiện kiểm toán của bản ghi {id}',
                checking: 'Đang kiểm tra…',
                heading: 'Kết quả kiểm tra toàn vẹn: {type}',
                heading_untyped: 'Kết quả kiểm tra toàn vẹn',
                record_ref: 'Mã bản ghi: {id}',
                case_ref: 'Mã vụ việc: {case}',
                chain_verified: 'Chuỗi băm đã được xác minh, {verified}/{events} sự kiện.',
                chain_broken: '{verified}/{events} sự kiện được xác minh trước điểm đứt.',
                // Fix sprint 20261021-2116 F2 — rows before the chain's cut-over.
                chain_legacy_unverifiable: '{count} sự kiện được ghi trước quy tắc liên kết hiện hành; nội dung khớp với mã kiểm tra đã lưu, nhưng không thể xác minh thứ tự của các sự kiện này trong chuỗi.',
                no_chain: 'Không có chuỗi băm nào gắn với bản ghi này.',
                break_checksum: 'Phát hiện điểm đứt trong chuỗi của {case} tại số thứ tự {sequence}: mã kiểm tra đã lưu không khớp với mã tính lại từ sự kiện liền trước.',
                break_gap: 'Phát hiện điểm đứt trong chuỗi của {case}: thiếu số thứ tự {sequence}.',
                break_withheld: 'Phát hiện điểm đứt trong chuỗi của {case} tại một sự kiện mà bạn không được phép xem, nên vị trí không được hiển thị. Không thể dựa vào chuỗi này; người được cấp quyền xem sự kiện bị hạn chế có thể xem vị trí điểm đứt.',
                no_break: 'Không phát hiện điểm đứt.',
                not_chained: '{source}: {events} sự kiện. Không có chuỗi băm; chỉ kiểm tra tính nhất quán.',
                not_chained_inconsistent: '{source}: {events} sự kiện. Không có chuỗi băm; chỉ kiểm tra tính nhất quán. {inconsistent} sự kiện có ngày không đọc được hoặc ở tương lai (đầu tiên: {at}).',
                proofs_link: 'Bằng chứng và neo được xác minh tại Trung tâm toàn vẹn.',
                failed: 'Không thể chạy kiểm tra toàn vẹn.',
                close: 'Đóng kết quả kiểm tra toàn vẹn',
                // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                unreadable: 'Không thể đọc {sources} khi kiểm tra; kết quả không bao gồm các sự kiện lưu tại đó.',
            },
        },
        evidence_packs: {
            title: 'Gói bằng chứng kiểm toán',
            assemble_pack: 'Tập hợp gói bằng chứng',
            framework: 'Khung tuân thủ',
            select_framework: 'Chọn khung tuân thủ...',
            framework_code_label: 'Mã khung',
            entity_types: 'Loại thực thể',
            select_entity_types: 'Chọn loại thực thể...',
            period: 'Kỳ',
            period_end: 'Kết thúc kỳ',
            period_start: 'Bắt đầu kỳ',
            completeness: 'Mức độ hoàn thiện',
            no_packs_found: 'Không tìm thấy gói bằng chứng nào.',
            verify: 'Xác minh',
            export_pack: 'Xuất',
            aria_export_pack: 'Xuất gói',
            export_format: 'Định dạng xuất',
            export_in_progress: 'Đang xuất...',
            aria_table: 'Gói bằng chứng kiểm toán',
            pack_exported: 'Gói bằng chứng đã xuất',
            pack_assembled: 'Gói bằng chứng đã tập hợp',
            status_draft: 'Bản nháp',
            status_assembled: 'Đã tập hợp',
            exporting: 'Đang xuất...',
            status_verified: 'Đã xác minh',
            status_submitted: 'Đã nộp',
            entity_type_labels: {
                user: 'Người dùng',
                case: 'Vụ việc',
                incident: 'Sự cố',
                assessment: 'Đánh giá',
                vendor: 'Nhà cung cấp',
                consent: 'Đồng thuận',
                policy: 'Chính sách',
                data_map: 'Bản đồ dữ liệu',
            },
            csv_headers: {
                // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                actor_name: 'Tên người thực hiện',
                module: 'Phân hệ',
                action: 'Hành động',
                actor_id: 'Tác nhân',
                actor_role: 'Vai trò tác nhân',
                entity_type: 'Loại thực thể',
                entity_id: 'Mã thực thể',
                summary: 'Tóm tắt',
                timestamp: 'Thời gian',
                record_type: 'Loại hồ sơ',
                framework_code: 'Khung tuân thủ',
            },
            template_id: 'Mã mẫu',
            // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
            status_partial: 'Chưa đầy đủ',
            status_returned: 'Đã trả lại',
            status_recorded_as_submitted: 'Đã ghi nhận là đã nộp',
            status_legacy_unversioned: 'Tập hợp trước khi có phiên bản',
            record_type_labels: {
                consent: 'Đồng thuận',
                data_map: 'Bản đồ dữ liệu',
                assessment: 'Đánh giá',
                vendor: 'Nhà cung cấp',
                incident: 'Sự cố',
                case: 'Vụ việc',
                policy: 'Chính sách',
                deletion: 'Xóa dữ liệu',
                monitoring: 'Giám sát',
                forms: 'Biểu mẫu',
                task: 'Công việc',
                access: 'Truy cập',
                user: 'Người dùng',
                platform: 'Nền tảng',
                evidence_pack_lifecycle: 'Vòng đời gói bằng chứng',
            },
            framework_none: 'không lọc theo khung tuân thủ',
            export_header_pack: 'Gói {pack_id} — phiên bản {version}',
            export_header_name: 'Tên: {name}',
            export_header_manifest: 'Mã SHA-256 của bản kê: {sha}',
            export_header_period_tz: 'Kỳ: {start} đến {end} ({timezone})',
            export_header_untagged_included: 'Sự kiện không gắn với khung tuân thủ: có bao gồm',
            export_header_untagged_excluded: 'Sự kiện không gắn với khung tuân thủ: không bao gồm',
            export_header_record_types: 'Loại hồ sơ: {types}',
            export_header_modules: 'Phân hệ: {names}',
            export_header_all_modules: 'Phân hệ: tất cả',
            export_header_counts: 'Sự kiện: {total} ({tagged} gắn với khung tuân thủ, {untagged} không gắn)',
            export_header_coverage: 'Độ bao phủ loại hồ sơ: {covered} trên {denominator} loại hồ sơ đã chọn có ít nhất một sự kiện',
            export_header_coverage_missing: 'Loại hồ sơ đã chọn không có sự kiện: {types}',
            export_header_unresolved: 'Tham chiếu không xác định được: {count}',
            export_header_unresolved_unchecked: 'Tham chiếu không xác định được: chưa kiểm tra',
            export_header_review: 'Xem xét: {decision} bởi {reviewer} ngày {date}',
            export_header_review_note: 'Ghi chú xem xét: {note}',
            export_header_not_reviewed: 'Xem xét: chưa xem xét',
            export_header_submission: 'Đã ghi nhận là đã nộp: tới {recipient} ngày {sent_on} qua {channel}, số tham chiếu {reference}',
            export_header_submission_disclaimer: 'Bản ghi này cho biết gói đã được gửi. Đây không phải bằng chứng bên nhận đã nhận hoặc chấp nhận gói.',
            export_header_no_submission: 'Gửi: chưa ghi nhận',
            export_header_source_collected: 'Nguồn {source} ({collection}): đã thu thập {count} sự kiện',
            export_header_source_failed: 'Nguồn {source} ({collection}): không đọc được ({error})',
            export_header_sources_not_read: 'Nguồn không đọc: {count} (không được chọn)',
            export_header_excluded_by_rule: 'Loại trừ theo quy tắc: sự kiện đọc bản tóm tắt bản ghi ({count})',
            export_header_excluded_by_rule_uncounted: 'Loại trừ theo quy tắc: sự kiện đọc bản tóm tắt bản ghi (chưa đếm được)',
            export_header_excluded_by_rule_not_applied: 'Loại trừ theo quy tắc: sự kiện đọc bản tóm tắt bản ghi (chưa áp dụng quy tắc: phiên bản này được tổng hợp trước khi có quy tắc)',
            value_none: 'không có',
            // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
            summary_composed: '{action} · {record_type}',
            export_doc_title: 'Gói bằng chứng {pack_id}',
            export_header_framework: 'Gói bằng chứng — Khung tuân thủ: {framework}',
            export_header_period: 'Kỳ: {start} đến {end}',
            export_header_completeness: 'Mức độ hoàn thành: {score}%',
            export_header_status: 'Trạng thái: {status}',
            col_entity_type: 'Loại thực thể',
            events_collected: 'sự kiện đã thu thập',
            missing_label: 'Thiếu',
            no_events_in_pack: 'Không tìm thấy sự kiện kiểm toán nào cho gói bằng chứng này.',
            // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
            page_description: 'Tập hợp các sự kiện kiểm toán được ghi nhận trong một kỳ thành một bộ hồ sơ có phiên bản. Mỗi phiên bản là một bản chụp: số lượng, danh sách sự kiện, độ bao phủ và mọi tệp xuất đều đọc cùng một tập sự kiện.',
            cross_ref_audit: 'Gói bằng chứng kiểm toán tập hợp các sự kiện kiểm toán được ghi nhận trong một kỳ; gói bằng chứng theo khung tuân thủ tập hợp các hạng mục bằng chứng của một khung tuân thủ để gửi cơ quan quản lý.',
            cross_ref_open_framework: 'Đến gói bằng chứng theo khung tuân thủ',
            cross_ref_framework: 'Gói bằng chứng theo khung tuân thủ tập hợp các hạng mục bằng chứng của một khung tuân thủ để gửi cơ quan quản lý; gói bằng chứng kiểm toán tập hợp các sự kiện kiểm toán được ghi nhận trong một kỳ.',
            cross_ref_open_audit: 'Đến gói bằng chứng kiểm toán',
            filter_status: 'Trạng thái',
            filter_all_statuses: 'Tất cả trạng thái',
            col_name: 'Tên',
            col_events: 'Sự kiện',
            col_version: 'Phiên bản',
            col_coverage: 'Độ bao phủ loại hồ sơ',
            col_assembled: 'Ngày tập hợp',
            coverage_short: '{covered} trên {denominator}',
            not_a_member_count: 'Không phải danh sách sự kiện',
            show_details: 'Xem chi tiết {name}',
            list_load_failed: 'Không đọc được danh sách gói bằng chứng.',
            list_retry_subject: 'danh sách gói bằng chứng',
            list_pagination: 'Trang của danh sách gói bằng chứng',
            field_name: 'Tên',
            name_hint: 'Một tên dễ nhận biết, ví dụ "Thanh tra quý 3".',
            framework_hint: 'Không bắt buộc. Chỉ liệt kê các khung tuân thủ đã cài đặt cho tổ chức của bạn.',
            no_framework_option: 'Không lọc theo khung tuân thủ',
            include_untagged: 'Bao gồm các sự kiện không gắn với khung tuân thủ nào',
            include_untagged_hint: 'Phần lớn sự kiện kiểm toán không gắn với khung tuân thủ nào. Hãy giữ ô này được chọn, trừ khi bạn chỉ muốn các sự kiện gắn với khung tuân thủ đã chọn. Dù chọn thế nào, gói vẫn cho biết số sự kiện của mỗi loại.',
            include_untagged_no_effect: 'Khi không chọn khung tuân thủ, mọi sự kiện trong kỳ đều được bao gồm và ô này không có tác dụng.',
            period_hint: 'Ngày được tính theo múi giờ của tổ chức, và ngày kết thúc được bao gồm trọn vẹn.',
            record_types_legend: 'Loại hồ sơ',
            record_types_hint: 'Chọn ít nhất một loại.',
            modules_legend: 'Phân hệ',
            modules_hint: 'Không bắt buộc. Để trống tất cả các ô để bao gồm mọi phân hệ.',
            error_name_required: 'Nhập tên.',
            error_period_start_required: 'Chọn ngày bắt đầu.',
            error_period_end_required: 'Chọn ngày kết thúc.',
            error_period_order: 'Ngày kết thúc phải bằng hoặc sau ngày bắt đầu.',
            error_record_types_required: 'Chọn ít nhất một loại hồ sơ.',
            assembling: 'Đang tập hợp…',
            detail_loading: 'Đang tải gói…',
            detail_load_failed: 'Không đọc được gói này.',
            detail_retry_subject: 'gói bằng chứng này',
            scope_heading: 'Phạm vi và số lượng',
            meta_period: 'Kỳ',
            meta_untagged: 'Sự kiện không gắn với khung tuân thủ',
            meta_record_types: 'Loại hồ sơ',
            meta_modules: 'Phân hệ',
            meta_counts: 'Sự kiện',
            meta_manifest: 'Mã băm SHA-256 của bảng kê',
            period_value: '{start} đến {end} ({timezone})',
            untagged_included: 'Có bao gồm',
            untagged_excluded: 'Không bao gồm',
            all_modules: 'Tất cả phân hệ',
            counts_value: '{total} ({tagged} gắn với khung tuân thủ, {untagged} không gắn)',
            not_checked: 'Chưa kiểm tra',
            legacy_notice: 'Gói này được tập hợp trước khi có phiên bản. Số lượng đã lưu không phải là danh sách sự kiện, nên không thể rà soát hoặc xuất gói. Hãy tập hợp lại để tạo một phiên bản có thể rà soát.',
            version_select: 'Phiên bản đang xem',
            version_option: 'Phiên bản {version}',
            reassemble: 'Tập hợp lại',
            reassembling: 'Đang tập hợp lại…',
            reassembled: 'Đã tập hợp phiên bản {version}.',
            drift_check: 'Kiểm tra thay đổi kể từ khi tập hợp',
            drift_checking: 'Đang kiểm tra…',
            drift_none: 'Không có thay đổi nào kể từ khi tập hợp phiên bản {version}.',
            drift_new: '{count} sự kiện được ghi nhận trong kỳ này kể từ khi tập hợp. Hãy tập hợp lại để bao gồm chúng.',
            drift_gone: '{count} sự kiện trong phiên bản này không còn tồn tại ở nguồn.',
            drift_changed: '{count} sự kiện trong phiên bản này đã thay đổi ở nguồn.',
            drift_failed_sources: 'Các nguồn không đọc được khi kiểm tra: {sources}.',
            drift_failed: 'Không thể kiểm tra thay đổi.',
            coverage_heading: 'Độ bao phủ loại hồ sơ',
            coverage_sentence: '{covered} trên {denominator} loại hồ sơ đã chọn có ít nhất một sự kiện.',
            coverage_missing: 'Không có sự kiện cho: {types}.',
            coverage_not: 'Chỉ số này cho biết loại hồ sơ đã chọn nào có sự kiện. Chỉ số này không đo mức đầy đủ của bằng chứng, cũng không đo việc tổ chức có tuân thủ pháp luật hay không.',
            history_heading: 'Lịch sử phiên bản',
            col_assembled_by: 'Người tập hợp',
            col_assembled_at: 'Ngày tập hợp',
            col_reviewer: 'Người rà soát',
            col_decision: 'Quyết định',
            col_note: 'Ghi chú',
            col_reviewed_at: 'Ngày rà soát',
            col_submission: 'Đã ghi nhận là đã nộp',
            decision_verified: 'Đã xác minh',
            decision_returned: 'Đã trả lại',
            not_reviewed: 'Chưa rà soát',
            no_submission: 'Chưa ghi nhận',
            submission_line: 'Gửi {recipient} ngày {sent_on} qua {channel}',
            items_heading: 'Sự kiện trong phiên bản {version}',
            items_loading: 'Đang tải các sự kiện của phiên bản này…',
            items_empty_scope: 'Phiên bản được tập hợp cho kỳ {period}; khung tuân thủ: {framework}; loại hồ sơ: {types}; phân hệ: {module_list}.',
            items_denied: 'Bạn không có quyền đọc các sự kiện của gói này. Cần quyền "Đọc sự kiện kiểm toán" (audit:events:read).',
            items_failed: 'Không đọc được các sự kiện của phiên bản này.',
            items_retry_subject: 'các sự kiện của phiên bản này',
            items_populated: 'Phiên bản này có {count} sự kiện.',
            partial_banner: 'Một số nguồn không đọc được khi tập hợp phiên bản này, nên phiên bản chưa đầy đủ và không thể xác minh. Các nguồn không đọc được: {sources}.',
            partial_reassemble_hint: 'Người có quyền xuất sự kiện kiểm toán có thể tập hợp lại phiên bản này.',
            partial_no_members: 'Không thu thập được sự kiện nào từ các nguồn đã phản hồi.',
            source_item: '{source} ({collection})',
            col_record_type: 'Loại hồ sơ',
            col_actor: 'Người thực hiện',
            col_timestamp: 'Thời gian',
            col_module: 'Phân hệ',
            col_action: 'Hành động',
            col_summary: 'Tóm tắt',
            items_pagination: 'Trang của danh sách sự kiện',
            review: 'Rà soát',
            review_title: 'Rà soát phiên bản {version}',
            review_decision: 'Quyết định',
            review_option_verified: 'Xác minh phiên bản này',
            review_option_returned: 'Trả lại cho người đã tập hợp',
            review_note: 'Ghi chú',
            review_note_hint: 'Bắt buộc khi trả lại phiên bản: nêu rõ cần thay đổi gì.',
            error_decision_required: 'Chọn một quyết định.',
            error_note_required: 'Nhập ghi chú nêu lý do trả lại phiên bản.',
            review_submit: 'Ghi nhận quyết định',
            review_recorded: 'Đã ghi nhận quyết định của bạn về phiên bản {version}.',
            review_own_version: 'Bạn là người tập hợp phiên bản này, nên người khác phải rà soát.',
            record_submission: 'Ghi nhận là đã nộp',
            submission_title: 'Ghi nhận phiên bản {version} là đã nộp',
            submission_disclaimer: 'Bản ghi này cho biết gói đã được gửi. Đây không phải bằng chứng bên nhận đã nhận hoặc chấp nhận gói.',
            submission_recipient: 'Bên nhận',
            submission_sent_on: 'Ngày gửi',
            submission_channel: 'Kênh gửi',
            submission_channel_hint: 'Cách thức gửi, ví dụ qua thư điện tử hoặc gửi trực tiếp.',
            submission_reference: 'Số tham chiếu',
            submission_reference_hint: 'Không bắt buộc. Ví dụ số công văn hoặc số phiếu.',
            submission_proof: 'Bằng chứng việc nộp',
            submission_proof_hint: 'Không bắt buộc. Tệp cho thấy gói đã được gửi, chẳng hạn biên nhận gửi.',
            error_recipient_required: 'Nhập bên nhận.',
            error_sent_on_required: 'Chọn ngày gửi.',
            error_channel_required: 'Nhập kênh gửi.',
            submission_recorded: 'Đã ghi nhận phiên bản {version} là đã nộp.',
            export_version: 'Xuất phiên bản {version}',
            // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
            reference_not_found: 'Không tìm thấy tham chiếu',
            reference_not_found_record: 'Sự kiện nguồn không nêu bản ghi nào.',
            reference_not_found_actor: 'Người thực hiện không có trong danh bạ người dùng.',
            unresolved_line: 'Tham chiếu không xác định được: {count}',
            unresolved_not_checked: 'Tham chiếu không xác định được: chưa kiểm tra',
            unresolved_breakdown: '{records} không nêu bản ghi; {actors} có người thực hiện không có trong danh bạ người dùng.',
            unresolved_breakdown_actors_unchecked: '{records} không nêu bản ghi; chưa kiểm tra người thực hiện.',
            server_error_reviewer_is_assembler: 'Bạn là người tập hợp phiên bản này nên không thể rà soát. Một người khác có quyền rà soát phải ghi nhận quyết định.',
            server_error_evidence_pack_version_not_found: 'Không tìm thấy phiên bản này của gói. Gói có thể đã được tập hợp lại hoặc đã bị gỡ bỏ.',
            server_error_submission_proof_not_owned: 'Tệp bằng chứng không thuộc phiên bản này. Hãy tải tệp lên cho phiên bản này trước.',
            server_error_evidence_pack_legacy: 'Gói này được tập hợp trước khi có phiên bản. Hãy tập hợp lại để tạo một phiên bản có thể kiểm tra.',
            server_error_evidence_pack_too_large: 'Số sự kiện khớp với phạm vi này vượt quá sức chứa của một gói. Hãy chọn khoảng thời gian ngắn hơn hoặc bộ lọc hẹp hơn.',
            server_error_evidence_pack_definition_invalid: 'Không thể tập hợp lại gói này từ định nghĩa đã lưu. Hãy tạo một gói mới.',
        },
        audit_periods: {
            title: 'Kỳ Kiểm toán',
            create_period: 'Tạo Kỳ Kiểm toán',
            framework_code_label: 'Mã khung',
            select_framework: 'Chọn khung tuân thủ...',
            period_label: 'Nhãn kỳ',
            framework: 'Khung tuân thủ',
            end: 'Kết thúc',
            start: 'Bắt đầu',
            period_end: 'Kết thúc kỳ',
            period_start: 'Bắt đầu kỳ',
            retention_days: 'Số ngày lưu giữ',
            section_period_dates: 'Ngày của kỳ',
            no_periods_found: 'Không tìm thấy kỳ kiểm toán nào.',
            aria_table: 'Kỳ kiểm toán',
            period_created: 'Kỳ kiểm toán đã tạo',
            suggested_boundaries: 'Ranh giới đề xuất',
            suggested_boundaries_info: 'Ranh giới kỳ đề xuất được rút ra từ chu kỳ báo cáo quy định của mỗi khung tuân thủ. Điều chỉnh theo nhu cầu tổ chức của bạn.',
            reporting_cycle: 'Chu kỳ báo cáo',
            suggested_start: 'Bắt đầu đề xuất',
            suggested_end: 'Kết thúc đề xuất',
            suggested_next: 'Kỳ tiếp theo đề xuất',
            cycle_monthly: 'Hàng tháng',
            cycle_quarterly: 'Hàng quý',
            cycle_semi_annual: 'Nửa năm',
            cycle_annual: 'Hàng năm',
            cycle_custom: 'Tùy chỉnh',
            period_deleted: 'Đã xóa kỳ kiểm toán',
            list_truncated: 'Chỉ đọc được {shown} trên {total} kỳ kiểm toán nên danh sách này chưa đầy đủ.',
        },
    },
    create_case: {
        customer_ref: 'Mã khách hàng',
        country: 'Quốc gia / Phạm vi pháp lý',
        preferred_channel: 'Kênh ưu tiên',
        channels: {
            email: 'Email',
            postal: 'Thư tín',
            phone: 'Điện thoại',
            portal: 'Cổng thông tin',
        },
        required_fields: 'Loại yêu cầu, tên và email là bắt buộc',
        source: 'Nguồn',
        sources: {
            admin_manual: 'Quản trị viên nhập thủ công',
            aesirx_cmp: 'AesirX CMP',
            cmp_gateway: 'Cổng CMP',
            email_received: 'Email nhận được',
            in_person: 'Trực tiếp',
            legacy_import: 'Nhập dữ liệu cũ',
            phone_received: 'Điện thoại nhận được',
            postal_received: 'Thư tín nhận được',
            public_form: 'Biểu mẫu công khai',
        },
        create_error: 'Không thể tạo yêu cầu',
        // fix-sprint #447 W9 (owner ruling OR3)
        third_party_involved: 'Bên xử lý dữ liệu cá nhân hoặc bên thứ ba phải tham gia thực hiện yêu cầu này',
        third_party_involved_hint: 'Các bản ghi thời hạn đã cài đặt có thể quy định thời hạn khác khi bên xử lý dữ liệu cá nhân hoặc bên thứ ba phải tham gia. Hồ sơ được tính hạn theo bản ghi phù hợp. Có thể thay đổi sau trên trang hồ sơ; khi đó hồ sơ được tính hạn lại từ ngày tiếp nhận.',
        request_type_not_offered: 'Loại yêu cầu này không phải là quyền theo các khung tuân thủ mà tổ chức của bạn đã cài đặt, nên không thể ghi nhận thành một yêu cầu mới.',
        request_type_no_installed_clock: 'Không có khung tuân thủ đã cài đặt nào nêu thời hạn cho loại yêu cầu này. Hồ sơ sẽ được xác định hạn theo chỉ tiêu SLA của tổ chức cho loại yêu cầu này hoặc, nếu không có, theo mặc định của nền tảng, vốn không phải thời hạn theo pháp luật.',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        request_type_handled_in_legal_matters: 'Loại yêu cầu này được tiếp nhận như một vụ việc pháp lý trong Vụ việc pháp lý. Không có thời hạn nào được đặt cho yêu cầu này.',
        legal_matter_right: 'Quyền được thực hiện',
        legal_matter_right_optional_hint: 'Không bắt buộc. Nếu chưa rõ, hãy để trống: người phụ trách sẽ ghi nhận quyền này trên vụ việc pháp lý.',
        legal_matter_right_not_known: 'Chưa xác định',
    },
    settings: {
        title: 'Cài đặt',
        request_types: 'Loại yêu cầu',
        request_types_desc: 'Cấu hình loại yêu cầu được bật cho biểu mẫu tiếp nhận công khai.',
        notifications: 'Thông báo',
        ticket_numbering: 'Đánh số phiếu',
        ticket_format: 'Định dạng hiện tại',
        ticket_prefix: 'Tiền tố',
        ticket_prefix_note: 'không thể cấu hình',
        sla_deadlines: 'Thời hạn SLA',
        sla_desc: 'Đặt thời gian phản hồi tối đa (theo ngày lịch) cho mỗi loại yêu cầu. Các hồ sơ gần đến ngưỡng sẽ hiển thị cảnh báo.',
        days: 'ngày',
        warning_threshold: 'Ngưỡng cảnh báo',
        escalation_label: 'Bật tự động leo thang',
        escalation_hint: 'Chạy các cấp leo thang bên dưới. Khi tắt, không cấp nào được thực hiện; cảnh báo và vi phạm vẫn được gắn cờ.',
        warning_threshold_hint: 'Hồ sơ không do khung tuân thủ đã cài đặt nào xác định thời hạn sẽ hiển thị cảnh báo khi đạt tỷ lệ thời hạn này và được gắn cờ nghiêm trọng ở mức 90%, hoặc ở tỷ lệ này nếu cao hơn. Hồ sơ do khung tuân thủ đã cài đặt xác định thời hạn được cảnh báo ở mức 75% và gắn cờ nghiêm trọng ở mức 90%.',
        // Gate E12.5 remediation 1 (fix-sprint #431) — cần quyền cấu hình SLA.
        escalation_settings_need_write: 'Cần quyền cấu hình SLA để thay đổi tự động leo thang hoặc ngưỡng cảnh báo.',
        // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
        sla_settings_read_only: 'Bạn có thể xem nhưng không thể thay đổi các thiết lập này: việc lưu cần quyền cấu hình SLA, hoặc quyền cập nhật vụ việc nếu chỉ thay đổi thời hạn.',
        sla_save_forbidden: 'Chưa lưu cấu hình SLA: cần quyền cấu hình SLA để thay đổi tự động leo thang hoặc ngưỡng cảnh báo.',
        sla_save_invalid: 'Chưa lưu cấu hình SLA: có thời hạn được đặt cho mục không phải là loại yêu cầu, hoặc có giá trị nằm ngoài phạm vi cho phép.',
        save_sla: 'Lưu cấu hình SLA',
        saving: 'Đang lưu...',
        saved: 'Đã lưu thành công',
        announce_loaded: 'Đã tải cài đặt',
        // fix-sprint #431 (phán quyết của chủ sản phẩm 2026-10-03) — tác dụng thực của công tắc.
        email_on_creation: 'Thông báo cho người phụ trách tuân thủ (vai trò Compliance Owner) khi nhận được yêu cầu thực hiện quyền (trên nền tảng và qua email, theo tùy chọn thông báo của từng người)',
        send_confirmation: 'Gửi email xác nhận kèm mã tham chiếu vụ việc cho người yêu cầu',
        users: 'Người dùng',
        roles: 'Vai trò',
        api_keys: 'Khóa API',
        mfa: 'MFA',
        sessions: 'Phiên',
        webhooks: 'Webhooks',
        modules: 'Phân hệ',
        // Sprint 62.45.0 (AC17) — the organisation calendar section.
        calendar: {
            heading: 'Ngày làm việc và ngày nghỉ lễ',
            effect_notice: 'Mọi thời hạn tính theo ngày làm việc trên nền tảng chỉ tính các ngày làm việc dưới đây, bỏ qua các ngày nghỉ lễ này và tính các ngày làm bù này — trong đó có yêu cầu quyền và các lần tạm dừng, sự cố, nghĩa vụ, thời hiệu của vụ việc xử phạt, hồ sơ nộp và báo cáo giám sát. Thời hạn được tính sau khi bạn lưu sẽ dùng lịch mới; ngày đến hạn đã ghi trên một hồ sơ vẫn giữ nguyên.',
            no_list_notice: 'Nền tảng không cung cấp danh sách ngày nghỉ lễ: hãy tự nhập các ngày nghỉ của tổ chức, bao gồm cả các ngày nghỉ lễ.',
            working_days_legend: 'Ngày làm việc trong tuần',
            working_days_hint: 'Thời hạn tính theo ngày làm việc chỉ tính các ngày này.',
            working_days_required: 'Hãy chọn ít nhất một ngày làm việc.',
            day_0: 'Chủ nhật',
            day_1: 'Thứ Hai',
            day_2: 'Thứ Ba',
            day_3: 'Thứ Tư',
            day_4: 'Thứ Năm',
            day_5: 'Thứ Sáu',
            day_6: 'Thứ Bảy',
            holidays_heading: 'Ngày nghỉ lễ',
            holidays_caption: 'Số ngày nghỉ lễ đã nhập: {count}',
            empty: 'Chưa nhập ngày nghỉ lễ nào: thời hạn tính theo ngày làm việc chỉ bỏ qua các ngày ngoài tuần làm việc.',
            add_legend: 'Thêm ngày nghỉ lễ',
            date_label: 'Ngày',
            label_label: 'Tên gọi (không bắt buộc)',
            label_hint: 'Tên ngắn cho ngày nghỉ, ví dụ lý do văn phòng đóng cửa.',
            add: 'Thêm ngày',
            remove: 'Xóa',
            remove_aria: 'Xóa ngày {date}',
            col_date: 'Ngày',
            col_label: 'Tên gọi',
            col_actions: 'Thao tác',
            no_label: 'Không có tên gọi',
            date_required: 'Hãy nhập ngày.',
            date_out_of_range: 'Hãy nhập ngày trong khoảng từ {min} đến {max}.',
            duplicate: 'Ngày {date} đã có trong danh sách.',
            limit_reached: 'Chỉ được nhập tối đa {max} ngày nghỉ lễ. Hãy xóa bớt một ngày trước khi thêm ngày mới.',
            save: 'Lưu lịch',
            saved: 'Đã lưu lịch. Các thời hạn tính theo ngày làm việc từ nay sẽ chỉ tính các ngày làm việc này, bỏ qua các ngày nghỉ lễ này và tính các ngày làm bù này.',
            save_error: 'Chưa lưu được lịch. Các nội dung bạn đã nhập vẫn được giữ nguyên.',
            conflict: 'Người khác đã lưu lịch sau khi bạn mở trang này, nên thay đổi của bạn chưa được lưu. Hãy tải lại lịch để xem phiên bản đó, rồi nhập lại thay đổi của bạn.',
            reload: 'Tải lại lịch',
            load_error: 'Không đọc được lịch, nên lịch không được hiển thị và không thể thay đổi tại đây.',
            retry: 'Thử lại',
            last_saved: 'Lưu lần gần nhất: {date}.',
            never_saved: 'Lịch này chưa được lưu lần nào tại đây.',
            read_only: 'Bạn có thể xem nhưng không thể thay đổi lịch này.',
            unsaved: 'Có thay đổi chưa lưu.',
            // Walkthrough N3 — ngày làm bù.
            holiday_is_extra: 'Ngày {date} đã được nhập là ngày làm bù. Một ngày chỉ có thể là ngày nghỉ lễ hoặc ngày làm bù.',
            extra_heading: 'Ngày làm bù',
            extra_intro: 'Ngày tổ chức bạn làm việc dù nằm ngoài các ngày làm việc trong tuần, ví dụ một ngày Thứ Bảy làm bù cho một ngày nghỉ liền kề ngày nghỉ lễ. Mọi thời hạn tính theo ngày làm việc đều tính ngày này là ngày làm việc.',
            extra_caption: 'Số ngày làm bù đã nhập: {count}',
            extra_empty: 'Chưa nhập ngày làm bù nào.',
            extra_add_legend: 'Thêm ngày làm bù',
            extra_date_label: 'Ngày làm bù',
            extra_label_label: 'Tên gọi của ngày làm bù (không bắt buộc)',
            extra_label_hint: 'Tên ngắn cho ngày làm bù, ví dụ ngày nghỉ mà ngày này làm bù.',
            extra_add: 'Thêm ngày làm bù',
            extra_remove_aria: 'Xóa ngày làm bù {date}',
            extra_is_holiday: 'Ngày {date} đã được nhập là ngày nghỉ lễ. Một ngày chỉ có thể là ngày nghỉ lễ hoặc ngày làm bù.',
            extra_limit_reached: 'Chỉ được nhập tối đa {max} ngày làm bù. Hãy xóa bớt một ngày trước khi thêm ngày mới.',
            extra_is_working_day: 'Ngày {date} là {weekday}, vốn đã là ngày làm việc. Ngày làm bù phải là một ngày nằm ngoài các ngày làm việc trong tuần.',
            extra_on_working_weekday: 'Các ngày làm bù sau rơi vào ngày làm việc trong tuần nên không làm thay đổi gì: {dates}. Hãy xóa các ngày này, hoặc bỏ ngày đó khỏi các ngày làm việc.',
        },
        // fix-sprint #431 F1 — Settings › Escalation ladder.
        escalation_ladder: {
            heading: 'Các cấp leo thang',
            intro: 'Khi một yêu cầu thực hiện quyền đạt tỷ lệ thời hạn của một cấp, quy trình kiểm tra SLA chạy nền xử lý vụ việc đó một lần: thông báo cho người được phân công xử lý vụ việc, cho những người và vai trò mà cấp đó chỉ định, trên nền tảng hoặc qua email, trong phạm vi cài đặt thông báo của từng người cho phép. Cấp phân công lại còn chuyển vụ việc cho người khác. Tỷ lệ được tính theo thời gian từ khi nhận yêu cầu đến hạn xử lý ghi trên vụ việc. Vụ việc đang tạm dừng và vụ việc không có hạn xử lý không bị leo thang.',
            off_notice: 'Tự động leo thang đang tắt nên không cấp nào được thực hiện. Cảnh báo và vi phạm vẫn được gắn cờ. Hãy bật lại trong mục Thời hạn SLA.',
            on_notice: 'Tự động leo thang đang bật. Quy trình kiểm tra SLA chạy nền thực hiện các cấp này.',
            suggested_notice: 'Tổ chức của bạn chưa lưu các cấp leo thang nên các cấp đề xuất này đang được áp dụng. Hãy lưu các cấp này, hoặc thay đổi rồi lưu, để chúng trở thành cấu hình của tổ chức.',
            caption: 'Số cấp leo thang: {count}',
            col_threshold: 'Tỷ lệ thời hạn',
            col_name: 'Tên',
            col_action: 'Hành động',
            col_targets: 'Thông báo cho',
            col_channels: 'Gửi qua',
            col_actions: 'Thao tác',
            action_notify: 'Thông báo',
            action_escalate_to_role: 'Leo thang đến một vai trò',
            action_reassign: 'Phân công lại',
            channel_in_app: 'Trên nền tảng',
            channel_email: 'Email',
            target_assignee: 'Người được phân công',
            target_role: 'Những người giữ vai trò {role}',
            target_users: 'Được chỉ định: {names}',
            reassign_to: 'Chuyển vụ việc cho: {target}',
            threshold_breach: '100% (đã quá hạn xử lý)',
            inactive: 'Không hoạt động',
            edit: 'Sửa',
            edit_aria: 'Sửa cấp ở mức {pct}%',
            remove: 'Xóa',
            remove_aria: 'Xóa cấp ở mức {pct}%',
            add: 'Thêm cấp',
            add_legend: 'Thêm một cấp',
            edit_legend: 'Sửa cấp ở mức {pct}%',
            threshold_label: 'Tỷ lệ thời hạn đã trôi qua (%)',
            threshold_hint: 'Số nguyên từ 1 đến 100. Ở mức 100, cấp này được thực hiện khi đã quá hạn xử lý.',
            name_label: 'Tên (không bắt buộc)',
            name_hint: 'Hiển thị trong thông báo và trong lịch sử leo thang của vụ việc. Nếu để trống, cấp được đặt tên theo tỷ lệ của cấp đó.',
            action_label: 'Hành động',
            action_hint: 'Mọi cấp đều thông báo cho người được phân công và những người được chỉ định. Leo thang đến một vai trò còn thông báo cho tất cả những người giữ vai trò đó. Phân công lại chuyển vụ việc cho người đầu tiên đang hoạt động trong danh sách, nếu không có thì cho người đầu tiên giữ vai trò đó, trừ khi vụ việc đã được giao cho một trong những người này.',
            role_label: 'Vai trò',
            role_none: 'Không chọn vai trò',
            users_label: 'Người được chỉ định',
            users_hint: 'Với cấp phân công lại, vụ việc được chuyển cho người đầu tiên đang hoạt động trong danh sách này.',
            user_select_label: 'Người cần thêm',
            user_placeholder: 'Chọn một người',
            user_add: 'Thêm người',
            user_remove_aria: 'Xóa {name}',
            users_empty: 'Chưa chỉ định ai.',
            users_retry_subject: 'danh sách người dùng',
            channels_legend: 'Gửi qua',
            channels_hint: 'Mỗi người vẫn chỉ nhận những gì cài đặt thông báo của chính họ cho phép.',
            active_label: 'Đang áp dụng',
            apply: 'Áp dụng vào các cấp leo thang',
            cancel: 'Hủy',
            save: 'Lưu các cấp leo thang',
            saved: 'Đã lưu các cấp leo thang. Quy trình kiểm tra SLA chạy nền áp dụng từ lần quét tiếp theo.',
            unsaved: 'Có thay đổi chưa lưu.',
            save_error: 'Chưa lưu được các cấp leo thang. Các thay đổi của bạn vẫn được giữ nguyên.',
            conflict: 'Người khác đã lưu các cấp leo thang sau khi bạn mở trang này, nên thay đổi của bạn chưa được lưu. Hãy tải lại để xem phiên bản đó, rồi thực hiện lại thay đổi của bạn.',
            reload: 'Tải lại các cấp leo thang',
            load_error: 'Không đọc được các cấp leo thang, nên các cấp này không được hiển thị và không thể thay đổi tại đây.',
            retry: 'Thử lại',
            read_only: 'Bạn có thể xem nhưng không thể thay đổi các cấp leo thang.',
            empty: 'Chưa có cấp nào. Hãy thêm ít nhất một cấp, hoặc tắt tự động leo thang trong mục Thời hạn SLA.',
            last_saved: 'Lưu lần gần nhất: {date}.',
            err_ladder_empty: 'Hãy thêm ít nhất một cấp. Để ngừng tự động leo thang, hãy tắt tính năng này trong mục Thời hạn SLA.',
            err_too_many_tiers: 'Chỉ được có tối đa {max} cấp leo thang.',
            err_invalid_value: 'Cấp {n}: có giá trị không hợp lệ.',
            err_threshold_out_of_range: 'Cấp {n}: tỷ lệ phải là số nguyên từ 1 đến 100.',
            err_threshold_duplicate: 'Cấp {n}: đã có một cấp khác ở cùng tỷ lệ này.',
            err_tier_id_duplicate: 'Cấp {n}: cấp này bị lặp lại.',
            err_channels_required: 'Cấp {n}: hãy chọn ít nhất một cách gửi.',
            err_target_role_required: 'Cấp {n}: hãy chọn vai trò cần leo thang đến.',
            err_target_role_unknown: 'Cấp {n}: vai trò này không phải là vai trò đang hoạt động của tổ chức bạn.',
            err_reassign_target_required: 'Cấp {n}: hãy chỉ định một người hoặc một vai trò để phân công lại vụ việc.',
            err_target_user_unknown: 'Cấp {n}: {name} không phải là người dùng đang hoạt động của tổ chức bạn.',
        },
        deadline_labels: {
            access: 'Truy cập (DSAR)',
            deletion: 'Xóa',
            correction: 'Chỉnh sửa',
            portability: 'Chuyển dữ liệu',
            objection: 'Phản đối',
            restriction: 'Hạn chế',
            consent_withdrawal: 'Rút lại sự đồng ý',
            protective_measures: 'Biện pháp, giải pháp bảo vệ dữ liệu cá nhân',
            processing_information: 'Được biết về xử lý dữ liệu',
            consent_decision: 'Đồng ý hoặc không đồng ý',
            complaint_or_compensation: 'Khiếu nại, tố cáo, khởi kiện, yêu cầu bồi thường thiệt hại',
        },
        sla_source_platform_default: 'Chưa lưu: hồ sơ thuộc loại này mà không có thời hạn đã cài đặt nào xác định hạn sẽ được xác định hạn theo mặc định của nền tảng là {days} ngày, vốn không phải thời hạn theo pháp luật. Lưu một giá trị để đặt chỉ tiêu riêng của tổ chức.',
        sla_source_tenant_target: 'Chỉ tiêu SLA của tổ chức: dùng để xác định hạn cho hồ sơ thuộc loại này khi không có thời hạn đã cài đặt nào đang có hiệu lực xác định hạn.',
        request_type_enabled_not_offered: 'Đã bật, nhưng không phải là quyền theo các khung tuân thủ mà tổ chức đã cài đặt: biểu mẫu công khai không cung cấp loại yêu cầu này. Bỏ chọn để xóa thiết lập.',
        request_types_from_frameworks: 'Các loại yêu cầu được liệt kê là các quyền mà các khung tuân thủ đã cài đặt quy định.',
    },
    section_assignments: {
        title: 'Phân công mục',
        assign_section: 'Giao mục',
        section_ref: 'Mã mục',
        section_label: 'Nhãn mục',
        department_id: 'Phòng ban',
        assignee_id: 'Người được giao',
        mandatory: 'Bắt buộc',
        assign: 'Giao',
        no_assignments: 'Không có phân công mục nào',
        status: 'Trạng thái',
        completion: 'Hoàn thành',
        mark_complete: 'Hoàn tất',
    },
    consolidation: {
        title: 'Tiến độ hợp nhất',
        add_child: 'Thêm nhiệm vụ con',
        child_task_id: 'Mã nhiệm vụ con',
        department_id: 'Phòng ban',
        link: 'Liên kết',
        overall_completion: 'Tổng hoàn thành',
        departments_complete: 'Phòng ban hoàn thành',
        overdue: 'Quá hạn',
        ready: 'Sẵn sàng',
        not_ready: 'Chưa sẵn sàng',
        master_status: 'Trạng thái tổng',
        department_breakdown: 'Chi tiết phòng ban',
        department: 'Phòng ban',
        status: 'Trạng thái',
        completion: 'Hoàn thành',
        due_date: 'Ngày hết hạn',
        overdue_flag: 'Quá hạn',
        child_tasks: 'Nhiệm vụ con',
        task: 'Nhiệm vụ',
    },
    conflict_detection: {
        title: 'Phát hiện xung đột',
        unresolved: 'chưa giải quyết',
        no_conflicts: 'Không phát hiện xung đột',
        section: 'Mục',
        field: 'Trường',
        dept_a: 'Phòng ban A',
        value_a: 'Giá trị A',
        dept_b: 'Phòng ban B',
        value_b: 'Giá trị B',
        severity: 'Mức độ nghiêm trọng',
        status: 'Trạng thái',
        resolved: 'Đã giải quyết',
        open: 'Đang mở',
        resolve: 'Giải quyết',
    },
    org_maturity: {
        title: 'Mức độ Trưởng thành Tổ chức',
        departments: 'Phòng ban',
        department_hierarchy: 'Hệ thống Phòng ban',
        create_department: 'Tạo Phòng ban',
        department_name: 'Tên Phòng ban',
        department_code: 'Mã',
        level: 'Cấp',
        division: 'Khối',
        department: 'Phòng ban',
        team: 'Nhóm',
        parent_department: 'Phòng ban Cấp trên',
        head_user: 'Trưởng Phòng ban',
        no_departments: 'Chưa cấu hình phòng ban nào.',
        user_mappings: 'Ánh xạ Người dùng',
        map_user: 'Ánh xạ Người dùng',
        user_id: 'Mã Người dùng',
        role: 'Vai trò',
        no_users: 'Chưa có người dùng nào được ánh xạ cho phòng ban này.',
        remove: 'Xóa',
        framework_assignments: 'Gán Khung tuân thủ',
        assign_framework: 'Gán Khung tuân thủ',
        entity_id: 'Mã Thực thể',
        entity_type: 'Loại Thực thể',
        framework_id: 'Mã Khung tuân thủ',
        framework_code: 'Mã Khung',
        no_assignments: 'Chưa có gán khung tuân thủ nào.',
        compliance_rollup: 'Tổng hợp Tuân thủ',
        entity_name: 'Thực thể',
        total_assessments: 'Tổng Đánh giá',
        completed: 'Hoàn thành',
        completion_pct: '% Hoàn thành',
        open_gaps: 'Lỗ hổng Mở',
        critical_gaps: 'Nghiêm trọng',
        compliance_score: 'Điểm',
        no_rollup: 'Không có dữ liệu tổng hợp tuân thủ.',
        entity_summary: 'Tóm tắt Tuân thủ Thực thể',
        frameworks_assigned: 'Khung tuân thủ',
        overall_score: 'Điểm Tổng thể',
        entity_type_department: 'Phòng ban',
        entity_type_organization: 'Tổ chức',
        enter_entity_id_placeholder: 'Nhập ID thực thể để xem phân công',
        assignment_removed: 'Phân công đã xóa',
        assign_framework_error: 'Không thể gán khung tuân thủ',
        framework_assigned_announce: 'Khung đã gán cho thực thể',
        framework_assigned: 'Khung đã gán',
        load_assignments_error: 'Không thể tải các phân công',
        load_rollup_error: 'Không thể tải tổng hợp',
        user_removed: 'Người dùng đã xóa',
        map_user_error: 'Không thể liên kết người dùng',
        user_mapped_announce: 'Người dùng đã gán vào phòng ban',
        user_mapped: 'Người dùng đã gán',
        create_department_error: 'Không thể tạo phòng ban',
        department_created_announce: 'Phòng ban đã tạo',
        department_created: 'Phòng ban đã tạo',
        load_users_error: 'Không thể tải người dùng',
        load_departments_error: 'Không thể tải phòng ban',
    },
    onboarding_maturity: {
        title: 'Trưởng thành khởi tạo',
        quick_starts: 'Bắt đầu nhanh',
        progress: 'Tiến trình khởi tạo',
        personas: 'Đường dẫn vai trò',
        persona_onboarding: 'Khởi tạo theo vai trò',
        quick_start_scenarios: 'Kịch bản bắt đầu nhanh',
        start: 'Bắt đầu',
        restart: 'Bắt đầu lại',
        estimated_minutes: 'Thời gian ước tính (phút)',
        steps: 'Bước',
        scenario: 'Kịch bản',
        no_scenarios: 'Không có kịch bản bắt đầu nhanh.',
        persona_id: 'Mã vai trò',
        persona_label: 'Vai trò',
        role_description: 'Mô tả vai trò',
        primary_modules: 'Phân hệ chính',
        recommended_tours: 'Hướng dẫn đề xuất',
        sample_data_modules: 'Phân hệ dữ liệu mẫu',
        quick_start_scenario: 'Kịch bản bắt đầu nhanh',
        no_personas: 'Không có đường dẫn vai trò nào.',
        framework_setup: 'Khung tuân thủ',
        framework_code: 'Mã khung tuân thủ',
        framework_name: 'Tên khung tuân thủ',
        related_modules: 'Phân hệ liên quan',
        persona_relevance: 'Vai trò liên quan',
        no_frameworks: 'Không có tùy chọn thiết lập khung tuân thủ.',
        tier_config: 'Cấu hình phân hệ theo cấp',
        tier: 'Cấp',
        available_modules: 'Phân hệ khả dụng',
        module_count: 'Số phân hệ',
        overall_completion: 'Hoàn thành tổng thể',
        wizard_completed: 'Trình hướng dẫn đã hoàn thành',
        tours_completed: 'Hướng dẫn đã hoàn thành',
        quick_starts_completed: 'Bắt đầu nhanh đã hoàn thành',
        selected_frameworks: 'Khung tuân thủ đã chọn',
        no_progress: 'Không có dữ liệu tiến trình khởi tạo.',
        detected_persona: 'Vai trò được phát hiện',
        current_tier: 'Cấp hiện tại',
        completion_pct: 'Hoàn thành',
        progress_page: {
            loaded: 'Đã tải tiến trình',
            no_progress: 'Không có dữ liệu tiến trình onboarding.',
            overall: 'Tiến trình tổng thể',
            detected_persona: 'Vai trò được phát hiện',
            tier: 'Cấp',
            modules: 'phân hệ',
            wizard: 'Trình hướng dẫn',
            completed: 'Hoàn thành',
            pending: 'Chờ xử lý',
            tours: 'Hướng dẫn',
            quick_starts: 'Bắt đầu nhanh',
            frameworks: 'Khung tuân thủ đã chọn',
            no_frameworks: 'Chưa chọn khung tuân thủ nào.',
            go_to_wizard: 'Hoàn thành Trình hướng dẫn cài đặt',
            framework_selection: 'Lựa chọn khung tuân thủ',
            framework_toggle: 'Bật hoặc tắt các khung tuân thủ cho chương trình tuân thủ của bạn.',
            framework_updated: 'Đã cập nhật lựa chọn khung tuân thủ.',
            view_tours: 'Xem hướng dẫn',
            view_quick_starts: 'Xem bắt đầu nhanh',
            // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
            not_started: 'Chưa bắt đầu',
            not_started_body: 'Chưa có bản ghi tiến trình onboarding nào của bạn. Việc mở trang này không tạo bản ghi: bản ghi được tạo khi bạn hoàn thành Trình hướng dẫn cài đặt hoặc lưu một vai trò trong Đường dẫn vai trò. Các số liệu bên dưới đếm các hướng dẫn và bắt đầu nhanh bạn đã hoàn thành.',
        },
        persona_page: {
            description: 'Chọn một persona để xem các phân hệ, hướng dẫn và kịch bản bắt đầu nhanh được đề xuất.',
            no_personas: 'Không có đường dẫn persona nào.',
            modules: 'Phân hệ',
            tours: 'Hướng dẫn',
            quick_start: 'Bắt đầu nhanh',
            details: 'Chi tiết',
            primary_modules: 'Phân hệ chính',
            recommended_tours: 'Hướng dẫn đề xuất',
            framework_options: 'Khung tuân thủ',
            no_frameworks: 'Không có tùy chọn thiết lập khung tuân thủ.',
            no_frameworks_explain: 'Tổ chức của bạn chưa cài đặt khung tuân thủ nào, nên chưa có gì để thiết lập ở đây.',
            no_frameworks_action: 'Cài đặt gói thị trường để thêm các khung tuân thủ áp dụng cho bạn',
            start_tour: 'Bắt đầu',
            no_tours: 'Không có hướng dẫn nào.',
            help_center: 'Trung tâm trợ giúp',
            no_articles: 'Không tìm thấy bài viết nào.',
            primary: 'Chính',
            framework_code: 'Mã khung tuân thủ',
            framework_label: 'Nhãn',
            region: 'Khu vực',
            category: 'Danh mục',
            enabled: 'Đã bật',
            disabled: 'Đã tắt',
            tier_modules: 'Cấu hình phân hệ theo cấp',
            tiers_loaded: 'Đã tải cấu hình cấp',
            no_tier_config: 'Không có cấu hình cấp nào.',
            view_tours: 'Xem tour phân hệ',
            view_quick_start: 'Bắt đầu nhanh',
            toggle_framework: 'Chuyển đổi khung tuân thủ',
            framework_updated: 'Đã cập nhật lựa chọn khung tuân thủ.',
            tier_desc_light: 'Những điều cốt lõi về quyền riêng tư — Cơ bản tuân thủ PDPL',
            tier_desc_standard: 'Bộ tuân thủ đầy đủ — tất cả phân hệ với quy trình công việc',
            tier_desc_enterprise: 'Quản trị và tự động hóa nâng cao — đa tổ chức, báo cáo nhóm',
            persona_highlight: 'Các phân hệ của bạn',
            // fix-sprint-20260411-0417 Findings 4 + 5 + 11 — aria-label keys.
            aria_go_to_module: '{name} — đến phân hệ',
            aria_start_tour: 'Bắt đầu {title}',
            aria_quick_start: 'Khởi động nhanh: {title}',
            aria_help_article: 'Bài viết trợ giúp: {title}',
            aria_view_framework: '{label}{primary} — xem chi tiết khung quy định',
            aria_primary_for_persona: '{name} (chính cho persona này)',
            primary_for_persona_suffix: ' (chính cho persona này)',
            aria_view_in_persona_paths: '{name} — xem trong Persona Paths',
            progress_of_total: '{completed} trên {total} đã hoàn thành',
        },
        // Sprint 63.28.0 — Persona Paths revised: the picker, the role detail and the saved persona.
        persona_paths: {
            page_description: 'Tìm vai trò của bạn trong số các vai trò mà tổ chức của bạn có thể sử dụng. Các vai trò được khung tuân thủ đã cài đặt nêu tên được hiển thị trước. Chọn một vai trò để xem các khung tuân thủ nêu tên vai trò đó, các sổ đăng ký mà vai trò đó làm việc và hướng dẫn bắt đầu nhanh của vai trò.',
            recommended_heading: 'Đề xuất cho bạn',
            your_persona: 'Vai trò của bạn',
            source_chosen: 'Do bạn chọn',
            source_detected: 'Được xác định tự động',
            source_detected_role: 'Xác định từ vai trò người dùng của bạn',
            source_detected_default: 'Gợi ý làm điểm bắt đầu vì không có thông tin nào khác phù hợp',
            chosen_unavailable: 'Vai trò bạn đã chọn không còn khả dụng cho tổ chức của bạn vì gói thị trường mà vai trò đó cần đã bị gỡ bỏ. Vai trò được xác định tự động được hiển thị thay thế.',
            recommended_none_ranked: 'Chưa có khung tuân thủ đã cài đặt nào nêu tên một vai trò, vì vậy chỉ vai trò của bạn được hiển thị.',
            recommended_failed: 'Không đọc được vai trò của bạn.',
            recommended_loading: 'Đang đọc vai trò của bạn…',
            recommended_ranked: 'Được khung tuân thủ đã cài đặt nêu tên',
            toolbar_label: 'Lọc vai trò',
            search_label: 'Tìm vai trò',
            search_placeholder: 'Vai trò, mô tả, ngành, tên khung tuân thủ hoặc mã',
            family_label: 'Nhóm vai trò',
            family_all: 'Tất cả',
            sector_label: 'Ngành',
            sector_placeholder: 'Tất cả các ngành',
            market_label: 'Thị trường',
            market_all: 'Tất cả thị trường',
            named_toggle: 'Được khung tuân thủ đã cài đặt nêu tên',
            sort_label: 'Sắp xếp theo',
            sort_relevance: 'Mức độ liên quan',
            sort_name: 'Tên',
            sort_id: 'Mã vai trò',
            showing: 'Đang hiển thị {shown}/{total} vai trò',
            clear_filters: 'Xóa bộ lọc',
            list_label: 'Vai trò theo nhóm',
            list_heading: 'Tất cả vai trò',
            list_heading_named: 'Vai trò được khung tuân thủ đã cài đặt nêu tên',
            group_count_one: '1 vai trò',
            group_count: '{count} vai trò',
            relevance_named_one: 'Được 1 khung tuân thủ đã cài đặt nêu tên',
            relevance_named: 'Được {n} khung tuân thủ đã cài đặt nêu tên',
            relevance_legacy_only: 'Chỉ có khung tuân thủ cũ hoặc dự thảo được cài đặt',
            selected: 'đã chọn',
            load_failed: 'Không tải được danh sách vai trò.',
            reload: 'Tải lại',
            none_available_title: 'Không có vai trò nào khả dụng cho tổ chức của bạn',
            none_available_description: 'Hãy cài đặt một gói thị trường để sử dụng các vai trò của gói đó.',
            no_match_title: 'Không có vai trò nào phù hợp với bộ lọc',
            no_match_description: 'Hãy thay đổi hoặc xóa bộ lọc để xem thêm vai trò.',
            no_match_outside_one: '1 vai trò phù hợp nằm ngoài các khung tuân thủ đã cài đặt.',
            no_match_outside: '{count} vai trò phù hợp nằm ngoài các khung tuân thủ đã cài đặt.',
            hidden_matches_one: 'Còn 1 vai trò phù hợp nằm ngoài các khung tuân thủ đã cài đặt.',
            hidden_matches: 'Còn {count} vai trò phù hợp nằm ngoài các khung tuân thủ đã cài đặt.',
            show_all: 'Hiển thị tất cả',
            notice_unavailable: '{name} ({id}) cần gói thị trường cho {market}, mà tổ chức của bạn chưa cài đặt.',
            notice_unknown: 'Không có vai trò nào có mã "{id}", vì vậy không có vai trò nào được mở.',
            notice_unknown_use_case: 'Không có tình huống sử dụng nào có mã "{id}", vì vậy không có vai trò nào được mở.',
            detail_label: 'Chi tiết vai trò',
            detail_back: 'Quay lại danh sách vai trò',
            detail_loading: 'Đang tải vai trò này…',
            detail_failed: 'Không tải được chi tiết của vai trò này.',
            detail_market: 'Thị trường',
            detail_sectors: 'Ngành',
            detail_family: 'Nhóm vai trò',
            detail_persona_id: 'Mã vai trò',
            frameworks_heading: 'Khung tuân thủ cho vai trò này',
            frameworks_description: 'Đọc từ các gói pháp lý: mọi khung tuân thủ nêu tên vai trò này, với tư cách vai trò chính hoặc hỗ trợ.',
            frameworks_installed: 'Đã cài đặt',
            frameworks_not_installed: 'Chưa cài đặt',
            frameworks_not_installed_count: 'Chưa cài đặt ({count})',
            frameworks_none_installed: 'Không có khung tuân thủ đã cài đặt nào nêu tên vai trò này.',
            frameworks_only_retired_installed: 'Chỉ có văn bản cũ và dự thảo được cài đặt.',
            frameworks_none_not_installed: 'Mọi khung tuân thủ nêu tên vai trò này đều đã được cài đặt.',
            frameworks_retired: 'Văn bản cũ và dự thảo ({count})',
            role_primary: 'Chính',
            role_supporting: 'Hỗ trợ',
            status_active: 'Đang có hiệu lực',
            status_upcoming: 'Sắp có hiệu lực từ {date}',
            status_upcoming_undated: 'Sắp có hiệu lực',
            status_legacy: 'Cũ',
            status_draft: 'Dự thảo',
            status_unknown: 'Chưa nêu trạng thái',
            status_temporary_active: 'Có hiệu lực tạm thời',
            find_in_catalog: 'Tìm trong danh mục gói thị trường',
            find_in_catalog_aria: 'Tìm trong danh mục gói thị trường: {name}',
            basis_label: 'Căn cứ: {article}',
            basis_link_aria: 'Mở {article} của {name} trong thẻ Văn bản nguồn',
            attached_through: 'Gắn qua {parent}',
            second_read_pending: 'Đang chờ đọc soát lần hai',
            basis_missing: 'Chưa ghi nhận điều khoản',
            destinations_heading: 'Nơi thực hiện công việc',
            destinations_description: 'Các sổ đăng ký mà vai trò này làm việc, mỗi sổ được đánh dấu theo việc gói dịch vụ của bạn có bao gồm sổ đó hay không.',
            in_tier: 'Có trong gói của bạn',
            not_in_tier: 'Không có trong gói của bạn',
            tier_checking: 'Đang kiểm tra gói của bạn…',
            tier_unchecked: 'Không kiểm tra được gói của bạn, vì vậy không sổ đăng ký nào được đánh dấu.',
            tier_not_permitted: 'Vai trò người dùng của bạn không xem được các phân hệ có trong gói, vì vậy không sổ đăng ký nào được đánh dấu.',
            destination_no_access: 'Vai trò người dùng của bạn không mở được sổ đăng ký này',
            quick_start_heading: 'Bắt đầu nhanh của bạn',
            quick_start_none: 'Chưa có hướng dẫn bắt đầu nhanh cho vai trò này.',
            quick_start_not_listed: 'Hướng dẫn bắt đầu nhanh "{id}" không có trong danh sách của bạn, vì vậy không mục nào được làm nổi bật.',
            tours_heading: 'Hướng dẫn tham quan',
            tour_title: 'Hướng dẫn {name}',
            start_tour: 'Bắt đầu',
            start_tour_aria: 'Bắt đầu: {title}',
            starting: 'Đang bắt đầu…',
            no_tours: 'Không có hướng dẫn tham quan cho vai trò này.',
            help_heading: 'Bài viết trợ giúp',
            help_none: 'Không có bài viết trợ giúp cho các phân hệ của vai trò này.',
            use_cases_heading: 'Tình huống sử dụng',
            use_cases_none: 'Không có tình huống sử dụng đã được kiểm chứng nào nêu tên vai trò này là vai trò chính.',
            about_link: 'Về vai trò này',
            save_action: 'Dùng làm vai trò hướng dẫn của tôi',
            save_action_aria: 'Dùng làm vai trò hướng dẫn của tôi: {name}',
            saving: 'Đang lưu…',
            save_success: '{name} hiện là vai trò hướng dẫn của bạn.',
            already_yours: 'Đây là vai trò hướng dẫn của bạn.',
            profile_label: 'Vai trò hướng dẫn',
            change_in_persona_paths: 'Thay đổi trong Đường dẫn vai trò',
        },
        // Sprint 63.28.0 (B8) — the shared persona select.
        persona_select: {
            placeholder: 'Chọn vai trò',
            no_options: 'Không có vai trò phù hợp',
            checking: 'Đang kiểm tra các vai trò mà tổ chức của bạn có thể sử dụng…',
            availability_failed: 'Không kiểm tra được các vai trò mà tổ chức của bạn có thể sử dụng, vì vậy mọi vai trò đều được liệt kê.',
            retry: 'Thử lại',
        },
        quick_starts_page: {
            description: 'Thực hành các quy trình phổ biến với kịch bản bắt đầu nhanh có hướng dẫn.',
            no_scenarios: 'Không có kịch bản bắt đầu nhanh nào.',
            started: 'Đã bắt đầu nhanh',
            status_completed: 'Hoàn thành',
            status_in_progress: 'Đang tiến hành',
            status_not_started: 'Chưa bắt đầu',
            minutes: 'phút',
            steps: 'bước',
            step: 'Bước',
            restart: 'Bắt đầu lại',
            start: 'Bắt đầu',
            go_to_module: 'Đi đến phân hệ',
            next_step: 'Bước tiếp theo',
            complete: 'Hoàn thành',
            reset: 'Đặt lại',
            step_of: 'trong',
            action_navigate: 'Điều hướng',
            action_form_fill: 'Điền biểu mẫu',
            action_review: 'Xem xét',
            action_confirm: 'Xác nhận',
            step_changed: 'Bước {current} trong {total}: {title}',
            completed_message: 'Hoàn thành bắt đầu nhanh thành công!',
            reset_message: 'Đã đặt lại bắt đầu nhanh.',
        },
        // Persona Path i18n — labels, descriptions, and quick start titles
        persona_p_vn_01_label: 'DPO doanh nghiệp',
        persona_p_vn_01_description: 'Cán bộ bảo vệ dữ liệu điều hành chương trình bảo vệ dữ liệu cá nhân toàn doanh nghiệp: đánh giá tác động và hồ sơ nộp cơ quan có thẩm quyền, yêu cầu thực hiện quyền của chủ thể dữ liệu, quyết định về vi phạm dữ liệu cá nhân và đầu mối làm việc với cơ quan quản lý',
        persona_p_vn_02_label: 'Chủ sở hữu dữ liệu cấp bộ phận',
        persona_p_vn_02_description: 'Trưởng bộ phận chịu trách nhiệm báo cáo tuân thủ theo phòng ban',
        persona_p_vn_03_label: 'Quản lý tuân thủ thương mại điện tử',
        persona_p_vn_03_description: 'Quản lý tuân thủ tại nền tảng thương mại điện tử phụ trách đăng ký, thông báo thay đổi và hồ sơ đăng ký của nền tảng, cùng thông tin người bán và dữ liệu truy xuất nguồn gốc cung cấp khi cơ quan có thẩm quyền yêu cầu',
        persona_p_vn_04_label: 'Quản lý pháp chế và quan hệ cơ quan quản lý viễn thông',
        persona_p_vn_04_description: 'Trưởng bộ phận pháp chế tại doanh nghiệp viễn thông, xử lý yêu cầu cung cấp thông tin thuê bao của cơ quan có thẩm quyền và quản trị việc cung cấp thông tin',
        persona_p_vn_05_label: 'Trưởng quản trị dữ liệu',
        persona_p_vn_05_description: 'Trưởng phụ trách lập bản đồ dữ liệu, phân loại dữ liệu và chứng thực quản trị hằng năm, cùng thông báo và báo cáo của nhà cung cấp dịch vụ dữ liệu khi áp dụng',
        persona_p_vn_06_label: 'Trưởng an ninh mạng & vận hành pháp chế',
        persona_p_vn_06_description: 'Trưởng an ninh mạng phụ trách quyết định báo cáo sự cố, yêu cầu và chỉ đạo của cơ quan có thẩm quyền, ghi nhận việc cung cấp thông tin, và mức độ sẵn sàng về lưu trữ dữ liệu và hiện diện tại Việt Nam',
        persona_p_vn_07_label: 'Trưởng quản trị AI',
        persona_p_vn_07_description: 'Trưởng giám sát quản trị hệ thống AI, đánh giá rủi ro và phân loại',
        persona_p_vn_08_label: 'Trưởng chuyển dữ liệu cá nhân xuyên biên giới & quản trị nhà cung cấp',
        persona_p_vn_08_description: 'Trưởng phụ trách đánh giá tác động chuyển dữ liệu cá nhân xuyên biên giới và hồ sơ nộp cơ quan có thẩm quyền, biện pháp bảo vệ đối với bên nhận và nhà cung cấp, và kiểm tra yêu cầu lưu trữ dữ liệu tại Việt Nam',
        persona_p_vn_09_label: 'Trưởng ứng phó vi phạm',
        persona_p_vn_09_description: 'Trưởng điều phối thông báo vi phạm dữ liệu và ứng phó sự cố',
        persona_p_vn_10_label: 'Trưởng kiểm toán nội bộ & thanh tra của cơ quan quản lý',
        persona_p_vn_10_description: 'Trưởng thực hiện kiểm toán nội bộ và đáp ứng thanh tra của cơ quan quản lý trên nhiều khung tuân thủ: mức độ đầy đủ của bằng chứng, khoảng trống, gói bằng chứng kiểm toán và phê duyệt',
        persona_p_vn_11_label: 'Quản lý dữ liệu nhân sự & nhân viên',
        persona_p_vn_11_description: 'Quản lý nhân sự xử lý hồ sơ đồng ý của nhân viên, hợp đồng lao động điện tử và DSAR nội bộ',
        persona_p_vn_12_label: 'Trưởng tuân thủ công ty con đa quốc gia',
        persona_p_vn_12_description: 'DPO tại công ty con Việt Nam của công ty nước ngoài cần lớp tuân thủ địa phương',
        persona_p_vn_13_label: 'Trưởng tuân thủ giáo dục',
        persona_p_vn_13_description: 'Trưởng tuân thủ tại trường học nước ngoài quản lý bảo vệ dữ liệu học sinh và phụ huynh',
        persona_p_vn_14_label: 'Cố vấn pháp lý bên ngoài',
        persona_p_vn_14_description: 'Đối tác cao cấp tại công ty luật tư vấn tuân thủ PDPL cho nhiều tổ chức khách hàng',
        persona_p_vn_15_label: 'Quản lý vận hành công ty luật',
        persona_p_vn_15_description: 'Điều phối viên quản lý theo dõi sản phẩm giao và phối hợp thời hạn cho khách hàng',
        persona_p_vn_16_label: 'Chủ doanh nghiệp / Giám đốc điều hành',
        persona_p_vn_16_description: 'CEO hoặc Tổng giám đốc chịu trách nhiệm pháp lý về bảo vệ dữ liệu theo PDPL',
        // Quick Start scenario titles
        qs_dpia_filing_title: 'Hoàn thành hồ sơ DPIA đầu tiên',
        qs_dept_compliance_title: 'Nộp phần tuân thủ bộ phận đầu tiên',
        qs_ecommerce_consent_title: 'Chuẩn bị hồ sơ đăng ký nền tảng thương mại điện tử',
        qs_telecom_data_title: 'Thiết lập xử lý dữ liệu thuê bao viễn thông',
        qs_data_flow_title: 'Lập bản đồ luồng dữ liệu đầu tiên',
        qs_incident_response_title: 'Cấu hình quy trình ứng phó sự cố',
        qs_ai_risk_title: 'Chạy đánh giá rủi ro hệ thống AI',
        qs_cross_border_title: 'Đánh giá chuyển dữ liệu cá nhân xuyên biên giới',
        qs_breach_notification_title: 'Mô phỏng quy trình thông báo vi phạm',
        qs_audit_trail_title: 'Xem xét nhật ký kiểm toán tuân thủ',
        qs_hr_consent_title: 'Đăng ký hồ sơ đồng ý của nhân viên',
        qs_hr_consent_description: 'Hướng dẫn đăng ký đồng ý của nhân viên và tạo báo cáo phạm vi.',
        qs_subsidiary_dpia_title: 'Tạo đánh giá DPIA cho công ty con',
        qs_subsidiary_dpia_description: 'Hướng dẫn tạo DPIA cho hoạt động xử lý dữ liệu của công ty con.',
        qs_student_consent_title: 'Thiết lập thu thập đồng ý học sinh',
        qs_student_consent_description: 'Hướng dẫn tạo chính sách đồng ý phụ huynh/người giám hộ cho dữ liệu học sinh.',
        qs_legal_advisor_portfolio_title: 'Xem xét danh mục tuân thủ đa khách hàng',
        qs_legal_advisor_portfolio_description: 'Xem xét tình trạng tuân thủ trên nhiều tổ chức khách hàng.',
        qs_law_firm_ops_title: 'Theo dõi trạng thái sản phẩm giao cho khách hàng',
        qs_law_firm_ops_description: 'Giám sát tiến độ sản phẩm tư vấn và tương tác với khách hàng.',
        qs_ceo_dashboard_title: 'Xem bảng điều khiển tuân thủ cho lãnh đạo',
        qs_ceo_dashboard_description: 'Xem xét tình trạng tuân thủ tổng quan và các chỉ số đầu tư.',
    },
    // Sprint 43.2.0 batch 3 (E1) — Trang quản trị Mẫu phản hồi
    rights_requests: {
        response_templates: {
            title: 'Mẫu phản hồi',
            subtitle: 'Quản lý các mẫu thư phản hồi DSR mà ResponseGenerator dùng để soạn phản hồi cho chủ thể dữ liệu.',
            create_btn: 'Tạo mẫu mới',
            edit_btn: 'Chỉnh sửa mẫu',
            filter_right_type: 'Lọc theo loại quyền',
            filter_locale: 'Lọc theo ngôn ngữ',
            filter_status: 'Lọc theo trạng thái',
            all_right_types: 'Tất cả loại quyền',
            any_right_type: 'Bất kỳ (áp dụng cho mọi loại)',
            all_locales: 'Tất cả ngôn ngữ',
            all_statuses: 'Tất cả',
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            empty_state: 'Không có mẫu nào khớp với bộ lọc hiện tại.',
            columns: {
                template_id: 'Mã mẫu',
                outcome: 'Kết quả',
                locale: 'Ngôn ngữ',
                right_type: 'Loại quyền',
                is_active: 'Trạng thái',
                last_updated: 'Cập nhật lần cuối',
                actions: 'Hành động',
            },
            actions: {
                edit: 'Chỉnh sửa',
                clone: 'Sao chép',
                deactivate: 'Ngừng hoạt động',
                reactivate: 'Kích hoạt lại',
                preview: 'Xem trước',
            },
            form: {
                template_id: 'Mã mẫu',
                template_id_help: 'Mã định danh chữ thường, ví dụ dsr_access_fulfilled_vi',
                outcome: 'Kết quả',
                locale: 'Ngôn ngữ',
                right_type: 'Loại quyền',
                subject: 'Tiêu đề',
                body: 'Nội dung mẫu',
                body_help: 'Dùng các biến như {case_reference}, {data_subject_name}, {fulfilled_at}. Tối thiểu 15 dòng cho thư nhiều mục.',
                is_active: 'Đang hoạt động',
                official_form_id: 'Mã biểu mẫu chính thức (tùy chọn)',
            },
            outcomes: {
                fulfilled: 'Đã đáp ứng',
                partially_fulfilled: 'Đáp ứng một phần',
                denied: 'Từ chối',
            },
            clone_modal_title: 'Sao chép mẫu',
            clone_target_locale: 'Ngôn ngữ đích',
            clone_help: 'Một mẫu mới sẽ được tạo ở ngôn ngữ đích với mẫu hiện tại làm điểm khởi đầu.',
            preview_modal_title: 'Xem trước mẫu',
            preview_note: 'Xem trước dùng các biến từ vụ việc demo DSR-DEMO-0020. Khi gửi thật, dữ liệu vụ việc thực tế sẽ thay thế.',
            preview_unavailable: 'Xem trước cần một vụ việc thực tế. Gắn mẫu này vào vụ việc rồi dùng chức năng Soạn phản hồi trong trang chi tiết.',
            deactivate_confirm: 'Ngừng hoạt động mẫu này? Mẫu vẫn tiếp tục áp dụng cho các vụ việc đang tham chiếu nó.',
            toasts: {
                created: 'Đã tạo mẫu',
                updated: 'Đã cập nhật mẫu',
                deactivated: 'Đã ngừng hoạt động mẫu',
                reactivated: 'Đã kích hoạt lại mẫu',
                cloned: 'Đã sao chép mẫu',
                load_error: 'Không tải được danh sách mẫu',
                save_error: 'Không lưu được mẫu',
                clone_error: 'Không sao chép được mẫu',
            },
        },
    },
    // Sprint 43.2.0 batch 3 (E2 + E4) — Trang quản trị tổ chức
    organizations: {
        departments: {
            title: 'Phòng ban',
            subtitle: 'Quản lý các phòng ban dùng để phân luồng vụ việc DSR, nhiệm vụ DPIA và quy trình tuân thủ.',
            create_btn: 'Tạo phòng ban',
            edit_btn: 'Chỉnh sửa phòng ban',
            columns: {
                name: 'Tên',
                description: 'Mô tả',
                active_cases: 'Vụ việc đang mở',
                members: 'Thành viên',
                head_user: 'Người phụ trách',
                status: 'Trạng thái',
                actions: 'Hành động',
            },
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            form: {
                name: 'Tên',
                description: 'Mô tả',
                head_user: 'Người phụ trách phòng ban',
                head_user_none: '— Không có —',
            },
            actions: {
                edit: 'Chỉnh sửa',
                deactivate: 'Ngừng hoạt động',
                reactivate: 'Kích hoạt lại',
                delete: 'Xóa',
                manage_members: 'Quản lý thành viên',
            },
            delete_modal_title: 'Xóa phòng ban',
            delete_warning: 'Phòng ban này vẫn còn vụ việc đang mở. Hãy chọn phòng ban nhận bàn giao trước khi xóa.',
            delete_no_cases: 'Phòng ban không còn vụ việc đang mở và có thể xóa an toàn.',
            reassign_prompt: 'Bàn giao các vụ việc đang mở cho:',
            reassign_none_available: 'Không có phòng ban khác đang hoạt động. Hãy tạo phòng ban khác trước.',
            member_section_title: 'Thành viên',
            add_member: 'Thêm thành viên',
            remove_member: 'Gỡ',
            member_role: 'Vai trò',
            no_members: 'Chưa có thành viên nào trong phòng ban này.',
            toasts: {
                created: 'Đã tạo phòng ban',
                updated: 'Đã cập nhật phòng ban',
                deactivated: 'Đã ngừng hoạt động phòng ban',
                reactivated: 'Đã kích hoạt lại phòng ban',
                deleted: 'Đã xóa phòng ban',
                reassigned_count: 'Đã bàn giao {count} vụ việc',
                member_added: 'Đã thêm thành viên',
                member_removed: 'Đã gỡ thành viên',
                load_error: 'Không tải được danh sách phòng ban',
                save_error: 'Không lưu được phòng ban',
                delete_error: 'Không xóa được phòng ban',
            },
        },
        retention_policies: {
            title: 'Chính sách lưu giữ',
            subtitle: 'Cấu hình thời hạn lưu giữ theo loại dữ liệu. Tài liệu xác minh danh tính DSR dùng chính sách này để đóng dấu retention_expires_at cho các lần tải lên sau.',
            create_btn: 'Tạo chính sách',
            edit_btn: 'Chỉnh sửa chính sách',
            columns: {
                target_type: 'Loại dữ liệu',
                retention_days: 'Thời hạn (ngày)',
                owner: 'Chủ sở hữu chính sách',
                effective_from: 'Hiệu lực từ',
                last_reviewed: 'Rà soát lần cuối',
                status: 'Trạng thái',
                actions: 'Hành động',
            },
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            form: {
                target_type: 'Loại dữ liệu',
                target_type_help: 'Loại dữ liệu chịu chi phối bởi chính sách lưu giữ. Hiện chỉ hỗ trợ tài liệu xác minh danh tính DSR.',
                retention_days: 'Thời hạn lưu giữ (ngày)',
                retention_days_help: 'Từ 1 đến 3650 ngày (tối đa 10 năm).',
                owner: 'Chủ sở hữu chính sách',
                owner_help: 'Thường là DPO. Chịu trách nhiệm quyết định lưu giữ theo DG-2.',
                effective_from: 'Hiệu lực từ',
            },
            target_types: {
                dsr_identity_verification_document: 'Tài liệu xác minh danh tính DSR',
            },
            deactivate_notice: 'Ngừng hoạt động không ảnh hưởng đến các tệp đã được đóng dấu — chỉ các lần tải lên sau sẽ không còn nhận thời hạn lưu giữ.',
            deactivate_confirm: 'Ngừng hoạt động chính sách lưu giữ này?',
            toasts: {
                created: 'Đã tạo chính sách lưu giữ',
                updated: 'Đã cập nhật chính sách lưu giữ',
                deactivated: 'Đã ngừng hoạt động chính sách lưu giữ',
                load_error: 'Không tải được chính sách lưu giữ',
                save_error: 'Không lưu được chính sách lưu giữ',
            },
        },
    },
};
export const viModuleTips = {
    show_guide: 'Hiện hướng dẫn',
    boundary_show_statements: 'Hiện các tuyên bố',
    boundary_hide_statements: 'Ẩn các tuyên bố',
    boundary_summary: '{count} tuyên bố từ {frameworks} khung tuân thủ đã cài đặt',
    hide_guide: 'Ẩn hướng dẫn',
    title: 'Hướng dẫn phân hệ',
    loading_aria: 'Đang tải gợi ý phân hệ',
    dismiss: 'Đóng hướng dẫn phân hệ',
    title_prefix: 'Mẹo phân hệ:',
    related_frameworks: 'Khung tuân thủ liên quan',
    quick_start_label: 'Bắt đầu nhanh',
    related_forms_note: 'Các biểu mẫu tuân thủ liên quan có sẵn bên dưới — hãy tìm huy hiệu Mẫu số để nhận diện các biểu mẫu quy định chính thức của Việt Nam.',
    read_full_guide: 'Xem hướng dẫn đầy đủ',
    view_full_requirements: 'Xem đầy đủ yêu cầu',
    relevant_for: 'Phù hợp cho',
    key_procedure: 'Quy trình chính',
    deletion_orchestration: 'Phối hợp yêu cầu xóa dữ liệu giữa các hệ thống và nhà cung cấp bên thứ ba với quy trình xác minh và nhật ký kiểm toán.',
    // fix-sprint #431 — đã nêu quyền chuyển dữ liệu là bắt buộc; nay liệt kê các
    // quyền như trong trợ giúp, không nêu tên văn bản nào.
    rights_requests: 'Quản lý các yêu cầu thực hiện quyền của chủ thể dữ liệu: truy cập, chỉnh sửa, xóa, hạn chế xử lý, phản đối, rút lại sự đồng ý và các biện pháp bảo vệ, theo những gì các khung tuân thủ đã cài đặt quy định. Khi một khung tuân thủ đã cài đặt quy định thời hạn cho yêu cầu thực hiện quyền, loại yêu cầu mà khung đó không quy định thời hạn sẽ bị từ chối khi tạo vụ việc.',
    consent: 'Quản lý chính sách và quy trình thu thập đồng ý. Bắt buộc để tuân thủ PDPL, Luật Thương mại điện tử và GDPR.',
    consent_properties: 'Cấu hình các điểm thu thập đồng ý và thuộc tính tích hợp CMP cho theo dõi đồng ý web và ứng dụng.',
    data_mapping: 'Lập bản đồ luồng dữ liệu trong tổ chức — hoạt động xử lý, hệ thống và chuyển dữ liệu xuyên biên giới.',
    data_mapping_registry: 'Duy trì danh mục chi tiết theo từng trường của mọi yếu tố dữ liệu cá nhân mà tổ chức nắm giữ. Gán từng yếu tố vào Danh mục dữ liệu, ghi đè độ nhạy hoặc thời hạn lưu trữ khi cần, và nhập từ đăng ký quy định (PDPL) mà không mất các chỉnh sửa của người vận hành.',
    data_mapping_ropa_completeness: 'Kiểm tra độ đầy đủ theo từng trường đối với mọi Hoạt động xử lý theo các trường bắt buộc của khung tuân thủ (PDPL, GDPR, Nghị định 165). Xác định các thiếu sót nghiêm trọng / cao / có điều kiện trước khi nộp hồ sơ và nhảy trực tiếp vào trình chỉnh sửa hoạt động để khắc phục.',
    assessments: 'Tạo và quản lý Đánh giá tác động bảo vệ dữ liệu (DPIA) và đánh giá rủi ro hệ thống AI. Bắt buộc theo Điều 24 Luật PDPL 91/2025.',
    classification: 'Phân loại và gán nhãn tài sản dữ liệu theo độ nhạy cảm, danh mục và yêu cầu quy định.',
    incidents: 'Ghi nhận và quản lý sự cố bảo mật và vi phạm dữ liệu. Bắt buộc cho thông báo vi phạm theo PDPL và Luật An ninh mạng.',
    incident_ops: 'Phối hợp hoạt động ứng phó sự cố với kích hoạt kịch bản, đường dẫn leo thang và bảng điều khiển phối hợp thời gian thực.',
    monitoring: 'Giám sát KPI tuân thủ, sức khỏe chương trình và tỷ lệ hoàn thành nhiệm vụ trong toàn tổ chức.',
    vendor_risk: 'Đánh giá và quản lý rủi ro nhà cung cấp thông qua bảng câu hỏi, danh mục rủi ro và chấm điểm tuân thủ.',
    governance: 'Quản lý quản trị nhà cung cấp, kịch bản và yêu cầu tuân thủ bên thứ ba.',
    program_governance: 'Cấu hình đánh giá mức độ trưởng thành quản trị chương trình, số liệu bảng điều khiển và khung chấm điểm tuân thủ.',
    discovery: 'Khám phá và kiểm kê tài sản dữ liệu trong tổ chức với quy trình quét và phân loại tự động.',
    discovery_cycles: 'Lập lịch và chạy các chu kỳ khám phá định kỳ để quét lại hệ thống, hiển thị các thay đổi delta và chuyển kết quả qua quy trình xem xét, phê duyệt và công bố.',
    connectors: 'Cấu hình tích hợp và kết nối dữ liệu để thu thập dữ liệu tuân thủ tự động.',
    task_management: 'Quản lý nhiệm vụ tuân thủ, phân công và thời hạn giữa các nhóm và phân hệ.',
    audit_trail: 'Xem và tìm kiếm nhật ký kiểm toán hợp nhất cho tất cả hoạt động tuân thủ và thu thập bằng chứng.',
    evidence_packs: 'Tập hợp, xác minh và nộp gói bằng chứng kiểm toán cho tuân thủ pháp quy. Gói bằng chứng thu thập sự kiện kiểm toán theo khung, loại thực thể và khoảng thời gian thành gói xuất cho xem xét nội bộ hoặc nộp pháp quy.',
    audit_periods: 'Xác định và quản lý kỳ báo cáo kiểm toán theo khung pháp quy. Kỳ kiểm toán thiết lập ranh giới thời gian và chính sách lưu giữ cho thu thập bằng chứng và chu kỳ báo cáo.',
    qs_evidence_pack: 'Tập hợp gói bằng chứng đầu tiên',
    qs_audit_period: 'Tạo kỳ báo cáo kiểm toán',
    compliance_forms: 'Duyệt danh mục biểu mẫu tuân thủ và tạo phiên bản biểu mẫu cho nộp hồ sơ quy định.',
    sector_overlays: 'Cài đặt các lớp phủ ngành tùy chọn mà tổ chức của bạn phải tuân thủ, chọn chế độ Trực tiếp hoặc Chỉ tham khảo cho từng lớp phủ, và làm việc trên các sổ đăng ký ngành mà một lớp phủ được cài đặt ở chế độ Trực tiếp đưa vào sử dụng, sắp xếp theo ngành trong Lớp phủ ngành. Có trong gói Enterprise.',
    sector_overlays_overview: 'Lớp phủ ngành › Quản lý lớp phủ › Tổng quan: các sổ đăng ký ngành mà các lớp phủ đang hoạt động đưa vào sử dụng, sắp xếp theo ngành, kèm các lớp phủ cung cấp từng nhóm và các màn hình dùng chung mà từng ngành sử dụng ở nơi khác trong menu. Một nhóm ngành chỉ xuất hiện tại đây và trong menu Lớp phủ ngành khi một lớp phủ khai báo nhóm đó được cài đặt ở chế độ Trực tiếp; lớp phủ được cài đặt ở chế độ Chỉ tham khảo không thêm màn hình nào. Cài đặt lớp phủ và thay đổi chế độ của lớp phủ tại Lớp phủ ngành › Quản lý lớp phủ › Danh mục lớp phủ.',
    partner_workspace: 'Quản lý không gian làm việc đối tác và đại lý với quyền truy cập phạm vi, quy trình đánh giá tuân thủ và công cụ cộng tác.',
    localization: 'Quản lý khung tuân thủ, gói ngôn ngữ và cài đặt bản địa hóa.',
    onboarding: 'Theo dõi tiến trình hướng dẫn, hoàn thành bắt đầu nhanh và theo dõi lộ trình tuân thủ theo vai trò.',
    organizations: 'Quản lý cấu trúc tổ chức, phòng ban và cấu hình đa tổ chức.',
    privacy_scanner: 'Giám sát tuân thủ quyền riêng tư trang web với quét tự động và phát hiện vấn đề.',
    access_accountability: 'Cấu hình chiến dịch đánh giá quyền truy cập và chính sách trách nhiệm cho quản trị truy cập dữ liệu.',
    dashboard: 'Xem tổng quan tuân thủ với tóm tắt trạng thái thời gian thực, phạm vi khung và các chỉ số tuân thủ chính.',
    platform: 'Quản lý cài đặt bảo mật nền tảng bao gồm khóa API, quyền truy cập khẩn cấp và cấu hình SSO.',
    framework_vn_pdpl: 'Luật Bảo vệ Dữ liệu Cá nhân VN (PDPL)',
    framework_vn_cybersecurity: 'Luật An ninh mạng VN',
    framework_vn_ecommerce: 'Luật Thương mại điện tử VN',
    framework_vn_ai_law: 'Luật AI VN',
    framework_vn_telecom: 'Luật Viễn thông VN',
    framework_vn_banking_circular_83: 'Thông tư 83/2025/TT-NHNN — Hệ thống kiểm soát nội bộ ngân hàng',
    framework_gdpr: 'EU GDPR',
    cross_framework: 'Liên khung — hỗ trợ tất cả hoạt động tuân thủ quy định',
    qs_dpia_filing: 'Hoàn thành hồ sơ DPIA đầu tiên',
    qs_dept_compliance: 'Nộp phần tuân thủ phòng ban đầu tiên',
    qs_ecommerce_consent: 'Chuẩn bị hồ sơ đăng ký nền tảng thương mại điện tử',
    qs_telecom_data: 'Thiết lập xử lý dữ liệu thuê bao viễn thông',
    qs_data_flow: 'Lập bản đồ luồng dữ liệu đầu tiên',
    qs_incident_response: 'Cấu hình quy trình ứng phó sự cố',
    qs_ai_risk: 'Thực hiện đánh giá rủi ro hệ thống AI',
    qs_cross_border: 'Đánh giá chuyển dữ liệu cá nhân xuyên biên giới',
    qs_breach_notification: 'Mô phỏng quy trình thông báo vi phạm',
    qs_audit_trail: 'Xem xét nhật ký kiểm toán tuân thủ',
    procedure_data_deletion_fulfillment: 'Thực hiện xóa dữ liệu',
    procedure_cross_system_deletion: 'Xóa liên hệ thống',
    procedure_dsar_intake: 'Tiếp nhận yêu cầu quyền chủ thể dữ liệu',
    procedure_cmp_property_config: 'Cấu hình thuộc tính CMP',
    procedure_consent_collection: 'Chính sách thu thập đồng ý',
    procedure_ropa_filing: 'Nộp hồ sơ hoạt động xử lý',
    procedure_cross_border_assessment: 'Đánh giá chuyển dữ liệu xuyên biên giới',
    procedure_data_classification: 'Phân loại dữ liệu',
    procedure_breach_notification: 'Thông báo vi phạm dữ liệu',
    procedure_incident_timeline: 'Theo dõi dòng thời gian sự cố',
    procedure_playbook_escalation: 'Tăng cấp quy trình hành động',
    procedure_compliance_kpi_monitoring: 'Giám sát KPI tuân thủ',
    procedure_vendor_risk_assessment: 'Đánh giá rủi ro nhà cung cấp',
    procedure_transfer_mechanism_review: 'Xem xét cơ chế chuyển dữ liệu',
    procedure_program_maturity: 'Đánh giá mức độ trưởng thành chương trình',
    procedure_data_discovery: 'Khám phá tài sản dữ liệu',
    procedure_connector_setup: 'Cài đặt và cấu hình kết nối',
    procedure_task_assignment: 'Phân công và ủy thác công việc',
    procedure_audit_evidence: 'Thu thập bằng chứng kiểm toán',
    procedure_form_submission: 'Nộp biểu mẫu tuân thủ',
    procedure_partner_onboarding: 'Quy trình onboarding đối tác',
    procedure_partner_access: 'Quyền truy cập đối tác',
    procedure_framework_pack_install: 'Cài đặt gói khung quy định',
    procedure_language_pack_deploy: 'Triển khai gói ngôn ngữ',
    procedure_org_onboarding: 'Cài đặt onboarding tổ chức',
    procedure_multi_org_config: 'Cấu hình đa tổ chức',
    procedure_privacy_scan: 'Quét tuân thủ quyền riêng tư',
    procedure_access_review: 'Chiến dịch xem xét quyền truy cập',
    procedure_dpia_filing: 'Quy trình nộp DPIA',
    security: 'Cấu hình bảo mật nền tảng: quản lý khóa API, chính sách phiên, thực thi MFA, tích hợp SSO, danh sách IP cho phép và kiểm soát truy cập khẩn cấp.',
    settings: 'Cấu hình toàn nền tảng: tùy chọn thông báo, quản lý tên miền, lịch xuất dữ liệu và cấu hình phân hệ cho tổ chức của bạn.',
    users: 'Quản lý tài khoản người dùng: tạo và quản lý tài khoản, phân vai trò, cấu hình thành viên phòng ban và theo dõi hoạt động đăng nhập.',
    roles: 'Quản lý vai trò và quyền: định nghĩa vai trò tùy chỉnh, cấu hình quyền chi tiết theo phân hệ và thực thi chính sách phân tách nhiệm vụ.',
    profile: 'Cài đặt hồ sơ cá nhân: tên hiển thị, thông tin liên hệ, ngôn ngữ ưu tiên, tùy chọn hướng dẫn phân hệ, cài đặt trợ năng và quản lý mật khẩu.',
    consent_properties_bindings: 'Quản lý ràng buộc chính sách đồng ý trên các thuộc tính số. Theo dõi phiên bản chính sách được triển khai cho từng thuộc tính, xác định ràng buộc lỗi thời và phối hợp triển khai chính sách.',
    consent_properties_templates: 'Quản lý mẫu chuẩn: định nghĩa cấu hình đồng ý tiêu chuẩn, so sánh thuộc tính với mẫu và triển khai thay đổi mẫu trên các nhóm thuộc tính.',
    consent_properties_compliance: 'Giám sát tuân thủ đồng ý: phát hiện không nhất quán cấu hình giữa thuộc tính và mẫu chuẩn, quản lý yêu cầu thay đổi qua quy trình phê duyệt và tạo gói bằng chứng cho sẵn sàng kiểm toán.',
    consent_properties_operations: 'Quản lý vận hành thuộc tính đồng ý: cấu hình lịch chụp ảnh tự động, tổ chức thuộc tính theo nhóm và xem xét nhật ký kiểm toán thuộc tính đồng ý.',
    consent_properties_audit: 'Kiểm toán và phân tích thuộc tính đồng ý: xem xét lịch sử thay đổi cấu hình, phân tích xu hướng tuân thủ thuộc tính và tạo báo cáo tuân thủ cho cơ quan quản lý.',
    classification_taxonomies: 'Định nghĩa và quản lý phân loại dữ liệu theo chiều độ nhạy cảm và mức độ quan trọng. Cấu hình cấp độ phân loại, yêu cầu xử lý và cấp rủi ro AI.',
    classification_labeling: 'Áp dụng nhãn phân loại cho tài sản dữ liệu. Gửi nhãn để xem xét, quản lý phân loại hàng loạt và theo dõi trạng thái gán nhãn trên toàn bộ kiểm kê dữ liệu.',
    classification_lifecycle: 'Theo dõi vòng đời tài sản dữ liệu đã phân loại qua các giai đoạn: nháp, đang sử dụng, đang xem xét, lưu trữ và chờ xóa. Giám sát thời hạn lưu giữ và kích hoạt quy trình xóa.',
    classification_gaps: 'Phát hiện và quản lý khoảng trống phân loại trên toàn kiểm kê dữ liệu. Chạy phát hiện khoảng trống để xác định tài sản chưa gán nhãn và thiếu phân công vòng đời, sau đó theo dõi khắc phục.',
    classification_reviews: 'Quản lý nhiệm vụ xem xét phân loại với bộ đếm leo thang. Xem xét nhãn đề xuất, xử lý ngoại lệ và đảm bảo quyết định phân loại kịp thời.',
    incidents_breach: 'Quản lý thông báo vi phạm dữ liệu và báo cáo cơ quan. Tạo gói thông báo, theo dõi thời hạn quy định và ghi nhận toàn bộ dòng thời gian ứng phó vi phạm.',
    incidents_authority: 'Xử lý yêu cầu chính thức từ cơ quan quản lý. Theo dõi các yêu cầu, tập hợp gói bằng chứng, quản lý danh sách kiểm tra xác minh và đáp ứng thời hạn phản hồi.',
    incidents_disclosure: 'Duy trì sổ đăng ký công bố tập trung cho tất cả các liên lạc với cơ quan quản lý. Ghi nhận ngày công bố, kênh liên lạc, người nhận và tóm tắt nội dung để tuân thủ kiểm toán.',
    incidents_reports: 'Tạo và quản lý báo cáo sự cố để đánh giá nội bộ và nộp cho cơ quan quản lý. Tạo tóm tắt sự cố chi tiết, theo dõi trạng thái báo cáo và xuất tài liệu.',
    incidents_tasks: 'Phối hợp các nhiệm vụ ứng phó sự cố giữa các nhóm. Tạo danh sách kiểm tra, phân công người phụ trách, theo dõi ưu tiên, leo thang mục bị chặn và giám sát tiến độ.',
    incidents_supplements: 'Quản lý tài liệu bổ sung theo yêu cầu của cơ quan quản lý sau thông báo vi phạm ban đầu. Theo dõi yêu cầu bổ sung, chuẩn bị gói phản hồi và giám sát thời hạn nộp.',
    incidents_remediation: 'Theo dõi các hành động khắc phục được giao cho các bộ phận và người dùng để xử lý nguyên nhân gốc rễ và tác động của sự cố. Giám sát thời hạn, xác minh hoàn thành và ghi nhận bằng chứng.',
    incident_ops_playbooks: 'Tạo và quản lý kịch bản ứng phó sự cố. Xác định đường dẫn leo thang, phân công nhóm ứng phó và kích hoạt kịch bản trong sự cố thực tế.',
    incident_ops_sla: 'Theo dõi SLA ứng phó sự cố và thời hạn quy định. Giám sát tuân thủ thời hạn thông báo luật định và đo lường chỉ số hiệu suất ứng phó.',
    incident_ops_evidence: 'Duy trì chuỗi bằng chứng sự cố và thực hiện đánh giá sau sự cố. Tạo gói xuất sẵn sàng cho cơ quan quản lý với tài liệu sự cố đầy đủ.',
    data_mapping_systems: 'Đăng ký và quản lý các hệ thống xử lý dữ liệu cá nhân. Ghi nhận quyền sở hữu hệ thống, danh mục dữ liệu, mục đích xử lý và chấm điểm tuân thủ.',
    data_mapping_flows: 'Lập bản đồ luồng dữ liệu giữa các hệ thống, bao gồm chuyển dữ liệu xuyên biên giới. Ghi nhận đường đi dữ liệu, cơ chế chuyển và khu vực pháp lý người nhận.',
    data_mapping_activities: 'Ghi nhận hoạt động xử lý và tạo Hồ sơ Hoạt động Xử lý (RoPA). Liên kết hoạt động với cơ sở pháp lý, mục đích và danh mục dữ liệu.',
    data_mapping_governance: 'Quản lý phân công quản trị dữ liệu, chu kỳ chứng thực và đánh giá rủi ro. Theo dõi quyền sở hữu quản trị và lịch xem xét trên toàn kiểm kê dữ liệu.',
    data_mapping_reference: 'Quản lý dữ liệu tham chiếu cho lập bản đồ dữ liệu: danh mục dữ liệu, mục đích xử lý, chính sách lưu giữ và cấu hình xuất.',
    data_mapping_categories: 'Quản lý danh mục dữ liệu: tên, độ nhạy cảm, ví dụ và nhãn. Cốt lõi của mọi hoạt động phân loại và ánh xạ dữ liệu.',
    data_mapping_purposes: 'Lập danh mục các mục đích xử lý với cơ sở pháp lý. Dùng cho ROPA, DPIA và kiểm toán hợp pháp.',
    data_mapping_retention: 'Cấu hình chính sách lưu trữ, phương pháp xử lý và chu kỳ đánh giá. Theo yêu cầu Nghị định 13/2023 và 356/2025.',
    data_mapping_exports: 'Xuất ROPA, hệ thống, mục đích, danh mục sang CSV, XLSX, PDF hoặc DOCX. Tất cả bản xuất hiển thị trong File Browser.',
    data_mapping_attestation: 'Lập lịch và giám sát chu kỳ chứng thực dữ liệu. Theo dõi hoàn thành chứng thực trên toàn bộ kho dữ liệu.',
    data_mapping_review: 'Xem xét phân loại dữ liệu: nhiệm vụ đánh giá, tín hiệu chất lượng, trạng thái duyệt.',
    data_mapping_risk: 'Đánh giá rủi ro cho các hoạt động xử lý: khung đánh giá, biện pháp giảm thiểu, và theo dõi hiệu quả.',
    data_mapping_evidence: 'Quản lý bằng chứng gắn với hoạt động xử lý: chứng chỉ bảo mật, hợp đồng DPA, báo cáo DPIA.',
    data_mapping_inventory: 'Xem tổng quan kho phân loại dữ liệu: nhãn, mức độ nhạy cảm, và phân bổ trên toàn tổ chức.',
    data_mapping_exceptions: 'Đăng ký ngoại lệ phân loại: lý do, người phê duyệt, thời hạn hiệu lực và lộ trình khắc phục.',
    data_mapping_history: 'Theo dõi lịch sử thay đổi phân loại dữ liệu: ai đã thay đổi gì, khi nào, và lý do.',
    access_accountability_logs: 'Luồng thời gian thực các sự kiện truy cập đặc quyền. Dùng cùng trang chính sách để xác định ai có thể tiếp cận mỗi vùng dữ liệu; dùng cùng trang điều tra để theo dõi bất thường.',
    access_accountability_policies: 'Soạn chính sách vùng truy cập: khai báo vai trò và người dùng nào có thể tiếp cận mỗi vùng dữ liệu, với ngoại lệ có giới hạn thời gian. Thúc đẩy kiểm tra thực thi xuất hiện trong nhật ký.',
    access_accountability_anchoring: 'Neo chống giả mạo nhật ký truy cập vào chuỗi hash. Bắt buộc cho tính toàn vẹn dấu vết kiểm toán theo Luật An ninh mạng VN.',
    access_accountability_investigations: 'Không gian làm việc case để điều tra sự kiện truy cập khả nghi. Dòng thời gian, nhật ký liên kết, gói bằng chứng và quy trình đóng case trong một góc nhìn.',
    assessment_triggers: 'Quy tắc tự động mở DPIA hoặc hồ sơ nộp khi thay đổi thượng nguồn (hoạt động xử lý mới, nhà cung cấp mới, luồng xuyên biên giới mới) thỏa mãn điều kiện trigger.',
    connectors_gap_register: 'Danh mục connector chưa triển khai hoặc chưa hoàn thiện. Phân loại mục, gán chủ sở hữu, theo dõi đóng gap theo roadmap.',
    connectors_system_inventory: 'Kho hệ thống có thể tiếp cận qua connector. Nguồn sự thật duy nhất cho bản đồ dữ liệu.',
    connectors_traceability: 'Dòng dõi đầu cuối của phần tử dữ liệu xuyên qua các hệ thống liên kết bằng connector. Thiết yếu cho độ chính xác ROPA và đáp ứng yêu cầu chủ thể.',
    consent_properties_change_requests: 'Pipeline các thay đổi đề xuất cho một thuộc tính đồng thuận (thêm mục đích, ngôn ngữ mới, cập nhật liên kết chính sách). Duyệt quản trị trước khi triển khai.',
    consent_properties_comparison: 'So sánh song song hình dạng thuộc tính đồng thuận giữa các domain hoặc triển khai. Phát hiện drift làm yếu khả năng thực thi.',
    forms: 'Danh mục biểu mẫu pháp quy, trình điền biểu mẫu, bản thể biểu mẫu và liên kết case nộp. Xương sống cho Mẫu số 02a/02b và các nộp hồ sơ liên quan.',
    governance_playbooks: 'Mẫu playbook thúc đẩy quy trình quản trị lặp lại (onboarding nhà cung cấp, rà soát DPA, đánh giá chuyển giao).',
    incident_ops_coordination: 'Bảng điều khiển phối hợp liên nhóm cho sự cố đang hoạt động. Một nơi xem ai đang tham gia, đang làm gì và cái gì bị chặn.',
    incident_ops_escalation: 'Nhật ký leo thang cho sự cố đang hoạt động. Chuyển trạng thái mức độ, thông báo các bên và tóm tắt điều hành ghi ở đây.',
    incident_ops_post_review: 'Rà soát sau sự cố: phân tích nguyên nhân gốc, dòng thời gian, hành động khắc phục và bài học. Phản hồi vào đào tạo và cập nhật playbook.',
    incident_ops_regulator_exports: 'Tạo gói nộp cơ quan quản lý cho các sự cố phải thông báo. Mẫu số 08 (vi phạm dữ liệu) + gói bằng chứng hỗ trợ.',
    incident_ops_vendor_coordination: 'Phối hợp ứng phó với nhà cung cấp có hệ thống bị xâm phạm. Nghĩa vụ hợp đồng, SLA ứng phó và chuỗi bằng chứng chia sẻ.',
    monitoring_authority_readiness: 'Checklist sẵn sàng và gói bằng chứng cho đợt thăm cơ quan quản lý sắp tới. Góc nhìn hợp nhất các lỗ hổng tồn đọng và kiểm soát hoàn thành.',
    monitoring_change_review: 'Rà soát định kỳ các thay đổi kiểm soát trên chương trình giám sát. Phát hiện drift trước khi trở thành lỗ hổng tuân thủ.',
    monitoring_framework_link: 'Liên kết các kiểm soát giám sát với điều luật khung tuân thủ cụ thể. Tạo bản đồ bằng chứng mà cơ quan quản lý kỳ vọng.',
    monitoring_ric: 'Case Truy vấn và Thư tín Pháp lý. Theo dõi câu hỏi và phản hồi từ cơ quan quản lý theo thời gian.',
    monitoring_tasks: 'Hàng đợi công việc giám sát: xác minh kiểm soát theo lịch, chứng thực và làm mới bằng chứng.',
    partner_workspace_reports: 'Báo cáo chia sẻ trên mạng lưới đối tác: sức khỏe tuân thủ, tóm tắt hàng tháng, chỉ số xuyên tổ chức.',
    partner_workspace_tickets: 'Ticket hỗ trợ giữa các đối tác về quy trình chia sẻ và leo thang.',
    program_governance_audit_readiness: 'Chấm điểm sẵn sàng kiểm toán với drill-down vào bằng chứng thiếu, sổ đăng ký gap và chủ sở hữu khắc phục.',
    program_governance_drafting: 'Không gian soạn thảo tài liệu có phiên bản, định tuyến rà soát và luồng phê duyệt.',
    program_governance_evidence_gaps: 'Sổ đăng ký gap bằng chứng: kiểm soát nào thiếu bằng chứng hiện tại và khi nào cần làm mới.',
    program_governance_executive_summaries: 'Mẫu tóm tắt điều hành có KPI tự điền cho bản báo cáo hội đồng quản trị và điều hành.',
    program_governance_health: 'Bảng điều khiển sức khỏe chương trình tổng thể: độ trưởng thành, độ bao phủ và chỉ báo xu hướng.',
    program_governance_issuance: 'Luồng phát hành tài liệu: phê duyệt, xuất bản, thông báo bên liên quan.',
    program_governance_model: 'Mô hình vận hành quản trị: vai trò, trách nhiệm, ủy ban và quyền quyết định.',
    program_governance_remediation: 'Kế hoạch khắc phục gắn với các gap đã xác định. Chủ sở hữu, hạn chót, trạng thái và bằng chứng xác minh.',
    program_governance_review_cadence: 'Chu kỳ rà soát theo lịch cho vật phẩm quản trị: chính sách, thủ tục, kiểm soát, bằng chứng.',
    program_governance_trends: 'Bảng xu hướng: độ trưởng thành theo thời gian, độ tươi của bằng chứng, vận tốc đóng gap.',
    task_management_conflicts: 'Phát hiện gán công việc xung đột (chủ sở hữu trùng, hạn chót chồng chéo) và giải quyết bằng gán lại.',
    task_management_consolidation: 'Hợp nhất công việc được tự động tạo bởi nhiều phân hệ vào một quy trình cho người được gán.',
    localization_dashboard: 'Tổng quan phân hệ bản địa hóa: tình trạng danh mục khung, trạng thái gói, độ bao phủ bản dịch và các cập nhật pháp lý đang chờ trong một bảng điều khiển.',
    localization_frameworks: 'Danh mục khung tuân thủ: PDPL, Nghị định 13/2023, Nghị định 356/2025, Luật An ninh mạng và GDPR. Mỗi khung liên kết điều luật tới kiểm soát nền tảng.',
    localization_framework_channels: 'Kênh nộp cơ quan quản lý liên kết mỗi khung (cổng BCA, endpoint MIC, email cơ quan). Thúc đẩy định tuyến nộp Mẫu số.',
    localization_framework_detail: 'Góc nhìn chi tiết một khung: điều luật, thủ tục, persona, biểu mẫu liên kết và chiều sâu triển khai trên nền tảng.',
    localization_framework_module_links: 'Bản đồ bao phủ Khung → phân hệ. Điều luật pháp lý nào được thực thi bởi phân hệ nào của nền tảng.',
    localization_language_packs: 'Danh mục gói ngôn ngữ trên 16 ngôn ngữ: trạng thái cài đặt, độ hoàn thiện bản dịch và lịch sử nộp gói.',
    localization_audit: 'Dấu vết kiểm toán thay đổi bản địa hóa: chỉnh sửa khung, cập nhật gói, sự kiện nộp.',
    localization_market_packs: 'Danh mục gói thị trường đóng gói khung + ngôn ngữ + biểu mẫu theo thị trường (VN, các nước EU, v.v.).',
    localization_module_mappings: 'Sổ đăng ký ánh xạ phân hệ: điều luật khung nào áp dụng cho phân hệ nào ở cấp tính năng.',
    localization_pack_detail: 'Góc nhìn chi tiết một gói: phiên bản, khung bao gồm, trạng thái bản dịch, lịch sử cài đặt.',
    localization_pack_submissions: 'Pipeline nộp gói: bản nộp đối tác, trạng thái rà soát, phê duyệt xuất bản.',
    localization_regulatory_updates: 'Cập nhật Quy định: sổ đăng ký những thay đổi của các khung tuân thủ đã cài đặt — các cập nhật do bạn ghi nhận và bản so sánh từng yêu cầu được tạo khi cài đặt một phiên bản gói mới hoặc một văn bản chính thức thay thế. Rà soát bản so sánh và xem những bản ghi biểu mẫu cần đọc lại.',
    localization_sector_overlays: 'Danh mục lớp phủ, trong Lớp phủ ngành › Quản lý lớp phủ: cài đặt các lớp phủ ngành tùy chọn mà tổ chức của bạn phải tuân thủ và chọn chế độ Trực tiếp hoặc Chỉ tham khảo cho từng lớp phủ. Lớp phủ được cài đặt ở chế độ Trực tiếp đưa các sổ đăng ký ngành của lớp phủ đó vào sử dụng, và nhóm ngành của các sổ đó xuất hiện trong mục Lớp phủ ngành trên menu. Lớp phủ được cài đặt ở chế độ Chỉ tham khảo không thêm màn hình nào; một nhóm ngành chỉ hiển thị khi một lớp phủ khai báo nhóm đó được cài đặt ở chế độ Trực tiếp.',
    localization_help_content_packs: 'Quản lý gói nội dung trợ giúp: cài đặt, phiên bản hóa và dịch các bài hướng dẫn, hướng dẫn phân hệ và khởi đầu nhanh hiển thị trong giao diện quản trị.',
    localization_translation_preview: 'Xem trước trực tiếp chuỗi UI trên các ngôn ngữ. So sánh song song để QA bản dịch.',
    organizations_groups_create: 'Tạo nhóm tổ chức mới (koncern): chọn tổ chức gốc, chọn thành viên, xác định chế độ kế thừa theo danh mục.',
    organizations_groups_detail: 'Chi tiết một nhóm tổ chức: thành viên, ma trận kế thừa, tài nguyên chia sẻ và tóm tắt kiểm toán.',
    organizations_groups_settings: 'Cài đặt nhóm tổ chức: SLA, lịch, thông báo, tiếp nhận, tích hợp CMP. Công tắc kế thừa/ghi đè theo danh mục.',
    organizations_groups_audit: 'Nhật ký kiểm toán mỗi nhóm: thay đổi thành viên, công tắc kế thừa, thêm/bớt tài nguyên chia sẻ.',
    organizations_groups_consolidated_audit: 'Góc nhìn kiểm toán hợp nhất trên tất cả tổ chức thành viên của một nhóm. Lọc theo thực thể, actor, cửa sổ thời gian.',
    organizations_groups_dashboard: 'Bảng điều khiển xuyên thành viên: sức khỏe tuân thủ, mục mở, vi phạm SLA cuộn lên cho toàn nhóm.',
    organizations_groups_reports: 'Báo cáo cấp nhóm: tóm tắt điều hành, tổng hợp hướng cơ quan quản lý, so sánh KPI xuyên thành viên.',
    organizations_groups_users: 'Người dùng hiển thị trên toàn nhóm: gán vai trò xuyên tổ chức, quản trị viên chia sẻ, chuỗi mạo danh.',
    organizations_groups_merged_vendors: 'Góc nhìn nhà cung cấp đã khử trùng trên các tổ chức thành viên — một nhà cung cấp xuất hiện một lần ngay cả khi onboarding riêng biệt.',
    organizations_groups_shared_templates: 'Mẫu biểu mẫu xuất bản ở cấp nhóm. Thành viên tham gia để kế thừa mẫu; không tham gia theo từng tổ chức.',
    organizations_groups_shared_frameworks: 'Khung tuân thủ chia sẻ trên toàn nhóm. Cập nhật trung tâm lan đến mọi thành viên với mode=inherit.',
    organizations_groups_shared_vendors: 'Sổ đăng ký nhà cung cấp cấp nhóm. Hợp đồng, trạng thái DPA và điểm rủi ro hiển thị với mọi thành viên.',
    onboarding_help_center: 'Trung tâm Trợ giúp: Liệt kê toàn bộ 309+ bài viết theo phân hệ, danh mục và persona. Tìm kiếm toàn bộ nội dung EN + VI.',
    onboarding_progress: 'Theo dõi tiến độ onboarding: bước go-live nào hoàn thành, bước nào đang chờ, ai sở hữu điểm nghẽn.',
    onboarding_personas: 'Đường dẫn vai trò: tìm vai trò của bạn trong mọi vai trò khả dụng cho tổ chức của bạn, bắt đầu từ các vai trò được khung tuân thủ đã cài đặt nêu tên. Mở một vai trò để xem các khung tuân thủ nêu tên vai trò đó, các sổ đăng ký mà vai trò đó làm việc, hướng dẫn bắt đầu nhanh và tình huống sử dụng, rồi lưu vai trò đó làm vai trò hướng dẫn của bạn.',
    onboarding_quick_starts: 'Kịch bản quick-start: chạy quy trình tuân thủ hoàn chỉnh đầu cuối trong sandbox. Lý tưởng cho đào tạo.',
    onboarding_sample_data: 'Trình tạo dữ liệu mẫu: nạp hàng demo thực tế vào bất kỳ phân hệ nào. Hữu ích cho bán hàng demo và đào tạo.',
    onboarding_tours: 'Tour hướng dẫn tương tác. Khởi chạy walkthrough theo phân hệ kể chuyện mỗi trang khi bạn điều hướng.',
    security_sessions: 'Góc nhìn trực tiếp các phiên đã xác thực: thiết bị, IP, hoạt động gần nhất, yếu tố MFA đã dùng. Kết thúc bằng một click.',
    security_api_keys: 'API key tài khoản dịch vụ: phạm vi, xoay vòng, lần dùng gần nhất và dấu vết kiểm toán mỗi key.',
    security_break_glass: 'Truy cập break-glass khẩn cấp: cấp super-admin có giới hạn thời gian với dấu vết kiểm toán bắt buộc và rà soát sau sự cố.',
    security_delegations: 'Cho thành viên khác mượn một phần hoặc toàn bộ quyền của bạn trong một khoảng thời gian xác định. Họ nắm giữ các quyền đó từ thời điểm bắt đầu đến khi kết thúc hoặc đến khi bạn thu hồi, được kiểm tra ở mỗi yêu cầu, và mỗi lần sử dụng được ghi vào nhật ký kiểm toán bảo mật.',
    security_ip_allowlist: 'Cấu hình danh sách IP cho phép: hạn chế đăng nhập admin từ mạng doanh nghiệp cụ thể hoặc dải VPN.',
    security_mfa: 'Chính sách xác thực đa yếu tố: bắt buộc theo vai trò, danh sách yếu tố cho phép (TOTP/SMS/WebAuthn), luồng đặt lại.',
    security_service_accounts: 'Tài khoản dịch vụ cho tích hợp hệ thống-hệ thống. Có phạm vi, kiểm toán và xoay vòng riêng khỏi người dùng con người.',
    security_session_settings: 'Chính sách phiên: hết hạn idle, tuổi phiên tối đa, giới hạn phiên đồng thời, kích hoạt xác thực lại.',
    security_sso: 'Cấu hình đăng nhập một lần: nhà cung cấp SAML / OIDC, ánh xạ thuộc tính, quy tắc tự cấp phép.',
    rights_requests_create: 'Tạo case yêu cầu quyền chủ thể dữ liệu mới. Ghi nhận danh tính người yêu cầu, loại yêu cầu, kênh và điểm khởi chạy đồng hồ SLA.',
    rights_requests_list: 'Toàn bộ case yêu cầu quyền với đếm ngược SLA, người được gán, trạng thái và chip lọc theo kênh + loại yêu cầu.',
    rights_requests_detail: 'Không gian làm việc một case: thư từ, hành động hoàn thành, bằng chứng, soạn phản hồi, chuyển trạng thái SLA.',
    rights_requests_cmp_sync: 'Cài đặt đồng bộ tích hợp CMP: endpoint webhook, quy tắc tự tạo case, chính sách thử lại.',
    rights_requests_extensions: 'Yêu cầu gia hạn SLA: lý do, định tuyến người phê duyệt và ghi dấu vết kiểm toán khi cấp/từ chối.',
    rights_requests_fulfillment: 'Không gian hoàn thành yêu cầu: thu thập gói dữ liệu, áp dụng che, đóng gói để giao.',
    rights_requests_responses: 'Soạn phản hồi với chèn mẫu, trường gộp và xem trước song song EN/VI.',
    rights_requests_response_templates: 'Danh mục mẫu phản hồi: mẫu được phê duyệt theo loại yêu cầu + thẩm quyền, có lịch sử phiên bản.',
    compliance_forms_consolidation_create: 'Tạo case hợp nhất mới: nhóm các biểu mẫu liên quan dưới một gói nộp duy nhất gửi tới một cơ quan quản lý.',
    compliance_forms_consolidation_detail: 'Không gian một case hợp nhất: đầu vào các phần, phê duyệt người rà soát, xuất hồ sơ hoàn chỉnh cho cơ quan quản lý.',
    compliance_forms_consolidation_cases: 'Tất cả case hợp nhất với trạng thái nộp, phân công người rà soát và theo dõi hạn chót.',
    compliance_forms_consolidation_review: 'Không gian rà soát case hợp nhất: phê duyệt đầu vào các phần, yêu cầu chỉnh sửa, ký duyệt trước khi xuất.',
    compliance_forms_dossier_export: 'Xuất hồ sơ hợp nhất dưới dạng gói PDF sẵn sàng cho cơ quan quản lý với thư gửi, phụ lục và chữ ký.',
    compliance_forms_section_input: 'Không gian chủ sở hữu phòng ban để hoàn thành một phần của case hợp nhất.',
    compliance_forms_filing_case_links: 'Liên kết giữa thực thể biểu mẫu và case nộp. Đảm bảo mỗi lần nộp được hỗ trợ bởi dữ liệu biểu mẫu đúng.',
    compliance_forms_section_routing: 'Quy tắc định tuyến phần: phòng ban nào sở hữu phần nào của mỗi mẫu biểu mẫu.',
    assessments_dashboard: 'Bảng điều khiển đánh giá: số DPIA, đánh giá chuyển giao, tóm tắt lỗ hổng, hạn chót rà soát sắp tới.',
    assessments_dossier_filing_detail: 'Một hồ sơ nộp: loại biểu mẫu (Mẫu số 02a/02b), phân công phần, trạng thái phần, chi tiết nộp, đồng hồ BCA.',
    assessments_dossier_filing_list: 'Tất cả hồ sơ nộp với loại biểu mẫu, trạng thái và tiến độ phân công phần. Lọc theo trạng thái và khung.',
    assessments_dpia_list: 'Sổ đăng ký DPIA: tất cả Đánh giá Tác động Bảo vệ Dữ liệu đang tiến hành, đã phê duyệt và lưu trữ.',
    assessments_gap_report: 'Báo cáo Lỗ hổng: độ bao phủ DPIA, yêu cầu DPIA chưa phân loại, rủi ro chưa giảm thiểu, hồ sơ thiếu. Bề mặt sẵn sàng kiểm toán chính.',
    assessments_transfer_list: 'Đánh giá chuyển giao cho luồng dữ liệu xuyên biên giới. Mỗi đánh giá ghi cơ chế, biện pháp bảo vệ và xếp hạng rủi ro.',
    deletion_orchestration_templates: 'Mẫu checklist cho các lần xóa. Mỗi mẫu là chuỗi công việc được viết kịch bản gắn với hệ thống và quy tắc lưu giữ cụ thể.',
    deletion_orchestration_dsr_integration: 'Tích hợp DSR-xóa: tự khởi chạy lần xóa từ yêu cầu xóa đã phê duyệt.',
    deletion_orchestration_tasks: 'Công việc thực thi cho các lần xóa đang hoạt động. Mỗi công việc theo dõi hệ thống, hành động, chủ sở hữu và bằng chứng xác minh.',
    deletion_orchestration_dashboard: 'Bảng điều khiển điều phối xóa: lần chạy đang hoạt động, tỷ lệ hoàn thành, xác minh đang chờ, độ tươi chuỗi bằng chứng.',
    deletion_orchestration_proof_chain: 'Chuỗi bằng chứng chống giả mạo cho mọi hành động xóa. Bắt buộc cho tuân thủ PDPL về xóa.',
    organizations_compliance_rollup: 'Tổng hợp tuân thủ xuyên thực thể: KPI theo thực thể, độ bao phủ cấp khung, nghĩa vụ còn tồn đọng.',
    organizations_departments_admin: 'Không gian quản trị phòng ban: tạo, gán lại chủ sở hữu, quản lý thành viên nhóm, đặt quy tắc định tuyến.',
    organizations_departments: 'Danh sách phòng ban xuyên thực thể. Thúc đẩy định tuyến phần, luồng phê duyệt và quy bằng chứng.',
    organizations_entity_frameworks: 'Gán khung tuân thủ cho mỗi thực thể. Thúc đẩy Mẫu số nào áp dụng và kiểm toán nào bắt buộc.',
    // fix-sprint #447 W8 — đúng các trường của biểu mẫu; biểu mẫu không ghi nhận đăng ký kinh doanh, địa chỉ hay người đại diện.
    organizations_entity_form: 'Tạo hoặc sửa một tổ chức: tên và đường dẫn, múi giờ, ngôn ngữ mặc định, quốc gia và tổ chức cha. Quốc gia để ở trạng thái chưa ghi nhận cho đến khi có người chọn. Khi sửa một tổ chức, nút Vô hiệu hóa cũng nằm tại đây.',
    organizations_list: 'Tất cả thực thể tổ chức trong tenant. Góc nhìn danh sách chính với bộ lọc trạng thái và hành động truy cập nhanh.',
    organizations_retention_policies_admin: 'Quản trị chính sách lưu giữ theo tổ chức: quy tắc mặc định, ghi đè theo thực thể.',
    task_management_calendar: 'Góc nhìn lịch các công việc và hạn chót. Mã màu theo phân hệ và trạng thái SLA.',
    task_management_my_tasks: 'Hàng đợi công việc cá nhân: được gán cho tôi, đến hạn sớm, quá hạn. Không gian làm việc hàng ngày chính.',
    task_management_overdue: 'Công việc quá hạn xuyên tổ chức. Bộ lọc theo chủ sở hữu, phân hệ và khoảng ngày quá hạn.',
    task_management_section_assignments: 'Tổng quan phân công phần: phòng ban nào sở hữu phần nào của mỗi hồ sơ nộp biểu mẫu đang hoạt động.',
    task_management_board: 'Góc nhìn bảng Kanban các công việc. Kéo giữa các cột (Chờ làm / Đang làm / Xem xét / Xong) để cập nhật trạng thái.',
    task_management_detail: 'Không gian một công việc: mô tả, bình luận, tệp đính kèm, công việc con, dấu vết kiểm toán, chuyển trạng thái.',
    task_management_list: 'Danh sách phẳng tất cả công việc với bộ lọc: chủ sở hữu, phân hệ, trạng thái, ưu tiên, khoảng ngày đến hạn.',
    consent_cmp_sync: 'Không gian đồng bộ CMP: kích hoạt đồng bộ, xem lịch sử đồng bộ, kiểm tra lỗi. Giữ trạng thái CMP căn chỉnh với nền tảng.',
    consent_cmp_bulk_sync: 'Đồng bộ CMP hàng loạt: đồng bộ lại tất cả bản ghi đồng thuận xuyên tất cả thuộc tính. Dùng cho di trú tenant và thay đổi CMP lớn.',
    consent_cmp_integration: 'Cài đặt tích hợp CMP: endpoint, xác thực, chính sách đồng bộ và sức khỏe kết nối.',
    consent_cmp_purpose_mapping: 'Ánh xạ mục đích CMP sang mục đích nền tảng. Đảm bảo tín hiệu đồng thuận chảy đúng giữa hai hệ thống.',
    consent_cmp_webhook: 'Cấu hình webhook CMP: định tuyến sự kiện đến, khóa ký, chính sách thử lại.',
    partner_workspace_brand_profiles: 'Hồ sơ thương hiệu đối tác: logo, khẩu hiệu, liên hệ, dịch vụ. Thúc đẩy danh sách marketplace đối tác.',
    partner_workspace_deliverable_templates: 'Danh mục mẫu sản phẩm bàn giao: gói dịch vụ chuẩn hóa đối tác có thể cung cấp (rà soát DPIA, kiểm toán nhà cung cấp, đào tạo).',
    partner_workspace_deliverables: 'Sản phẩm bàn giao đang hoạt động xuyên các lần gắn kết đối tác. Theo dõi phạm vi, ngày đến hạn, tiêu chí chấp nhận.',
    partner_workspace_service_tiers: 'Định nghĩa tầng dịch vụ: Đồng / Bạc / Vàng với phạm vi, SLA và dải giá.',
    partner_workspace_workflow_instances: 'Thực thể quy trình đang hoạt động xuyên đối tác: các lần chạy onboarding, rà soát, leo thang, offboarding.',
    partner_workspace_workflow_templates: 'Danh mục mẫu quy trình cho gắn kết đối tác. Mỗi mẫu xác định bước, chủ sở hữu, SLA và đầu ra.',
    access_accountability_justification_reviews: 'Rà soát lý do truy cập do người dùng gửi. Phê duyệt, thách thức hoặc leo thang theo chính sách vùng.',
    access_accountability_review_campaigns: 'Chiến dịch rà soát truy cập theo lịch: định kỳ, phạm vi, phân công người rà soát, theo dõi hoàn thành.',
    access_reviews_list: 'Tất cả chiến dịch rà soát truy cập với bộ lọc: chu kỳ, trạng thái, người rà soát, hệ thống mục tiêu.',
    access_reviews_campaign_detail: 'Không gian một chiến dịch: phạm vi, người rà soát, hàng đợi mục, trạng thái chứng nhận, dấu vết kiểm toán.',
    access_reviews_my_reviews: 'Rà soát được gán cho tôi: quyết định đang chờ có bối cảnh, chứng nhận hoặc thu hồi từng mục.',
    attestation_schedules_list: 'Sổ đăng ký lịch chứng thực: định kỳ, phạm vi, ngày đến hạn kế tiếp, tỷ lệ hoàn thành mỗi lịch.',
    attestation_schedules_create: 'Tạo lịch chứng thực mới: chọn tần suất, phạm vi, người chứng thực và yêu cầu bằng chứng.',
    attestation_schedules_edit: 'Sửa lịch chứng thực hiện có. Thay đổi áp dụng cho chu kỳ kế tiếp; chu kỳ trước giữ nguyên.',
    attestation_schedules_history: 'Dòng thời gian hoàn thành lịch sử của lịch chứng thực. Vật phẩm bằng chứng liên kết mỗi chu kỳ.',
    governance_board_report: 'Báo cáo tuân thủ sẵn sàng cho hội đồng quản trị: tóm tắt điều hành, xu hướng điểm, rủi ro tồn đọng, trạng thái khắc phục.',
    governance_compliance_score: 'Tính toán điểm tuân thủ xuyên các khung. Drill-down vào đóng góp mỗi kiểm soát.',
    governance_drift_monitor: 'Phát hiện drift kiểm soát: thay đổi cài đặt, cấp quyền, ngoại lệ chính sách làm yếu tư thế.',
    governance_audit: 'Nhật ký kiểm toán riêng của quản trị: sự kiện quy trình, quyết định phê duyệt, tín hiệu drift.',
    governance_evidence: 'Danh mục bằng chứng cho kiểm soát quản trị. Tài liệu hỗ trợ, ảnh chụp màn hình, biên nhận chứng thực.',
    partner_workspace_escalation: 'Bảng điều khiển leo thang xuyên đối tác: leo thang đang mở, mức độ, đồng hồ SLA và chủ sở hữu hành động kế tiếp.',
    partner_workspace_audit: 'Nhật ký kiểm toán không gian đối tác: thay đổi gắn kết, cập nhật sản phẩm bàn giao, chuyển trạng thái leo thang.',
    partner_workspace_partner_detail: 'Hồ sơ một tổ chức đối tác: lịch sử gắn kết, sản phẩm bàn giao đang hoạt động, liên hệ, điểm hiệu suất.',
    partner_workspace_partner_list: 'Sổ đăng ký tổ chức đối tác: đối tác đang hoạt động, đang onboarding và đã offboarding với tầng dịch vụ + huy hiệu hiệu suất.',
    platform_license: 'Cài đặt giấy phép: tầng, số ghế, trạng thái gia hạn, công tắc tính năng theo giấy phép đã cài.',
    platform_notifications_admin_queue: 'Hàng đợi thông báo quản trị: cảnh báo đang bay, lỗi gửi, trạng thái thử lại xuyên kênh email/SMS/trong ứng dụng.',
    platform_notifications_prefs: 'Tùy chọn thông báo mỗi người dùng: đăng ký kênh, lịch tóm tắt, chủ đề tắt tiếng.',
    settings_overview: 'Tổng quan cài đặt tenant: liên kết nhanh tới domain, email, xuất, webhook và cấu hình khác toàn nền tảng.',
    settings_domains: 'Tên miền cho phép đăng ký và SSO. Hạn chế tạo tài khoản với tên miền doanh nghiệp đã xác minh.',
    settings_email: 'Cấu hình email đi: nhà cung cấp SMTP, danh tính người gửi, trạng thái DKIM/SPF, công cụ test-email.',
    settings_export_schedules: 'Xuất theo lịch: tạo CSV/XLSX/PDF định kỳ chuyển đến File Browser theo lịch cron.',
    settings_webhooks: 'Endpoint webhook đi: đăng ký hệ thống bên ngoài nhận sự kiện nền tảng. Ký, thử lại, kiểm toán mỗi lần gửi.',
    sod_rules_create: 'Tạo quy tắc Phân tách Trách nhiệm: chọn cặp vai trò xung đột, phạm vi và chế độ thực thi (chặn/cảnh báo).',
    sod_rules_edit: 'Sửa quy tắc SoD hiện có. Thay đổi có hiệu lực ngay; vi phạm tồn tại được đánh giá lại ở lần truy cập kế tiếp.',
    sod_rules_list: 'Tất cả quy tắc SoD trong tenant: chế độ thực thi, cặp vai trò, số ngoại lệ, số vi phạm.',
    connectors_definitions: 'Sổ đăng ký loại connector: định nghĩa mỗi tích hợp (hình dạng endpoint, xác thực, thao tác hỗ trợ).',
    connectors_list: 'Tất cả thực thể connector đấu nối với tenant. Trạng thái, đồng bộ gần nhất, nhật ký lỗi, hành động đồng bộ theo yêu cầu.',
    connectors_credentials: 'Két thông tin đăng nhập connector: token OAuth, API key, mật khẩu tài khoản dịch vụ. Lịch xoay vòng + kiểm toán.',
    connectors_event_bus: 'Event bus connector: sự kiện đến từ tích hợp, quy tắc định tuyến, phát lại hàng đợi thư chết.',
    consent_change_awareness: 'Phát hiện thay đổi đồng thuận có ý nghĩa: thêm mục đích, cập nhật chính sách, mở rộng phạm vi. Thúc đẩy chiến dịch tái đồng thuận.',
    consent_audit: 'Dấu vết kiểm toán đồng thuận: mọi cấp, cập nhật, rút xuyên quy trình. Bề mặt chính cho đoàn kiểm tra cơ quan quản lý.',
    consent_reconciliation: 'Đối soát trạng thái đồng thuận xuyên CMP, nền tảng và hệ thống hạ nguồn. Gắn cờ khác biệt.',
    consent_withdrawal_propagation: 'Pipeline lan truyền rút đồng thuận: khi chủ thể rút, đảm bảo nó chảy đến mọi hệ thống liên kết.',
    consent_policies: 'Danh mục chính sách đồng thuận: phiên bản chính sách bảo mật, biến thể ngôn ngữ, cửa sổ kích hoạt.',
    consent_purpose_catalog: 'Danh mục mục đích dùng bởi quy trình đồng thuận. Mỗi mục đích liên kết tới cơ sở pháp lý và quy tắc lưu giữ.',
    consent_snapshots: 'Ảnh chụp đồng thuận: chụp thời điểm cụ thể tất cả đồng thuận đang hoạt động. Bắt buộc cho kiểm toán và giữ pháp lý.',
    consent_workflows: 'Danh mục quy trình đồng thuận. Mỗi quy trình xác định trigger, luồng thu thập và hành động hạ nguồn.',
    discovery_classification_lineage: 'Dòng dõi phân loại: truy cách phần tử dữ liệu được phân loại xuyên các lần quét và rà soát thủ công.',
    discovery_classification_rules: 'Sổ đăng ký quy tắc phân loại: regex, từ vựng, mẫu ML dùng để tự động phân loại trường phát hiện.',
    discovery_dedup_rules: 'Quy tắc khử trùng lặp cho bản ghi phát hiện. Ngăn cùng một trường xuất hiện nhiều lần.',
    discovery_import_templates: 'Mẫu nhập để đưa danh mục dữ liệu hiện có vào phân hệ Khám phá qua CSV hoặc công cụ bên ngoài.',
    discovery_owner_review: 'Hàng đợi rà soát chủ sở hữu: phân loại đề xuất chờ chủ sở hữu dữ liệu phê duyệt hoặc từ chối.',
    discovery_staging: 'Khu vực staging cho trường mới phát hiện. Phân loại trước khi thăng tiến sang danh mục Ánh xạ Dữ liệu.',
    discovery_suggestion_analytics: 'Phân tích cho bộ máy đề xuất: độ chính xác, độ bao phủ, phân loại bị ghi đè nhiều nhất.',
    discovery_suggestion_queue: 'Hàng đợi rà soát phân loại đề xuất trước khi áp dụng. Chấp nhận, sửa hoặc từ chối từng mục.',
    compliance_forms_filler: 'Trình điền biểu mẫu tương tác: bước qua biểu mẫu pháp quy với xác thực, tự lưu và hỗ trợ đồng soạn.',
    compliance_forms_instance_detail: 'Một thực thể biểu mẫu: trạng thái hiện tại, lịch sử, case nộp liên kết, bản xuất.',
    compliance_forms_outputs: 'Đầu ra được tạo cho mỗi thực thể biểu mẫu: gói PDF, DOCX, JSON với thư gửi và chữ ký.',
    compliance_forms_submission_packages: 'Danh mục gói nộp: gói sẵn sàng cho cơ quan quản lý với manifest, chữ ký và biên nhận giao.',
    incident_ops_regulatory_sla: 'Cấu hình SLA pháp lý cho thông báo sự cố (Luật An ninh mạng VN 72 giờ, PDPL 72 giờ, GDPR 72 giờ).',
    incident_ops_response_metrics: 'Chỉ số ứng phó sự cố: MTTD, MTTR, tỷ lệ tuân thủ SLA, số lần leo thang.',
    incident_ops_sla_dashboard: 'Bảng điều khiển SLA: sự cố đang hoạt động so với đồng hồ SLA, cảnh báo rủi ro, lịch sử vi phạm.',
    incident_ops_sla_definitions: 'Định nghĩa SLA: tầng mức độ, cửa sổ ứng phó và giải quyết mỗi tầng, trích dẫn khung.',
    monitoring_governance_raci: 'Ma trận RACI cho kiểm soát quản trị: trách nhiệm, chịu trách nhiệm, tham vấn, thông báo mỗi kiểm soát.',
    monitoring_periods: 'Chu kỳ giám sát: chu kỳ rà soát năm/quý/tháng căn chỉnh với cửa sổ nộp pháp lý.',
    monitoring_programs_list: 'Danh mục chương trình giám sát: chương trình căn chỉnh khung với phạm vi, chủ sở hữu, tần suất.',
    monitoring_task_templates: 'Mẫu công việc cho chu kỳ giám sát. Mỗi mẫu xác định phạm vi, vai trò người được gán, yêu cầu bằng chứng.',
    access_accountability_evidence_detail: 'Chi tiết một gói bằng chứng: chuỗi giám sát, chữ ký, tùy chọn xuất cho nộp cơ quan quản lý.',
    access_accountability_evidence_packages: 'Danh mục gói bằng chứng cho trách nhiệm truy cập: gói đã ký, chống giả mạo, sẵn sàng cho kiểm toán.',
    access_reviews_role_analytics: 'Phân tích vai trò: mật độ sử dụng vai trò, mở rộng quyền, xu hướng quyết định chứng nhận.',
    access_accountability_pattern_analysis: 'Phân tích mẫu trên nhật ký truy cập: thời gian truy cập bất thường, điểm địa lý ngoại lai, tín hiệu leo thang quyền.',
    access_accountability_session_dashboard: 'Bảng điều khiển phiên: phiên đã xác thực trực tiếp, bản đồ nhiệt phiên theo giờ/ngày, cảnh báo phiên không hoạt động.',
    classification_exports: 'Xuất phân loại: xuất kho phân loại dưới dạng CSV/XLSX cho công cụ kiểm toán bên ngoài.',
    classification_discovery_cycles: 'Chu kỳ khám phá phân loại: quét theo lịch làm nổi bật phần tử dữ liệu mới thêm hoặc thay đổi.',
    classification_enterprise_data_map: 'Bản đồ dữ liệu doanh nghiệp: góc nhìn phân loại tổng hợp xuyên hệ thống, phòng ban và tầng nhạy cảm.',
    connectors_automation_planning: 'Lập kế hoạch tự động hóa: xác định chuỗi trigger → connector → hành động cho vận hành dữ liệu theo sự kiện.',
    connectors_brd_capture: 'Ghi nhận Tài liệu Yêu cầu Kinh doanh cho việc xây dựng connector mới.',
    connectors_poc_scorecards: 'Thẻ điểm proof-of-concept để đánh giá tích hợp connector mới so với tiêu chí chấp nhận.',
    discovery_connector_management: 'Quản lý connector khám phá: scanner nào được cài, sức khỏe của chúng và hệ thống nào chúng nhắm tới.',
    discovery_scans_list: 'Tất cả quét khám phá với trạng thái, độ bao phủ và phát hiện. Drill-down vào phát hiện mỗi quét.',
    governance_approvals: 'Sổ đăng ký phê duyệt quản trị: các yêu cầu được ghi nhận cho quyết định về nhà cung cấp, chuyển dữ liệu xuyên biên giới, quy trình và lệch cấu hình, kèm người quyết định và lý do. Yêu cầu do một người khác với người gửi yêu cầu quyết định.',
    governance_offboarding: 'Quy trình offboarding nhà cung cấp: chấm dứt hợp đồng, trả/xóa dữ liệu, thu hồi truy cập, kiểm toán cuối.',
    governance_periodic_review: 'Rà soát nhà cung cấp định kỳ: đánh giá lại rủi ro, thời hiệu DPA, danh sách nhà xử lý phụ theo lịch.',
    incident_ops_playbook_activations: 'Bản thể playbook đang hoạt động: playbook nào chạy trên sự cố nào, tiến độ, công việc tồn đọng.',
    incident_ops_playbook_links: 'Sổ đăng ký liên kết Playbook → sự cố. Cũng liên kết tới mẫu thông báo cơ quan quản lý mỗi playbook.',
    incident_ops_playbook_templates: 'Danh mục mẫu playbook: kịch bản ứng phó sự cố có thể tái dùng theo loại sự cố và mức độ.',
    organizations_groups_settings_inheritance: 'Ma trận kế thừa cài đặt xuyên thành viên nhóm. Công tắc kế thừa/ghi đè theo danh mục có dấu vết kiểm toán.',
    organizations_intake_config_form: 'Tạo hoặc sửa cấu hình tiếp nhận: đường dẫn URL biểu mẫu công khai, giới hạn tốc độ, captcha, định tuyến thông báo.',
    organizations_intake_configs_list: 'Danh mục cấu hình tiếp nhận: điểm vào yêu cầu quyền hướng công chúng mỗi thị trường/thương hiệu.',
    privacy_scanner_monitoring_dashboard: 'Bảng điều khiển giám sát privacy-scanner: độ bao phủ thuộc tính, tần suất quét, tín hiệu gắn cờ mỗi thuộc tính.',
    privacy_scanner_monitoring_settings: 'Cài đặt giám sát: tần suất quét, quy tắc ngưỡng, định tuyến cảnh báo, lưu giữ kết quả quét.',
    privacy_scanner_settings: 'Cài đặt scanner: danh mục quét bật, cập nhật chữ ký, thông tin đăng nhập endpoint AesirX scanner.',
    privacy_scanner_real_time_scan: 'Quét thời gian thực: kích hoạt quét đặc biệt và xem kết quả luồng. Lý tưởng cho xác minh nhanh.',
    privacy_scanner_scan_result_detail: 'Góc nhìn chi tiết một kết quả quét: cookie, beacon, tracker tìm thấy, phân loại, hành động khắc phục.',
    privacy_scanner_results_archive: 'Lưu trữ kết quả quét lịch sử. Lọc theo thuộc tính, khoảng ngày, loại tín hiệu.',
    task_management_workflow_detail: 'Một thực thể quy trình: tất cả bước, chủ sở hữu, SLA, logic phân nhánh, bằng chứng hoàn thành.',
    task_management_workflow_list: 'Tất cả thực thể quy trình xuyên phân hệ: trạng thái, chủ sở hữu, hạn chót, phân hệ gốc.',
    task_management_workflow_templates: 'Danh mục mẫu quy trình: quy trình đa-bước có thể tái dùng, có thể khởi tạo từ bất kỳ phân hệ nguồn nào.',
    audit_trail_classic: 'Góc nhìn dấu vết kiểm toán cổ điển (cũ). Đang bị ngừng sử dụng nhường cho phân hệ kiểm toán thống nhất.',
    audit_trail_unified: 'Dấu vết kiểm toán thống nhất xuyên mọi phân hệ. Bề mặt kiểm toán chính.',
    iat_settings: 'Cấu hình tiện ích Nhật ký kiểm toán bất biến: kích hoạt, phạm vi, chế độ bằng chứng mặc định, ghi đè phân loại, hạn mức, và xoay vòng thông tin xác thực AesirX. Đọc hướng dẫn phân hệ chuẩn trước; bài viết đồng hành sâu là "Bật và cấu hình".',
    iat_status: 'Duyệt mọi proof_link với bộ lọc trạng thái, chế độ, phân hệ, và ngày. Nhấp bất kỳ huy hiệu nào để mở ngăn bằng chứng. Bài viết đồng hành sâu: "Hiểu Trạng thái bằng chứng".',
    iat_integrity_center: 'Chạy đợt xác minh phát hiện trôi trên khoảng ngày; xuất Gói toàn vẹn cho bất kỳ phạm vi kiểm toán nào. Bài viết đồng hành sâu: "Sử dụng Trung tâm toàn vẹn".',
    iat_usage: 'Theo dõi số dư hiện tại, tiêu thụ tháng, trạng thái hạn mức, và đổi gói tín dụng. Bài viết đồng hành sâu: "Tín dụng và hạn mức".',
    classification_exceptions: 'Hàng đợi ngoại lệ phân loại: bản ghi gắn cờ là trường hợp biên, chờ rà soát thủ công.',
    classification_review_tasks: 'Hàng đợi công việc rà soát phân loại: phân loại tự động đề xuất chờ chủ sở hữu xác nhận.',
    consent_properties_audit_log: 'Dấu vết kiểm toán cho thay đổi thuộc tính đồng thuận: ai cập nhật gì, khi nào, giá trị cũ/mới.',
    consent_properties_analytics: 'Phân tích thuộc tính đồng thuận: tỷ lệ thu đồng thuận, tỷ lệ rút, xu hướng pha trộn mục đích.',
    consent_properties_evidence_packs: 'Gói bằng chứng cho thuộc tính đồng thuận: gói sẵn sàng cơ quan quản lý chứng minh tuân thủ thu đồng thuận.',
    consent_properties_inconsistencies: 'Bất nhất thuộc tính đồng thuận: thuộc tính mà nền tảng và CMP không đồng thuận về trạng thái.',
    consent_properties_golden_templates: 'Mẫu banner đồng thuận vàng: hình dạng banner đã phê duyệt, rà soát, sẵn sàng quản trị triển khai xuyên thuộc tính.',
    consent_properties_template_rollouts: 'Pipeline triển khai mẫu: staging, pilot, đợt production để đẩy mẫu vàng ra thuộc tính.',
    consent_properties_property_groups: 'Nhóm thuộc tính: nhóm thuộc tính liên quan cho quản trị và tổng hợp báo cáo.',
    consent_properties_snapshot_schedules: 'Lịch ảnh chụp: tự động hóa chụp trạng thái đồng thuận định kỳ cho mục đích kiểm toán và giữ pháp lý.',
    deletion_orchestration_completeness: 'Theo dõi độ hoàn chỉnh: xác minh mỗi hệ thống trong phạm vi lưu giữ đã xác nhận xóa.',
    deletion_orchestration_runs_list: 'Tất cả lần xóa: trạng thái, thời lượng, hệ thống được bao phủ, tỷ lệ hoàn thành.',
    deletion_orchestration_vendor_requests: 'Yêu cầu xóa đi nhà cung cấp: theo dõi, SLA, vật phẩm xác minh.',
    deletion_orchestration_vendor_verification: 'Xác minh nhà cung cấp: theo dõi từng yêu cầu xóa gửi nhà cung cấp với hạn xác nhận và người liên hệ leo thang, rồi đánh dấu đã xác nhận hoặc leo thang. Nền tảng không gửi gì đến nhà cung cấp.',
    compliance_forms_catalog: 'Danh mục biểu mẫu tuân thủ: mọi biểu mẫu pháp quy (Mẫu số, GDPR Điều 30, ISO 27701) có sẵn để nộp.',
    compliance_forms_template_browser: 'Trình duyệt mẫu: tìm mẫu biểu mẫu đã phê duyệt xuyên các khung.',
    governance_cross_border: 'Sổ đăng ký chuyển giao xuyên biên giới ở cấp quản trị: cơ chế, biện pháp bảo vệ, đánh giá rủi ro quốc gia.',
    governance_subprocessors: 'Sổ đăng ký nhà xử lý phụ: nhà cung cấp dùng ai, lan truyền DPA, thông báo thay đổi.',
    incidents_detail: 'Không gian một sự cố: dòng thời gian, chủ sở hữu, mức độ, khắc phục, trạng thái thông báo cơ quan.',
    incidents_list: 'Sổ đăng ký sự cố: tất cả sự cố đang hoạt động + lịch sử với bộ lọc theo mức độ, kênh, khung.',
    partner_workspace_partner_dashboard: 
    // Sprint 64.169.0 — see the note on the EN string. `xu hướng sức khỏe`
    // removed for the same reason; the governed terms in this sentence
    // (`Bảng điều khiển`, `đối tác`, `leo thang`) are unchanged, and only the
    // clause that named a metric the product does not have is dropped.
    'Bảng điều khiển đối tác: KPI cấp cao cho tất cả gắn kết đối tác và số leo thang.',
    partner_workspace_support_tickets: 'Danh mục ticket hỗ trợ đối tác. Chia sẻ giữa đội đối tác và tenant với theo dõi SLA.',
    program_governance_acknowledgment_tracking: 'Theo dõi xác nhận tài liệu: ai đã đọc và xác nhận mỗi chính sách hoặc thủ tục.',
    program_governance_change_log: 'Nhật ký thay đổi tài liệu: mọi chỉnh sửa tài liệu quản trị với diff và chuỗi phê duyệt.',
    roles_detail: 'Chi tiết một vai trò: quyền gắn vai trò này, người dùng được gán, kiểm toán thay đổi thành viên.',
    roles_list: 'Danh mục vai trò: mọi vai trò tích hợp và tùy chỉnh với số thành viên và phạm vi quyền.',
    consent_properties_cross_property_dashboard: 'Bảng điều khiển xuyên thuộc tính: sức khỏe đồng thuận xuyên mọi thuộc tính. Tổng hợp tỷ lệ thu, tỷ lệ rút, chỉ báo drift.',
    consent_properties_property_registry: 'Sổ đăng ký thuộc tính: mọi thuộc tính tenant quản lý với trạng thái banner đồng thuận, CMP liên kết, phiên bản chính sách.',
    // Sprint 47.7.4 AC-6 — tóm tắt Forseti module-tip (EN+VI; 14 ngôn ngữ → 50.10.0).
    forseti_projects_overview: 'Không gian làm việc Forseti: dự án gom tài liệu, hội thoại, lượt chạy quy trình và đánh giá dạng bảng vào một vùng chứa để cố vấn AI giữ ngữ cảnh xuyên suốt.',
    forseti_workflows_list: 'Mẫu prompt AI đã lưu để tạo công việc và bản nháp chứng cứ theo yêu cầu. Dùng các quy trình mặc định hoặc tự soạn; mỗi lượt chạy được gửi đến nhà cung cấp AI mà tổ chức của bạn đã cấu hình, và mức tiêu thụ được hiển thị tại Forseti → Mức sử dụng.',
    forseti_tabular_reviews_list: 'Đánh giá lặp lại theo từng cột trên một bộ tài liệu. Đặt câu hỏi một lần, chạy cùng đánh giá cho nhiều tệp đính kèm, và nhận lưới câu trả lời song song kèm trích dẫn.',
    forseti_document_review_index: 'Hộp thư phân loại cho các tài liệu có chỉnh sửa do AI đề xuất hoặc phiên bản do Forseti tạo. Tài liệu vào hàng đợi qua hội thoại, lượt chạy quy trình, hoặc nút "Gửi tài liệu để đánh giá" ở trang này.',
    forseti_audit_lineage: 'Truy vết mọi tạo phẩm do AI sinh ra gắn với một bản ghi: hội thoại, lời gọi công cụ, công việc, bản nháp chứng cứ, phiên bản tài liệu. Bắt buộc cho khả năng bảo vệ chứng cứ và hậu kiểm.',
    forseti_draft_evidence: 'Bản nháp chứng cứ do AI soạn đang chờ con người duyệt. Mọi bản nháp phải được người có thẩm quyền duyệt trước khi thành chứng cứ chính thức — Forseti không tự nộp.',
    forseti_usage: 'Mức Forseti đã tiêu thụ cho tổ chức này: số yêu cầu và token trong khoảng thời gian đã chọn, phân theo nhà cung cấp, mô hình, mức suy luận và tính năng, cùng định tuyến hiện tại của từng tính năng và hoạt động gần đây.',
    forseti_ai_model: 'Cấu hình nhà cung cấp AI cho Forseti. Mỗi thẻ nhà cung cấp giữ khóa API, mô hình, mô hình nhanh và mức suy luận riêng, kèm kiểm tra kết nối; phần cài đặt chung chọn nhà cung cấp đang hoạt động.',
    // Sprint 47.7.4.2 AC-8 — tóm tắt module-tip cho các trang chat Forseti.
    forseti_overview: 'Điểm vào của Cố vấn Pháp lý AI Forseti: chọn vai trò, duyệt hội thoại gần đây, và bắt đầu cuộc trò chuyện mới. Vai trò chọn tại đây sẽ đặt giọng mặc định cho các hội thoại mới.',
    forseti_chat_detail: 'Một cuộc hội thoại Forseti: xem lịch sử tin nhắn, đổi vai trò giữa chừng, gửi tin nhắn mới, và xuất bản ghi dưới dạng PDF cho mục đích lưu chứng cứ.',
    forseti_cross_framework_conflicts: 'Danh mục các xung đột giữa các gói pháp lý mà tổ chức của bạn đã cài đặt. Lọc theo khung / chủ đề / mức độ nghiêm trọng / trạng thái xác nhận, bấm vào một dòng để xem quan điểm của hai khung cạnh nhau và xác nhận / phân công / giải quyết. Forseti phát hiện và trình bày; con người quyết định.',
    users_detail: 'Hồ sơ một người dùng: danh tính, vai trò, MFA, lịch sử phiên, dấu vết kiểm toán.',
    users_list: 'Danh bạ người dùng: tất cả người dùng đang hoạt động, đã mời và vô hiệu hóa với bộ lọc nhanh và hành động hàng loạt.',
    governance_vendors: 'Quản lý vòng đời quản trị nhà cung cấp: quy trình tiếp nhận, đánh giá định kỳ, phê duyệt và kết thúc hợp tác. Theo dõi trạng thái tuân thủ và tuân thủ kịch bản.',
    governance_compliance: 'Giám sát tình hình tuân thủ tổ chức với chấm điểm, phát hiện sai lệch và báo cáo ban lãnh đạo. Theo dõi xu hướng tuân thủ và tạo báo cáo cấp điều hành.',
    governance_transfers: 'Quản lý chuyển dữ liệu xuyên biên giới, đánh giá DPA và quản trị bên xử lý phụ. Ghi nhận cơ chế chuyển và đảm bảo tuân thủ quy định cho luồng dữ liệu quốc tế.',
    vendor_risk_assessment: 'Đánh giá và chấm điểm rủi ro nhà cung cấp trong danh mục. Giám sát tín hiệu rủi ro, theo dõi đánh giá lại và duy trì cái nhìn tổng quan danh mục rủi ro.',
    vendor_risk_contracts: 'Quản lý hợp đồng nhà cung cấp, bảng câu hỏi thẩm định và đánh giá cổng. Theo dõi điều khoản hợp đồng, ngày gia hạn và yêu cầu tuân thủ.',
    vendor_risk_onboarding: 'Quản lý vòng đời tiếp nhận nhà cung cấp: đánh giá tiếp nhận, định tuyến xem xét, quy trình phê duyệt và quyết định chấp nhận rủi ro.',
    vendor_risk_dashboard: 'Tổng quan trung tâm về tư thế rủi ro nhà cung cấp: phân bổ danh mục theo hạng, thay đổi hạng gần đây, hợp đồng hết hạn trong 90 ngày, KPI rà soát và tình trạng chấp nhận rủi ro. Bắt đầu từ đây để có cái nhìn nhanh trước khi đi sâu vào chấm điểm, tín hiệu hoặc cổng phê duyệt.',
    vendor_risk_scoring: 'Cấu hình các chiều chấm điểm và đánh giá nhà cung cấp theo tiêu chí rủi ro có trọng số. Theo dõi điểm tổng hợp, hạng được gán và lịch sử điểm theo thời gian.',
    vendor_risk_signals: 'Theo dõi các sự kiện bất lợi có thể yêu cầu đánh giá lại nhà cung cấp: hết hạn chứng nhận, thông báo vi phạm, bất ổn tài chính và thay đổi tuân thủ. Tín hiệu kích hoạt các tác vụ đánh giá lại tiếp theo.',
    vendor_risk_reviews: 'Thực hiện rà soát nhà cung cấp định kỳ với ảnh chụp trạng thái hợp đồng, phát hiện và khuyến nghị. Theo dõi kết quả rà soát (đạt yêu cầu, có mối lo ngại, cần khắc phục, leo thang) theo chương trình và kỳ.',
    vendor_risk_reassessments: 'Kích hoạt và quản lý các tác vụ đánh giá lại khi tín hiệu hoặc lịch định kỳ cho thấy tư thế rủi ro đã thay đổi. Ghi nhận kết quả và mức rủi ro cập nhật; việc đánh giá lại đã hoàn tất sẽ được đưa trở lại hệ thống chấm điểm.',
    vendor_risk_acceptance: 'Chấp nhận chính thức rủi ro tồn dư của nhà cung cấp với các điều kiện rõ ràng, ngày hết hạn và lý do biện minh. Duy trì Sổ đăng ký Chấp nhận Rủi ro — hồ sơ có thể kiểm toán gồm các quyết định chấp nhận đang hoạt động, đã hết hạn và bị thu hồi.',
    vendor_risk_onboarding_cases: 'Quản lý vòng đời hồ sơ tiếp nhận nhà cung cấp: tiếp nhận mới, rà soát thay đổi trọng yếu và rà soát định kỳ. Làm việc qua các tab được phân giai đoạn với chỉ báo hoàn thành và kích hoạt cổng phê duyệt khi sẵn sàng.',
    vendor_risk_review_routing: 'Định tuyến các tác vụ rà soát nhà cung cấp đến người rà soát cụ thể theo người dùng và vai trò. Phân công bảng câu hỏi, theo dõi phân công người rà soát và ghi nhận kết quả định tuyến với lịch sử kiểm toán đầy đủ.',
    vendor_risk_gates: 'Đánh giá các cổng phê duyệt mua sắm trước khi nhà cung cấp chính thức hoạt động. Cấu hình tiêu chí cổng (chứng nhận, DPA đã ký, bảng câu hỏi đã hoàn thành, rủi ro trong ngưỡng), ghi lại lý do ghi đè và liên kết bằng chứng.',
    vendor_risk_questionnaires: 'Xây dựng mẫu bảng câu hỏi thẩm định và phân công cho nhà cung cấp. Theo dõi mức hoàn thành trả lời, chú thích rà soát và phát hiện đưa vào chấm điểm rủi ro và cổng phê duyệt.',
    monitoring_programs: 'Tạo và quản lý chương trình giám sát tuân thủ liên kết với khung tuân thủ. Phân công nhiệm vụ qua quản trị RACI và theo dõi thực thi chương trình.',
    monitoring_kpi: 'Theo dõi KPI tuân thủ, điểm sẵn sàng cơ quan và trạng thái xem xét thay đổi. Bảng điều khiển hiệu suất chương trình giám sát trên tất cả khung hoạt động.',
    discovery_scans: 'Chạy quét khám phá dữ liệu trên các hệ thống kết nối. Quản lý chu kỳ khám phá với phát hiện delta, cấu hình kết nối và theo dõi kết quả quét.',
    discovery_classification: 'Xem xét đề xuất khám phá, quản lý khu vực tạm cho dữ liệu mới phát hiện và thực hiện xem xét chủ sở hữu với khuyến nghị phân loại.',
    discovery_management: 'Cấu hình quy tắc loại trùng, quản lý mẫu nhập và truy vết nguồn gốc phân loại từ khám phá đến nhãn phân loại được phê duyệt.',
    connectors_integration: 'Cấu hình kết nối hệ thống, quản lý thông tin xác thực và giám sát bus sự kiện để đồng bộ dữ liệu thời gian thực giữa các hệ thống tích hợp.',
    connectors_automation: 'Lập kế hoạch chiến lược tự động hóa, thu thập yêu cầu nghiệp vụ và đánh giá thẻ điểm chứng minh khái niệm cho dự án tích hợp hệ thống.',
    connectors_governance: 'Quản lý kiểm kê hệ thống, thực hiện đánh giá bảo mật, duy trì ma trận truy xuất nguồn gốc và theo dõi sổ đăng ký khoảng trống tích hợp.',
    task_management_tasks: 'Xem và quản lý nhiệm vụ tuân thủ: danh sách nhiệm vụ, hàng đợi cá nhân, nhiệm vụ quá hạn, bảng nhiệm vụ và lịch để theo dõi công việc trên tất cả phân hệ.',
    task_management_workflows: 'Thiết kế và quản lý quy trình tuân thủ: tạo mẫu quy trình, cấu hình phân công phần và giám sát thực thi quy trình.',
    compliance_forms_templates: 'Duyệt và quản lý mẫu biểu mẫu tuân thủ. Truy cập danh mục mẫu, tạo mẫu tùy chỉnh và cấu hình bố cục biểu mẫu cho nộp hồ sơ quy định.',
    compliance_forms_submissions: 'Hoàn thành và nộp biểu mẫu tuân thủ. Theo dõi phiên bản biểu mẫu, xem xét kết quả đầu ra và quản lý gói nộp cho hồ sơ quy định.',
    compliance_forms_filing: 'Quản lý liên kết vụ hồ sơ, định tuyến phần và hợp nhất đa phòng ban cho chuẩn bị và nộp hồ sơ quy định.',
    organizations_entities: 'Quản lý thực thể tổ chức, gán khung tuân thủ theo thực thể và cấu hình phân cấp phòng ban cho tuân thủ đa thực thể.',
    organizations_groups: 'Quản lý nhóm tổ chức cho doanh nghiệp đa thực thể. Chia sẻ nhà cung cấp, khung và mẫu biểu mẫu giữa các thành viên nhóm với báo cáo hợp nhất.',
    organizations_intake: 'Cấu hình cài đặt tiếp nhận và quản lý kế thừa cài đặt trên các cấp bậc tổ chức.',
    access_accountability_sessions: 'Giám sát phiên người dùng, xem xét nhật ký truy cập và phân tích mẫu truy cập. Theo dõi hoạt động phiên với trạng thái neo cho bằng chứng trách nhiệm.',
    access_accountability_reviews: 'Chạy chiến dịch đánh giá quyền truy cập, quản lý đánh giá lý do và theo dõi tiến độ người đánh giá. Phối hợp chứng nhận truy cập định kỳ trên toàn tổ chức.',
    access_accountability_sod: 'Định nghĩa quy tắc phân tách nhiệm vụ, phát hiện vi phạm và quản lý ngoại lệ. Thực thi kiểm soát xung đột lợi ích trên các vai trò và quyền.',
    access_accountability_evidence: 'Tạo gói bằng chứng cho đánh giá quyền truy cập, thực hiện điều tra và phân tích mẫu sử dụng vai trò cho báo cáo trách nhiệm truy cập.',
    program_governance_maturity: 'Đánh giá và theo dõi mức độ trưởng thành chương trình tuân thủ trên các khung. Định nghĩa mô hình trưởng thành, chạy đánh giá và giám sát xu hướng cải thiện theo thời gian.',
    program_governance_obligations: 'Ánh xạ nghĩa vụ pháp lý với các đơn vị tổ chức theo quản trị RACI. Định nghĩa mô hình quản trị và theo dõi kế hoạch khắc phục cho các khoảng trống đã xác định.',
    program_governance_documents: 'Quản lý sổ đăng ký tài liệu quản trị: soạn thảo, xem xét, ban hành và theo dõi xác nhận chính sách tuân thủ và tài liệu vận hành.',
    program_governance_reporting: 'Tạo tóm tắt điều hành, báo cáo sẵn sàng kiểm toán và phân tích khoảng trống bằng chứng. Theo dõi kỳ báo cáo và giám sát chỉ số sức khỏe chương trình.',
    deletion_orchestration_runs: 'Thực thi và giám sát các lần chạy điều phối xóa. Theo dõi nhiệm vụ thực thi, xác minh hoàn thành và quản lý bảng điều khiển xóa.',
    deletion_orchestration_vendors: 'Phối hợp yêu cầu xóa nhà cung cấp, xác minh tuân thủ nghĩa vụ xóa của nhà cung cấp và duy trì tài liệu chuỗi bằng chứng.',
    consent_management: 'Quản lý chính sách đồng ý, danh mục mục đích và quy trình đồng ý. Cấu hình quy tắc thu thập đồng ý và theo dõi ảnh chụp đồng ý theo thời gian.',
    consent_cmp: 'Tích hợp với Nền tảng Quản lý Đồng ý (CMP): cấu hình webhook, ánh xạ mục đích CMP sang mục đích danh mục GRC và đồng bộ bản ghi đồng ý.',
    consent_compliance: 'Giám sát tuân thủ đồng ý: phát hiện thay đổi cấu hình, đối chiếu không khớp, theo dõi lan truyền rút lại và xem xét nhật ký kiểm toán đồng ý.',
    partner_workspace_partners: 'Quản lý tổ chức đối tác, thực hiện kiểm toán đối tác và xử lý leo thang. Theo dõi trạng thái tiếp nhận đối tác và yêu cầu tuân thủ.',
    partner_workspace_operations: 'Quản lý sản phẩm giao đối tác, mẫu quy trình, cấp dịch vụ và hồ sơ thương hiệu. Cấu hình quy trình vận hành cho cộng tác đối tác.',
    privacy_scanner_scanning: 'Chạy quét quyền riêng tư thời gian thực trên trang web và ứng dụng. Xem xét kết quả quét, theo dõi bằng chứng theo dõi trước đồng ý và quản lý lưu trữ quét.',
    privacy_scanner_monitoring: 'Cấu hình bảng điều khiển giám sát quét quyền riêng tư và cài đặt. Thiết lập lịch quét tự động và quản lý cấu hình quét.',
    consent_import: 'Nhập hàng loạt bản ghi đồng thuận từ tệp CSV. Lý tưởng để đăng ký đồng thuận trên giấy (hợp đồng lao động, biểu mẫu đồng thuận phụ huynh/nhà trường) ở quy mô lớn. Hỗ trợ các loại luồng cấp, thu hồi, hạn chế và phản đối.',
    cases_import: 'Nhập hàng loạt các vụ yêu cầu quyền chủ thể dữ liệu từ tệp CSV. Hỗ trợ tiếp nhận đa kênh bao gồm điện thoại, thư tín, email và trực tiếp cho các tổ chức nhận khối lượng lớn DSAR.',
    data_subjects: 'Xem hồ sơ chủ thể dữ liệu hợp nhất tổng hợp tất cả các vụ yêu cầu quyền và bản ghi đồng thuận cho mỗi cá nhân. Theo dõi toàn bộ vòng đời quyền riêng tư theo người — lịch sử đồng thuận, vụ DSR và dòng thời gian tương tác.',
    files_config: 'Thiết lập chính sách tải tệp toàn nền tảng — giới hạn kích thước, danh sách loại tệp cho phép, thời hạn lưu trữ mặc định và quy tắc tạm giữ pháp lý. Các ghi đè theo loại chủ sở hữu cho phép siết chính sách đối với các phân hệ nhạy cảm (ví dụ: bằng chứng yêu cầu quyền) trong khi phần còn lại giữ mặc định. Thời hạn lưu trữ hỗ trợ nguyên tắc tối thiểu hóa dữ liệu theo Điều 3 Luật Bảo vệ Dữ liệu Cá nhân 91/2025; tạm giữ pháp lý dừng việc xóa dữ liệu trong thời gian điều tra hoặc tranh chấp đang mở.',
    files_browser: 'Duyệt, tìm kiếm và quản lý mọi tệp đính kèm đã tải lên trong toàn tổ chức. Lọc theo phân hệ chủ sở hữu, người tải lên, ngày và loại MIME; mở bảng chi tiết tệp để xem lịch sử kiểm toán, thực thể liên kết và trạng thái lưu trữ. Đây là công cụ vận hành — không gắn với khung tuân thủ cụ thể — dành cho quản trị viên và kiểm toán viên để tìm và xử lý tệp.',
    files_dashboard: 'Tổng quan vận hành về hệ thống tệp: tổng dung lượng lưu trữ, tăng trưởng theo thời gian, tệp mồ côi (không có thực thể cha) và mức tuân thủ chính sách lưu trữ theo loại chủ sở hữu. Dùng để phát hiện sai lệch chính sách (ví dụ: phân hệ giữ tệp quá hạn lưu trữ toàn cục) và lập kế hoạch dung lượng lưu trữ. Công cụ vận hành — không gắn với khung tuân thủ cụ thể.',
    // Module Guide records for pages that rendered no guide (idea-catalog #79).
    continuity: 'Kế hoạch duy trì hoạt động liên tục và các cuộc diễn tập kiểm chứng kế hoạch. Ghi nhận từng cuộc diễn tập, các hành động cải tiến phát sinh và việc mỗi hành động đã được kiểm tra lại hay chưa — cuộc diễn tập còn hành động chờ kiểm tra lại không được hiển thị là đã hoàn tất. Nhịp độ hiển thị là nhịp độ do tổ chức của bạn tự đặt.',
    deidentification: 'Các lượt khử nhận dạng đối với dữ liệu cá nhân. Mỗi lượt ghi nhận phương pháp, kết quả kiểm tra mẫu và đánh giá của một người có tên về rủi ro tái nhận dạng còn lại. Một lượt chỉ được phê duyệt khi đã ghi nhận đủ cả hai, và sổ đăng ký cho biết bản nháp còn thiếu những gì.',
    rights_requests_batch: 'Tải lên tệp CSV gồm các yêu cầu thực hiện quyền của chủ thể dữ liệu để Forseti soạn bản nháp phản hồi cho từng dòng. Mỗi bản nháp chờ một người chấp nhận hoặc từ chối — nền tảng không tự gửi bất cứ nội dung nào.',
    assessments_dossier_materialisation_health: 'Tình trạng tạo hồ sơ cho các hồ sơ nộp đã được phê duyệt: hồ sơ nộp còn chờ tạo hồ sơ, hồ sơ nộp có lần tạo gần nhất bị lỗi và có thể thử lại, và hồ sơ không được hồ sơ nộp nào tham chiếu. Chỉ phát hiện — không tự động dọn dẹp.',
    pdpl_evidence_pack_builder: 'Lập gói bằng chứng đánh giá tác động từ các tệp thành phần. Gói luôn được lập theo cùng một cách và hiển thị mã kiểm tra cùng bản kê nội dung, để gói bạn gửi đúng là gói bạn đã rà soát.',
    pdpl_evidence_pack_detail: 'Một gói bằng chứng: bản kê nội dung, trạng thái và hồ sơ liên kết, cùng thư điện tử kèm theo dạng bản nháp, thao tác gửi và lịch sử mọi lần gửi. Gói không được chỉnh sửa tại đây — hãy lập lại gói nếu có sai sót.',
    pdpl_evidence_pack_draft: 'Soạn và chỉnh sửa thư điện tử kèm theo gói bằng chứng, mỗi ngôn ngữ một bản nháp. Hãy đọc thông báo về kênh gửi hiển thị phía trên nội dung trước khi chỉnh sửa; bản nháp do bạn rà soát và gửi.',
    pdpl_evidence_pack_email_providers: 'Cấu hình nhà cung cấp dịch vụ thư điện tử mà tổ chức của bạn dùng để gửi gói bằng chứng. Gói được gửi từ nhà cung cấp do bạn cấu hình — nền tảng không bao giờ là bên gửi mặc định.',
    forseti_project_detail: 'Một dự án Forseti: thư mục và tài liệu, thành viên, cùng các cuộc hội thoại, đánh giá và bản nháp được gom trong dự án để cố vấn giữ được ngữ cảnh. Đổi tên hoặc xóa dự án từ phần tổng quan.',
    forseti_mcp: 'Kết nối Claude, tác tử Slack và các ứng dụng MCP khác với tổ chức này. Sao chép thông tin kết nối, đăng ký ứng dụng OAuth, xem các ứng dụng đã được kết nối và cấp khóa API cho script. Client secret hoặc khóa API chỉ hiển thị một lần — hãy sao chép trước khi đóng hộp thoại.',
    forseti_document_review: 'Rà soát các chỉnh sửa do AI đề xuất cho một tài liệu theo từng phiên bản, và chấp nhận hoặc từ chối từng chỉnh sửa. Không đề xuất nào của Forseti làm thay đổi tài liệu cho đến khi có người chấp nhận.',
    forseti_tabular_reviews_compare: 'So sánh song song hai đánh giá dạng bảng trên các tài liệu chung. Các câu hỏi tương ứng được xếp cạnh nhau, câu trả lời khác nhau được đánh dấu, và câu hỏi chỉ có ở một bên được liệt kê riêng. Chỉ xem.',
    forseti_tabular_reviews_detail: 'Một đánh giá dạng bảng: lưới tài liệu đối chiếu với các câu hỏi của bạn, mỗi câu trả lời kèm trích dẫn nguồn. Chi phí ước tính được hiển thị trước khi chạy, và các ô được điền dần khi hoàn tất.',
    forseti_memory: 'Duyệt và tuyển chọn những gì Forseti đã ghi nhớ về tổ chức của bạn qua các cuộc hội thoại. Chỉnh sửa hoặc xóa một mục, hoặc nâng mục đó thành mục tuyển chọn để lượt dọn dẹp lưu trữ hằng đêm giữ lại và mọi vai trò cố vấn đều dùng được.',
    credit_information_uses: 'Ghi nhận từng lần sử dụng thông tin tín dụng của chủ thể dữ liệu, mục đích sử dụng và sự đồng ý làm căn cứ. Các mục đích lấy từ khung tuân thủ đã cài đặt, và một lần sử dụng cần có sự đồng ý sẽ bị từ chối khi chưa ghi nhận sự đồng ý. Nền tảng không chấm điểm và không đưa ra quyết định cho vay.',
    legal_matters: 'Các vụ việc pháp lý tổ chức của bạn đã tiếp nhận — khiếu nại, tố cáo, yêu cầu bồi thường và các vụ việc khác. Mỗi vụ việc cho biết nó căn cứ trên một quyền do khung tuân thủ đã cài đặt tuyên bố hay được theo dõi theo lựa chọn của tổ chức, và vụ việc chưa được xác nhận với người nêu không bao giờ được hiển thị là đã xong.',
    onboarding_persona_detail: 'Một vai trò: nội dung vai trò mô tả, thị trường, nhiệm vụ đầu tiên được đề xuất và các phân hệ chính — được đối chiếu với các phân hệ đã bật cho tổ chức của bạn khi đọc được thông tin đó — kèm liên kết đến các tình huống sử dụng. Nội dung được đọc từ danh mục vai trò của sản phẩm, không soạn riêng cho trang này.',
    onboarding_use_case_detail: 'Một tình huống sử dụng trong bộ tham chiếu và vai trò mà tình huống này được xây dựng cho, kèm liên kết đến vai trò đó và lộ trình khởi tạo của vai trò. Các bước nằm trong tài liệu tham chiếu của tình huống sử dụng; trang này nêu tên tài liệu thay vì chép lại. Nội dung được đọc từ danh mục tình huống sử dụng của sản phẩm, không soạn riêng cho trang này.',
    procedure_continuity_exercise_retest: 'Diễn tập duy trì hoạt động liên tục và kiểm tra lại',
    procedure_deidentification_run: 'Phê duyệt lượt khử nhận dạng',
    procedure_ai_output_human_review: 'Con người rà soát kết quả do AI hỗ trợ',
    procedure_evidence_pack_submission: 'Lập và gửi gói bằng chứng',
    procedure_mcp_key_lifecycle: 'Kết nối ứng dụng MCP: ứng dụng OAuth, ứng dụng đã kết nối và khóa API',
    procedure_framework_conflict_resolution: 'Xử lý xung đột giữa các khung tuân thủ',
    procedure_credit_information_use: 'Ghi nhận việc sử dụng thông tin tín dụng',
    procedure_legal_matter_intake: 'Tiếp nhận và xác nhận vụ việc pháp lý',
};
export const viFrameworkRequirements = {
    vn_dp_draft_f: {
        title: 'Khiếu nại, tranh chấp và quản lý rủi ro',
        description: 'Đơn vị vận hành sàn phải tiếp nhận khiếu nại, có quy trình xử lý tranh chấp với việc lưu giữ bằng chứng, nhận diện rủi ro và cơ chế cảnh báo sớm, và rà soát định kỳ các rủi ro về quyền riêng tư, an ninh mạng và quản lý truy cập.',
    },
    vn_dp_draft_e: {
        title: 'Quy trình giao dịch và giấy chứng nhận quyền sở hữu đối với dữ liệu',
        description: 'Giao dịch bao gồm đăng ký tài khoản, phê duyệt niêm yết, hợp đồng điện tử, xác nhận thanh toán, hồ sơ giao nhận, và cấp giấy chứng nhận quyền sở hữu đối với dữ liệu khi áp dụng. Lịch sử giao dịch và khả năng truy xuất phải được lưu giữ trong sổ đăng ký giao dịch.',
    },
    vn_dp_draft_d: {
        title: 'Kiểm soát môi trường thử nghiệm (sandbox)',
        description: 'Môi trường thử nghiệm phải được cách ly, hạn chế truy cập, giám sát an ninh và được rà soát trước khi trích xuất kết quả. Không được trích xuất dữ liệu nguồn khỏi môi trường thử nghiệm; việc đóng phiên và dọn sạch môi trường phải được ghi nhận.',
    },
    vn_dp_draft_c: {
        title: 'Kiểm soát niêm yết sản phẩm và dịch vụ dữ liệu',
        description: 'Việc niêm yết phải có hồ sơ nguồn hợp pháp và quyền sở hữu/sử dụng, hồ sơ kiểm định kỹ thuật, công bố chất lượng, và hồ sơ rà soát-phê duyệt niêm yết từ phía sàn. Dữ liệu bị cấm phải được lọc trước khi niêm yết.',
    },
    vn_dp_draft_b: {
        title: 'Điều kiện và tiếp nhận bên tham gia',
        description: 'Đơn vị vận hành sàn dữ liệu phải kiểm tra tư cách pháp lý, năng lực pháp lý và điều kiện đối với ngành nghề kinh doanh có điều kiện cho bên bán và bên mua, đăng ký tài khoản giao dịch và thanh toán, và duy trì sổ đăng ký bên tham gia.',
    },
    vn_dp_draft_a: {
        title: 'Phân loại sàn dữ liệu và mô hình vận hành',
        description: 'Đơn vị vận hành sàn dữ liệu phải thiết lập việc phân loại sàn (sàn dữ liệu quốc gia so với các sàn dữ liệu khác), xác định điều kiện và trách nhiệm của đơn vị vận hành, và lập tài liệu về giá, phí, quy tắc vận hành và quản trị kỹ thuật.',
    },
    d165_r5: {
        title: 'Truy cập thông tin cơ sở dữ liệu quốc gia',
        description: 'Yêu cầu trích xuất/sử dụng thông tin từ cơ sở dữ liệu số quốc gia sử dụng các biểu mẫu quy định (Mẫu số 07a/07b).',
    },
    d165_r4: {
        title: 'Báo cáo đánh giá rủi ro hằng năm',
        description: 'Báo cáo đánh giá rủi ro hằng năm phải được nộp theo các biểu mẫu quy định (Mẫu số 05a/05b, 06).',
    },
    d165_r3: {
        title: 'Thông báo chấm dứt chuyển dữ liệu xuyên biên giới',
        description: 'Việc chấm dứt chuyển dữ liệu xuyên biên giới phải được thông báo theo các biểu mẫu quy định (Mẫu số 04a/04b).',
    },
    d165_r2: {
        title: 'Thông báo kết quả đánh giá',
        description: 'Cơ quan có thẩm quyền ban hành thông báo kết quả đánh giá theo biểu mẫu quy định (Mẫu số 03).',
    },
    d165_r1: {
        title: 'Nộp hồ sơ đánh giá tác động chuyển dữ liệu xuyên biên giới',
        description: 'Tổ chức phải nộp hồ sơ đánh giá tác động chuyển/xử lý dữ liệu xuyên biên giới theo các biểu mẫu quy định (Mẫu số 01a/01b, 02).',
    },
    a964190d_8d1d_4284_bc7d_4023e2866751: {
        title: 'Đồng ý cho cookie và các công nghệ tương tự',
        description: 'Việc lưu trữ hoặc truy cập thông tin trên thiết bị đầu cuối chỉ được phép khi có sự đồng ý trước một cách có hiểu biết, ngoại trừ việc lưu trữ cần thiết về mặt kỹ thuật.',
    },
    '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
        title: 'Bảo mật thông tin liên lạc',
        description: 'Các quốc gia thành viên phải đảm bảo tính bảo mật của các thông tin liên lạc và dữ liệu lưu lượng liên quan thông qua mạng truyền thông công cộng và các dịch vụ truyền thông điện tử công khai.',
    },
    c5420073_50c6_4981_816a_398460e5f546: {
        title: 'Đồng ý cho tiếp thị trực tiếp (chọn tham gia)',
        description: 'Việc sử dụng hệ thống truyền thông điện tử cho tiếp thị trực tiếp yêu cầu sự đồng ý trước của người đăng ký.',
    },
    c8bf67d2_874b_4647_92d7_39aaa3230819: {
        title: 'Chọn tham gia mềm cho khách hàng hiện tại',
        description: 'Tiếp thị trực tiếp đến khách hàng hiện tại cho các sản phẩm/dịch vụ tương tự được phép mà không cần đồng ý nếu có cung cấp tùy chọn từ chối.',
    },
    b546df40_9217_4204_b71c_5af5022a0ff9: {
        title: 'Thông báo vi phạm cho nhà cung cấp viễn thông',
        description: 'Các nhà cung cấp dịch vụ truyền thông điện tử công khai phải thông báo cho cơ quan quốc gia có thẩm quyền về các vi phạm dữ liệu cá nhân.',
    },
    '5bd66ae4_dee3_4611_9104_71742df55484': {
        title: 'Quyền phản đối tiếp thị trực tiếp',
        description: 'Người đăng ký phải được cung cấp cơ hội phản đối việc nhận tin nhắn tiếp thị trực tiếp bất cứ lúc nào và miễn phí.',
    },
    a10af31f_ba28_494e_ab50_f8679e86a0b0: {
        title: 'Lưu giữ và xóa dữ liệu lưu lượng',
        description: 'Dữ liệu lưu lượng liên quan đến người đăng ký và người dùng phải được xóa hoặc ẩn danh khi không còn cần thiết cho việc truyền tải, ngoại trừ cho mục đích thanh toán hoặc thanh toán kết nối.',
    },
    '8577e60f_66a8_4651_8336_d561db9929ad': {
        title: 'Điều kiện xử lý dữ liệu vị trí',
        description: 'Dữ liệu vị trí ngoài dữ liệu lưu lượng chỉ có thể được xử lý khi đã ẩn danh hoặc có sự đồng ý của người dùng, trong phạm vi và thời gian cần thiết.',
    },
    '7f3abb41_8713_4276_93b5_6902f419a016': {
        title: 'Tính hợp pháp của việc xử lý — đồng ý',
        description: 'Việc xử lý chỉ hợp pháp khi chủ thể dữ liệu đã đồng ý cho việc xử lý dữ liệu cá nhân của họ cho một hoặc nhiều mục đích cụ thể.',
    },
    '1c2a945a_8eac_428d_8105_e80e95288ea4': {
        title: 'Tính hợp pháp của việc xử lý — hợp đồng',
        description: 'Việc xử lý là cần thiết để thực hiện hợp đồng mà chủ thể dữ liệu là một bên.',
    },
    '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
        title: 'Tính hợp pháp của việc xử lý — nghĩa vụ pháp lý',
        description: 'Việc xử lý là cần thiết để tuân thủ nghĩa vụ pháp lý mà bên kiểm soát dữ liệu phải tuân theo.',
    },
    '702ea760_9e5c_4c32_babc_0b5094ca9735': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích sống còn',
        description: 'Việc xử lý là cần thiết để bảo vệ lợi ích sống còn của chủ thể dữ liệu hoặc một thể nhân khác.',
    },
    '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích công cộng',
        description: 'Việc xử lý là cần thiết để thực hiện nhiệm vụ vì lợi ích công cộng hoặc trong việc thực thi quyền lực chính thức.',
    },
    '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích hợp pháp',
        description: 'Việc xử lý là cần thiết cho các mục đích của lợi ích hợp pháp được theo đuổi bởi bên kiểm soát dữ liệu hoặc bên thứ ba.',
    },
    afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
        title: 'Xử lý các danh mục đặc biệt (dữ liệu nhạy cảm)',
        description: 'Việc xử lý các danh mục dữ liệu cá nhân đặc biệt (nguồn gốc chủng tộc, quan điểm chính trị, sức khỏe, v.v.) bị cấm trừ khi đáp ứng các điều kiện cụ thể trong Art. 9(2).',
    },
    '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
        title: 'Điều kiện cho sự đồng ý',
        description: 'Bên kiểm soát dữ liệu phải chứng minh rằng sự đồng ý đã được đưa ra. Sự đồng ý phải được tự do đưa ra, cụ thể, có hiểu biết và rõ ràng. Việc rút lại phải dễ dàng như việc đưa ra sự đồng ý.',
    },
    '27697f99_efef_49e6_b8e7_c2dc550d1791': {
        title: 'Sự đồng ý của trẻ em — dịch vụ số',
        description: 'Việc xử lý dữ liệu cá nhân của trẻ em dưới 16 tuổi (hoặc độ tuổi thấp hơn do quốc gia thành viên quy định, không dưới 13) yêu cầu sự đồng ý của cha mẹ đối với các dịch vụ xã hội thông tin.',
    },
    '53883480_508e_4f8e_984f_9354c18de285': {
        title: 'Thông tin và truyền thông minh bạch',
        description: 'Bên kiểm soát dữ liệu phải cung cấp thông tin liên quan đến việc xử lý một cách ngắn gọn, minh bạch, dễ hiểu và dễ tiếp cận.',
    },
    b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
        title: 'Thông tin khi thu thập trực tiếp',
        description: 'Khi dữ liệu cá nhân được thu thập từ chủ thể dữ liệu, bên kiểm soát phải cung cấp thông tin được quy định tại thời điểm thu thập.',
    },
    e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
        title: 'Thông tin khi thu thập gián tiếp',
        description: 'Khi dữ liệu cá nhân không được thu thập từ chủ thể dữ liệu, bên kiểm soát phải cung cấp thông tin được quy định trong thời hạn hợp lý.',
    },
    '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
        title: 'Quyền truy cập',
        description: 'Chủ thể dữ liệu có quyền nhận được xác nhận về việc xử lý và truy cập vào dữ liệu cá nhân cùng thông tin bổ sung.',
    },
    '0e011e25_dcea_4a1e_ae55_583e683cea44': {
        title: 'Quyền chỉnh sửa',
        description: 'Chủ thể dữ liệu có quyền yêu cầu chỉnh sửa dữ liệu cá nhân không chính xác mà không chậm trễ quá mức.',
    },
    e8ec5a55_f657_4a99_9b92_3e9861067d28: {
        title: 'Quyền xóa (quyền được quên)',
        description: 'Chủ thể dữ liệu có quyền yêu cầu xóa dữ liệu cá nhân mà không chậm trễ quá mức khi các căn cứ được quy định áp dụng.',
    },
    '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
        title: 'Quyền hạn chế xử lý',
        description: 'Chủ thể dữ liệu có quyền yêu cầu hạn chế việc xử lý khi các điều kiện được quy định áp dụng.',
    },
    '3b829862_39f4_41be_8f2b_d469469a801b': {
        title: 'Nghĩa vụ thông báo — chỉnh sửa/xóa/hạn chế',
        description: 'Bên kiểm soát phải thông báo mọi việc chỉnh sửa, xóa hoặc hạn chế cho từng bên nhận mà dữ liệu đã được tiết lộ.',
    },
    '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
        title: 'Quyền chuyển đổi dữ liệu',
        description: 'Chủ thể dữ liệu có quyền nhận dữ liệu cá nhân của mình ở định dạng có cấu trúc, thông dụng và máy có thể đọc được.',
    },
    becae433_90b9_4cc0_bd33_df50976c1561: {
        title: 'Quyền phản đối',
        description: 'Chủ thể dữ liệu có quyền phản đối việc xử lý dựa trên lợi ích công cộng hoặc lợi ích hợp pháp, bao gồm lập hồ sơ.',
    },
    '4ef387f1_439c_4579_82a3_01565e6dc05d': {
        title: 'Ra quyết định tự động cá nhân, bao gồm lập hồ sơ',
        description: 'Chủ thể dữ liệu có quyền không phải chịu quyết định chỉ dựa trên xử lý tự động mà tạo ra các hiệu lực pháp lý hoặc ảnh hưởng đáng kể tương tự.',
    },
    '667ae5eb_a39a_4809_a032_923cc804eaed': {
        title: 'Thông báo vi phạm cho cơ quan giám sát (72 giờ)',
        description: 'Trong trường hợp vi phạm dữ liệu cá nhân, bên kiểm soát phải thông báo cho cơ quan giám sát trong vòng 72 giờ kể từ khi biết, trừ khi không có khả năng gây ra rủi ro.',
    },
    '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu (rủi ro cao)',
        description: 'Khi vi phạm có khả năng gây ra rủi ro cao đối với quyền và tự do, bên kiểm soát phải thông báo vi phạm cho chủ thể dữ liệu mà không chậm trễ quá mức.',
    },
    '9dbb47bd_172f_4335_b7f3_4558476c4192': {
        title: 'Phản hồi yêu cầu của chủ thể dữ liệu (30 ngày)',
        description: 'Bên kiểm soát phải phản hồi yêu cầu của chủ thể dữ liệu mà không chậm trễ quá mức và trong vòng một tháng kể từ khi nhận được, có thể gia hạn thêm hai tháng cho các yêu cầu phức tạp.',
    },
    '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
        title: 'Đánh giá tác động bảo vệ dữ liệu',
        description: 'Khi việc xử lý có khả năng gây ra rủi ro cao đối với quyền và tự do, bên kiểm soát phải thực hiện DPIA trước khi xử lý.',
    },
    e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
        title: 'Tham vấn trước với cơ quan giám sát',
        description: 'Bên kiểm soát phải tham vấn cơ quan giám sát khi DPIA cho thấy việc xử lý sẽ gây ra rủi ro cao mà không có biện pháp giảm thiểu.',
    },
    '047b7838_bae5_4861_9c38_c99fedec9c96': {
        title: 'Danh sách xử lý bắt buộc DPIA',
        description: 'DPIA được yêu cầu cho việc đánh giá/lập hồ sơ có hệ thống, xử lý quy mô lớn các danh mục đặc biệt và giám sát có hệ thống các khu vực công cộng.',
    },
    aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
        title: 'Hồ sơ hoạt động xử lý (bên kiểm soát)',
        description: 'Mỗi bên kiểm soát phải duy trì hồ sơ các hoạt động xử lý thuộc trách nhiệm của mình, bao gồm mục đích, danh mục, người nhận, chuyển giao và thời hạn lưu giữ.',
    },
    b7eb6296_5962_449c_b267_d322d7919122: {
        title: 'Hồ sơ hoạt động xử lý (bên xử lý)',
        description: 'Mỗi bên xử lý phải duy trì hồ sơ tất cả các danh mục hoạt động xử lý được thực hiện thay mặt cho bên kiểm soát.',
    },
    '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
        title: 'Bảo mật của việc xử lý',
        description: 'Bên kiểm soát và bên xử lý phải thực hiện các biện pháp kỹ thuật và tổ chức phù hợp để đảm bảo an ninh tương xứng với rủi ro.',
    },
    ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
        title: 'Bảo vệ dữ liệu theo thiết kế và mặc định',
        description: 'Bên kiểm soát phải thực hiện các biện pháp phù hợp để đảm bảo chỉ xử lý dữ liệu cá nhân cần thiết cho từng mục đích cụ thể (tối thiểu hóa dữ liệu).',
    },
    '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
        title: 'Chỉ định viên chức bảo vệ dữ liệu',
        description: 'Bên kiểm soát và bên xử lý phải chỉ định DPO khi việc xử lý được thực hiện bởi cơ quan công quyền, liên quan đến giám sát thường xuyên và có hệ thống, hoặc xử lý quy mô lớn các danh mục đặc biệt.',
    },
    '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
        title: 'Nguyên tắc chung cho chuyển giao',
        description: 'Mọi việc chuyển giao dữ liệu cá nhân sang nước thứ ba chỉ được thực hiện khi các điều kiện của Ch. V được tuân thủ.',
    },
    '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
        title: 'Chuyển giao dựa trên quyết định về mức độ bảo vệ đầy đủ',
        description: 'Chuyển giao có thể được thực hiện khi Ủy ban đã quyết định rằng nước thứ ba đảm bảo mức độ bảo vệ đầy đủ.',
    },
    cde8c7cd_3727_40ce_a321_e6b72b335179: {
        title: 'Chuyển giao với các biện pháp bảo vệ phù hợp',
        description: 'Khi không có quyết định về mức độ bảo vệ đầy đủ, chuyển giao có thể được thực hiện khi có các biện pháp bảo vệ phù hợp, bao gồm SCC hoặc BCR.',
    },
    '0e8edaa1_2156_44d9_95f2_1b284f581658': {
        title: 'Điều khoản hợp đồng tiêu chuẩn',
        description: 'Các điều khoản hợp đồng tiêu chuẩn được Ủy ban hoặc cơ quan giám sát thông qua cung cấp các biện pháp bảo vệ phù hợp cho chuyển giao quốc tế.',
    },
    '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
        title: 'Quy tắc ràng buộc doanh nghiệp',
        description: 'Các quy tắc ràng buộc doanh nghiệp được cơ quan giám sát có thẩm quyền phê duyệt cung cấp các biện pháp bảo vệ phù hợp cho chuyển giao quốc tế nội bộ tập đoàn.',
    },
    '420ced80_0d3b_4a28_adad_14ba54df3e40': {
        title: 'Ngoại lệ cho các tình huống cụ thể',
        description: 'Khi không có quyết định về mức độ bảo vệ đầy đủ hoặc biện pháp bảo vệ, chuyển giao có thể được thực hiện dựa trên sự đồng ý rõ ràng, cần thiết cho hợp đồng, lợi ích công cộng, yêu cầu pháp lý, lợi ích sống còn hoặc sổ đăng ký công khai.',
    },
    c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
        title: 'Trách nhiệm của ban quản lý và đào tạo',
        description: 'Ban quản lý của các thực thể thiết yếu và quan trọng phải phê duyệt các biện pháp quản lý rủi ro an ninh mạng và tham gia đào tạo định kỳ.',
    },
    ffb95a09_993c_4db0_a4bb_c48000e43b33: {
        title: 'Các biện pháp quản lý rủi ro an ninh mạng',
        description: 'Các thực thể thiết yếu và quan trọng phải thực hiện các biện pháp kỹ thuật, vận hành và tổ chức phù hợp và tương xứng để quản lý rủi ro an ninh mạng.',
    },
    '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
        title: 'Chính sách và quy trình xử lý sự cố',
        description: 'Các thực thể phải triển khai chính sách xử lý sự cố bao gồm phát hiện, phân tích, ngăn chặn và quy trình ứng phó.',
    },
    de019eab_9992_4361_be7c_3e592f209d76: {
        title: 'Liên tục kinh doanh và quản lý khủng hoảng',
        description: 'Các thực thể phải triển khai các biện pháp liên tục kinh doanh bao gồm quản lý sao lưu, khôi phục thảm họa và quản lý khủng hoảng.',
    },
    '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
        title: 'An ninh chuỗi cung ứng',
        description: 'Các thực thể phải giải quyết các khía cạnh liên quan đến an ninh trong mối quan hệ với nhà cung cấp trực tiếp và nhà cung cấp dịch vụ, bao gồm đánh giá rủi ro chuỗi cung ứng.',
    },
    c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
        title: 'An ninh trong hệ thống mạng và thông tin',
        description: 'Các thực thể phải triển khai an ninh trong việc mua sắm, phát triển và bảo trì hệ thống mạng và thông tin, bao gồm xử lý lỗ hổng.',
    },
    '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
        title: 'Xử lý và tiết lộ lỗ hổng',
        description: 'Các thực thể phải triển khai chính sách và quy trình cho việc tiết lộ và xử lý lỗ hổng có phối hợp.',
    },
    f009e65d_d55e_4c98_9532_63c150694698: {
        title: 'Hiệu quả đánh giá rủi ro an ninh mạng',
        description: 'Các thực thể phải triển khai chính sách và quy trình để đánh giá hiệu quả của các biện pháp quản lý rủi ro an ninh mạng.',
    },
    '00e88824_675d_4b37_960c_757b3176c079': {
        title: 'Chính sách mật mã và mã hóa',
        description: 'Các thực thể phải triển khai chính sách và quy trình liên quan đến việc sử dụng mật mã và mã hóa khi thích hợp.',
    },
    de29d287_81f1_46b1_b327_9cbf06ddad3d: {
        title: 'An ninh nhân sự, kiểm soát truy cập, quản lý tài sản',
        description: 'Các thực thể phải triển khai an ninh nhân sự, chính sách kiểm soát truy cập và quản lý tài sản.',
    },
    '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
        title: 'Xác thực đa yếu tố',
        description: 'Các thực thể phải sử dụng xác thực đa yếu tố hoặc giải pháp xác thực liên tục, truyền thông thoại/video/văn bản được bảo mật và hệ thống truyền thông khẩn cấp được bảo mật.',
    },
    '359474f9_1820_4e3e_9031_a593b66ccde4': {
        title: 'Cảnh báo sớm cho CSIRT (24 giờ)',
        description: 'Các thực thể phải gửi cảnh báo sớm cho CSIRT có thẩm quyền trong vòng 24 giờ kể từ khi biết về sự cố nghiêm trọng.',
    },
    '66acd92c_3091_4148_b935_68b0d24308e7': {
        title: 'Thông báo sự cố cho CSIRT (72 giờ)',
        description: 'Các thực thể phải gửi thông báo sự cố cho CSIRT có thẩm quyền trong vòng 72 giờ kể từ khi biết về sự cố nghiêm trọng.',
    },
    '68269688_06b2_43ea_8e3e_a84532086557': {
        title: 'Báo cáo cuối cùng cho CSIRT (1 tháng)',
        description: 'Các thực thể phải gửi báo cáo cuối cùng cho CSIRT có thẩm quyền không muộn hơn một tháng sau thông báo sự cố.',
    },
    c8512bde_b064_4926_b333_06b9c95cecd7: {
        title: 'Xác định khả năng áp dụng cho các hoạt động AI',
        description: 'Xác định xem tổ chức có thực hiện các hoạt động AI thuộc phạm vi điều chỉnh của Luật AI Việt Nam hay không và ghi nhận quyết định về khả năng áp dụng.',
    },
    '3a87ad63_179a_45ba_8743_98d865df80ab': {
        title: 'Phân loại hệ thống AI theo mức độ rủi ro',
        description: 'Phân loại từng hệ thống AI theo mức độ rủi ro (tối thiểu, hạn chế, cao, không thể chấp nhận) sử dụng phương pháp phân loại có hướng dẫn.',
    },
    '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
        title: 'Duy trì mục trong Sổ đăng ký hệ thống AI',
        description: 'Duy trì sổ đăng ký các hệ thống AI với tên hệ thống, chủ sở hữu, mục đích, bối cảnh triển khai, mức độ rủi ro và sự tham gia của nhà cung cấp.',
    },
    '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
        title: 'Hồ sơ hệ thống AI với danh mục tài liệu và bằng chứng',
        description: 'Xây dựng và duy trì hồ sơ hệ thống với danh mục tài liệu yêu cầu, thư viện bằng chứng và lịch sử phiên bản.',
    },
    e92cfb42_d91b_444f_adbc_c03903844eac: {
        title: 'Nghĩa vụ minh bạch cho nhà vận hành hệ thống AI',
        description: 'Triển khai các biện pháp minh bạch cho hệ thống AI bao gồm thông báo người dùng, tiết lộ sự tham gia của AI và tài liệu hệ thống.',
    },
    '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
        title: 'Gắn nhãn, đánh dấu chìm và ký siêu dữ liệu đầu ra AI',
        description: 'Triển khai gắn nhãn và đánh dấu chìm cho nội dung do AI tạo ra bao gồm ký siêu dữ liệu khi có thể áp dụng.',
    },
    '9566c94d_0dd3_419c_869f_6707d2a972f9': {
        title: 'Yêu cầu tiết lộ đối với nội dung do AI tạo ra',
        description: 'Tiết lộ cho người dùng khi nội dung được tạo bởi AI và cung cấp cơ chế để người dùng nhận biết sự tham gia của AI.',
    },
    '21d39ea5_df78_45c6_8710_b203db229e25': {
        title: 'Nghĩa vụ giải thích và minh bạch đối với người dùng',
        description: 'Cung cấp kết quả có thể giải thích được cho các quyết định AI ảnh hưởng đến cá nhân và duy trì tài liệu minh bạch hướng tới người dùng.',
    },
    f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
        title: 'Nghĩa vụ giám sát con người đối với hệ thống AI rủi ro cao',
        description: 'Đảm bảo các cơ chế giám sát con người được triển khai cho hệ thống AI rủi ro cao bao gồm khả năng leo thang và can thiệp ghi đè.',
    },
    bd180a89_4976_4790_9e50_f1c5c97dc576: {
        title: 'Giám sát và phát hiện sự cố AI',
        description: 'Triển khai khả năng giám sát để phát hiện sự cố, trục trặc và hậu quả bất lợi của hệ thống AI.',
    },
    e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
        title: 'Báo cáo sự cố AI nghiêm trọng cho cơ quan có thẩm quyền',
        description: 'Báo cáo các sự cố AI nghiêm trọng cho cơ quan có thẩm quyền trong thời hạn quy định (72 giờ).',
    },
    '97f15adc_558a_49f2_bb06_86597cb9579b': {
        title: 'Tuân thủ thời hạn thông báo sự cố AI',
        description: 'Tuân thủ các thời hạn thông báo sự cố AI bao gồm thông báo ban đầu, cập nhật và báo cáo kết thúc.',
    },
    '96c33d7e_fe52_4730_adac_70c8c477c88e': {
        title: 'Đánh giá tác động AI đối với hệ thống rủi ro cao',
        description: 'Thực hiện đánh giá tác động cho hệ thống AI rủi ro cao bao gồm rủi ro, biện pháp giảm thiểu, giám sát con người và kế hoạch theo dõi.',
    },
    '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
        title: 'Xác định đủ điều kiện thử nghiệm có kiểm soát / sandbox',
        description: 'Xác định đủ điều kiện tham gia thử nghiệm có kiểm soát (sandbox) và lập tài liệu đánh giá điều kiện.',
    },
    '4a6aed20_f655_4182_a760_aef1f329d5df': {
        title: 'Nộp đơn đăng ký sandbox và theo dõi phê duyệt',
        description: 'Nộp đơn đăng ký sandbox cho cơ quan có thẩm quyền và theo dõi trạng thái phê duyệt trong suốt vòng đời nộp đơn.',
    },
    '8376988d_cd32_491e_a5b8_3340d3973358': {
        title: 'Các mốc kiểm tra giám sát sandbox và hoàn thành/thoát',
        description: 'Thực hiện các mốc kiểm tra giám sát định kỳ trong quá trình tham gia sandbox và chuẩn bị báo cáo hoàn thành/thoát.',
    },
    '7cb2b544_0512_4702_a092_9f1c10872376': {
        title: 'Tài liệu miễn trừ/ngoại lệ ghi nhãn kèm lý do',
        description: 'Lập tài liệu về bất kỳ miễn trừ hoặc ngoại lệ ghi nhãn nào kèm lý do và bằng chứng hỗ trợ.',
    },
    dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
        title: 'Lưu trữ dữ liệu nội địa cho người dùng Việt Nam',
        description: 'Dữ liệu của người dùng Việt Nam trên các dịch vụ cung cấp tại Việt Nam phải được lưu trữ trong lãnh thổ Việt Nam.',
    },
    '9eb86a03_e165_4963_8813_2272b638d529': {
        title: 'Báo cáo sự cố an ninh mạng',
        description: 'Các sự cố an ninh mạng phải được báo cáo cho cơ quan có thẩm quyền một cách kịp thời.',
    },
    '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
        title: 'Phân loại hệ thống thông tin',
        description: 'Hệ thống thông tin phải được phân loại theo mức độ quan trọng và áp dụng các biện pháp an ninh phù hợp.',
    },
    ffb807cf_f121_4b52_8e50_e091c33180c9: {
        title: 'Đánh giá rủi ro an ninh mạng',
        description: 'Phải thực hiện đánh giá rủi ro an ninh mạng định kỳ cho các hệ thống thông tin.',
    },
    a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
        title: 'Xác định phạm vi áp dụng (cây quyết định)',
        description: 'Tổ chức phải xác định liệu có thuộc phạm vi áp dụng của Nghị định 53 hay không bằng cây quyết định quy định.',
    },
    '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
        title: 'Đánh giá và sẵn sàng lưu trữ dữ liệu nội địa',
        description: 'Tổ chức phải đánh giá các yêu cầu lưu trữ dữ liệu nội địa và chứng minh sự sẵn sàng.',
    },
    cf24f011_dc23_4d84_aef5_994dde440571: {
        title: 'Danh mục kiểm tra sẵn sàng lưu trữ và bảo quản',
        description: 'Phải hoàn thành danh mục kiểm tra sẵn sàng lưu trữ và bảo quản để tuân thủ yêu cầu lưu trữ dữ liệu nội địa.',
    },
    b3884cb8_c785_4258_9517_9e393be4f4e1: {
        title: 'Đánh giá yêu cầu hiện diện tại chỗ',
        description: 'Tổ chức phải đánh giá liệu yêu cầu hiện diện tại chỗ có áp dụng hay không và lập kế hoạch phù hợp.',
    },
    c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
        title: 'Tiếp nhận và xác minh yêu cầu của cơ quan chức năng',
        description: 'Các yêu cầu của cơ quan chức năng phải được tiếp nhận, xác minh và xử lý theo quy trình quy định.',
    },
    d4571651_f39d_4c51_8f2d_7248c37f7b14: {
        title: 'Phản hồi yêu cầu cơ quan chức năng trong thời hạn',
        description: 'Phản hồi yêu cầu của cơ quan chức năng phải được cung cấp trong thời hạn quy định.',
    },
    '66c1df70_3456_40e9_b645_6c6bd3be702e': {
        title: 'Nhật ký xử lý yêu cầu cơ quan chức năng',
        description: 'Phải duy trì nhật ký tất cả các hoạt động xử lý yêu cầu của cơ quan chức năng.',
    },
    '04bbba2a_9804_4199_a517_054b6f4689fb': {
        title: 'Xác định phạm vi áp dụng và đối tượng điều chỉnh (hợp nhất)',
        description: 'Tổ chức phải xác định liệu có thuộc phạm vi điều chỉnh theo luật an ninh mạng hợp nhất hay không, bao gồm phạm vi của Luật 24/2018 và Nghị định 53/2022.',
    },
    '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
        title: 'Phân loại hệ thống thông tin theo mức độ quan trọng',
        description: 'Hệ thống thông tin phải được phân loại theo mức độ quan trọng (thông thường, quan trọng, đặc biệt quan trọng) với các nghĩa vụ an ninh tương ứng.',
    },
    '83ad981e_23ff_4a92_bebd_cd24487e912f': {
        title: 'Cơ cấu quản trị an ninh mạng và nhân sự chịu trách nhiệm',
        description: 'Tổ chức phải thiết lập cơ cấu quản trị an ninh mạng và chỉ định nhân sự chịu trách nhiệm.',
    },
    '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
        title: 'Đánh giá rủi ro an ninh mạng định kỳ',
        description: 'Phải thực hiện đánh giá rủi ro an ninh mạng định kỳ cho tất cả các hệ thống thông tin đã phân loại.',
    },
    '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
        title: 'Biện pháp an ninh theo phân loại hệ thống thông tin',
        description: 'Các biện pháp an ninh kỹ thuật phải được áp dụng phù hợp với từng mức phân loại hệ thống thông tin.',
    },
    b69a8be2_ae0c_408c_b8f1_73b573eca876: {
        title: 'Kiểm soát truy cập và quản lý danh tính',
        description: 'Các biện pháp kiểm soát truy cập và quản lý danh tính phải được triển khai cho hệ thống thông tin.',
    },
    ac774c26_feb7_4123_b799_c54f77bd87b5: {
        title: 'Ghi nhật ký, giám sát và dấu vết kiểm toán',
        description: 'Hệ thống thông tin phải duy trì khả năng ghi nhật ký, giám sát và dấu vết kiểm toán.',
    },
    a86cb01d_239b_44e2_9e09_e6973bcf9658: {
        title: 'Quản lý lỗ hổng và cập nhật bản vá',
        description: 'Quy trình quản lý lỗ hổng và cập nhật bản vá phải được thiết lập và duy trì.',
    },
    '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
        title: 'Lưu trữ dữ liệu nội địa cho dữ liệu người dùng Việt Nam',
        description: 'Dữ liệu của người dùng Việt Nam trên các dịch vụ cung cấp tại Việt Nam phải được lưu trữ trong lãnh thổ Việt Nam.',
    },
    '8bee2b51_ace8_4118_89c8_010b96640016': {
        title: 'Yêu cầu hiện diện tại chỗ đối với nhà cung cấp dịch vụ nước ngoài',
        description: 'Nhà cung cấp dịch vụ nước ngoài hoạt động tại Việt Nam phải duy trì hiện diện tại chỗ theo quy định.',
    },
    '74d89250_b45f_41b6_bfe3_f915071580de': {
        title: 'Kiến trúc lưu trữ nội địa và tài liệu luồng dữ liệu xuyên biên giới',
        description: 'Kiến trúc lưu trữ nội địa phải được lập tài liệu và các luồng dữ liệu xuyên biên giới phải được ánh xạ và kiểm soát.',
    },
    '04dec6f8_3280_494c_8506_292804b16d4d': {
        title: 'Phát hiện và ứng phó sự cố an ninh mạng',
        description: 'Tổ chức phải có khả năng phát hiện và ứng phó các sự cố an ninh mạng.',
    },
    faeda71b_5050_497d_b08b_e3f3f5313ad4: {
        title: 'Báo cáo sự cố bắt buộc cho cơ quan có thẩm quyền',
        description: 'Các sự cố an ninh mạng đủ điều kiện phải được báo cáo cho MPS/MIC trong thời hạn quy định.',
    },
    d732b5a2_2b2c_48a0_955d_97a39daf132d: {
        title: 'Tuân thủ thời hạn thông báo sự cố',
        description: 'Thông báo sự cố phải tuân thủ các yêu cầu về thời hạn quy định (báo cáo ban đầu trong 24 giờ, báo cáo đầy đủ trong 72 giờ).',
    },
    '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
        title: 'Xử lý yêu cầu và phối hợp với cơ quan chức năng',
        description: 'Các yêu cầu của cơ quan chức năng phải được xử lý kịp thời và phối hợp theo quy trình quy định.',
    },
    e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
        title: 'Sổ đăng ký vụ việc yêu cầu cơ quan chức năng và nhật ký công khai',
        description: 'Tất cả yêu cầu của cơ quan chức năng và việc công khai phải được ghi nhận trong sổ đăng ký vụ việc với dấu vết kiểm toán đầy đủ.',
    },
    '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
        title: 'Đánh giá an ninh nhà cung cấp và bên thứ ba',
        description: 'Các nhà cung cấp bên thứ ba có quyền truy cập hệ thống thông tin phải trải qua đánh giá an ninh.',
    },
    '69891e1b_97f6_464c_9cce_b1276794fe4a': {
        title: 'Kiểm soát truy cập dữ liệu và thông báo sự cố của nhà cung cấp',
        description: 'Quyền truy cập dữ liệu của nhà cung cấp phải được lập tài liệu và nghĩa vụ thông báo sự cố theo hợp đồng phải được thiết lập.',
    },
    d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
        title: 'Đào tạo nhận thức an ninh và diễn tập',
        description: 'Phải tổ chức đào tạo nhận thức an ninh mạng và diễn tập trên bàn định kỳ.',
    },
    f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
        title: 'Kiểm tra xâm nhập và kiểm toán an ninh định kỳ',
        description: 'Phải thực hiện kiểm tra xâm nhập và kiểm toán an ninh định kỳ trên các hệ thống thông tin đã phân loại.',
    },
    '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
        title: 'Duy trì kiểm kê dữ liệu số',
        description: 'Tổ chức phải duy trì bản kiểm kê dữ liệu số toàn diện liệt kê tất cả tài sản dữ liệu.',
    },
    '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
        title: 'Phân loại dữ liệu theo mức độ nhạy cảm và quan trọng',
        description: 'Dữ liệu phải được phân loại theo mức độ nhạy cảm và quan trọng như Luật Dữ liệu quy định.',
    },
    b89981ac_7559_46f9_a74c_a143976dde4e: {
        title: 'Xác định quy tắc xử lý theo mức phân loại',
        description: 'Phải thiết lập quy tắc xử lý cho từng mức phân loại dữ liệu.',
    },
    '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
        title: 'Theo dõi chia sẻ và công khai dữ liệu kèm phê duyệt',
        description: 'Tất cả hoạt động chia sẻ và công khai dữ liệu phải được theo dõi với các phê duyệt phù hợp.',
    },
    '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
        title: 'Quản trị chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu xuyên biên giới phải tuân thủ các yêu cầu quản trị theo Luật Dữ liệu.',
    },
    '006b8aa6_9379_4eff_b8ab_935f9a087601': {
        title: 'Duy trì chính sách quản trị và phân công trách nhiệm',
        description: 'Tổ chức phải duy trì chính sách quản trị dữ liệu và phân công trách nhiệm quản trị.',
    },
    f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
        title: 'Xác nhận quản trị hàng năm',
        description: 'Phải hoàn thành xác nhận quản trị hàng năm để khẳng định sự tuân thủ liên tục.',
    },
    f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
        title: 'Đánh giá rủi ro dữ liệu',
        description: 'Phải thực hiện đánh giá rủi ro dữ liệu để xác định và giảm thiểu các rủi ro quản trị dữ liệu.',
    },
    '6f228998_1c61_49cf_a585_3d3e9baff55a': {
        title: 'Trách nhiệm giải trình và nghĩa vụ quản trị của chủ sàn',
        description: 'Thiết lập cơ cấu trách nhiệm giải trình của chủ sàn bao gồm chính sách quản trị, trách nhiệm giám sát và nghĩa vụ thực thi.',
    },
    eee1d423_3783_4530_bc64_eef6fd9d02dc: {
        title: 'Công bố quy chế sàn và minh bạch thông tin',
        description: 'Công bố quy chế sàn, điều kiện giao dịch và thông tin hoạt động một cách minh bạch và dễ tiếp cận.',
    },
    '50b57433_7a51_4e92_9c84_c748d6865b70': {
        title: 'Thông báo website/ứng dụng bán hàng thương mại điện tử cho Bộ Công Thương',
        description: 'Nộp thông báo cho Bộ Công Thương về website và ứng dụng bán hàng thương mại điện tử qua Online.gov.vn hoặc kênh chỉ định.',
    },
    '92854abe_6654_4155_8495_f3419a8a8da0': {
        title: 'Đăng ký sàn dịch vụ thương mại điện tử/chợ trực tuyến với Bộ Công Thương',
        description: 'Đăng ký sàn dịch vụ thương mại điện tử và chợ trực tuyến với Bộ Công Thương bao gồm nộp hồ sơ và theo dõi phê duyệt.',
    },
    b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
        title: 'Quản lý sổ đăng ký người bán (trong nước và nước ngoài)',
        description: 'Duy trì sổ đăng ký tất cả người bán trên sàn bao gồm người bán trong nước và nước ngoài với quy trình tiếp nhận, xác minh và theo dõi vòng đời.',
    },
    '75778639_5235_4d13_a728_fa772c27fc0c': {
        title: 'Sổ đăng ký người bán livestream và kiểm soát tuân thủ',
        description: 'Duy trì sổ đăng ký người bán livestream với xác minh danh tính, theo dõi hoạt động và kiểm soát tuân thủ.',
    },
    '405f2932_742a_4bcb_9784_1a3d81f2e714': {
        title: 'Sổ đăng ký đối tác liên kết và tiếp thị',
        description: 'Duy trì sổ đăng ký người tiếp thị liên kết và đối tác giới thiệu với trạng thái hợp đồng, hướng dẫn nội dung và xác nhận tuân thủ.',
    },
    cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
        title: 'Kiểm soát đăng bán sản phẩm và thực thi hàng cấm/hàng có điều kiện',
        description: 'Triển khai kiểm soát đăng bán sản phẩm bao gồm sàng lọc, giám sát và hành động thực thi đối với hàng cấm và hàng có điều kiện.',
    },
    '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
        title: 'Sổ đăng ký khiếu nại người tiêu dùng với tiếp nhận, hành động và dấu vết kiểm toán',
        description: 'Duy trì sổ đăng ký khiếu nại với hồ sơ tiếp nhận, hành động điều tra, kết quả giải quyết và dấu vết kiểm toán đầy đủ.',
    },
    '8a21764e_e5f4_4934_9913_c235f4c462ca': {
        title: 'Quy trình xử lý khiếu nại và thời hạn giải quyết',
        description: 'Thiết lập quy trình xử lý khiếu nại với thời hạn xác nhận, điều tra và giải quyết rõ ràng.',
    },
    '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
        title: 'Kiểm duyệt nội dung, giám sát và thực thi gỡ bỏ',
        description: 'Triển khai quy trình kiểm duyệt nội dung, giám sát và thực thi gỡ bỏ đối với nội dung vi phạm pháp luật, bị cấm hoặc vi phạm quy chế.',
    },
    '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
        title: 'Sổ đăng ký hành động gỡ bỏ và thực thi kèm bảo quản bằng chứng',
        description: 'Duy trì sổ đăng ký hành động gỡ bỏ và thực thi với hồ sơ quyết định, bảo quản bằng chứng và thông báo cho người bán.',
    },
    c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
        title: 'Sổ đăng ký chính sách và công bố của sàn (phiên bản, ngày công bố, chấp nhận)',
        description: 'Duy trì sổ đăng ký chính sách và công bố của sàn với lịch sử phiên bản, ngày công bố và bằng chứng chấp nhận.',
    },
    c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
        title: 'Nghĩa vụ thông tin người tiêu dùng — thông tin sản phẩm chính xác và chống lừa dối',
        description: 'Đảm bảo thông tin sản phẩm và dịch vụ chính xác, rõ ràng và triển khai biện pháp ngăn chặn hành vi lừa dối trên sàn.',
    },
    '8f49603b_454c_4470_ab07_2f845a447595': {
        title: 'Điều kiện hoạt động và đăng ký/thông báo cho sàn nước ngoài',
        description: 'Tuân thủ điều kiện hoạt động cho sàn nước ngoài phục vụ người dùng Việt Nam bao gồm yêu cầu đăng ký hoặc thông báo.',
    },
    e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
        title: 'Đại diện tại chỗ hoặc ủy quyền cho sàn nước ngoài',
        description: 'Chỉ định đại diện tại chỗ hoặc tổ chức được ủy quyền tại Việt Nam cho sàn nước ngoài theo quy định của pháp luật.',
    },
    '5719f47c_f298_429e_90c3_df6ceed856e1': {
        title: 'Phối hợp xử lý người bán xuyên biên giới và giải quyết khiếu nại',
        description: 'Phối hợp xử lý người bán xuyên biên giới và giải quyết khiếu nại bao gồm leo thang và quản lý bằng chứng.',
    },
    c0b3db29_9161_4cef_a889_94e3b85d78fe: {
        title: 'Sẵn sàng tương tác và báo cáo với hệ thống quản lý thương mại điện tử quốc gia',
        description: 'Chuẩn bị cho việc tương tác với hệ thống quản lý thương mại điện tử quốc gia bao gồm ánh xạ trường dữ liệu và khả năng báo cáo.',
    },
    '145a7016_720a_4a06_badd_00145842821e': {
        title: 'Thông báo thay đổi trọng yếu sau khi được phê duyệt',
        description: 'Nộp thông báo về các thay đổi trọng yếu đối với thông tin nền tảng, chính sách hoặc hoạt động sau khi được phê duyệt ban đầu.',
    },
    '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
        title: 'Thông báo chấm dứt và ngừng hoạt động',
        description: 'Nộp thông báo chấm dứt hoặc ngừng hoạt động khi dừng hoạt động thương mại điện tử bao gồm thông báo công khai và xử lý bảo vệ người tiêu dùng.',
    },
    be95bc85_2b25_4de7_a3e7_154b780289e4: {
        title: 'Đủ điều kiện và duy trì chương trình dấu tin cậy và xếp hạng uy tín',
        description: 'Đánh giá điều kiện tham gia chương trình dấu tin cậy và xếp hạng uy tín, đồng thời duy trì bằng chứng tuân thủ để tiếp tục tham gia.',
    },
    f639afe2_18f2_4c0a_82d9_626308994267: {
        title: 'Đồng ý cho việc xử lý dữ liệu cá nhân',
        description: 'Việc xử lý dữ liệu cá nhân yêu cầu sự đồng ý của chủ thể dữ liệu trừ khi có trường hợp miễn trừ áp dụng.',
    },
    '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
        title: 'Dữ liệu cá nhân nhạy cảm: thông báo cho chủ thể dữ liệu rằng dữ liệu là dữ liệu nhạy cảm',
        description: 'Đối với xử lý dữ liệu cá nhân nhạy cảm, chủ thể dữ liệu phải được thông báo rằng dữ liệu cần xử lý là dữ liệu cá nhân nhạy cảm (khoản 8 Điều 11). Điều 28 bổ sung các biện pháp bảo vệ: áp dụng các biện pháp quy định tại khoản 2 Điều 26 và Điều 27; chỉ định bộ phận có chức năng bảo vệ dữ liệu cá nhân, chỉ định nhân sự phụ trách bảo vệ dữ liệu cá nhân và trao đổi thông tin về bộ phận và cá nhân phụ trách bảo vệ dữ liệu cá nhân với Cơ quan chuyên trách bảo vệ dữ liệu cá nhân; và thông báo cho chủ thể dữ liệu biết việc dữ liệu cá nhân nhạy cảm của chủ thể dữ liệu được xử lý, trừ trường hợp quy định tại khoản 4 Điều 13, Điều 17 và Điều 18.',
    },
    '5a958c76_6943_4449_a15c_ddb503ce526f': {
        title: 'Quyền truy cập',
        description: 'Chủ thể dữ liệu có quyền truy cập dữ liệu cá nhân của mình do bên kiểm soát dữ liệu nắm giữ.',
    },
    '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
        title: 'Quyền chỉnh sửa',
        description: 'Chủ thể dữ liệu có quyền yêu cầu chỉnh sửa dữ liệu cá nhân không chính xác.',
    },
    '25c61e57_273f_48cb_a318_d6315ad744f1': {
        title: 'Quyền xóa dữ liệu',
        description: 'Chủ thể dữ liệu có quyền yêu cầu xóa dữ liệu cá nhân của mình.',
    },
    '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
        title: 'Quyền hạn chế xử lý',
        description: 'Chủ thể dữ liệu có quyền yêu cầu hạn chế việc xử lý dữ liệu cá nhân của mình.',
    },
    '77f9677a_f436_43ec_a947_3bdc44fc192a': {
        title: 'Quyền phản đối',
        description: 'Chủ thể dữ liệu có quyền phản đối việc xử lý dữ liệu cá nhân của mình.',
    },
    c8b19e70_782c_49c9_8a71_14bef4147d6b: {
        title: 'Quyền rút lại sự đồng ý',
        description: 'Chủ thể dữ liệu có quyền rút lại sự đồng ý đã cho trước đó vào bất kỳ lúc nào.',
    },
    '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
        title: 'Thông báo vi phạm cho cơ quan chức năng (72 giờ)',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho Bộ Công an trong vòng 72 giờ kể từ khi phát hiện vi phạm dữ liệu cá nhân.',
    },
    '98bed7ab_e814_43f5_8e70_e95c413266a1': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho các chủ thể dữ liệu bị ảnh hưởng về vi phạm dữ liệu cá nhân mà không chậm trễ quá mức.',
    },
    '404a3576_dd84_4bac_acf4_29719615c45e': {
        title: 'DPIA cho hoạt động xử lý có rủi ro cao',
        description: 'Phải thực hiện Đánh giá tác động bảo vệ dữ liệu đối với các hoạt động xử lý có khả năng dẫn đến rủi ro cao.',
    },
    '29d9ed94_9360_457c_9aca_37aefac66969': {
        title: 'Đánh giá chuyển dữ liệu xuyên biên giới',
        description: 'Phải thực hiện đánh giá tác động trước khi chuyển dữ liệu cá nhân ra ngoài Việt Nam.',
    },
    '9620ff08_b83c_4e1f_9350_eba835b145fe': {
        title: 'Hồ sơ hoạt động xử lý',
        description: 'Bên kiểm soát dữ liệu phải duy trì Hồ sơ hoạt động xử lý (RoPA) ghi nhận tất cả các hoạt động xử lý.',
    },
    '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
        title: 'Hồ sơ thỏa thuận xử lý dữ liệu',
        description: 'Phải duy trì hồ sơ các thỏa thuận xử lý dữ liệu với bên xử lý và bên xử lý phụ.',
    },
    '434dc319_1922_49e4_b757_37622135c4b2': {
        title: 'Biện pháp bảo vệ chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu cá nhân ra ngoài Việt Nam yêu cầu các biện pháp bảo vệ phù hợp và đánh giá được ghi nhận.',
    },
    '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
        title: 'Xác nhận trong vòng 2 ngày làm việc',
        description: 'Yêu cầu của chủ thể dữ liệu phải được xác nhận trong vòng 2 ngày làm việc kể từ khi nhận được.',
    },
    b15fa62d_3f52_4e42_8442_f4969e08883a: {
        title: 'Hoàn thành trong vòng 15 ngày làm việc',
        description: 'Yêu cầu của chủ thể dữ liệu phải được hoàn thành trong vòng 15 ngày làm việc, có thể gia hạn thêm 5 ngày làm việc.',
    },
    '5d81233c_f671_4827_b874_827762edfc20': {
        title: 'Nộp hồ sơ đánh giá tác động cho Bộ Công an',
        description: 'Tổ chức thực hiện xử lý có rủi ro cao phải nộp hồ sơ đánh giá tác động cho Bộ Công an theo thủ tục hành chính quy định.',
    },
    '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
        title: 'Nộp hồ sơ chuyển dữ liệu xuyên biên giới cho Bộ Công an',
        description: 'Tổ chức chuyển dữ liệu cá nhân ra ngoài Việt Nam phải nộp hồ sơ đánh giá tác động chuyển dữ liệu xuyên biên giới cho Bộ Công an.',
    },
    '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
        title: 'Chứng nhận nhà cung cấp dịch vụ xử lý',
        description: 'Tổ chức cung cấp dịch vụ xử lý dữ liệu cá nhân phải được cấp chứng nhận dịch vụ xử lý từ Bộ Công an.',
    },
    b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
        title: 'Hồ sơ đánh giá tác động xử lý khi đáp ứng điều kiện kích hoạt',
        description: 'Phải nộp hồ sơ đánh giá tác động xử lý khi các điều kiện kích hoạt theo quy định được đáp ứng.',
    },
    '9d34a206_b85e_4649_a23b_e2c6f1398402': {
        title: 'Hồ sơ tác động chuyển dữ liệu xuyên biên giới',
        description: 'Phải lập và nộp hồ sơ tác động chuyển dữ liệu xuyên biên giới cho việc chuyển dữ liệu quốc tế.',
    },
    '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
        title: 'Chỉ định nhân viên bảo vệ dữ liệu',
        description: 'Phải chỉ định nhân viên bảo vệ dữ liệu theo yêu cầu của Nghị định 13.',
    },
    '776183b2_2797_40e8_8d81_c4f512c566d2': {
        title: 'Tài liệu quản trị (chính sách, vai trò, quy trình)',
        description: 'Các tài liệu quản trị bao gồm chính sách, vai trò và quy trình phải được duy trì.',
    },
    '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
        title: 'Bộ bằng chứng đầy đủ và nhật ký kiểm toán bất biến',
        description: 'Bộ bằng chứng phải đầy đủ và nhật ký kiểm toán bất biến phải được duy trì.',
    },
    f6826e26_8991_4a0f_9117_481490230bdd: {
        title: 'Hỗ trợ quy trình nộp hồ sơ',
        description: 'Phải hỗ trợ quy trình nộp hồ sơ cho đánh giá tác động và hồ sơ chuyển dữ liệu xuyên biên giới.',
    },
    '8a28ee33_305a_491f_a93f_90e59f8da91b': {
        title: 'Yêu cầu hợp đồng với bên xử lý',
        description: 'Phải thiết lập và giám sát các yêu cầu hợp đồng đối với bên xử lý dữ liệu.',
    },
    '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
        title: 'Rà soát tuân thủ hằng năm',
        description: 'Phải thực hiện rà soát tuân thủ hằng năm để xác minh việc tuân thủ liên tục.',
    },
    '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
        title: 'Hồ sơ nộp cho cơ quan chức năng',
        description: 'Phải duy trì hồ sơ tất cả các lần nộp hồ sơ cho cơ quan chức năng.',
    },
    e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
        title: 'Cơ sở pháp lý cho việc xử lý dữ liệu cá nhân',
        description: 'Việc xử lý dữ liệu cá nhân phải dựa trên cơ sở pháp lý được quy định bởi Luật BVDLCN.',
    },
    c919c66c_6a74_4403_bb97_04b773d270bf: {
        title: 'Dữ liệu cá nhân nhạy cảm: xác định, bảo vệ và thông báo khi xin sự đồng ý',
        description: 'Khoản 3 Điều 2 Luật 91/2025/QH15 định nghĩa dữ liệu cá nhân nhạy cảm là dữ liệu cá nhân gắn liền với quyền riêng tư của cá nhân, khi bị xâm phạm sẽ gây ảnh hưởng trực tiếp đến quyền, lợi ích hợp pháp của cơ quan, tổ chức, cá nhân, thuộc danh mục do Chính phủ ban hành; khoản 1 Điều 4 Nghị định 356/2025/NĐ-CP là danh mục đó. Luật không đặt ra quy định riêng về sự đồng ý đối với dữ liệu cá nhân nhạy cảm nói chung; các điều khoản theo lĩnh vực có quy định riêng. Điểm a khoản 1 Điều 26 quy định đối với thông tin sức khỏe và trong hoạt động kinh doanh bảo hiểm: "Phải có sự đồng ý của chủ thể dữ liệu cá nhân trong quá trình thu thập, xử lý dữ liệu cá nhân, trừ trường hợp quy định tại khoản 1 Điều 19 của Luật này". Điểm b khoản 1 Điều 27 quy định: "Không sử dụng thông tin tín dụng của chủ thể dữ liệu cá nhân để chấm điểm, xếp hạng tín dụng, đánh giá thông tin tín dụng, đánh giá mức độ tín nhiệm về tín dụng của chủ thể dữ liệu cá nhân khi chưa có sự đồng ý của chủ thể dữ liệu cá nhân". Điểm a khoản 3 Điều 31 quy định: "Không áp dụng việc theo dõi định vị qua thẻ nhận dạng tần số vô tuyến và các công nghệ khác, trừ trường hợp có sự đồng ý của chủ thể dữ liệu cá nhân hoặc trường hợp có yêu cầu của cơ quan có thẩm quyền theo quy định của pháp luật hoặc trường hợp pháp luật có quy định khác". Trong số những yêu cầu các văn bản đặt ra đối với dữ liệu nhạy cảm: trong quá trình xử lý, cơ quan, tổ chức phải thiết lập quy định phân quyền giới hạn truy cập, quy trình xử lý và các biện pháp bảo mật (khoản 2 Điều 4 Nghị định 356); đối với việc xin sự đồng ý xử lý dữ liệu cá nhân nhạy cảm, chủ thể dữ liệu phải được thông báo rằng dữ liệu cần xử lý là dữ liệu cá nhân nhạy cảm (khoản 4 Điều 6 Nghị định 356); việc chuyển giao dữ liệu cá nhân nhạy cảm phải có biện pháp bảo mật vật lý đối với thiết bị lưu trữ và truyền tải, biện pháp mã hóa, ẩn danh dữ liệu cá nhân và các biện pháp bảo mật khác (khoản 2 Điều 7 Nghị định 356); tổ chức, cá nhân hoạt động trong lĩnh vực tài chính, ngân hàng, hoạt động thông tin tín dụng phải "Thực hiện đầy đủ quy định về bảo vệ dữ liệu cá nhân nhạy cảm" (điểm a khoản 1 Điều 27 Luật 91), và "Trong thời hạn không quá 72 giờ sau khi phát hiện lộ, mất dữ liệu nhạy cảm của chủ thể dữ liệu cá nhân trong lĩnh vực tài chính, ngân hàng, hoạt động thông tin tín dụng, tổ chức, cá nhân trực tiếp thu thập dữ liệu cá nhân của chủ thể dữ liệu có trách nhiệm thông báo cho cơ quan chuyên trách bảo vệ dữ liệu cá nhân và chủ thể dữ liệu cá nhân" (khoản 3 Điều 8 Nghị định 356); và doanh nghiệp nhỏ, doanh nghiệp khởi nghiệp, hộ kinh doanh, doanh nghiệp siêu nhỏ "trực tiếp xử lý dữ liệu cá nhân nhạy cảm" không được hưởng quyền lựa chọn hoặc miễn trừ đối với Điều 21, Điều 22 và khoản 2 Điều 33 của Luật theo khoản 2, khoản 3 Điều 38 Luật 91 và Điều 41 Nghị định 356. Hãy ghi nhận những dữ liệu cá nhân nào của tổ chức thuộc danh mục đó.',
    },
    '419cc7c3_a257_4293_a054_304dc2fcb8df': {
        title: 'Quyền của chủ thể dữ liệu (truy cập, chỉnh sửa, xóa, hạn chế, phản đối, chuyển đổi)',
        description: 'Chủ thể dữ liệu có các quyền toàn diện bao gồm truy cập, chỉnh sửa, xóa, hạn chế, phản đối và chuyển đổi dữ liệu.',
    },
    '638e6e25_3a74_4864_8bff_8e21ab82860b': {
        title: 'Thông báo vi phạm cho cơ quan chức năng',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho cơ quan có thẩm quyền về vi phạm dữ liệu cá nhân trong thời hạn quy định.',
    },
    '6e75c111_66b1_4726_8c27_6537846edb30': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho các chủ thể dữ liệu bị ảnh hưởng về vi phạm dữ liệu cá nhân.',
    },
    f686123d_3bdd_4a61_a53c_efe5b52d9058: {
        title: 'Đánh giá tác động bảo vệ dữ liệu',
        description: 'Phải thực hiện đánh giá tác động bảo vệ dữ liệu đối với các hoạt động xử lý có rủi ro cao.',
    },
    '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
        title: 'Quản trị chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu cá nhân xuyên biên giới phải tuân thủ các yêu cầu quản trị.',
    },
    cf9a5b29_46fd_4072_b877_13b8a69df82c: {
        title: 'Chỉ định nhân viên bảo vệ dữ liệu',
        description: 'Tổ chức phải chỉ định nhân viên bảo vệ dữ liệu theo yêu cầu.',
    },
    f5a4965d_eb0c_47df_9933_f185ae46463b: {
        title: 'Hồ sơ hoạt động xử lý',
        description: 'Tổ chức phải duy trì hồ sơ tất cả các hoạt động xử lý.',
    },
    ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
        title: 'Nghĩa vụ hợp đồng với bên xử lý',
        description: 'Phải thiết lập nghĩa vụ hợp đồng với bên xử lý dữ liệu.',
    },
    '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
        title: 'Nộp hồ sơ đánh giá tác động',
        description: 'Phải nộp hồ sơ đánh giá tác động cho cơ quan có thẩm quyền theo quy định.',
    },
    '65dba62f_0788_44c1_89e3_09d802e28eb9': {
        title: 'Hồ sơ tác động chuyển dữ liệu xuyên biên giới',
        description: 'Phải lập và nộp hồ sơ tác động chuyển dữ liệu xuyên biên giới cho việc chuyển dữ liệu quốc tế.',
    },
    '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
        title: 'Bổ nhiệm và thông báo DPO',
        description: 'Phải bổ nhiệm nhân viên bảo vệ dữ liệu và thông báo cho cơ quan chức năng.',
    },
    '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
        title: 'Chính sách và quy trình quản trị',
        description: 'Phải thiết lập và duy trì các chính sách và quy trình quản trị.',
    },
    '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
        title: 'Bộ bằng chứng và nhật ký kiểm toán bất biến',
        description: 'Bộ bằng chứng phải đầy đủ và nhật ký kiểm toán bất biến phải được duy trì.',
    },
    '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
        title: 'Vòng đời hồ sơ nộp',
        description: 'Vòng đời hồ sơ nộp phải được quản lý qua các giai đoạn quy định.',
    },
    '83f2f346_47bb_4377_a711_3cef26742f39': {
        title: 'Yêu cầu hợp đồng với bên xử lý',
        description: 'Phải thiết lập và giám sát các yêu cầu hợp đồng đối với bên xử lý dữ liệu.',
    },
    cb972e06_f773_492e_8ad5_f65d1e999b79: {
        title: 'Chứng nhận nhà cung cấp dịch vụ xử lý',
        description: 'Tổ chức cung cấp dịch vụ xử lý dữ liệu phải được cấp chứng nhận.',
    },
    '4ba36ab5_39f0_4d79_927e_c39587bed226': {
        title: 'Hồ sơ nộp cho cơ quan chức năng',
        description: 'Phải duy trì hồ sơ tất cả các lần nộp hồ sơ cho cơ quan chức năng.',
    },
    f7a62c88_3cda_4031_bc52_1598661a5128: {
        title: 'Rà soát tuân thủ hằng năm',
        description: 'Phải thực hiện rà soát tuân thủ hằng năm để xác minh việc tuân thủ liên tục.',
    },
    '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
        title: 'Biện pháp kỹ thuật bảo mật dữ liệu',
        description: 'Phải triển khai và duy trì các biện pháp kỹ thuật để bảo mật dữ liệu.',
    },
    '45f4e745_a923_4879_94ce_5a2b41b7e886': {
        title: 'Yêu cầu Mẫu số chính thức theo Nghị định 356',
        description: '13 Mẫu số hành chính chính thức (Mẫu số 01a–10) quy định tại Nghị định 356/2025/NĐ-CP phải được sử dụng khi nộp hồ sơ tương ứng cho Bộ Công an. Các Phụ lục I–VIII chỉ là cấu trúc dữ liệu nội bộ, không phải mẫu chính thức do cơ quan ban hành.',
    },
    ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
        title: 'Nộp hồ sơ DPIA cho A05',
        description: 'Tổ chức phải nộp hồ sơ DPIA cho A05/MPS đối với hoạt động xử lý liên quan đến dữ liệu cá nhân nhạy cảm hoặc xử lý quy mô lớn.',
    },
    '003600ae_7a0f_4034_b206_72f59409b8f3': {
        title: 'Nộp hồ sơ chuyển dữ liệu xuyên biên giới',
        description: 'Tổ chức phải nộp hồ sơ chuyển dữ liệu xuyên biên giới để A05/MPS xem xét và phê duyệt.',
    },
    '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
        title: 'Đăng ký chứng nhận dịch vụ xử lý',
        description: 'Nhà cung cấp dịch vụ xử lý dữ liệu phải đăng ký cấp chứng nhận từ A05/MPS.',
    },
    be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
        title: 'Thông báo hoạt động xử lý dữ liệu cá nhân',
        description: 'Bên kiểm soát phải thông báo cho A05/MPS về hoạt động xử lý dữ liệu cá nhân sử dụng biểu mẫu quy định (Mẫu số 01a/01b).',
    },
    '39f50471_27cc_46bd_acff_9aa2211d43ab': {
        title: 'Báo cáo tình hình hằng năm',
        description: 'Bên kiểm soát phải nộp báo cáo tình hình xử lý dữ liệu cá nhân hằng năm (Mẫu số 09).',
    },
    '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
        title: 'Bảo mật thông tin người dùng theo mặc định',
        description: 'Thông tin người dùng phải được xử lý như thông tin bảo mật theo mặc định.',
    },
    '2994510e_b1f8_4cdd_947c_d9542746433b': {
        title: 'Biện pháp bảo vệ tổ chức và kỹ thuật tối thiểu',
        description: 'Phải triển khai các biện pháp bảo vệ tổ chức và kỹ thuật tối thiểu.',
    },
    '8643160f_9a10_4d93_baf5_082c6a7c768a': {
        title: 'Chỉ tiết lộ khi có cơ sở hợp pháp',
        description: 'Việc tiết lộ thông tin người dùng chỉ được phép khi có cơ sở pháp lý hợp pháp.',
    },
    c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
        title: 'Ghi nhật ký và khả năng kiểm toán bắt buộc cho việc tiết lộ',
        description: 'Tất cả các lần tiết lộ phải được ghi nhật ký và có khả năng kiểm toán.',
    },
    efa36bc0_abae_417a_b676_9509af37f89d: {
        title: 'Quy trình phê duyệt nội bộ cho việc tiết lộ',
        description: 'Phải thiết lập quy trình phê duyệt nội bộ cho tất cả các lần tiết lộ.',
    },
    d086fdcd_9a23_4500_893c_db8ac62bd787: {
        title: 'Xử lý yêu cầu từ cơ quan chức năng',
        description: 'Yêu cầu từ cơ quan chức năng phải được xử lý và phản hồi theo thủ tục quy định.',
    },
};
/**
 * Sprint 62.58.0 (Slice C) — tóm tắt khung tuân thủ và ghi nhận phổ biến thông tin,
 * góp ý. Đề xuất, chờ Compliance Manager phê duyệt theo R-44.
 */
export const viFrameworkBriefings = {
    summary_export: {
        title: 'Tóm tắt khung tuân thủ: {name}',
        section_status: 'Trạng thái và các mốc ngày',
        col_field: 'Thông tin',
        col_value: 'Giá trị',
        field_code: 'Mã khung tuân thủ',
        field_status: 'Trạng thái',
        field_issued: 'Ngày ban hành',
        field_effective: 'Có hiệu lực từ',
        draft_not_in_force: 'Dự thảo — chưa có hiệu lực',
        not_stated: 'Không nêu',
        section_parent: 'Khung tuân thủ gốc',
        parent_line: 'Khung tuân thủ này được đọc cùng khung tuân thủ gốc {name} ({code}), trạng thái: {status}. Các dòng có mốc ngày dưới đây thuộc khung tuân thủ gốc, không thuộc khung tuân thủ này.',
        parent_not_installed: 'Khung tuân thủ gốc {code} chưa được cài đặt trong tổ chức này nên các dòng có mốc ngày của khung đó không được hiển thị.',
        no_parent: 'Khung tuân thủ này không nêu khung tuân thủ gốc.',
        parent_no_rows: 'Tổ chức này không sử dụng dòng có mốc ngày nào của khung tuân thủ gốc.',
        col_belongs_to: 'Thuộc về',
        section_guidance: 'Hướng dẫn',
        no_guidance: 'Chưa cài đặt ghi chú hướng dẫn nào cho khung tuân thủ này.',
        col_title: 'Tiêu đề',
        col_summary: 'Tóm tắt',
        section_requirements: 'Yêu cầu',
        no_requirements: 'Chưa cài đặt yêu cầu nào cho khung tuân thủ này.',
        col_requirement_id: 'Mã',
        col_reference: 'Tham chiếu',
        col_addressed_to: 'Đối tượng hướng tới',
        col_mandatory: 'Bắt buộc',
        mandatory_yes: 'Có',
        mandatory_no: 'Không',
        mandatory_not_stated: 'Không nêu',
        mandatory_no_draft: 'Không — đề xuất trong dự thảo',
        mandatory_no_legacy: 'Không — khung tuân thủ đã cũ',
        mandatory_no_suspended: 'Không — việc áp dụng khung tuân thủ đang bị tạm ngừng',
        section_deadlines: 'Thời hạn',
        no_deadlines: 'Chưa cài đặt dòng thời hạn nào cho khung tuân thủ này.',
        col_deadline: 'Thời hạn',
        col_date_or_period: 'Ngày hoặc khoảng thời gian',
        col_note: 'Ghi chú',
        stated_date: '{date} (theo văn bản)',
        authority_clock: 'Khoảng thời gian của cơ quan có thẩm quyền, không phải nghĩa vụ của tổ chức.',
        section_pack: 'Gói',
        pack_line: 'Được cài đặt từ gói {code}, phiên bản {version}.',
        pack_not_recorded: 'Không có thông tin về gói đã dùng để cài đặt khung tuân thủ này.',
        generated_line: 'Được tạo ngày {date} từ bản ghi khung tuân thủ đã cài đặt của tổ chức này. Bản tóm tắt này chỉ dùng để đọc; ComplianceOne không nộp bất kỳ nội dung nào cho cơ quan có thẩm quyền.',
    },
    record: {
        tab: 'Phổ biến và góp ý',
        heading: 'Phổ biến thông tin và ý kiến góp ý dự thảo',
        intro: 'Ghi nhận các buổi phổ biến thông tin mà tổ chức của bạn đã thực hiện về khung tuân thủ này và các ý kiến góp ý dự thảo mà tổ chức đã gửi. Mỗi mục được giữ nguyên như khi ghi nhận; việc đính chính là một mục mới nêu rõ mục được đính chính.',
        boundary: 'Mục ý kiến góp ý dự thảo ghi nhận nội dung tổ chức của bạn đã gửi, gửi cho ai và vào ngày nào. ComplianceOne không nộp bất kỳ nội dung nào cho cơ quan có thẩm quyền.',
        kind_briefing: 'Phổ biến thông tin',
        kind_consultation_comment: 'Ý kiến góp ý dự thảo',
        channel_meeting: 'Cuộc họp',
        channel_email: 'Thư điện tử',
        channel_document: 'Văn bản',
        channel_webinar: 'Hội thảo trực tuyến',
        channel_other: 'Khác',
        col_kind: 'Loại',
        col_audience: 'Đối tượng tiếp nhận',
        col_held_on: 'Ngày',
        col_channel: 'Hình thức',
        col_approver: 'Người phê duyệt',
        col_approval: 'Phê duyệt',
        col_pack_version: 'Phiên bản gói',
        approval_none: 'Chưa chỉ định người phê duyệt',
        approval_pending: 'Chờ phê duyệt',
        approval_approved: 'Đã phê duyệt ngày {date}',
        record: 'Ghi nhận một mục',
        recording: 'Đang ghi nhận…',
        approve: 'Phê duyệt',
        approving: 'Đang phê duyệt…',
        approve_aria: 'Phê duyệt mục ngày {date} dành cho {audience}',
        field_kind: 'Loại',
        field_audience: 'Đối tượng tiếp nhận',
        field_audience_hint_comment: 'Đối với ý kiến góp ý dự thảo: nơi tổ chức của bạn đã gửi ý kiến.',
        field_held_on: 'Ngày tổ chức hoặc ngày gửi',
        field_held_on_hint: 'Một ngày theo lịch, không sau ngày hôm nay.',
        field_channel: 'Hình thức',
        field_channel_note: 'Chi tiết hình thức',
        field_channel_note_required: 'Mô tả hình thức khi chọn Khác.',
        field_reference: 'Số tham chiếu',
        field_reference_hint: 'Số tham chiếu của nội dung đã gửi, chẳng hạn số công văn hoặc mã tiếp nhận trên cổng thông tin.',
        field_summary: 'Tóm tắt',
        field_approver: 'Người phê duyệt',
        field_correction_of: 'Đính chính mục',
        field_correction_note: 'Ghi chú đính chính',
        empty: 'Chưa có buổi phổ biến thông tin hoặc ý kiến góp ý dự thảo nào được ghi nhận cho khung tuân thủ này.',
        load_failed: 'Không tải được danh sách phổ biến thông tin và góp ý.',
        record_failed: 'Không ghi nhận được mục này.',
        approve_failed: 'Không phê duyệt được mục này.',
        recorded_toast: 'Đã ghi nhận mục này.',
        approved_toast: 'Đã phê duyệt mục này.',
        pack_changed: 'Được ghi nhận theo phiên bản gói {version}; khung tuân thủ này đã thay đổi kể từ đó — xem Cập nhật Quy định',
        corrected_by: 'Đã được đính chính bởi một mục sau',
        correction_label: 'Đính chính',
        evidence_count: 'Tài liệu: {count}',
        attach_summary_offer: 'Đính kèm bản tóm tắt này vào một mục',
        kind_placeholder: 'Chọn loại',
        channel_placeholder: 'Chọn hình thức',
        approver_placeholder: 'Không chỉ định người phê duyệt',
        correction_none: 'Không phải đính chính',
        correction_option: '{kind} ngày {date} dành cho {audience}',
        correction_picker_failed: 'Không đọc được các mục trước đó nên hiện chưa thể chọn mục cần đính chính.',
        error_required: 'Trường này là bắt buộc.',
        error_held_on_future: 'Ngày không được sau ngày hôm nay.',
        error_correction_note_required: 'Nêu rõ mục này đính chính nội dung gì.',
        error_approver_not_found: 'Người phê duyệt không phải là người dùng của tổ chức này.',
        error_not_approver: 'Chỉ người phê duyệt được chỉ định mới có thể phê duyệt mục này.',
        error_already_approved: 'Mục này đã được phê duyệt.',
        error_self_approval: 'Người phê duyệt phải là người khác với người ghi nhận mục này.',
        form_intro: 'Ghi nhận buổi phổ biến thông tin đã diễn ra hoặc ý kiến góp ý dự thảo đã gửi. Tài liệu được thêm sau khi mục được ghi nhận, bằng nút Tài liệu trên dòng của mục đó.',
        summary_count: '{count}/{max} ký tự',
        loading: 'Đang tải danh sách phổ biến thông tin và góp ý…',
        retry_subject: 'danh sách phổ biến thông tin và góp ý',
        reload: 'Tải lại',
        reloading: 'Đang tải lại…',
        pagination: 'Các trang phổ biến thông tin và góp ý',
        col_summary: 'Tóm tắt',
        col_documents: 'Tài liệu',
        documents_open: 'Tài liệu',
        documents_open_aria: 'Tài liệu của mục ngày {date} dành cho {audience}',
        reference_line: 'Số tham chiếu: {reference}',
        channel_note_line: 'Chi tiết hình thức: {note}',
        correction_note_line: 'Ghi chú đính chính: {note}',
        corrects_line: 'Đính chính mục ngày {date}',
        corrects_unknown: 'Đính chính một mục trước đó',
        pack_changed_open_comparison: 'Xem nội dung thay đổi',
        pack_changed_open_updates: 'Mở Cập nhật Quy định',
        replaced_since: 'Được ghi nhận theo văn bản này; kể từ đó văn bản đã được thay thế bởi một khung tuân thủ khác —',
        pack_version_not_recorded: 'Chưa ghi nhận',
        recorded_while: 'Trạng thái khung tuân thủ khi ghi nhận: {status}',
        framework_status_suspended: 'Tạm ngừng áp dụng',
        recorded_line: 'Do {person} ghi nhận ngày {date}',
        files_heading: 'Tài liệu',
        files_intro: 'Các tài liệu kèm theo mục này, chẳng hạn bản trình chiếu, công văn đã gửi hoặc bản tóm tắt khung tuân thủ đã tải xuống.',
        files_slot: 'Tài liệu của mục này',
        files_none: 'Chưa đính kèm tài liệu nào cho mục này.',
        file_hash: 'Mã SHA-256: {hash}',
        file_not_held: 'Tài liệu không còn được lưu giữ',
        file_download: 'Tải xuống',
        file_remove: 'Gỡ bỏ',
        file_upload: 'Thêm tài liệu',
        file_bound: 'Đã thêm tài liệu.',
        file_removed: 'Đã gỡ bỏ tài liệu.',
        file_bind_failed: 'Không thể thêm tài liệu.',
        file_remove_failed: 'Không thể gỡ bỏ tài liệu.',
        file_not_owned: 'Không tìm thấy mục mà tài liệu này thuộc về.',
        file_remove_confirm: 'Gỡ tài liệu này khỏi mục và xóa tài liệu?',
        files_approved_kept: 'Mục này đã được phê duyệt nên các tài liệu của mục được giữ nguyên như khi phê duyệt và không thể gỡ bỏ. Để đính chính, hãy ghi nhận một mục đính chính.',
        documents_load_failed: 'Không tải được tài liệu.',
        export_label: 'Xuất tệp tóm tắt',
        export_done: 'Đã xuất tệp tóm tắt khung tuân thủ.',
        export_failed: 'Không xuất được tệp tóm tắt khung tuân thủ.',
        attach_summary_hint: 'Để lưu bản tóm tắt này cùng một mục, mở thẻ Phổ biến và góp ý, chọn Tài liệu trên mục đó và thêm tệp đã tải xuống.',
        directory_unavailable: 'Không đọc được danh sách người dùng nên hiện chưa thể chọn người phê duyệt.',
        approver_retry_subject: 'danh sách người dùng',
    },
};
/** Sprint 62.58.0 (Slice F) — so sánh cập nhật quy định, rà soát bản so sánh và dòng kiểm tra lại. */
export const viRegulatoryUpdateComparison = {
    page: {
        module_guide: 'Những gì đã thay đổi giữa văn bản đã cài đặt trước đây và văn bản đã cài đặt hiện nay, theo từng yêu cầu, cùng nhận định của tổ chức bạn và các bản ghi đã được điền theo văn bản trước đây.',
        title: 'Nội dung thay đổi: {name}',
        title_fallback: 'Nội dung thay đổi',
        description: 'Các yêu cầu của văn bản đã cài đặt trước đây, đặt cạnh văn bản đã cài đặt hiện nay. Bản so sánh này chỉ để tham khảo và không làm thay đổi nghĩa vụ của tổ chức bạn.',
        back: 'Quay lại Cập nhật Quy định',
        loading: 'Đang tải bản so sánh…',
        load_failed: 'Không tải được bản so sánh.',
        not_found: 'Không tìm thấy cập nhật quy định này.',
        retry_subject: 'bản so sánh',
        reload: 'Tải lại',
        reloading: 'Đang tải lại…',
        no_comparison: 'Cập nhật quy định này không có bản so sánh yêu cầu. Cập nhật được ghi nhận thủ công, hoặc được cài đặt trước khi có bản so sánh, sẽ không có bản so sánh.',
        status_heading: 'Thay đổi trạng thái',
        status_change: '{from} → {to}',
        replaced_by: 'Được thay thế bởi {name}',
        status_not_recorded: 'Chưa ghi nhận',
        basis_computed: 'So sánh từng yêu cầu giữa hai phiên bản đã cài đặt của khung tuân thủ này.',
        basis_declared: 'So sánh theo bảng tương ứng do khung tuân thủ thay thế khai báo.',
        derived_on_read: 'Các dòng này được xác định khi mở trang, từ các khung tuân thủ đã cài đặt hiện nay.',
        from_heading: 'Văn bản trước đây',
        to_heading: 'Văn bản đã cài đặt hiện nay',
        side_status: 'Trạng thái: {status}',
        side_pack_version: 'Phiên bản gói: {version}',
        counts_heading: 'Tóm tắt thay đổi',
        count_line: '{label}: {count}',
        change_changed: 'Đã thay đổi',
        change_removed: 'Đã bỏ',
        change_new: 'Mới',
        change_unchanged: 'Không thay đổi',
        rows_heading: 'Yêu cầu',
        filter_label: 'Hiển thị',
        filter_all: 'Tất cả yêu cầu',
        showing: 'Đang hiển thị {shown}/{total}',
        col_requirement: 'Yêu cầu',
        col_change: 'Thay đổi',
        col_from_title: 'Tiêu đề trong văn bản trước đây',
        col_to_title: 'Tiêu đề trong văn bản đã cài đặt hiện nay',
        col_mandatory: 'Bắt buộc',
        col_note: 'Ghi chú',
        mandatory_yes: 'Có',
        mandatory_no: 'Không',
        mandatory_not_stated: 'Không nêu',
        mandatory_change: '{from} → {to}',
        not_in_text: 'Không có trong văn bản này',
        also_corresponds: 'Cũng tương ứng với: {ids}',
        rows_empty: 'Không có yêu cầu nào khớp với bộ lọc này.',
        rows_pagination: 'Các trang yêu cầu',
        superseded_texts_not_installed: 'Không có bản so sánh: một trong hai văn bản không còn được cài đặt cho tổ chức bạn, hoặc khung tuân thủ thay thế không còn khai báo việc thay thế văn bản trước đây. Cập nhật quy định này được giữ nguyên như khi ghi nhận.',
        withheld_other_pack: 'Không có bản so sánh: bản ghi trước đó được cài đặt từ một gói khác ({code}).',
        withheld_other_pack_unknown: 'Không có bản so sánh: bản ghi trước đó không được cài đặt từ gói này.',
    },
    review: {
        heading: 'Nhận định của tổ chức bạn',
        intro: 'Ghi nhận mức độ ảnh hưởng của từng yêu cầu đã thay đổi, đã bỏ hoặc mới đối với tổ chức bạn và kết luận của bạn. Nhận định là bản ghi của chính tổ chức bạn và không làm thay đổi yêu cầu của khung tuân thủ, trạng thái hay bất kỳ thời hạn nào.',
        record: 'Ghi nhận rà soát bản so sánh',
        recording: 'Đang ghi nhận…',
        none: 'Chưa có lần rà soát bản so sánh nào được ghi nhận.',
        current_heading: 'Lần rà soát hiện tại',
        recorded_line: 'Do {person} ghi nhận ngày {date}',
        conclusion: 'Kết luận',
        next_steps: 'Các bước tiếp theo',
        no_next_steps: 'Chưa ghi nhận bước tiếp theo.',
        readings_heading: 'Nhận định',
        col_reading: 'Nhận định',
        reading_affects_us: 'Có ảnh hưởng đến tổ chức',
        reading_does_not_affect_us: 'Không ảnh hưởng đến tổ chức',
        reading_to_assess: 'Cần đánh giá',
        no_rows_read: 'Lần rà soát này không nêu nhận định cho yêu cầu nào.',
        history_heading: 'Các lần rà soát trước đây',
        history_pagination: 'Phân trang các lần rà soát trước đây',
        col_recorded: 'Ngày ghi nhận',
        col_recorded_by: 'Người ghi nhận',
        col_conclusion: 'Kết luận',
        col_readings: 'Nhận định',
        readings_summary: '{affects} có ảnh hưởng, {not_affect} không ảnh hưởng, {to_assess} cần đánh giá',
        modal_intro: 'Đưa ra nhận định cho mọi yêu cầu đã thay đổi, đã bỏ hoặc mới, sau đó nêu kết luận của bạn.',
        row_legend: '{id}: {title}',
        field_reading: 'Nhận định',
        field_reading_placeholder: 'Chọn nhận định',
        field_note: 'Ghi chú',
        field_conclusion: 'Kết luận',
        field_next_steps: 'Các bước tiếp theo',
        text_count: '{count}/{max} ký tự',
        error_reading_required: 'Chọn nhận định cho yêu cầu này.',
        error_conclusion_required: 'Nêu kết luận của bạn.',
        record_failed: 'Không ghi nhận được rà soát bản so sánh.',
        recorded_toast: 'Đã ghi nhận rà soát bản so sánh.',
        no_comparison_error: 'Cập nhật này không có bản so sánh để rà soát.',
        rows_loading: 'Đang tải các yêu cầu cần nêu nhận định…',
        rows_load_failed: 'Không tải được các yêu cầu cần nêu nhận định.',
        no_rows_to_read: 'Mọi yêu cầu đều không thay đổi nên chỉ cần nêu kết luận.',
    },
    reread: {
        heading: 'Bản ghi được điền theo văn bản đã bị thay thế',
        intro: 'Các bản ghi này được điền khi văn bản trước đây còn áp dụng. Chúng được giữ nguyên: không nội dung nào bị thay đổi, chấm điểm lại hay mở lại. Hãy đọc lại từng bản ghi theo văn bản cuối cùng.',
        forms_heading: 'Bản ghi biểu mẫu',
        col_record: 'Bản ghi',
        col_form: 'Biểu mẫu',
        col_status: 'Trạng thái',
        col_updated: 'Cập nhật lần cuối',
        forms_none: 'Không có bản ghi biểu mẫu nào được điền theo văn bản đã bị thay thế.',
        forms_withheld: 'Bản ghi biểu mẫu chỉ được liệt kê cho người có quyền xem bản ghi biểu mẫu.',
        untitled: 'Bản ghi chưa đặt tên',
        forms_pagination: 'Các trang bản ghi biểu mẫu',
        questionnaires_heading: 'Bảng câu hỏi nhà cung cấp',
        notice: 'Được điền theo {from}, văn bản này đã được thay thế bởi {to} — hãy đọc lại bản ghi theo văn bản cuối cùng',
        notice_link: 'Xem nội dung thay đổi',
        notice_link_aria: 'Xem nội dung thay đổi giữa {from} và {to}',
        notice_same_text: 'Được điền theo phiên bản gói {version} của {name}; văn bản này đã thay đổi kể từ đó — hãy đọc lại bản ghi theo văn bản đã cài đặt hiện nay',
        notice_same_text_unversioned: 'Được điền theo một phiên bản gói trước đây của {name}; văn bản này đã thay đổi kể từ đó — hãy đọc lại bản ghi theo văn bản đã cài đặt hiện nay',
        notice_same_text_link_aria: 'Xem nội dung thay đổi trong {name}',
    },
    recheck: {
        will_draft: 'Đơn vị phát hành gói sẽ kiểm tra lại dự thảo này vào ngày {date}.',
        will_legacy: 'Đơn vị phát hành gói sẽ kiểm tra lại khung tuân thủ đã cũ này vào ngày {date}.',
        will_suspended: 'Đơn vị phát hành gói sẽ kiểm tra lại khung tuân thủ đang bị tạm ngừng áp dụng này vào ngày {date}.',
        was_due_draft: 'Đơn vị phát hành gói đã dự kiến kiểm tra lại dự thảo này vào ngày {date}; bản ghi này chưa được cập nhật.',
        was_due_legacy: 'Đơn vị phát hành gói đã dự kiến kiểm tra lại khung tuân thủ đã cũ này vào ngày {date}; bản ghi này chưa được cập nhật.',
        was_due_suspended: 'Đơn vị phát hành gói đã dự kiến kiểm tra lại khung tuân thủ đang bị tạm ngừng áp dụng này vào ngày {date}; bản ghi này chưa được cập nhật.',
        records_line: 'Bản ghi của khung tuân thủ này chỉ thay đổi khi một phiên bản gói mới được cài đặt; các bản ghi của tổ chức bạn không bao giờ bị thay đổi.',
    },
    list: {
        col_changed: 'Yêu cầu thay đổi',
        no_comparison_short: 'Không có bản so sánh',
        pagination: 'Phân trang cập nhật quy định',
        view_comparison: 'Xem bản so sánh',
        view_comparison_aria: 'Xem bản so sánh của {title}',
        reviews_count: 'Số lần rà soát: {count}',
    },
};
/** Sprint 63.37.0 (E0, E5) — Settings › Organisation identity and Fill from record. */
export const viOrgIdentity = {
    nav_label: 'Thông tin định danh tổ chức',
    module_guide: 'Thông tin định danh tổ chức: tên pháp lý, trụ sở, thông tin đăng ký, mã số thuế, thông tin liên hệ và người đại diện theo pháp luật dùng để điền biểu mẫu chính thức và thư giới thiệu hồ sơ.',
    title: 'Thông tin định danh tổ chức',
    description: 'Ghi nhận một lần tên pháp lý, trụ sở, thông tin đăng ký, mã số thuế, thông tin liên hệ và người đại diện theo pháp luật của tổ chức. Khi bạn chọn Điền từ bản ghi, các biểu mẫu chính thức được điền từ bản ghi này; thư giới thiệu hồ sơ cũng đọc từ đây.',
    section_names: 'Tên và trụ sở',
    section_registration: 'Thông tin đăng ký',
    section_contact: 'Thông tin liên hệ',
    section_representative: 'Người đại diện theo pháp luật',
    field_legal_name: 'Tên pháp lý',
    field_legal_name_help: 'Tên đúng như trên giấy chứng nhận đăng ký.',
    field_trading_name: 'Tên giao dịch',
    field_trading_name_help: 'Tên viết tắt hoặc tên giao dịch, nếu tổ chức sử dụng.',
    field_head_office_address: 'Địa chỉ trụ sở chính',
    field_transaction_office_address: 'Địa chỉ giao dịch',
    field_transaction_office_address_help: 'Để trống nếu trùng với trụ sở chính.',
    field_registration_number: 'Số giấy chứng nhận đăng ký doanh nghiệp',
    field_registration_instrument: 'Giấy tờ đăng ký',
    field_registration_instrument_help: 'Văn bản ghi nhận việc đăng ký, ví dụ giấy chứng nhận đăng ký doanh nghiệp hoặc quyết định thành lập.',
    field_registration_issuer: 'Cơ quan cấp',
    field_registration_issue_date: 'Ngày cấp',
    field_registration_issue_place: 'Nơi cấp',
    field_tax_code: 'Mã số thuế',
    field_tax_code_help: 'Mã số thuế được ghi nhận riêng với số đăng ký, kể cả khi hai số trùng nhau.',
    field_phone: 'Số điện thoại',
    field_email: 'Thư điện tử',
    field_website: 'Trang thông tin điện tử',
    field_website_help: 'Bắt đầu bằng https:// hoặc http://.',
    field_legal_representative_name: 'Họ và tên',
    field_legal_representative_title: 'Chức vụ',
    save: 'Lưu thông tin định danh',
    saving: 'Đang lưu…',
    saved: 'Đã lưu thông tin định danh tổ chức.',
    save_failed: 'Không thể lưu thông tin định danh tổ chức.',
    fix_fields: 'Cần sửa một số trường trước khi lưu thông tin định danh.',
    load_failed: 'Không thể đọc thông tin định danh tổ chức.',
    retry: 'Thử lại',
    conflict_message: 'Người khác đã lưu thông tin định danh tổ chức sau khi bạn mở trang này. Thay đổi của bạn chưa được lưu. Hãy tải lại thông tin đã lưu, rồi nhập lại thay đổi của bạn.',
    conflict_reload: 'Tải lại thông tin đã lưu',
    conflict_reloaded: 'Đang hiển thị thông tin định danh đã lưu. Hãy nhập lại thay đổi của bạn, rồi lưu.',
    loading: 'Đang tải thông tin định danh tổ chức',
    read_only: 'Bạn có thể xem thông tin định danh tổ chức. Để lưu, bạn cần quyền ghi phần cài đặt.',
    restricted_title: 'Bạn không có quyền truy cập thông tin định danh tổ chức',
    restricted_message: 'Hãy đề nghị quản trị viên cấp quyền đọc phần cài đặt.',
    last_saved: 'Lưu lần cuối {date}',
    never_saved: 'Chưa có thông tin nào được ghi nhận. Trường để trống cũng sẽ để trống trên biểu mẫu.',
    error_too_long: 'Giá trị này quá dài.',
    error_invalid_date: 'Hãy nhập một ngày hợp lệ.',
    error_invalid_email: 'Hãy nhập địa chỉ thư điện tử, ví dụ ten@example.com.',
    error_invalid_url: 'Hãy nhập địa chỉ trang bắt đầu bằng https:// hoặc http://.',
    error_invalid_phone: 'Chỉ dùng chữ số, dấu cách và các ký tự + ( ) . -.',
    error_invalid: 'Giá trị này không được chấp nhận.',
};
export const viFormPrefill = {
    draft_undeclared: 'Biểu mẫu không chỉ rõ vị trí của {count} trường trong bản nháp, nên các trường đó không được soạn.',
    fill_from_record: 'Điền từ bản ghi',
    fill_from_named_record: 'Điền từ {record}',
    filling: 'Đang điền…',
    result: 'Đã điền {filled} trường; {differs} trường khác với bản ghi.',
    failed: 'Không thể điền biểu mẫu từ bản ghi.',
    from_source: 'Lấy từ {source}',
    not_recorded_in_source: 'Chưa ghi nhận trong {source}',
    family_organisation: 'thông tin định danh tổ chức',
    family_dpo: 'quyết định chỉ định nhân sự bảo vệ dữ liệu cá nhân',
    family_dpia: 'hồ sơ đánh giá tác động xử lý dữ liệu cá nhân',
    family_transfer: 'hồ sơ đánh giá tác động chuyển dữ liệu cá nhân ra nước ngoài',
    family_incident: 'sự cố',
    family_dossier: 'hồ sơ nộp',
    record_generic: 'bản ghi',
    hint: 'Chỉ điền các trường còn trống. Giá trị bạn đã nhập không bao giờ bị thay thế.',
};
/**
 * Idea #717 (owner, 2026-10-26) — tiêu đề, đề mục và tên cột của các tệp xuất
 * sổ quản lý thuế hải quan (R-57). Giá trị do gói khai báo in theo nhãn của gói.
 */
export const viCustomsTaxExports = {
    titles: {
        dossiers: 'Hồ sơ thuế và văn bản nhận được từ cơ quan hải quan',
        payments: 'Các lần nộp thuế hải quan',
        duty_free_lists: 'Danh mục miễn thuế',
        examinations: 'Các cuộc kiểm tra thuế',
        debt_instruments: 'Văn bản về tiền thuế nợ',
        transition_statement: 'Bảng kê chuyển tiếp',
    },
    sections: {
        dossiers: 'Hồ sơ thuế',
        documents_received: 'Văn bản đã nhận',
        figures_side_by_side: 'Đối chiếu số liệu ghi trên văn bản',
        deductions: 'Trừ lùi theo ghi nhận của cơ quan hải quan',
        usage_notifications: 'Thông báo tình hình sử dụng đã ghi nhận',
        steps: 'Các bước kiểm tra',
        office_examinations: 'Kiểm tra tại trụ sở cơ quan hải quan',
        determinations: 'Các xác định đã ghi nhận',
        refund_records: 'Bản ghi hoàn thuế',
        enforcement_notices: 'Văn bản cưỡng chế đã nhận',
        instalment_payments: 'Các lần nộp đã ghi nhận theo thông báo nộp dần (mỗi lần theo chứng từ; không cộng tổng)',
        noted_alternatives: 'Các cách đọc khác được ghi chú của lịch thông báo sử dụng',
    },
    columns: {
        paper_form_number: 'Số mẫu văn bản giấy',
        electronic_indicator_set: 'Bộ chỉ tiêu thông tin điện tử',
        matched_by_title: 'Đối chiếu theo tên',
        issued_by_authority: 'Do cơ quan hải quan ban hành',
        dossier: 'Hồ sơ thuế',
        procedure: 'Thủ tục',
        register_role: 'Vai trò trong sổ',
        customs_records: 'Hồ sơ hải quan',
        channel: 'Kênh',
        signed_off_on: 'Ngày ký duyệt',
        lodged_on: 'Ngày nộp hồ sơ',
        confirmation_answer: 'Trả lời xác nhận',
        install_mode_when_made: 'Chế độ cài đặt khi ghi nhận',
        review_approval: 'Phê duyệt rà soát',
        document: 'Văn bản',
        kind: 'Loại văn bản',
        number_as_printed: 'Số văn bản theo bản in',
        received_on: 'Ngày nhận',
        stated_figures: 'Số liệu ghi trên văn bản',
        stated_deadlines: 'Thời hạn ghi trên văn bản',
        reply: 'Trả lời',
        payment: 'Lần nộp thuế',
        paid_on: 'Ngày nộp tiền',
        stated_amount: 'Số tiền ghi trên văn bản',
        payer_class: 'Người nộp',
        payment_date_basis: 'Căn cứ xác định ngày nộp',
        document_number: 'Số chứng từ',
        pays: 'Nộp cho',
        receipt: 'Chứng từ',
        received_document: 'Văn bản đã nhận',
        figure: 'Số liệu',
        payment_figure_as_stated: 'Số liệu trên chứng từ nộp (như đã ghi)',
        document_figure_as_stated: 'Số liệu trên văn bản (như đã ghi)',
        comparison: 'Kết quả đối chiếu',
        closed: 'Đã đóng',
        list: 'Danh mục',
        reference: 'Số hiệu danh mục',
        acknowledged_on: 'Ngày tiếp nhận',
        imports_ended_on: 'Ngày kết thúc nhập khẩu',
        expires_on: 'Ngày danh mục hết hiệu lực',
        deduction: 'Trừ lùi',
        customs_record: 'Hồ sơ hải quan',
        goods_as_stated: 'Hàng hóa như đã ghi',
        unit_as_stated: 'Đơn vị tính như đã ghi',
        cleared_as_stated: 'Lượng đã thông quan (như đã ghi)',
        remaining_as_stated: 'Lượng còn lại (như đã ghi)',
        notification: 'Thông báo',
        notice_kind: 'Loại thông báo',
        financial_year_ended_on: 'Năm tài chính kết thúc ngày',
        notified_on: 'Ngày thông báo',
        examination: 'Cuộc kiểm tra',
        purpose: 'Mục đích',
        decision_signed_on: 'Ngày ký quyết định',
        decision_sent_on: 'Ngày gửi quyết định',
        announced_on: 'Ngày công bố',
        re_examination_of: 'Kiểm tra lại của cuộc kiểm tra',
        step: 'Bước',
        occurred_on: 'Ngày xảy ra',
        refused_to_sign: 'Từ chối ký',
        record: 'Bản ghi',
        authority_request: 'Yêu cầu của cơ quan có thẩm quyền',
        explanation_sent_on: 'Ngày gửi giải trình',
        outcome_on: 'Ngày nhận kết quả',
        instrument: 'Văn bản',
        role: 'Vai trò',
        effective_on: 'Ngày có hiệu lực',
        instalment_band: 'Mức nộp dần',
        approved_on: 'Ngày được chấp thuận',
        stated_debt_figure: 'Số tiền thuế nợ ghi trên văn bản',
        guarantee_valid_until: 'Bảo lãnh có hiệu lực đến',
        record_type: 'Loại bản ghi',
        governing_date: 'Ngày căn cứ',
        outcome: 'Kết quả',
        reading: 'Trạng thái kết quả đọc',
        open: 'Còn mở',
        determination: 'Xác định',
        determined_on: 'Ngày xác định',
        refund_record: 'Bản ghi hoàn thuế',
        dossier_register_role: 'Vai trò của hồ sơ trong sổ',
        classification: 'Kết quả phân loại',
        decision_document: 'Văn bản quyết định',
        offset_notice_document: 'Thông báo bù trừ đã nhận',
        post_refund_examination: 'Kiểm tra sau hoàn thuế',
        stated_case: 'Trường hợp theo quyết định',
        stated_case_source: 'Trường hợp được nêu tại',
        stated_case_citation: 'Quy định được viện dẫn cho trường hợp',
        received_as: 'Tư cách nhận',
        document_date: 'Ngày văn bản',
        amount_stated_for_notice: 'Số tiền chứng từ ghi cho thông báo này',
        financial_year_reported: 'Năm tài chính được báo cáo (kết thúc ngày)',
        noted_alternative_year: 'Năm tài chính theo cách đọc được ghi chú (kết thúc ngày)',
        note: 'Ghi chú',
        reading_kind: 'Loại cách đọc',
        classification_case: 'Trường hợp nêu tại thông báo phân loại',
        acceptance_notice: 'Thông báo tiếp nhận hồ sơ (thời hạn của cơ quan có thẩm quyền)',
        handling_time: 'Thời hạn giải quyết (thời hạn của cơ quan có thẩm quyền)',
        stated_case_choices: 'Trường hợp đã cài đặt được đối chiếu (theo văn bản)',
        stated_due_on: 'Thời hạn nộp theo văn bản',
        period_ceiling: 'Thời gian nộp dần tối đa',
        stated_plan_end: 'Thời hạn muộn nhất theo Luật',
        enforcement_from: 'Ngưỡng cưỡng chế (thời hạn của cơ quan có thẩm quyền)',
    },
    cells: {
        review_approved: '{name} phê duyệt ngày {date}',
        review_approved_earlier_version: '{name} phê duyệt ngày {date}, trên một phiên bản trước của bản ghi',
        register_role_refund: 'Hoàn thuế',
        register_role_overpaid_refund: 'Hoàn tiền nộp thừa',
        noted_not_due: 'Cách đọc được ghi chú, không phải thời hạn',
        received_as_third_party: 'Nhận với tư cách bên thứ ba',
        received_as_enforced_taxpayer: 'Người nộp thuế bị cưỡng chế',
        clock_authority_ends: 'Thời hạn của cơ quan có thẩm quyền kết thúc {date} ({clock})',
        clock_authority_ends_before_excluded: 'Thời hạn của cơ quan có thẩm quyền kết thúc {date}, chưa cộng thời gian không tính vào thời hạn ({clock})',
        clock_authority_no_date: 'Thời hạn của cơ quan có thẩm quyền; chưa có ngày vì sự kiện làm phát sinh thời hạn chưa được ghi nhận ({clock})',
        clock_period_ends: 'Thời hạn kết thúc {date} ({clock})',
        clock_closed: 'Đã kết thúc ngày {date}',
        clock_not_applicable: 'Không áp dụng cho bản ghi này ({clock})',
        clock_not_live: 'Chưa có hiệu lực ({clock})',
        clock_classification_not_recorded: 'Chưa có ngày: chưa ghi nhận kết quả phân loại mà cơ quan có thẩm quyền đã thông báo',
        clock_not_declared: 'Không có thời hạn đã cài đặt nào khai báo nội dung này',
        clock_instrument_not_installed: 'Được quy định tại văn bản chưa được cài đặt ({instruments}); không có ngày',
        clock_no_date: 'Chưa có ngày ({clock})',
        plan_end_by_ceiling: '{date}, ngày kết thúc thời gian tối đa; lịch nộp ngắn hơn trong văn bản chấp thuận là lịch của chính kế hoạch',
        plan_end_by_guarantee: '{date}, ngày bảo lãnh hết hiệu lực, đến trước; lịch nộp ngắn hơn trong văn bản chấp thuận là lịch của chính kế hoạch',
        clock_not_counted_before_commencement: 'Nền tảng không tính: tiền thuế nợ còn nợ đến hết ngày {outstanding} được xử lý theo Luật ({reference}), nhưng không hiển thị mốc nào đối với thời hạn nộp trước {activation}',
        clock_not_counted_before_commencement_unnamed: 'Nền tảng không tính: không hiển thị mốc nào đối với thời hạn nộp trước {activation}',
        meta_boundary: 'Giới hạn của nền tảng ({framework}): {summary}',
        meta_boundary_none: 'Giới hạn của nền tảng: không có khung tuân thủ đã cài đặt nào khai báo nội dung về giới hạn cho các sổ này.',
        meta_amounts_as_stated: 'Mọi số tiền là số liệu một người nhập từ văn bản; không có số liệu nào ở đây được tính toán.',
        meta_statement_for: 'Bảng kê cho ngày: {date}',
        meta_nothing_remains: 'Không còn hồ sơ nào đang mở theo quy định trước đó.',
        meta_records_remain: 'Còn hồ sơ đang mở theo quy định trước đó.',
        meta_transition_unavailable: 'Cách xác định quy định chuyển tiếp không có trên bản cài đặt này.',
        role_notified_debt: 'Tiền thuế nợ theo thông báo',
        role_freeze: 'Khoanh nợ',
        role_freeze_end: 'Chấm dứt khoanh nợ',
        role_write_off: 'Xóa nợ',
        role_restoration: 'Khôi phục nợ',
        role_instalment_request: 'Đề nghị nộp dần',
        role_instalment_notice: 'Thông báo nộp dần',
        received_document_label: '{kind} số {number}, ngày {date}',
        received_document_label_no_number: '{kind}, nhận ngày {date}',
        case_no_longer_installed: 'Trường hợp theo văn bản không còn được cài đặt',
    },
};
/** OQ-86-6 (owner, 2026-10-26) — Hồ sơ chuyển dữ liệu xuyên biên giới trỏ sang sổ hồ sơ thuế. */
export const viCrossBorderFilingFence = {
    register_customs_tax_dossiers: 'Hồ sơ thuế và văn bản nhận được từ cơ quan hải quan',
    refused: 'Mẫu này được ghi nhận tại {register}, không phải dưới dạng hồ sơ chuyển dữ liệu xuyên biên giới. Hãy ghi nhận hồ sơ tại đó.',
    open_register: 'Mở {register}',
    option_owned_elsewhere: '{form} — ghi nhận tại {register}',
    picker_hint: 'Một số mẫu đã cài đặt được ghi nhận tại {register} và không thể chọn tại đây.',
    advisory_title: 'Mẫu này hiện được ghi nhận tại {register}',
    advisory_body: 'Hồ sơ này được ghi nhận tại đây trước khi các mẫu như vậy chuyển sang {register}. Hồ sơ vẫn xem được tại đây và không bị thay đổi. Hãy ghi nhận hồ sơ mới trên mẫu này tại {register}.',
    list_marker: 'Hiện ghi nhận tại {register}',
};
/** Sprint 63.43.0 — Phân hệ Lớp phủ ngành: trang Tổng quan (R-44 batch 2026-10-31-63.43.0-module-30-navigation). */
export const viSectorOverlaysModule = {
    nav: {
        overview: 'Tổng quan',
    },
    overview: {
        title: 'Lớp phủ ngành',
        subtitle: 'Những gì các lớp phủ ngành đang hoạt động đưa vào sử dụng, sắp xếp theo ngành.',
        loading: 'Đang tải danh sách lớp phủ ngành…',
        load_failed: 'Không tải được danh sách lớp phủ ngành.',
        not_entitled_title: 'Lớp phủ ngành không có trong gói dịch vụ của bạn',
        not_entitled_body: 'Lớp phủ ngành là phân hệ thuộc gói Enterprise. Gói dịch vụ của tổ chức bạn không bao gồm phân hệ này, nên tại đây không hiển thị lớp phủ ngành nào.',
        empty_title: 'Chưa có lớp phủ ngành nào đang hoạt động',
        empty_body: 'Cài đặt một lớp phủ trong Danh mục lớp phủ và chọn Trực tiếp để đưa các màn hình của lớp phủ đó vào sử dụng tại đây. Lớp phủ được cài đặt ở chế độ Chỉ tham khảo không thêm màn hình nào.',
        open_catalog: 'Mở Danh mục lớp phủ',
        organization_profile: 'Hồ sơ tổ chức',
        organization_profile_hint: 'Lĩnh vực hoạt động của tổ chức bạn quyết định những lớp phủ nào áp dụng trực tiếp cho tổ chức.',
        provided_by: 'Lớp phủ cung cấp',
        views_heading: 'Màn hình thuộc ngành này',
        shared_views_heading: 'Màn hình dùng chung mà ngành này sử dụng',
        shared_view_location: 'Tại {location}',
        open_view_aria: '{view} — {location}',
        no_visible_views: 'Vai trò của bạn không được truy cập màn hình nào của ngành này.',
        sector_count_one: '1 lớp phủ đang hoạt động',
        sector_count: '{count} lớp phủ đang hoạt động',
    },
};
/**
 * Sprint 62.47.0 Slice A — the product-identification registers (Sector
 * Overlays › Manufacturing): Product Scope Determinations and Sector Watch.
 * Vietnamese page chrome (R-44 terminology lint before and after): every option, label, note, status basis and citation is
 * read from the installed pack through the API (R-46). The two page titles are
 * the work file's B0 leaf names.
 */
export const viProductIdentification = {
    common: {
        retry: 'Thử lại',
        loading: 'Đang tải…',
        cancel: 'Hủy',
        close: 'Đóng',
        no_access_title: 'Không có quyền truy cập',
        no_access_message: 'Vai trò của bạn không được xem sổ đăng ký này.',
        install_direct: 'Cài đặt ở chế độ Trực tiếp',
        install_reference: 'Cài đặt ở chế độ Chỉ tham khảo',
        reference_mode_body: 'Lớp phủ khai báo sổ đăng ký này được cài đặt ở chế độ Chỉ tham khảo. Bạn vẫn có thể ghi nhận, xem các kết quả xác định phạm vi và xem phần theo dõi văn bản tại đây; không nghĩa vụ hay thời hạn nào được áp dụng từ đó cho đến khi lớp phủ được cài đặt ở chế độ Trực tiếp từ Danh mục lớp phủ.',
    },
    participation: {
        compulsory: 'Bắt buộc',
        voluntary: 'Tự nguyện',
        outside_scope: 'Không thuộc phạm vi',
        compulsory_rules_disapplied: 'Không áp dụng quy định bắt buộc',
        not_determined: 'Đang để ngỏ việc xác định',
        unclassified: 'Chưa có phân loại được khai báo',
    },
    scope: {
        title: 'Xác định phạm vi áp dụng theo dòng sản phẩm',
        subtitle: 'Đánh giá được ghi nhận của người có trách nhiệm, theo từng dòng sản phẩm, về việc chế độ định danh và truy xuất nguồn gốc có áp dụng hay không, cùng văn bản làm căn cứ và việc rà soát của một người khác. Sổ đăng ký này ghi nhận đánh giá; không bao giờ tự quyết định.',
        page_guide: 'Ghi nhận một kết quả xác định cho mỗi dòng sản phẩm từ các lựa chọn mà gói pháp lý đã cài đặt khai báo. Không có lựa chọn nào được chọn sẵn hay gợi ý. Khi ghi nhận lại, phiên bản trước được giữ nguyên, và các dòng sản phẩm bắt buộc và tự nguyện luôn được đếm riêng.',
        load_failed: 'Không tải được các kết quả xác định phạm vi.',
        none_declared: 'Chưa có khung tuân thủ nào đã cài đặt khai báo sổ đăng ký xác định phạm vi.',
        applicability_heading: 'Đối tượng áp dụng của chế độ, theo gói pháp lý đã cài đặt',
        mandatory_scope: 'Bắt buộc',
        voluntary_scope: 'Tự nguyện',
        out_of_scope: 'Không thuộc phạm vi',
        mandatory_rules_disapplied: 'Không áp dụng quy định bắt buộc',
        list_status_note: 'Tình trạng danh mục',
        counts_heading: 'Dòng sản phẩm theo phân loại',
        filter_label: 'Phân loại',
        filter_all: 'Tất cả phân loại',
        table_caption: 'Kết quả xác định hiện hành theo dòng sản phẩm',
        col_product: 'Dòng sản phẩm',
        col_class: 'Phân loại',
        col_option: 'Kết quả xác định',
        col_instrument: 'Văn bản làm căn cứ',
        col_determined_by: 'Người xác định',
        col_reviewed_by: 'Người rà soát',
        col_determined_on: 'Ngày xác định',
        col_version: 'Phiên bản',
        col_actions: 'Thao tác',
        empty: 'Chưa có kết quả xác định nào được ghi nhận.',
        empty_filtered: 'Không có dòng sản phẩm nào thuộc phân loại này.',
        record_button: 'Ghi nhận kết quả xác định',
        record_again: 'Ghi nhận lại',
        history_button: 'Lịch sử',
        duties_button: 'Nghĩa vụ',
        form_title: 'Ghi nhận kết quả xác định phạm vi',
        field_product: 'Dòng sản phẩm',
        field_framework: 'Khung tuân thủ',
        choose: 'Chọn…',
        no_default_note: 'Không có lựa chọn nào được chọn sẵn hay gợi ý: đánh giá là của bạn.',
        instrument_required: 'Bắt buộc đối với kết quả xác định đã chọn.',
        cited_basis_note_heading: 'Cách đọc danh mục của gói pháp lý đã cài đặt',
        reviewer_same: 'Người rà soát phải là người khác với người xác định.',
        field_required: 'Trường này là bắt buộc.',
        save: 'Lưu',
        saving: 'Đang lưu…',
        save_failed: 'Không lưu được kết quả xác định.',
        saved: 'Đã ghi nhận kết quả xác định.',
        products_failed: 'Không tải được danh sách dòng sản phẩm.',
        no_products: 'Chưa có dòng sản phẩm nào trong sổ đăng ký sản phẩm.',
        conflict: 'Đã có người ghi nhận kết quả xác định cho dòng sản phẩm này trong lúc đó. Hãy tải lại sổ đăng ký và thử lại.',
        history_title: 'Lịch sử xác định — {product}',
        history_failed: 'Không tải được lịch sử.',
        status_current: 'Hiện hành',
        status_superseded: 'Đã được thay thế',
        duties_title: 'Nghĩa vụ đối với {product}',
        duties_failed: 'Không tải được danh sách nghĩa vụ.',
        duties_no_task: 'Không có nhiệm vụ hay thời hạn nào được tạo tại đây từ các nghĩa vụ này. Trường hợp Nghị định quy định thời hạn, nội dung của yêu cầu nêu thời hạn đó.',
        duties_reference: 'Cài đặt ở chế độ Chỉ tham khảo: không hiển thị nghĩa vụ nào. Hãy cài đặt lớp phủ ở chế độ Trực tiếp để áp dụng.',
        duties_compulsory_heading: 'Bắt buộc',
        duties_voluntary_heading: 'Tự nguyện',
        duties_role_heading: 'Gắn với vai trò',
        duties_open: 'Việc xác định đang để ngỏ: phạm vi của dòng sản phẩm này vẫn còn phải xác định. Đây không phải là kết luận rằng không có nghĩa vụ nào áp dụng.',
        duties_counts: 'Bắt buộc: {applies} · Áp dụng cho cách bạn tham gia (tự nguyện): {voluntary}',
        duties_none: 'Chưa có yêu cầu nào được khai báo.',
        col_requirement: 'Yêu cầu',
        col_state: 'Tình trạng đối với dòng sản phẩm',
        col_reference: 'Căn cứ',
    },
    duty_state: {
        applies: 'Áp dụng (bắt buộc)',
        applies_voluntarily: 'Áp dụng cho cách bạn tham gia (tự nguyện)',
        not_raised_by_option: 'Không phát sinh từ kết quả xác định này',
        open_determination: 'Đang để ngỏ việc xác định',
        role_attached: 'Gắn với vai trò; xem điều kiện của yêu cầu',
        voluntary_practice: 'Thực hành tự nguyện',
    },
    watch: {
        title: 'Theo dõi văn bản của bộ, ngành',
        subtitle: 'Những văn bản mà gói pháp lý đã cài đặt theo dõi là cơ quan có thẩm quyền chưa ban hành, cùng tình trạng được ghi nhận và thời điểm phải kiểm tra lại, và các thời hạn mà gói pháp lý đặt cho cơ quan có thẩm quyền. Tổ chức của bạn không có nghĩa vụ nào tại đây.',
        page_guide: 'Xem từng văn bản được theo dõi theo đúng nội dung gói pháp lý đã cài đặt. Hạn kiểm tra lại đã qua được hiển thị là quá hạn kiểm tra lại; không ngày nào bị thay đổi. Các thời hạn của cơ quan có thẩm quyền là thời hạn của cơ quan đó, không phải của tổ chức bạn.',
        load_failed: 'Không tải được phần theo dõi văn bản.',
        items_heading: 'Văn bản chưa được ban hành',
        items_caption: 'Các văn bản được theo dõi, theo gói pháp lý đã cài đặt',
        col_output: 'Văn bản',
        col_status: 'Tình trạng',
        col_basis: 'Căn cứ của tình trạng',
        col_checked: 'Ngày kiểm tra',
        col_recheck: 'Hạn kiểm tra lại',
        col_install: 'Chế độ cài đặt',
        status_not_issued: 'Chưa ban hành',
        status_not_retrieved: 'Đã được nêu tên, chưa lấy được văn bản',
        status_announced_operational: 'Đã công bố đủ điều kiện vận hành',
        status_issued: 'Đã ban hành',
        recheck_overdue: 'Quá hạn kiểm tra lại',
        existing_basis: 'Văn bản được ghi nhận là căn cứ hiện có',
        note_label: 'Ghi chú',
        items_none: 'Chưa có khung tuân thủ nào đã cài đặt theo dõi văn bản cho sổ đăng ký này.',
        clocks_heading: 'Thời hạn của cơ quan có thẩm quyền và thời hạn không được quy định',
        clocks_caption: 'Các bản ghi mà gói pháp lý đã cài đặt đặt cho cơ quan có thẩm quyền, hoặc không quy định thời hạn',
        col_clock: 'Bản ghi',
        col_kind: 'Ý nghĩa đối với tổ chức của bạn',
        col_period: 'Thời hạn theo văn bản',
        col_binds: 'Áp dụng từ',
        authority_clock: 'Thời hạn của bộ — tổ chức không có nghĩa vụ nào',
        set_by_instrument: 'Do văn bản của bộ hoặc pháp luật chuyên ngành quy định',
        no_period: 'Không quy định thời hạn',
        period_months: '{n} tháng',
        period_days: '{n} ngày',
        period_other: '{n} ({unit})',
        not_stated: 'Không nêu',
        clocks_none: 'Chưa có thời hạn nào của cơ quan có thẩm quyền được khai báo.',
    },
};
/**
 * Sprint 62.57.0 Slice E (stream F) — Sổ theo dõi Giấy chứng nhận đủ điều kiện
 * kinh doanh dịch vụ xử lý dữ liệu cá nhân và bản ghi về nhà cung cấp (§12.6.2,
 * §12.13.9). R-44 batch `2026-11-03-62.57.0-e` (đang xem xét).
 */
export const viServiceCertificates = {
    nav: 'Giấy chứng nhận dịch vụ xử lý dữ liệu cá nhân',
    common: {
        loading: 'Đang tải…',
        load_failed: 'Không đọc được nội dung này.',
        retry: 'Thử lại',
        reloading: 'Đang đọc lại…',
        recording: 'Đang ghi nhận…',
        none: '—',
        not_recorded: 'Chưa ghi nhận',
        unknown_user: 'Người dùng không xác định',
        yes: 'Có',
        no: 'Không',
        close: 'Đóng',
        record_changed: 'Bản ghi này đã được người khác thay đổi sau khi bạn mở. Bản ghi đã được đọc lại và nội dung bạn nhập vẫn được giữ: hãy kiểm tra lại và lưu lần nữa.',
        save_failed: 'Chưa ghi nhận được nội dung này.',
        scope_not_stated: 'Thông tin tổ chức của bạn ghi nhận tại Hồ sơ tổ chức không nêu dịch vụ nào thuộc phạm vi của Giấy chứng nhận này, nên không thể tạo bản ghi mới.',
        recorded_by: 'Do {person} ghi nhận ngày {date}',
        not_verified: 'Do {person} ghi nhận — ComplianceOne không xác minh thông tin này',
        corrects: 'Đính chính mục ngày {date}',
        corrected_by_later: 'Đã được đính chính bởi một mục sau',
        correction_note: 'Ghi chú đính chính: {note}',
        field_correction_note: 'Lý do đính chính',
        field_corrects: 'Mục được đính chính',
        corrects_none: 'Không phải đính chính — một mục mới',
        error_required: 'Vui lòng nhập nội dung này.',
        error_future: 'Ngày này muộn hơn ngày hôm nay.',
        error_date_order: 'Ngày này sớm hơn ngày mà nó phải theo sau.',
        error_field_refused: 'Hãy kiểm tra lại trường này.',
        error_correction_note_required: 'Hãy nêu lý do đính chính mục này.',
        owner: 'Người chịu trách nhiệm',
        owner_placeholder: 'Chọn một người',
        owner_retry_subject: 'danh sách người dùng',
        files: 'Tệp: {count}',
        files_none: 'Chưa có tệp',
        files_open: 'Tệp',
        files_heading: 'Tệp',
        files_intro: 'Các tệp tổ chức của bạn lưu cho mục này. ComplianceOne lưu giữ tệp như khi được tải lên và không đọc nội dung tệp.',
        file_hash: 'SHA-256',
        file_not_held: 'Tệp này không còn được lưu giữ.',
        file_download: 'Tải xuống',
        file_remove: 'Gỡ bỏ',
        file_upload: 'Tải lên tệp',
        file_bound: 'Đã thêm tệp.',
        file_removed: 'Đã gỡ bỏ tệp.',
        file_bind_failed: 'Không thêm được tệp.',
        file_remove_failed: 'Không gỡ bỏ được tệp.',
        file_not_owned: 'Tệp này không thuộc bản ghi này.',
        file_remove_confirm: 'Gỡ bỏ tệp này khỏi bản ghi?',
        export_done: 'Tệp xuất đã sẵn sàng.',
        export_failed: 'Không tạo được tệp xuất.',
        history: 'Lịch sử',
        history_none: 'Chưa ghi nhận thay đổi nào.',
        history_col_event: 'Nội dung thay đổi',
        history_col_changes: 'Trường thông tin',
        history_col_recorded: 'Ngày ghi nhận',
        history_pagination: 'Trang lịch sử',
        history_retry_subject: 'lịch sử',
        boundary_heading: 'Phạm vi của sổ theo dõi này',
        pack_text_from: 'Theo khung tuân thủ đã cài đặt',
    },
    register: {
        title: 'Giấy chứng nhận dịch vụ xử lý dữ liệu cá nhân',
        subtitle: 'Bản ghi của tổ chức bạn về Giấy chứng nhận đủ điều kiện đang có hoặc đang đề nghị cấp, và về các văn bản của cơ quan có thẩm quyền liên quan.',
        module_guide: 'Ghi nhận Giấy chứng nhận tổ chức của bạn đang có, tạo hồ sơ đề nghị cấp và lưu các văn bản cơ quan có thẩm quyền gửi đến. Sổ theo dõi chỉ hiển thị khi tổ chức của bạn đã ghi nhận tại Hồ sơ tổ chức rằng tổ chức cung cấp một dịch vụ xử lý dữ liệu cá nhân trong danh mục. ComplianceOne không quyết định thay cơ quan có thẩm quyền.',
        scope_heading: 'Phạm vi áp dụng',
        scope_intro: 'Đối tượng của Giấy chứng nhận, theo khung tuân thủ đã cài đặt.',
        out_of_scope_heading: 'Không thuộc phạm vi áp dụng, theo cách hiểu của khung tuân thủ đã cài đặt',
        statement_heading: 'Thông tin tổ chức của bạn đã ghi nhận',
        statement_services: 'Dịch vụ đã ghi nhận: {list}',
        statement_none: 'Tổ chức của bạn đã ghi nhận rằng tổ chức không cung cấp dịch vụ nào trong danh mục.',
        statement_unknown: 'Tổ chức của bạn đã ghi nhận là chưa rõ.',
        statement_not_stated: 'Chưa có thông tin nào được ghi nhận tại Hồ sơ tổ chức.',
        statement_outside: 'Các dịch vụ đã ghi nhận không thuộc phạm vi của Giấy chứng nhận này.',
        statement_by: 'Do {person} ghi nhận ngày {date} · Tài liệu chứng minh: {count}',
        position_line: 'Dịch vụ đã ghi nhận: {list} · Giấy chứng nhận đã ghi nhận: {certificates} · Hồ sơ đang xử lý: {applications}',
        profile_link: 'Thay đổi tại Hồ sơ tổ chức',
        not_offered: 'Sổ theo dõi này hiển thị khi tổ chức của bạn đã ghi nhận tại Hồ sơ tổ chức rằng tổ chức cung cấp một trong các dịch vụ xử lý dữ liệu cá nhân trong danh mục. ComplianceOne không suy luận điều này từ bất kỳ thông tin nào khác.',
        cannot_create: 'Chỉ ghi nhận được Giấy chứng nhận và hồ sơ đề nghị mới khi thông tin tổ chức của bạn đã ghi nhận nêu một dịch vụ trong danh mục. Các bản ghi hiện có vẫn đọc được và vẫn ghi nhận tiếp được.',
        availability_failed: 'Không đọc được thông tin sổ theo dõi này có được cung cấp hay không.',
        availability_retry_subject: 'trạng thái cung cấp của sổ theo dõi',
        declarations_failed: 'Không đọc được nội dung khai báo của khung tuân thủ đã cài đặt.',
        declarations_retry_subject: 'nội dung khai báo đã cài đặt',
        certificates_heading: 'Giấy chứng nhận',
        applications_heading: 'Hồ sơ đề nghị',
        record_certificate: 'Ghi nhận Giấy chứng nhận đang có',
        open_application: 'Tạo hồ sơ đề nghị',
        export: 'Xuất sổ theo dõi',
        certificates_none: 'Chưa ghi nhận Giấy chứng nhận nào.',
        applications_none: 'Chưa ghi nhận hồ sơ đề nghị nào.',
        certificates_failed: 'Không đọc được danh sách Giấy chứng nhận.',
        certificates_retry_subject: 'danh sách Giấy chứng nhận',
        applications_failed: 'Không đọc được danh sách hồ sơ đề nghị.',
        applications_retry_subject: 'danh sách hồ sơ đề nghị',
        certificates_pagination: 'Trang danh sách Giấy chứng nhận',
        applications_pagination: 'Trang danh sách hồ sơ đề nghị',
        col_number: 'Số Giấy chứng nhận',
        col_valid_from: 'Có hiệu lực từ (theo Giấy chứng nhận)',
        col_authority: 'Cơ quan cấp theo Giấy chứng nhận',
        col_services: 'Dịch vụ',
        col_status: 'Trạng thái',
        col_owner: 'Người chịu trách nhiệm',
        col_application: 'Hồ sơ đề nghị',
        col_reference: 'Số tham chiếu',
        col_governing_text: 'Văn bản áp dụng',
        col_latest_communication: 'Văn bản gần nhất',
        col_next_period: 'Thời hạn tiếp theo',
        end_date_stated: 'Giấy chứng nhận có ghi thời hạn kết thúc',
        return_outstanding: 'Chưa ghi nhận việc nộp lại Giấy chứng nhận',
        governing_not_determined: 'Chưa xác định — chưa ghi nhận ngày cơ quan tiếp nhận hồ sơ',
        open_certificate: 'Mở Giấy chứng nhận số {number}',
        open_application_row: 'Mở hồ sơ đề nghị: {kind}, {date}',
        certificate_recorded: 'Đã ghi nhận Giấy chứng nhận.',
        application_opened: 'Đã tạo hồ sơ đề nghị.',
    },
    certificate_status: {
        current: 'Hiện hành',
        replaced: 'Đã được thay thế',
        revoked: 'Đã bị thu hồi',
    },
    application_status: {
        preparing: 'Đang chuẩn bị',
        filed: 'Đã nộp',
        decided: 'Đã có quyết định',
        withdrawn: 'Đã rút',
    },
    application_kind: {
        issue: 'Cấp Giấy chứng nhận',
        replacement: 'Cấp lại, cấp đổi',
    },
    document_kind: {
        certificate_paper: 'Giấy chứng nhận, bản giấy',
        certificate_electronic: 'Giấy chứng nhận, bản điện tử',
        issue_decision: 'Quyết định cấp',
        revocation_decision: 'Quyết định thu hồi',
        dossier_as_filed: 'Hồ sơ đã nộp',
        other: 'Tài liệu khác',
    },
    part_state: {
        provided: 'Đã có',
        not_provided: 'Chưa có',
        not_required_exception: 'Không phải nộp — {exception}',
    },
    decision_outcome: {
        issued: 'Cấp Giấy chứng nhận',
        refused: 'Từ chối cấp',
    },
    comm_kind_decision: 'Quyết định',
    validity: {
        valid_from: 'Có hiệu lực từ (theo Giấy chứng nhận)',
        no_end_date: 'Không ghi thời hạn kết thúc',
        states_end_date: 'Giấy chứng nhận có ghi thời hạn kết thúc',
        end_date: 'Ngày kết thúc theo Giấy chứng nhận',
        end_date_note: 'ComplianceOne không tính toán gì từ ngày này.',
    },
    authority: {
        as_named: 'Cơ quan cấp theo Giấy chứng nhận',
        declared: 'Cơ quan cấp (theo khung tuân thủ đã cài đặt)',
        declared_none: 'Khung tuân thủ đã cài đặt không nêu',
    },
    certificate: {
        back: 'Quay lại sổ theo dõi',
        heading: 'Giấy chứng nhận số {number}',
        not_found: 'Giấy chứng nhận này không có trong sổ theo dõi của tổ chức bạn.',
        load_failed: 'Không đọc được Giấy chứng nhận.',
        retry_subject: 'Giấy chứng nhận',
        facts_heading: 'Giấy chứng nhận',
        number: 'Số Giấy chứng nhận',
        services: 'Dịch vụ được chứng nhận',
        forms_held: 'Hình thức lưu giữ',
        paper: 'Bản giấy',
        electronic: 'Bản điện tử',
        notes: 'Ghi chú',
        status: 'Trạng thái',
        supersedes: 'Thay thế Giấy chứng nhận trước đó',
        superseded_by: 'Được thay thế bởi Giấy chứng nhận sau',
        issued_by_application: 'Được ghi nhận từ quyết định đối với hồ sơ đề nghị',
        open_linked: 'Mở',
        correct: 'Đính chính Giấy chứng nhận',
        corrected: 'Đã ghi nhận nội dung đính chính.',
        export: 'Xuất',
        documents_heading: 'Tài liệu',
        add_document: 'Thêm tài liệu',
        documents_none: 'Chưa lưu tài liệu nào.',
        document_col_kind: 'Loại tài liệu',
        document_col_file: 'Tệp',
        document_col_added: 'Ngày thêm',
        document_added: 'Đã thêm tài liệu.',
        revocation_heading: 'Thu hồi',
        record_revocation: 'Ghi nhận việc thu hồi',
        revocations_none: 'Chưa ghi nhận việc thu hồi nào.',
        decision_number: 'Số quyết định',
        decision_date: 'Ngày quyết định',
        ground: 'Căn cứ',
        ground_not_declared: 'Căn cứ mà khung tuân thủ đã cài đặt không còn khai báo',
        effective_on: 'Ngày có hiệu lực',
        decision_received_on: 'Ngày nhận quyết định',
        voluntary_request: 'Tổ chức của bạn đề nghị ngày {date}',
        revocation_recorded: 'Đã ghi nhận việc thu hồi.',
        return_heading: 'Nộp lại Giấy chứng nhận',
        record_return: 'Ghi nhận việc nộp lại',
        returns_none: 'Chưa ghi nhận việc nộp lại.',
        returned_on: 'Ngày nộp lại',
        return_recorded: 'Đã ghi nhận việc nộp lại.',
        return_files_heading: 'Nộp lại Giấy chứng nhận — tệp',
        publication_heading: 'Đã thấy công bố',
        record_publication: 'Ghi nhận công bố đã thấy',
        publications_none: 'Chưa ghi nhận công bố nào.',
        seen_on: 'Ngày thấy',
        reference_text: 'Nơi thấy công bố',
        publication_recorded: 'Đã ghi nhận công bố.',
        period_heading: 'Thời hạn nộp lại Giấy chứng nhận',
        communications_heading: 'Văn bản của cơ quan có thẩm quyền',
    },
    application: {
        back: 'Quay lại sổ theo dõi',
        heading: '{kind}: {label}',
        not_found: 'Hồ sơ đề nghị này không có trong sổ theo dõi của tổ chức bạn.',
        load_failed: 'Không đọc được hồ sơ đề nghị.',
        retry_subject: 'hồ sơ đề nghị',
        facts_heading: 'Hồ sơ đề nghị',
        kind: 'Hồ sơ đề nghị',
        status: 'Trạng thái',
        reference: 'Số tham chiếu hồ sơ',
        services_applied_for: 'Dịch vụ đề nghị cấp',
        replaces: 'Giấy chứng nhận được thay thế',
        grounds: 'Căn cứ',
        ground_review: 'Không phải căn cứ theo văn bản áp dụng — hãy xem lại',
        ground_windows: 'Được nêu trong: {windows}',
        notes: 'Ghi chú',
        edit: 'Chỉnh sửa hồ sơ đề nghị',
        updated: 'Đã cập nhật hồ sơ đề nghị.',
        export: 'Xuất',
        withdraw: 'Rút hồ sơ đề nghị',
        withdrawn_on: 'Đã rút ngày {date}',
        withdrawn: 'Đã rút hồ sơ đề nghị.',
        parts_heading: 'Thành phần hồ sơ',
        parts_unstamped: 'Thành phần hồ sơ phải nộp tùy thuộc ngày cơ quan tiếp nhận hồ sơ',
        parts_stamped: 'Các thành phần mà văn bản áp dụng nêu cho thủ tục này.',
        parts_window: '{window} — {procedure}',
        parts_col_part: 'Thành phần',
        parts_col_state: 'Tình trạng',
        parts_col_owner: 'Người phụ trách và ngày nội bộ của tổ chức',
        parts_col_files: 'Tệp',
        parts_col_action: 'Thao tác',
        part_not_recorded: 'Chưa ghi nhận',
        record_part: 'Ghi nhận',
        record_part_label: 'Ghi nhận thành phần: {part}',
        part_form: 'Biểu mẫu: {form}',
        part_recorded: 'Đã ghi nhận thành phần hồ sơ.',
        own_date: 'Ngày nội bộ của tổ chức bạn: {date}',
        conditions_heading: 'Kết quả đánh giá tác động',
        result_as_recorded: 'Kết quả đã ghi nhận: {label}',
        result_none: 'Hồ sơ nộp này chưa ghi nhận kết quả',
        link_filings: 'Liên kết hồ sơ nộp',
        link_filings_label: 'Liên kết hồ sơ nộp: {condition}',
        conditions_none: 'Chưa liên kết hồ sơ nộp nào.',
        conditions_recorded: 'Đã ghi nhận liên kết.',
        open_filing: 'Mở hồ sơ nộp',
        filing_heading: 'Nộp hồ sơ',
        record_filing: 'Ghi nhận việc nộp hồ sơ',
        filing_none: 'Hồ sơ đề nghị chưa được nộp.',
        filed_on: 'Ngày nộp',
        channel: 'Hình thức nộp',
        channel_not_declared: 'Hình thức nộp mà khung tuân thủ đã cài đặt không còn khai báo',
        reviewed_by: 'Người rà soát',
        signed_by: 'Người ký',
        signed_on: 'Ngày ký',
        filing_recorded: 'Đã ghi nhận việc nộp hồ sơ.',
        receipt_recorded: 'Đã ghi nhận ngày cơ quan tiếp nhận hồ sơ.',
        receipt_no_deficiency: 'Không có thông báo bổ sung, hoàn thiện hồ sơ — hồ sơ đã đầy đủ khi được tiếp nhận',
        periods_heading: 'Thời hạn',
        periods_none: 'Chưa có thời hạn nào áp dụng.',
        deficiency_heading: 'Thông báo bổ sung, hoàn thiện hồ sơ và hồ sơ bổ sung',
        record_request: 'Ghi nhận thông báo bổ sung, hoàn thiện hồ sơ',
        requests_none: 'Chưa ghi nhận thông báo bổ sung, hoàn thiện hồ sơ nào.',
        request_from: 'Ngày nhận thông báo: {date}',
        request_items: 'Nội dung yêu cầu',
        request_period: 'Thời hạn bổ sung, hoàn thiện hồ sơ theo văn bản thông báo: {text}',
        no_period: 'Văn bản không nêu thời hạn',
        stated_deadline: 'Ngày được nhập theo văn bản thông báo: {date}',
        item_owner: 'Người phụ trách: {person}',
        request_recorded: 'Đã ghi nhận thông báo bổ sung, hoàn thiện hồ sơ.',
        supplements_heading: 'Hồ sơ bổ sung đã gửi',
        record_sent: 'Ghi nhận đã gửi hồ sơ bổ sung',
        sent_none: 'Chưa ghi nhận hồ sơ bổ sung nào đã gửi.',
        sent_on: 'Ngày gửi',
        sent_for: 'Theo thông báo nhận ngày {date}',
        sent_recorded: 'Đã ghi nhận hồ sơ bổ sung.',
        supplement_files_heading: 'Hồ sơ bổ sung đã gửi — tệp',
        evaluation_heading: 'Đánh giá hồ sơ',
        record_evaluation: 'Ghi nhận kết quả đánh giá hồ sơ',
        evaluation_none: 'Chưa ghi nhận kết quả đánh giá hồ sơ.',
        evaluation_not_declared: 'Văn bản áp dụng không nêu bước đánh giá hồ sơ cho thủ tục này.',
        evaluation_recorded_no_decision: 'Đã ghi nhận đánh giá: {label} — chưa ghi nhận quyết định',
        evaluation_recorded: 'Đã ghi nhận kết quả đánh giá hồ sơ.',
        different_text: 'Được ghi nhận theo một văn bản áp dụng khác — hãy xem lại',
        notice_date: 'Ngày của văn bản',
        notice_received_on: 'Ngày nhận văn bản',
        option_not_declared: 'Lựa chọn mà khung tuân thủ đã cài đặt không còn khai báo',
        decision_heading: 'Quyết định',
        record_decision: 'Ghi nhận quyết định',
        decision_none: 'Chưa ghi nhận quyết định.',
        outcome: 'Quyết định',
        reasons: 'Lý do theo văn bản',
        issued_certificate: 'Giấy chứng nhận được ghi nhận từ quyết định này',
        decision_recorded: 'Đã ghi nhận quyết định.',
        communication: 'Văn bản',
        communications_heading: 'Văn bản của cơ quan có thẩm quyền',
    },
    periods: {
        col_period: 'Thời hạn',
        col_whose: 'Thời hạn của bên nào',
        col_date: 'Ngày',
        col_basis: 'Tính từ',
        authority: 'Thời hạn của cơ quan có thẩm quyền — để tham khảo',
        organization: 'Thời hạn của tổ chức',
        not_stated: 'Văn bản không nêu đây là thời hạn của bên nào',
        provisional: 'Ngày tạm tính — sự kiện bắt đầu tính thời hạn còn chờ xác nhận pháp lý',
        passed: 'Đã qua thời hạn — chưa ghi nhận kết quả',
        counted_on: 'Tính theo: {calendar}',
        no_date: 'Chưa có ngày — {reason}',
        guidance: 'Lưu ý: {precaution}',
        counts_from: '{event} ngày {date}',
        counts_from_not_evidenced: '{event} — chưa ghi nhận',
        declared: '{count} {unit}',
        trigger: {
            application_received: 'Cơ quan tiếp nhận hồ sơ đề nghị',
            complete_dossier_received: 'Cơ quan tiếp nhận đầy đủ hồ sơ hợp lệ',
            replacement_request_received: 'Cơ quan tiếp nhận văn bản đề nghị',
            deficiency_notice_received: 'Tổ chức nhận văn bản thông báo bổ sung, hoàn thiện hồ sơ',
            revocation_decision_received: 'Tổ chức nhận quyết định thu hồi',
            not_stated: 'Văn bản không nêu thời điểm bắt đầu',
        },
        reason: {
            owner_not_stated: 'văn bản không nêu đây là thời hạn của bên nào',
            governing_text_not_determined: 'chưa ghi nhận ngày cơ quan tiếp nhận hồ sơ',
            trigger_not_evidenced: 'chưa ghi nhận sự kiện bắt đầu tính thời hạn',
            uncomputable_period: 'không tính được thời hạn như đã khai báo',
            statutory_calendar_not_declared: 'chưa nêu lịch ngày làm việc của cơ quan nhà nước',
            statutory_calendar_unavailable: 'chưa cài đặt lịch ngày làm việc của cơ quan nhà nước',
            statutory_calendar_not_covering: 'chưa cài đặt lịch ngày làm việc của cơ quan nhà nước cho năm đó',
        },
        unit: {
            working_days: 'ngày làm việc',
            calendar_days: 'ngày theo lịch',
            days: 'ngày',
        },
    },
    receipt: {
        no_deficiency_notice: 'Không có thông báo bổ sung, hoàn thiện hồ sơ — hồ sơ đã đầy đủ khi được tiếp nhận',
        no_deficiency_hint: 'Ghi nhận hồ sơ đầy đủ là hồ sơ được tiếp nhận ngày {date}, ngày cơ quan tiếp nhận hồ sơ đề nghị. Chỉ chọn khi cơ quan không gửi thông báo bổ sung, hoàn thiện hồ sơ.',
        no_deficiency_blocked: 'Hồ sơ đề nghị này đã ghi nhận thông báo bổ sung, hoàn thiện hồ sơ, nên không có lựa chọn này.',
        no_deficiency_needs_receipt: 'Hãy ghi nhận ngày cơ quan tiếp nhận hồ sơ đề nghị trước.',
        no_deficiency_date_mismatch: 'Với lựa chọn này, ngày ghi nhận là ngày cơ quan tiếp nhận hồ sơ đề nghị.',
    },
    modals: {
        certificate_title_create: 'Ghi nhận Giấy chứng nhận đang có',
        certificate_title_correct: 'Đính chính Giấy chứng nhận',
        certificate_intro: 'Nhập thông tin đúng như ghi trên Giấy chứng nhận. ComplianceOne ghi nhận thông tin bạn nhập và không xác minh thông tin này.',
        kind: 'Giấy chứng nhận',
        services: 'Dịch vụ được chứng nhận',
        services_hint: 'Các dịch vụ ghi trên Giấy chứng nhận, theo tên gọi của khung tuân thủ đã cài đặt.',
        forms_held_legend: 'Hình thức lưu giữ',
        issuing_authority_hint: 'Đúng như tên ghi trên Giấy chứng nhận.',
        notes: 'Ghi chú',
        document_title: 'Thêm tài liệu',
        document_kind: 'Loại tài liệu',
        document_file: 'Tệp',
        document_file_required: 'Hãy chọn một tệp.',
        revocation_title: 'Ghi nhận việc thu hồi',
        revocation_intro: 'Ghi nhận quyết định thu hồi đúng như văn bản của cơ quan có thẩm quyền. Khi ghi nhận, Giấy chứng nhận được đánh dấu đã bị thu hồi và một nhiệm vụ nộp lại Giấy chứng nhận được tạo.',
        revocation_communication: 'Quyết định đã được ghi nhận trong mục văn bản',
        voluntary_legend: 'Theo đề nghị của tổ chức',
        voluntary_requested: 'Tổ chức của bạn đã đề nghị thu hồi',
        voluntary_requested_on: 'Ngày đề nghị',
        voluntary_note: 'Ghi chú về đề nghị',
        return_title: 'Ghi nhận việc nộp lại Giấy chứng nhận',
        return_note: 'Ghi chú',
        publication_title: 'Ghi nhận công bố đã thấy',
        publication_reference_hint: 'Nơi tổ chức của bạn thấy việc thu hồi được công bố, như cách bạn sẽ trích dẫn.',
        application_title: 'Tạo hồ sơ đề nghị',
        application_intro: 'Hồ sơ đề nghị là bản ghi của tổ chức bạn về những gì tổ chức nộp và cách cơ quan có thẩm quyền xử lý.',
        application_kind_legend: 'Nội dung đề nghị',
        replaces: 'Giấy chứng nhận được thay thế',
        replaces_hint: 'Một Giấy chứng nhận hiện hành.',
        replaces_none: 'Chưa ghi nhận Giấy chứng nhận hiện hành nào.',
        grounds_legend: 'Căn cứ',
        grounds_hint: 'Mỗi căn cứ được hiển thị cùng các văn bản áp dụng có nêu căn cứ đó.',
        ground_option: '{ground} — {windows}',
        reference: 'Số tham chiếu hồ sơ',
        reference_hint: 'Số tham chiếu của tổ chức bạn, hoặc của cơ quan có thẩm quyền khi được cấp.',
        application_edit_title: 'Chỉnh sửa hồ sơ đề nghị',
        part_title: 'Ghi nhận thành phần: {part}',
        part_state_legend: 'Tình trạng',
        part_form: 'Biểu mẫu đã điền',
        part_form_hint: 'Một biểu mẫu tổ chức của bạn đã điền.',
        part_owner: 'Người phụ trách',
        part_internal_due: 'Ngày nội bộ của tổ chức bạn',
        part_internal_due_hint: 'Ngày nội bộ do tổ chức của bạn đặt. ComplianceOne không đề xuất ngày nào.',
        part_note: 'Ghi chú',
        conditions_title: 'Liên kết hồ sơ nộp: {condition}',
        conditions_intro: 'Liên kết các hồ sơ nộp đánh giá tác động của tổ chức bạn. Mỗi hồ sơ hiển thị kết quả đúng như đã ghi nhận; ComplianceOne không coi kết quả nào là đáp ứng điều kiện.',
        conditions_filings: 'Hồ sơ nộp',
        conditions_none_offered: 'Chưa ghi nhận hồ sơ nộp nào thuộc loại mà điều kiện này nêu.',
        filing_title: 'Ghi nhận việc nộp hồ sơ',
        filing_channel: 'Hình thức nộp',
        filing_reviewed_by: 'Người rà soát',
        filing_signed_by: 'Người ký (họ tên)',
        filing_signed_on: 'Ngày ký',
        request_title: 'Ghi nhận thông báo bổ sung, hoàn thiện hồ sơ',
        request_intro: 'Ghi nhận thông báo đúng như văn bản của cơ quan có thẩm quyền. Thời hạn bổ sung, hoàn thiện hồ sơ của tổ chức bạn là thời hạn mà văn bản thông báo nêu.',
        request_communication: 'Thông báo đã được ghi nhận trong mục văn bản',
        request_communication_hint: 'Hãy ghi nhận thông báo trong mục Văn bản của cơ quan có thẩm quyền trước, với loại Yêu cầu bổ sung hồ sơ.',
        request_items_legend: 'Nội dung văn bản yêu cầu bổ sung',
        request_item: 'Nội dung {n}',
        request_item_owner: 'Người phụ trách nội dung {n}',
        request_add_item: 'Thêm nội dung',
        request_remove_item: 'Xóa nội dung {n}',
        request_item_added: 'Đã thêm nội dung {n}.',
        request_item_removed: 'Đã xóa nội dung {n}.',
        request_items_required: 'Hãy thêm ít nhất một nội dung.',
        request_period: 'Thời hạn bổ sung, hoàn thiện hồ sơ theo văn bản thông báo',
        request_period_hint: 'Ghi nguyên văn thời hạn mà văn bản thông báo nêu, kèm thời điểm bắt đầu nếu văn bản có nêu. Thời hạn bổ sung, hoàn thiện hồ sơ của tổ chức bạn là thời hạn mà văn bản thông báo nêu, do người dùng ghi nhận. ComplianceOne không tính thời hạn bổ sung nào.',
        request_deadline: 'Ngày mà văn bản thông báo nêu',
        request_deadline_hint: 'Chỉ nhập khi văn bản thông báo có nêu ngày, đúng như văn bản ghi.',
        sent_title: 'Ghi nhận đã gửi hồ sơ bổ sung',
        sent_request: 'Thông báo bổ sung, hoàn thiện hồ sơ',
        sent_note: 'Ghi chú',
        evaluation_title: 'Ghi nhận kết quả đánh giá hồ sơ',
        evaluation_intro: 'Kết quả đánh giá hồ sơ không phải là quyết định. Hãy ghi nhận quyết định riêng khi cơ quan có thẩm quyền ban hành.',
        evaluation_option: 'Kết quả đánh giá hồ sơ',
        evaluation_options_none: 'Chưa xác định văn bản áp dụng nên chưa có lựa chọn kết quả đánh giá. Hãy ghi nhận ngày cơ quan tiếp nhận hồ sơ đề nghị trước.',
        evaluation_communication: 'Văn bản đã được ghi nhận trong mục văn bản',
        decision_title: 'Ghi nhận quyết định',
        decision_outcome_legend: 'Quyết định của cơ quan có thẩm quyền',
        decision_number: 'Số quyết định',
        decision_reasons: 'Lý do theo văn bản',
        decision_reasons_hint: 'Ghi nguyên văn lý do mà quyết định nêu.',
        decision_certificate_legend: 'Giấy chứng nhận được cấp theo quyết định',
        decision_communication: 'Quyết định đã được ghi nhận trong mục văn bản',
        withdraw_title: 'Rút hồ sơ đề nghị',
        withdraw_on: 'Ngày rút',
        withdraw_reason: 'Lý do',
        communication_pick_empty: 'Bản ghi này chưa ghi nhận văn bản nào.',
        communication_pick_failed: 'Không đọc được danh sách văn bản.',
        communication_pick_manual_label: 'Mã văn bản',
        communication_pick_manual_hint: 'Hãy chọn từ danh sách khi danh sách đọc được.',
        communication_pick_off_list: 'Một văn bản không có ở trang đầu ({value})',
        communication_pick_retry_subject: 'danh sách văn bản',
        communication_option: '{kind}, nhận ngày {date}',
        filing_pick_empty: 'Chưa ghi nhận hồ sơ nộp nào.',
        filing_pick_failed: 'Không đọc được danh sách hồ sơ nộp.',
        filing_pick_manual_label: 'Mã hồ sơ nộp',
        filing_pick_manual_hint: 'Hãy chọn từ danh sách khi danh sách đọc được.',
        filing_pick_off_list: 'Một hồ sơ nộp không có ở trang đầu ({value})',
        filing_pick_retry_subject: 'danh sách hồ sơ nộp',
        form_pick_empty: 'Chưa ghi nhận biểu mẫu đã điền nào.',
        form_pick_failed: 'Không đọc được danh sách biểu mẫu đã điền.',
        form_pick_manual_label: 'Mã biểu mẫu đã điền',
        form_pick_manual_hint: 'Hãy chọn từ danh sách khi danh sách đọc được.',
        form_pick_off_list: 'Một biểu mẫu không có ở trang đầu ({value})',
        form_pick_retry_subject: 'danh sách biểu mẫu đã điền',
    },
    errors: {
        kind_not_declared: 'Khung tuân thủ đã cài đặt không khai báo Giấy chứng nhận này.',
        service_not_declared: 'Có dịch vụ được chọn không thuộc danh mục của khung tuân thủ đã cài đặt.',
        owner_not_in_organisation: 'Người này không phải thành viên của tổ chức bạn.',
        document_kind_invalid: 'Hãy chọn loại tài liệu.',
        already_revoked: 'Đã ghi nhận việc thu hồi. Hãy đính chính mục đó.',
        not_revoked: 'Giấy chứng nhận chưa được ghi nhận là đã bị thu hồi.',
        ground_not_declared: 'Căn cứ được chọn không thuộc các căn cứ khung tuân thủ đã cài đặt khai báo.',
        communication_invalid: 'Hãy chọn một văn bản đúng loại đã được ghi nhận trong bản ghi này.',
        replaces_invalid: 'Hãy chọn một Giấy chứng nhận hiện hành.',
        application_decided: 'Hồ sơ đề nghị này đã ghi nhận quyết định.',
        application_not_preparing: 'Hồ sơ đề nghị đã được nộp.',
        application_not_filed: 'Hồ sơ đề nghị chưa được nộp.',
        part_not_declared: 'Thành phần này không được văn bản áp dụng nào khai báo.',
        part_exception_not_declared: 'Thành phần này không có trường hợp không phải nộp.',
        form_instance_invalid: 'Hãy chọn một biểu mẫu đã điền đúng mẫu của thành phần này.',
        condition_link_not_declared: 'Điều kiện này không được khai báo.',
        filing_invalid: 'Hãy chọn hồ sơ nộp thuộc loại mà điều kiện này nêu.',
        channel_not_declared: 'Hãy chọn một hình thức nộp mà khung tuân thủ đã cài đặt khai báo.',
        supplement_request_not_found: 'Hãy chọn một thông báo bổ sung, hoàn thiện hồ sơ đã ghi nhận.',
        evaluation_not_declared: 'Văn bản áp dụng không nêu bước đánh giá hồ sơ cho thủ tục này.',
        governing_text_not_determined: 'Hãy ghi nhận ngày cơ quan tiếp nhận hồ sơ đề nghị trước.',
        evaluation_option_not_offered: 'Hãy chọn một lựa chọn mà văn bản áp dụng nêu.',
        replaced_certificate_not_current: 'Giấy chứng nhận được thay thế không còn là Giấy chứng nhận hiện hành.',
        decision_reasons_required: 'Hãy ghi nguyên văn lý do mà quyết định nêu.',
        decision_certificate_required: 'Hãy nhập thông tin Giấy chứng nhận được cấp theo quyết định.',
        not_offered: 'Sổ theo dõi này không được cung cấp cho tổ chức của bạn.',
    },
};
/** Sprint 62.57.0 Slice E (§12.13.9) — bản ghi về Giấy chứng nhận của nhà cung cấp. */
export const viVendorServiceCertificate = {
    action: 'Dịch vụ xử lý dữ liệu cá nhân và Giấy chứng nhận',
    action_label: 'Dịch vụ xử lý dữ liệu cá nhân và Giấy chứng nhận: {vendor}',
    title: '{vendor}: dịch vụ xử lý dữ liệu cá nhân và Giấy chứng nhận',
    loading: 'Đang tải…',
    load_failed: 'Không đọc được bản ghi.',
    retry: 'Thử lại',
    retry_subject: 'bản ghi về nhà cung cấp',
    reloading: 'Đang đọc lại…',
    basis_heading: 'Lý do tổ chức của bạn có thể ghi nhận thông tin này',
    statement_legend: 'Dịch vụ xử lý dữ liệu cá nhân nhà cung cấp này thực hiện thay mặt tổ chức của bạn',
    state_selected: 'Có cung cấp các dịch vụ này',
    state_none: 'Không cung cấp dịch vụ nào trong số này',
    state_unknown: 'Chưa rõ',
    not_recorded: 'Chưa ghi nhận',
    current_statement: 'Thông tin hiện tại: {state}',
    stated_by: 'Do {person} ghi nhận ngày {date}',
    activities_legend: 'Dịch vụ',
    activities_required: 'Hãy chọn ít nhất một dịch vụ.',
    statement_note: 'Ghi chú',
    save_statement: 'Lưu thông tin',
    statement_saved: 'Đã lưu thông tin.',
    certificate_heading: 'Giấy chứng nhận của nhà cung cấp, theo thông tin nhà cung cấp nêu hoặc xuất trình',
    record_entry: 'Ghi nhận thông tin nhà cung cấp',
    correct_entry: 'Sửa mục này',
    correct_entry_label: 'Sửa mục ngày {date}',
    entry_saved: 'Đã ghi nhận mục này.',
    entries_none: 'Chưa ghi nhận thông tin nào về Giấy chứng nhận của nhà cung cấp.',
    entries_heading: 'Các mục đã ghi nhận',
    entries_pagination: 'Trang các mục đã ghi nhận',
    entries_failed: 'Không đọc được các mục đã ghi nhận.',
    entries_retry_subject: 'các mục đã ghi nhận',
    position_legend: 'Thông tin nhà cung cấp nêu',
    position: {
        holds: 'Có Giấy chứng nhận',
        applying: 'Đang đề nghị cấp',
        does_not_hold: 'Không có',
        declined_to_state: 'Từ chối cung cấp thông tin',
    },
    source: 'Hình thức nhà cung cấp cung cấp thông tin',
    sources: {
        written_statement: 'Văn bản xác nhận',
        document_shown: 'Tài liệu được xuất trình',
        contract_clause: 'Điều khoản hợp đồng',
        other: 'Khác',
    },
    stated_on: 'Ngày cung cấp thông tin',
    number: 'Số Giấy chứng nhận theo thông tin được cung cấp',
    issuing_body: 'Cơ quan cấp theo thông tin được cung cấp',
    issue_date: 'Ngày cấp theo thông tin được cung cấp',
    services_covered: 'Dịch vụ được chứng nhận theo thông tin được cung cấp',
    services_text: 'Dịch vụ theo cách nhà cung cấp ghi',
    services_provided: 'Dịch vụ thực hiện thay mặt tổ chức của bạn, theo thông tin đã ghi nhận',
    states_end_date: 'Giấy chứng nhận có ghi thời hạn kết thúc',
    end_date: 'Ngày kết thúc theo thông tin được cung cấp',
    end_date_note: 'ComplianceOne không tính toán gì từ ngày này.',
    declared_authority: 'Cơ quan cấp theo khung tuân thủ đã cài đặt',
    note: 'Ghi chú',
    correction_note: 'Lý do sửa mục này',
    correction_note_required: 'Hãy nêu lý do sửa mục này.',
    corrects: 'Sửa mục ngày {date}',
    corrected: 'Đã được sửa bởi một mục sau',
    entry_line: 'Do {name} ghi nhận theo thông tin nhà cung cấp nêu hoặc xuất trình — ComplianceOne không xác minh thông tin này',
    services_not_stated: 'Hãy ghi nhận trước các dịch vụ trong danh mục mà nhà cung cấp thực hiện thay mặt tổ chức của bạn',
    part_hidden: 'Thông tin về Giấy chứng nhận của nhà cung cấp được ghi nhận tại đây khi thông tin trên nêu một dịch vụ trong danh mục.',
    error_required: 'Vui lòng nhập nội dung này.',
    error_future: 'Ngày này muộn hơn ngày hôm nay.',
    error_end_before_issue: 'Ngày kết thúc sớm hơn ngày cấp.',
    conflict: 'Bản ghi này đã được người khác thay đổi sau khi bạn mở. Bản ghi đã được đọc lại và nội dung bạn nhập vẫn được giữ: hãy xem lại và lưu lần nữa.',
    save_failed: 'Chưa ghi nhận được nội dung này.',
    evidence: 'Tài liệu chứng minh: {count}',
    evidence_legend: 'Tài liệu chứng minh',
    evidence_hint: 'Các tệp nhà cung cấp gửi hoặc xuất trình, được tải lên cho nhà cung cấp này. ComplianceOne lưu giữ tệp như khi được tải lên và không đọc nội dung tệp.',
    evidence_none: 'Chưa có tệp nào.',
    evidence_upload: 'Tải lên tệp',
    evidence_remove: 'Gỡ bỏ',
    evidence_remove_label: 'Gỡ bỏ {filename}',
    evidence_remove_failed: 'Không gỡ bỏ được tệp.',
    evidence_limit: 'Chỉ thêm được tối đa {max} tệp.',
    evidence_not_held: 'Tệp này không còn được lưu giữ.',
    evidence_not_held_error: 'Có tệp không còn được lưu giữ cho nhà cung cấp này. Hãy gỡ bỏ tệp đó và tải lên lại.',
    evidence_download_label: 'Tải xuống {filename}',
    close: 'Đóng',
};
/**
 * Sprint 62.58.0 (Slice N) — đối tượng tiếp nhận và hình thức của thông báo về
 * bảo vệ dữ liệu cá nhân, bảng kê phân quyền truy cập dữ liệu theo từng triển
 * khai, và hai phần mới trong tệp xuất hoạt động xử lý dữ liệu cá nhân. R-44:
 * batch `2026-11-02-62.58.0-pr3-deployment-privacy`.
 */
export const viDeploymentPrivacy = {
    audience_kinds: {
        public: 'Công chúng',
        data_subject_group: 'Các chủ thể dữ liệu được xác định',
    },
    channels: {
        website: 'Trang thông tin điện tử',
        point_of_collection: 'Tại nơi thu thập dữ liệu',
        employment_documentation: 'Hồ sơ, tài liệu lao động',
        contract_or_terms: 'Hợp đồng hoặc điều khoản',
        in_app: 'Trong ứng dụng',
        email: 'Thư điện tử',
        other: 'Khác',
    },
    access_kinds: {
        view: 'Xem',
        extract: 'Trích xuất',
        export: 'Xuất',
        delete: 'Xóa',
        configure: 'Cấu hình',
    },
    export: {
        coverage_heading: 'Thông báo về bảo vệ dữ liệu cá nhân theo chủ thể dữ liệu',
        coverage_withheld: 'Chi tiết thông báo chỉ hiển thị cho người được quyền xem sổ đăng ký thông báo về bảo vệ dữ liệu cá nhân.',
        col_activity: 'Hoạt động xử lý dữ liệu cá nhân',
        col_data_subject: 'Chủ thể dữ liệu',
        col_notice: 'Thông báo',
        col_revision: 'Phiên bản',
        col_channels: 'Hình thức',
        col_effective_from: 'Có hiệu lực từ',
        col_status: 'Trạng thái',
        not_recorded: 'Chưa ghi nhận',
        no_current_notice: 'Không có thông báo hiện hành: mọi thông báo nêu chủ thể dữ liệu này đều ở trạng thái đã gỡ bỏ hoặc đã được thay thế, hoặc đã có phiên bản mới hơn thay thế',
        status_replaced: '{status}, đã có phiên bản mới hơn thay thế',
        not_stated: 'Chưa nêu',
        access_heading: 'Những ai được truy cập dữ liệu',
        col_version: 'Phiên bản',
        col_reviewed_on: 'Ngày rà soát',
        col_role_or_position: 'Vai trò hoặc vị trí',
        col_access_kinds: 'Thao tác được phép',
        col_means: 'Cách thức truy cập',
        col_conditions: 'Điều kiện kèm theo',
        no_access_statement: 'Chưa ghi nhận bảng kê phân quyền truy cập dữ liệu',
        no_data_subjects: 'Chưa ghi nhận chủ thể dữ liệu nào',
        public_linked: 'Cũng được liên kết: {count} thông báo công khai, không nêu chủ thể dữ liệu nào',
        public_linked_one: 'Cũng được liên kết: 1 thông báo công khai, không nêu chủ thể dữ liệu nào',
        coverage_truncated: 'Một số hoạt động xử lý dữ liệu cá nhân có nhiều thông báo được liên kết hơn số mà bản xuất này liệt kê, nên với các hoạt động đó, thông báo đã công bố được liệt kê trước, rồi đến thông báo đã phê duyệt, bản nháp, thông báo đã gỡ bỏ và thông báo đã được thay thế; trong mỗi nhóm, thông báo được soạn gần đây nhất ở trước. Các hoạt động xử lý dữ liệu cá nhân liên quan: {activities}. Một chủ thể dữ liệu chỉ được ghi "Chưa ghi nhận" khi không có thông báo được liên kết nào nêu chủ thể đó.',
        access_withheld: 'Các bảng kê phân quyền truy cập dữ liệu chỉ hiển thị cho người được quyền xem hoạt động xử lý dữ liệu cá nhân.',
        col_approved_by: 'Người phê duyệt',
        col_systems: 'Hệ thống',
    },
    notice_form: {
        heading: 'Đối tượng tiếp nhận, hình thức và phạm vi triển khai',
        intro: 'Nêu phiên bản này được cung cấp cho ai, bằng hình thức nào, có hiệu lực từ ngày nào, và cho những hoạt động xử lý dữ liệu cá nhân và hệ thống nào.',
        audience_label: 'Đối tượng tiếp nhận thông báo',
        audience_public_hint: 'Bất kỳ ai cũng có thể đọc, ví dụ trên trang thông tin điện tử của tổ chức. Thông báo công khai không nêu chủ thể dữ liệu nào, nên phần thông báo của một hoạt động xử lý dữ liệu cá nhân không liệt kê thông báo này cho chủ thể dữ liệu nào.',
        audience_group_hint: 'Các chủ thể dữ liệu được xác định của những hoạt động xử lý dữ liệu cá nhân được liên kết, như người lái xe, hành khách hoặc người lao động.',
        audience_group_need_grant: 'Để nêu các chủ thể dữ liệu được xác định, cần quyền xem hoạt động xử lý dữ liệu cá nhân mà vai trò của bạn không có: chủ thể dữ liệu được chọn từ những hoạt động xử lý dữ liệu cá nhân được liên kết. Bạn vẫn có thể cung cấp thông báo cho công chúng.',
        data_subjects_label: 'Chủ thể dữ liệu được cung cấp thông báo',
        data_subjects_hint: 'Chọn từ các chủ thể dữ liệu được ghi nhận trên những hoạt động xử lý dữ liệu cá nhân được liên kết.',
        data_subjects_need_activity: 'Hãy liên kết ít nhất một hoạt động xử lý dữ liệu cá nhân trước: chủ thể dữ liệu được chọn từ những chủ thể mà hoạt động đó ghi nhận.',
        data_subjects_none: 'Những hoạt động xử lý dữ liệu cá nhân được liên kết chưa ghi nhận chủ thể dữ liệu nào.',
        data_subjects_required: 'Hãy chọn ít nhất một chủ thể dữ liệu.',
        data_subjects_unknown: '{value} (không được ghi nhận trên những hoạt động xử lý dữ liệu cá nhân được liên kết)',
        channels_label: 'Hình thức cung cấp thông báo cho đối tượng tiếp nhận',
        channels_hint: 'Chọn ít nhất một hình thức. Nhãn dán hoặc màn hình tại nơi thu thập dữ liệu, ví dụ trong xe, thuộc hình thức "Tại nơi thu thập dữ liệu".',
        channels_required: 'Hãy chọn ít nhất một hình thức.',
        channel_note_label: 'Mô tả hình thức khác',
        channel_note_required: 'Hãy mô tả hình thức khác.',
        effective_from_label: 'Có hiệu lực từ',
        effective_from_hint: 'Ngày thông báo có hiệu lực, theo xác định của tổ chức. Để trống nếu chưa xác định được ngày.',
        activities_label: 'Hoạt động xử lý dữ liệu cá nhân được cung cấp thông báo',
        activities_hint: 'Thông báo chỉ có thể nêu các chủ thể dữ liệu của những hoạt động xử lý dữ liệu cá nhân này.',
        systems_label: 'Hệ thống trong bản đồ dữ liệu thuộc phạm vi thông báo',
        links_need_grant: 'Để liên kết thông báo với hoạt động xử lý dữ liệu cá nhân hoặc hệ thống, cần quyền xem hoạt động xử lý dữ liệu cá nhân mà vai trò của bạn không có. Bạn vẫn có thể soạn thông báo mà không liên kết.',
        systems_need_grant: 'Để chọn hệ thống trong bản đồ dữ liệu, cần quyền xem hệ thống mà vai trò của bạn không có.',
        picker_loading: 'Đang tải danh sách…',
        activities_empty: 'Chưa ghi nhận hoạt động xử lý dữ liệu cá nhân nào.',
        systems_empty: 'Chưa ghi nhận hệ thống nào trong bản đồ dữ liệu.',
        activities_failed: 'Không đọc được danh sách hoạt động xử lý dữ liệu cá nhân.',
        systems_failed: 'Không đọc được danh sách hệ thống trong bản đồ dữ liệu.',
        activities_manual_label: 'Mã hoạt động xử lý dữ liệu cá nhân',
        systems_manual_label: 'Mã hệ thống trong bản đồ dữ liệu',
        manual_hint: 'Nhập các mã, phân tách bằng dấu phẩy; các mã được kiểm tra khi bạn lưu.',
        off_list: '{value} (không có trong danh sách đã đọc)',
        retry: 'Tải lại',
        reloading: 'Đang tải lại…',
        activities_retry_subject: 'danh sách hoạt động xử lý dữ liệu cá nhân',
        systems_retry_subject: 'danh sách hệ thống trong bản đồ dữ liệu',
        error_link_grant: 'Vai trò của bạn không được liên kết thông báo với hoạt động xử lý dữ liệu cá nhân hoặc hệ thống. Hãy bỏ các liên kết, hoặc đề nghị cấp quyền xem hoạt động xử lý dữ liệu cá nhân.',
        error_link_not_found: 'Không tìm thấy một hoạt động xử lý dữ liệu cá nhân hoặc hệ thống được liên kết trong tổ chức này. Nội dung bạn đã nhập vẫn được giữ.',
        error_audience_invalid: 'Một chủ thể dữ liệu đã chọn không được ghi nhận trên những hoạt động xử lý dữ liệu cá nhân được liên kết. Nội dung bạn đã nhập vẫn được giữ.',
        col_audience: 'Đối tượng tiếp nhận',
        col_channels: 'Hình thức',
        col_effective_from: 'Có hiệu lực từ',
        col_links: 'Liên kết với',
        links_activities: 'Hoạt động xử lý dữ liệu cá nhân: {names}',
        links_activities_count: '{count} hoạt động xử lý dữ liệu cá nhân',
        links_activities_count_one: '1 hoạt động xử lý dữ liệu cá nhân',
        links_systems: 'Hệ thống: {names}',
        links_systems_count: '{count} hệ thống',
        links_systems_count_one: '1 hệ thống',
        links_none: 'Không có',
        other_channel: 'Khác: {note}',
        data_subjects_unconfirmed: '{value} (chưa xác nhận được: không đọc được một hoạt động xử lý dữ liệu cá nhân được liên kết)',
        error_systems_grant: 'Vai trò của bạn không được liên kết thông báo với hệ thống trong bản đồ dữ liệu. Hãy bỏ các hệ thống, hoặc đề nghị cấp quyền xem hệ thống.',
        audience_with_subjects: '{label}: {subjects}',
    },
    coverage: {
        heading: 'Thông báo về bảo vệ dữ liệu cá nhân',
        intro: 'Với từng chủ thể dữ liệu được ghi nhận trên hoạt động này, các thông báo về bảo vệ dữ liệu cá nhân được liên kết có nêu chủ thể đó, kèm phiên bản, hình thức, ngày có hiệu lực và trạng thái. Phần này không đếm và không chấm điểm.',
        not_visible: 'Vai trò của bạn không được xem sổ đăng ký thông báo về bảo vệ dữ liệu cá nhân, nên các thông báo không hiển thị ở đây. Điều này không cho biết có hay không có thông báo.',
        no_subjects: 'Hoạt động này chưa ghi nhận chủ thể dữ liệu nào.',
        list_label: 'Thông báo theo chủ thể dữ liệu',
        notice_line: '{title}, phiên bản {revision}',
        notice_meta: 'Hình thức: {channels} · Có hiệu lực từ {date} · Trạng thái: {status}',
        load_failed: 'Không đọc được các thông báo.',
        retry_subject: 'các thông báo của hoạt động này',
        pagination: 'Các trang chủ thể dữ liệu',
        public_heading: 'Cũng được liên kết: thông báo công khai',
        public_intro: 'Các thông báo này dành cho công chúng. Thông báo công khai không nêu chủ thể dữ liệu nào, nên không được liệt kê cho chủ thể dữ liệu nào ở trên.',
        public_failed: 'Không đọc được các thông báo công khai được liên kết với hoạt động này.',
        public_retry_subject: 'các thông báo công khai',
        truncated: 'Số thông báo được liên kết với hoạt động này nhiều hơn số được liệt kê ở đây, nên thông báo đã công bố được liệt kê trước, rồi đến thông báo đã phê duyệt, bản nháp, thông báo đã gỡ bỏ và thông báo đã được thay thế; trong mỗi nhóm, thông báo được soạn gần đây nhất ở trước. Một chủ thể dữ liệu chỉ được ghi "Chưa ghi nhận" khi không có thông báo được liên kết nào nêu chủ thể đó.',
        subject_state: '{subject}: {state}',
    },
    access: {
        intro: 'Những ai được xem, trích xuất, xuất, xóa hoặc cấu hình dữ liệu của hoạt động này, bằng cách thức nào và với điều kiện kèm theo nào. Mọi phiên bản đều được lưu giữ; phiên bản mới nhất ở trên cùng.',
        record: 'Ghi nhận phiên bản mới',
        record_first: 'Ghi nhận bảng kê phân quyền truy cập dữ liệu',
        current: 'Hiện hành',
        version: 'Phiên bản {version}',
        reviewed: 'Ngày rà soát: {date}',
        approved: 'Người phê duyệt: {name}',
        not_approved: 'Chưa ghi nhận người phê duyệt',
        recorded: 'Do {name} ghi nhận ngày {date}',
        systems: 'Hệ thống: {names}',
        entries_label: 'Các mục của phiên bản {version}',
        evidence_count: 'Tài liệu: {count}',
        evidence_none: 'Chưa đính kèm tài liệu',
        evidence_open: 'Tài liệu',
        evidence_open_aria: 'Tài liệu của phiên bản {version}',
        load_failed: 'Không đọc được bảng kê phân quyền truy cập dữ liệu.',
        retry_subject: 'bảng kê phân quyền truy cập dữ liệu',
        pagination: 'Các trang phiên bản của bảng kê phân quyền truy cập dữ liệu',
        history_label: 'Các phiên bản của bảng kê phân quyền truy cập dữ liệu',
        unknown_person: 'Người không xác định',
    },
    access_form: {
        title: 'Ghi nhận phiên bản mới của bảng kê phân quyền truy cập dữ liệu',
        replaces: 'Phiên bản này thay thế phiên bản {version}. Các phiên bản trước vẫn được lưu giữ.',
        first: 'Đây là phiên bản đầu tiên của bảng kê phân quyền truy cập dữ liệu cho hoạt động này.',
        entries_heading: 'Những ai được truy cập dữ liệu',
        entry_legend: 'Mục {n}',
        role_label: 'Vai trò hoặc vị trí',
        role_hint: 'Một vai trò hoặc vị trí, như "Nhân viên phụ trách an toàn đội xe". Không cần nêu tên người.',
        access_kinds_label: 'Thao tác được phép',
        means_label: 'Cách thức truy cập',
        means_hint: 'Ví dụ: tên phần mềm trích xuất, bảng điều khiển, hoặc yêu cầu gửi bộ phận công nghệ thông tin.',
        conditions_label: 'Điều kiện kèm theo',
        conditions_hint: 'Ví dụ: có phiếu yêu cầu, có hai người cùng thực hiện, hoặc nêu rõ mục đích.',
        add_entry: 'Thêm một mục',
        remove_entry: 'Xóa mục {n}',
        reviewed_on_label: 'Ngày rà soát',
        reviewed_on_hint: 'Ngày rà soát bảng kê. Không được sau ngày hôm nay.',
        approver_label: 'Người phê duyệt (không bắt buộc)',
        approver_none: 'Không có người phê duyệt',
        approver_retry_subject: 'danh sách người dùng',
        systems_label: 'Hệ thống trong bản đồ dữ liệu thuộc phạm vi bảng kê',
        systems_kept: 'Các hệ thống của phiên bản hiện hành được giữ nguyên, vì vai trò của bạn không được xem hệ thống trong bản đồ dữ liệu.',
        missing: 'Hãy nêu vai trò hoặc vị trí và chọn ít nhất một thao tác được phép cho mỗi mục, và nhập ngày rà soát.',
        submit: 'Ghi nhận phiên bản',
        saving: 'Đang ghi nhận…',
        recorded: 'Đã ghi nhận phiên bản.',
        record_failed: 'Không ghi nhận được phiên bản. Nội dung bạn đã nhập vẫn được giữ.',
        conflict: 'Trong lúc bạn soạn, một người khác đã ghi nhận phiên bản {version} của bảng kê này, nên phiên bản của bạn chưa được ghi nhận. Nội dung bạn đã nhập nay được cập nhật theo phiên bản {version}: các mục và hệ thống bạn không thay đổi nay theo phiên bản {version}, còn những gì bạn đã thêm, sửa hoặc bỏ vẫn được giữ nguyên. Hãy kiểm tra các mục rồi ghi nhận lại.',
        conflict_reread_failed: 'Trong lúc bạn soạn, một người khác đã ghi nhận phiên bản mới hơn của bảng kê này, nên phiên bản của bạn chưa được ghi nhận, và không đọc được phiên bản mới hơn đó. Nội dung bạn đã nhập vẫn được giữ; hãy ghi nhận lại để đọc lại phiên bản đó.',
    },
    access_files: {
        heading: 'Tài liệu',
        intro: 'Các tài liệu làm căn cứ cho phiên bản này, như chính sách truy cập, danh sách vai trò của phần mềm trích xuất hoặc văn bản phê duyệt đã ký.',
        slot: 'Tài liệu của phiên bản này',
        none: 'Chưa đính kèm tài liệu nào cho phiên bản này.',
        file_hash: 'Mã SHA-256: {hash}',
        not_held: 'Tài liệu không còn được lưu giữ',
        download: 'Tải xuống',
        remove: 'Gỡ bỏ',
        upload: 'Thêm tài liệu',
        bound: 'Đã thêm tài liệu.',
        removed: 'Đã gỡ bỏ tài liệu.',
        bind_failed: 'Không thể thêm tài liệu.',
        remove_failed: 'Không thể gỡ bỏ tài liệu.',
        not_owned: 'Không tìm thấy phiên bản mà tài liệu này thuộc về.',
        remove_confirm: 'Gỡ tài liệu này khỏi phiên bản và xóa tài liệu?',
        load_failed: 'Không tải được tài liệu.',
    },
};
//# sourceMappingURL=vi-extras.js.map